import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  Paper,
  Skeleton,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { createFilterOptions } from "@mui/material/Autocomplete";
import { alpha } from "@mui/material/styles";
import {
  Add,
  ArticleOutlined as ArticleOutlinedIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  DeleteOutline as DeleteOutlineIcon,
  DeleteSweep as DeleteSweepIcon,
  ErrorOutline as ErrorOutlineIcon,
  ExpandMore,
  FileDownload as FileDownloadIcon,
  FolderOpen as FolderOpenIcon,
  MergeType as MergeTypeIcon,
  PersonOutline,
  PlayArrow as PlayArrowIcon,
  ReadMore as ReadMoreIcon,
  Replay as ReplayIcon,
  Stop as StopIcon,
  Visibility,
  VisibilityOff,
  VpnKeyOutlined as VpnKeyOutlinedIcon,
  ImageSearch as ImageSearchIcon,
  InfoOutlined as InfoOutlinedIcon,
  UploadFile as UploadFileIcon,
  Business as BusinessIcon,
  Language as LanguageIcon,
  VerifiedUserOutlined as VerifiedUserIcon,
  WarningAmberOutlined as WarningAmberIcon,
  SmartToyOutlined as SmartToyIcon,
  Tune as TuneIcon,
  StraightenOutlined as StraightenIcon,
  GroupsOutlined as GroupsIcon,
  CheckCircleOutline as CheckCircleIcon,
  ErrorOutline as ErrorCircleIcon,
  Layers as LayersIcon,
  Replay as ReplayTagIcon,
  FolderZipOutlined as FolderZipIcon,
  UnarchiveOutlined as UnarchiveIcon,
  SendOutlined as SendIcon,
  ScheduleOutlined as ScheduleIcon,
  EventBusyOutlined as UnscheduleIcon,
  Remove as RemoveIcon,
  MoreVert as MoreVertIcon,
  RemoveCircleOutline as RemoveCircleOutlineIcon,
  HourglassEmptyOutlined as HourglassEmptyIcon,
} from "@mui/icons-material";
import {
  BotOptionSpec,
  BotNote,
  BotRegistryItem,
  DocumentTypeUse,
  MajorJob,
  MERGE_REPORT_KINDS,
  MergeFileReport,
  MergeReportKind,
  MergeReportsResponse,
  useCreateBackendUserBotConnectionMutation,
  useCreateMajorJobMutation,
  useCreateTasksBatchMutation,
  useDeleteAdminConnectionMutation,
  useDeleteMajorJobMutation,
  useGetMajorJobDeletePreviewQuery,
  useDeleteTaskJobMutation,
  useGetBotRegistryQuery,
  useGetMajorJobsQuery,
  useGetTaskJobsQuery,
  useTaskJobsBatchDeleteMutation,
  useAddTasksToJobMutation,
  useReduceJobTasksMutation,
  useRunTaskMutation,
  useRunTasksBatchMutation,
  useStopTaskMutation,
  useStopTasksBatchMutation,
  useSubmitTask2FACodeMutation,
  useUpdateAdminConnectionMutation,
  useUpdateAdminTaskMutation,
  useUpdateMajorJobMutation,
  useRefreshTaskJobStatsMutation,
  useUpdateTaskJobMutation,
  useLazyGetJobMatchesFileContentQuery,
  useDeleteJobMatchesFileMutation,
  usePresignJobMatchesMergeUploadMutation,
  useMergeJobMatchesReportsMutation,
  useLazyGetJobUnmatchedReviewQuery,
  useMarkJobUnmatchedReviewMutation,
  useLazyGetJobLogsArchiveQuery,
  useClearJobLogsMutation,
  useRunJobNewRowsMutation,
  useRunJobRosterTaskMutation,
  useLazyGetJobRosterQuery,
  useGetJobSftpExportQuery,
  useDeleteJobRosterMutation,
  useRecoverJobRosterMutation,
  usePresignJobArtifactUploadMutation,
  useReplaceJobMatchesMutation,
  useReplaceJobRosterMutation,
  useLazyGetJobSsnReadinessQuery,
  useLazyGetMajorJobSsnReadinessQuery,
  useRunJobMatchesPreflightMutation,
  useLazyGetMajorJobBrowserProfilesQuery,
  useDeleteMajorJobBrowserProfilesMutation,
  useLazyGetJobLogsInventoryQuery,
  useCreateMajorJobSsnJobMutation,
  useUpdateMajorJobSsnJobMutation,
  useDeleteMajorJobSsnJobMutation,
  useDeleteMajorJobSsnFileMutation,
  useLazyGetMajorJobSsnFileContentQuery,
  useLazyGetJobRosterDownloadUrlQuery,
  useLazyGetMajorJobSsnFileDownloadUrlQuery,
  useScheduleJobRunMutation,
  useScheduleTaskRunMutation,
  useCancelJobRunScheduleMutation,
  useCancelTaskRunScheduleMutation,
  adminUsersApi,
} from "features/adminUsers/adminUsers";
import type {
  BrowserProfilesInfo,
  MatchesPreflight,
  RosterState,
  JobRosterInfo,
  MatchesFileInfo,
  MatchesSummary,
  SsnReadiness,
  SsnSourceReader,
  ScheduledRunInfo,
  UnmatchedReview,
  UnmatchedReviewAction,
  UnmatchedReviewDetail,
} from "features/adminUsers/adminUsers";
import { useAppDispatch, useAppSelector } from "features/store";
import { botsManagementWs, BotsManagementWsEvent } from "features/ws/botsManagementWs";
import { ChangeEvent, Fragment, MouseEvent, ReactElement, ReactNode, SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import useAppNavigate from "hooks/useAppNavigate";
import useViewAs, { CLIENT_VIEW_READ_ONLY_TOOLTIP } from "hooks/useViewAs";
import { isBotsOperator } from "utils/roles";
import { AdminVendorConnection } from "types/interfaces/adminUsers";
import BotsTableScroll, { BOTS_HEADER_CELL_SX, BOTS_BODY_CELL_SX } from "components/botsManagement/BotsTableScroll";
import BotsFitLayout from "components/botsManagement/BotsFitLayout";
import DeleteJobConfirmDialog from "components/botsManagement/DeleteJobConfirmDialog";
import {
  BULK_DELETE_PHRASE,
  majorJobDeleteSummary,
  rosterKeptNotice,
  splitMajorJobDeleteSummary,
} from "components/botsManagement/deleteConfirm";
import { pushNotification } from "features/notificationSlice";
import DataStorageFolderPickerDialog from "components/botsManagement/DataStorageFolderPickerDialog";
import MergeReportsSlot, {
  EMPTY_MERGE_SLOT,
  MergeSlotState,
  mergeSlotTitle,
} from "components/botsManagement/MergeReportsSlot";
import EmployeeProgressBar from "components/botsManagement/EmployeeProgressBar";
import {
  editJobLockedReason,
  parseInFlightTasks,
} from "components/botsManagement/jobEditLock";
import { takeJobDeepLink } from "components/botsManagement/jobDeepLink";
import type { CountersSource } from "components/botsManagement/employeeCounters";
import {
  countersSource,
  taskCountersTooltip,
} from "components/botsManagement/employeeCounters";
import MetaPill, { type MetaTone } from "components/botsManagement/MetaPill";
import SoftTag from "components/botsManagement/SoftTag";
import StatusChipMj from "components/botsManagement/StatusChip";
import { BOT_KIND_OPTIONS, botKindMeta, isSplitKind } from "components/botsManagement/botKind";
import SplitJobsTab from "components/botsManagement/split/SplitJobsTab";
import { splitProvidersOf } from "components/botsManagement/split/splitJobUi";
import { useGetPdfToolsConfigQuery } from "features/pdfTools/pdfTools";
import {
  UnmatchedEmployeeStateChip,
  UnmatchedReviewActions,
  UnmatchedReviewConfirmBody,
  UnmatchedReviewFileLabel,
  UnmatchedReviewInstructions,
  UnmatchedReviewTags,
} from "components/botsManagement/UnmatchedReview";
import {
  describeReviewError,
  describeReviewOutcome,
  jobUnmatchedReview,
  matchesFileLabel,
  type RunHint,
  unmatchedKindForFilename,
  unmatchedReviewLegend,
} from "components/botsManagement/unmatchedReview";
import ConfirmDialog from "components/common/ConfirmDialog";
import { STRONG_DROPDOWN_PAPER_SX } from "components/common/dropdownPaper";
import { VIEWPORT_SHORT_MAX_MEDIA } from "config";
import ActionsMenu, {
  type ActionsMenuItem,
} from "components/botsManagement/ActionsMenu";
import BotsRecordTable, {
  type BotsColumn,
} from "components/botsManagement/BotsRecordTable";
import useResponsive from "hooks/useResponsive";
import MajorJobWorkspaceSkeleton from "components/botsManagement/MajorJobWorkspaceSkeleton";
import WorkspaceMetaList, {
  type WorkspaceMetaRow,
} from "components/botsManagement/WorkspaceMetaList";
import TaskLiveViewDialog from "components/botsManagement/TaskLiveViewDialog";
import MatchesPreflightAlert from "components/botsManagement/MatchesPreflightAlert";
import RosterStateAlert from "components/botsManagement/RosterStateAlert";
import SftpExportDialog from "components/botsManagement/SftpExportDialog";
import SsnUpdaterDialog from "components/botsManagement/SsnUpdaterDialog";
import RosterFileView from "components/botsManagement/RosterFileView";
import {
  SPLIT_JOB_EVENT,
  createCoalescer,
  routePanelWsEvent,
  splitEventConcerns,
} from "components/botsManagement/panelWsEvents";
import SsnFileView from "components/botsManagement/SsnFileView";
import SourceSsnFileField from "components/botsManagement/SourceSsnFileField";
import {
  describeSourceReaders,
  resolveSourceChoice,
  type SourceSsnChoice,
} from "components/botsManagement/sourceSsnChoice";
import ScheduleRunDialog from "components/botsManagement/ScheduleRunDialog";
import {
  scheduledRunTagLabel,
  scheduledRunTooltip,
} from "components/botsManagement/scheduledRuns";
import {
  EMPTY_SSN_FILE_MESSAGE,
  formatFileSize,
  type SsnFileInfo,
} from "components/botsManagement/ssnFile";
import AppDialog from "components/common/AppDialog";
import CustomScrollbars from "components/CustomScrollbars";
import {
  AutocompleteScrollListbox,
  autocompleteScrollPaperProps,
} from "components/common/AutocompleteScrollListbox";
import BotOptionFields from "components/botsManagement/BotOptionFields";
import BotOptionSummary, {
  botOptionTagText,
  malformedOptions,
  missingRequiredOptions,
  summarizeBotOptions,
} from "components/botsManagement/BotOptionSummary";
import LogsDialog from "components/botsManagement/LogsDialog";
import { LogsScope } from "components/botsManagement/LogsView";
import {
  isLiveViewReady,
  shouldAutoOpenLiveView,
} from "components/botsManagement/liveViewPrompt";
import { isHiddenJob, jobOrdinal, visibleJobs } from "components/botsManagement/jobOrdinals";
import SsnFileUploadDialog from "components/botsManagement/SsnFileUploadDialog";
import BotNoteView from "components/botsManagement/BotNoteView";
import {
  majorJobHoldReason,
  parseMajorJobHold,
  type MajorJobHold,
  parseRunHeldBy,
  rosterBuildReason,
  ROSTER_RUN_BLOCKED_REASON,
  runHeldReason,
  type RunHeldBy,
} from "components/botsManagement/runHold";
import {
  isTwoFaAutoPhase,
  isTwoFaCodeNeeded,
  isTwoFaCodeSubmitted,
} from "components/botsManagement/twoFaPrompt";
import { buildDataStorageFolderMappingSeed } from "utils/dataStorageNavigation";
import {
  OPTION_KEYS,
  applySkipErrorEmployees,
  encodeOptionJson,
  extractSkipErrorEmployees,
  keepOptions,
  readListOption,
} from "utils/additionalOptions";
import {
  ChipColor,
  botWorkStatusChipColor,
  taskRunStatusChipColor,
} from "utils/botStatusColors";

type ConnPlanEditorRow = {
  connection_id: number | "";
  /** Raw digit string while editing (may be ""); parsed in normalizePlans / bulk-add helpers. */
  task_target_count: string;
};

function connPlanTasksDigitsOnly(raw: string): string {
  return raw.replace(/\D/g, "").slice(0, 4);
}

/** API-ready connection plan row (numeric connection + positive task count). */
type NormalizedConnPlanRow = {
  connection_id: number;
  task_target_count: number;
};

/** Normalize connection plan editor rows for API PATCH (shared by create-job + edit dialog). */
function normalizePlans(rows: ConnPlanEditorRow[]): NormalizedConnPlanRow[] {
  return rows
    .map((r) => {
      const d = connPlanTasksDigitsOnly(r.task_target_count);
      const n = d === "" ? Number.NaN : Math.floor(Number(d));
      return {
        connection_id: r.connection_id,
        task_target_count: n,
      };
    })
    .filter(
      (r): r is { connection_id: number; task_target_count: number } =>
        typeof r.connection_id === "number" &&
        r.connection_id >= 1 &&
        Number.isFinite(r.task_target_count) &&
        r.task_target_count >= 1,
    )
    .map((r) => ({
      connection_id: r.connection_id,
      task_target_count: Math.min(1000, Math.max(1, r.task_target_count)),
    }));
}

const MAJOR_FORM_LISTBOX = { style: { maxHeight: 280 } };

const filterMajorFormVendors = createFilterOptions<string>();
const filterChildDialogBots = createFilterOptions<BotRegistryItem>({
  stringify: (option) =>
    `${option.vendor} ${option.bot_slug} ${option.job_kind} ${option.job_kind}.${option.bot_slug}`,
});

const MAX_BATCH_TASKS = 30;

// The Manage job dialog groups its actions into labeled tiles.
// The job's progress card: chips on the top line, the bar underneath.
const MJ_PROGRESS_CARD_SX = {
  width: "100%",
  alignSelf: "stretch",
  boxSizing: "border-box",
  px: 1.25,
  py: 1,
  mb: 1,
  borderRadius: 1.5,
  border: 1,
  borderColor: "divider",
  bgcolor: (theme: { palette: { mode: string } }) =>
    theme.palette.mode === "dark" ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.018)",
} as const;

const MJ_MANAGE_GROUP_SX = {
  border: "1px solid",
  borderColor: "divider",
  borderRadius: 1,
  p: 1.25,
} as const;

const MJ_MANAGE_GROUP_LABEL_SX = {
  display: "block",
  mb: 0.75,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  color: "text.secondary",
} as const;

function connectionIdsForPlanRow(
  pool: number[],
  rows: ConnPlanEditorRow[],
  rowIndex: number,
): number[] {
  const row = rows[rowIndex];
  const cur =
    typeof row?.connection_id === "number" && row.connection_id >= 1 ? row.connection_id : null;
  const taken = new Set(
    rows.flatMap((r, j) =>
      j !== rowIndex && typeof r.connection_id === "number" && r.connection_id >= 1 ? [r.connection_id] : [],
    ),
  );
  return pool.filter((cid) => !taken.has(cid) || cid === cur);
}

function nextUnusedPoolConnectionId(
  poolSortedIds: number[],
  rows: ConnPlanEditorRow[],
): number | undefined {
  const used = new Set(
    rows
      .map((r) => (typeof r.connection_id === "number" ? r.connection_id : null))
      .filter((x): x is number => x != null && x >= 1),
  );
  return poolSortedIds.find((cid) => !used.has(cid));
}

function TabPanel(props: {
  children?: ReactNode;
  value: number | string;
  index: number | string;
  /**
   * Height the content is entitled to. Without it a flex child whose own
   * content scrolls happily shrinks to nothing when the panel is short - the
   * task list at 1366x625 was three pixels tall.
   */
  minHeight?: number;
  /** Lay out at natural height inside the page scroller (touch layout). */
  flow?: boolean;
}) {
  const { children, value, index, minHeight = 0, flow = false } = props;
  const active = value === index;
  if (!active) return <div role="tabpanel" hidden />;
  // `flow` is the touch layout: the panel is part of one page-long scroll, so
  // it must not open a scroller of its own - a swipe inside a nested scroller
  // stops at its end instead of carrying on down the page.
  if (flow) {
    return (
      <div role="tabpanel">
        <Box sx={{ pt: 2 }}>{children}</Box>
      </div>
    );
  }
  return (
    <div
      role="tabpanel"
      style={{ display: "flex", flexDirection: "column", flex: 1, minHeight }}
    >
      <CustomScrollbars sx={{ minHeight: 0 }} trackGutter>
        <Box sx={{ pt: 2 }}>{children}</Box>
      </CustomScrollbars>
    </div>
  );
}

/** Eight placeholder rows matching the task-jobs table's 9 columns. */
function MjJobsSkeletonRows() {
  return (
    <>
      {Array.from({ length: 8 }).map((_, i) => (
        <TableRow key={i}>
          <TableCell padding="checkbox">
            <Skeleton variant="rounded" width={18} height={18} sx={{ mx: "auto" }} />
          </TableCell>
          <TableCell>
            <Skeleton variant="text" width="70%" />
          </TableCell>
          <TableCell align="center">
            <Skeleton variant="rounded" width={80} height={22} sx={{ mx: "auto" }} />
          </TableCell>
          {[0, 1, 2, 3].map((c) => (
            <TableCell key={c} align="center">
              <Skeleton variant="text" width={32} sx={{ mx: "auto" }} />
            </TableCell>
          ))}
          <TableCell align="center">
            <Skeleton variant="rounded" width={92} height={24} sx={{ mx: "auto" }} />
          </TableCell>
          <TableCell padding="checkbox" />
        </TableRow>
      ))}
    </>
  );
}

/** Meta tile for the major-jobs list header; the workspace strip uses MetaPill directly. */
function MajorJobStripItem({ title, value, avatar }: { title: string; value: string; avatar?: boolean }) {
  return (
    <MetaPill
      title={title}
      value={value}
      tone={avatar ? "primary" : "neutral"}
      icon={avatar ? <PersonOutline /> : undefined}
    />
  );
}

/** The sticky error-mode switch, toned like the tags next to it: warning when
 * on (a run retries current ERRORs only), muted when off. */
function ErrorModeToggle({
  checked,
  disabled,
  onChange,
  tooltip,
}: {
  checked: boolean;
  disabled: boolean;
  onChange: (next: boolean) => void;
  tooltip: string;
}) {
  return (
    <Tooltip title={tooltip}>
      <Box
        component="label"
        sx={(theme) => {
          const main = checked ? theme.palette.warning.main : theme.palette.text.secondary;
          const dark = theme.palette.mode === "dark";
          return {
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            pl: 1.25,
            pr: 0.25,
            py: 0,
            minHeight: { xs: 40, sm: 32 },
            boxSizing: "border-box",
            borderRadius: 1,
            border: 1,
            borderColor: alpha(main, checked ? (dark ? 0.6 : 0.4) : dark ? 0.35 : 0.2),
            bgcolor: alpha(main, checked ? (dark ? 0.22 : 0.1) : dark ? 0.1 : 0.04),
            color: checked ? main : "text.secondary",
            cursor: disabled ? "default" : "pointer",
            opacity: disabled ? 0.6 : 1,
            "& svg": { fontSize: 15, color: main },
          };
        }}
      >
        <ReplayTagIcon />
        <Typography variant="caption" sx={{ fontWeight: 600, whiteSpace: "nowrap", lineHeight: 1 }}>
          Process error EEs
        </Typography>
        <Switch
          size="small"
          color="warning"
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
        />
      </Box>
    </Tooltip>
  );
}

// Numbers behind a job's progress bar; total is null while the ranges are provisional.
function mjJobProgressProps(job: Record<string, unknown>) {
  const stats = job.stats as Record<string, unknown> | undefined;
  const totalRaw = stats?.actual_total_employees ?? job.actual_total_employees;
  const inStorage = mjEmployeeFolders(job);
  return {
    success: Number(stats?.success_employees_count ?? 0),
    error: Number(stats?.error_employees_count ?? 0),
    total: mjRosterEmpty(job)
      ? 0
      : mjRangesPending(job) || totalRaw == null
        ? null
        : Number(totalRaw),
    // Delete bots walk the roster, not a folder, so there is nothing to count.
    extra:
      inStorage != null
        ? `${inStorage.toLocaleString()} employee folder(s) in Data Storage`
        : String(job.bot_name ?? "").startsWith("delete_bots.")
          ? undefined
          : "Employee folders in Data Storage: not counted yet - press Refresh",
  };
}



function normalizeBotRegistryPayload(
  data: { results?: BotRegistryItem[] } | BotRegistryItem[] | undefined | null,
): BotRegistryItem[] {
  if (Array.isArray(data)) return data;
  return data?.results ?? [];
}

function normVendor(v: string | undefined | null): string {
  return (v ?? "").trim().toLowerCase();
}

/** Login line for tables/dropdowns under a major job — vendor omitted (shown on the major job header). */
function connectionPoolRowLabel(all: AdminVendorConnection[], connectionId: number): string {
  const c = all.find((x) => x.id === connectionId);
  if (!c) return "";
  const u = (c.username ?? "").trim();
  return u ? `@${u}` : "-";
}

function mjConnLabelVerbose(all: AdminVendorConnection[], connectionId: number): string {
  const short = connectionPoolRowLabel(all, connectionId);
  return short || "-";
}

/** Compose a 1-based ordinal label for a connection within a major job's pool. */
function mjConnLabelByOrdinal(
  poolIds: number[],
  all: AdminVendorConnection[],
  connectionId: number,
): string {
  const idx = poolIds.indexOf(connectionId);
  const ord = idx >= 0 ? idx + 1 : null;
  const short = mjConnLabelVerbose(all, connectionId);
  if (ord == null) return short;
  return `${ord}. ${short}`;
}

// The dedicated "Document types to delete" block is the only editor of this
// key; the registry-driven BotOptionFields must skip it or two inputs write it.
const DOCS_TYPE_OMIT = ["DOCS_TYPE"];
// Which side the SSN file is spelled for follows the major job's kind, so the
// backend derives it and an operator has nothing to decide here. Mirrors
// _SSN_UPLOAD_FLAG_KEYS in backend/app/modules/tasks/views.py.
const SSN_DERIVED_OMIT = [
  "IS_SSN_FOR_UPLOAD_ON_PAYCOM",
  "IS_SSN_FOR_UPLOAD_ON_UKG",
  "IS_SSN_FOR_UPLOAD_ON_RIPPLING",
  "IS_FOR_UPLOAD",
];
// The read-only option views skip what dedicated controls already show.
const MJ_OPTION_SUMMARY_OMIT = [...DOCS_TYPE_OMIT, ...SSN_DERIVED_OMIT];
const SSN_BOT_NAME = "ssn_bots.ssn_bot";

/** Parse a stored DOCS_TYPE value the way the bot entrypoint does: strip one
 * pair of matching outer quotes (the API/Postman spelling is
 * DOCS_TYPE='["A"]'), then JSON, then the Python-literal spelling with single
 * quotes. An element that is itself a JSON-encoded list - the double encoding
 * the old reader produced - is flattened, so a corrupted value heals on the
 * next save. Returns null when the value is not a list in any spelling. */
function parseDocTypesValue(value: string): string[] | null {
  let v = (value || "").trim();
  if (v.length >= 2 && v[0] === v[v.length - 1] && (v[0] === "'" || v[0] === '"')) {
    v = v.slice(1, -1).trim();
  }
  const tryJson = (s: string): unknown => {
    try {
      return JSON.parse(s);
    } catch {
      return undefined;
    }
  };
  let parsed = tryJson(v);
  if (parsed === undefined) parsed = tryJson(v.replace(/'/g, '"'));
  if (!Array.isArray(parsed)) return null;
  const flat: string[] = [];
  for (const item of parsed) {
    const text = String(item).trim();
    const inner = text.startsWith("[") ? tryJson(text) : undefined;
    if (Array.isArray(inner)) {
      for (const nested of inner) {
        const nestedText = String(nested).trim();
        if (nestedText) flat.push(nestedText);
      }
    } else if (text) {
      flat.push(text);
    }
  }
  return flat;
}

/** Trim, unwrap pasted JSON lists and dedupe - what a save actually stores. */
function cleanDocTypes(docTypes: string[]): string[] {
  const clean: string[] = [];
  for (const raw of docTypes) {
    const text = raw.trim();
    if (!text) continue;
    const parsed = text.startsWith("[") ? parseDocTypesValue(text) : null;
    for (const item of parsed ?? [text]) {
      if (item && !clean.includes(item)) clean.push(item);
    }
  }
  return clean;
}

function extractDocsTypeFromOptions(additionalOptions: string): { types: string[]; remaining: string } {
  const parts = (additionalOptions || "").split(";").map((s) => s.trim()).filter(Boolean);
  const docsTypePart = parts.find((p) => p.startsWith("DOCS_TYPE="));
  const remaining = parts.filter((p) => !p.startsWith("DOCS_TYPE=")).join(";");
  if (!docsTypePart) return { types: [""], remaining: additionalOptions || "" };
  const value = docsTypePart.slice("DOCS_TYPE=".length);
  const parsed = parseDocTypesValue(value);
  if (parsed) return { types: parsed.length > 0 ? parsed : [""], remaining };
  // Not a list in any known spelling: keep a hand-written bare name, but never
  // a bracketed literal - a raw literal shown as a "type" and re-saved is
  // exactly how the double encoding started (CAL-5923).
  const bare = value.trim().replace(/^['"]|['"]$/g, "").trim();
  return { types: bare && !bare.startsWith("[") ? [bare] : [""], remaining };
}

function buildDeleteAdditionalOptions(baseOpts: string, docTypes: string[]): string {
  const docsTypePart = `DOCS_TYPE=${encodeOptionJson(cleanDocTypes(docTypes))}`;
  const { remaining } = extractDocsTypeFromOptions(baseOpts);
  return remaining ? `${remaining};${docsTypePart}` : docsTypePart;
}

const isSSNJob = isHiddenJob;

/** Normalize em/en dashes in labels for display (ASCII hyphen with spaces). */
function displayJobNameShortDash(label: string): string {
  return label.replace(/\s*[\u2013\u2014]\s*/g, " - ");
}

function extractDefaultFolderSlugFromBotName(botName: string): string {
  const parts = botName.split(".");
  return parts[parts.length - 1] || "data";
}


/** Task row data as used in accordion (matches connection-tasks admin API + 2FA). */
export type MjConnectionTask = {
  id: number;
  job?: number | null;
  task_status: string;
  bot_work_status: string;
  task2fa_status?: string;
  task2fa_auto_until?: string | null;
  live_view_status?: string;
  live_view_phase?: string;
  started_by?: number | null;
  bot_name: string;
  employee_number_from: number;
  employee_number_to: number;
  connection?: number;
  range_role?: string;
  // Live attempt counters (only grow) and the stored-log snapshot written by
  // the job's artifact scan, attributed by the task's current range.
  successfully_processed_employees_count?: number;
  error_employees_count?: number;
  success_employees_snapshot?: number | null;
  error_employees_snapshot?: number | null;
  // What the task stands at now: the snapshot plus this run's progress, or the
  // raw counters when the task was never scanned (counters_source says which).
  success_employees_current?: number | null;
  error_employees_current?: number | null;
  counters_source?: CountersSource | null;
  retry_error_employees?: boolean;
  additional_options?: string | null;
  browser_profile_id?: number | null;
  started_by_username?: string | null;
  document_type?: string;
  path_to_s3_base_folder?: string;
  use_ssn_match?: boolean;
  upload_as_zip?: boolean;
  unzip_folder_before_uploading?: boolean;
  committed_from?: number | null;
  committed_to?: number | null;
  queue_run_source?: string;
  /** The run booked for this task, or null. */
  scheduled_run?: ScheduledRunInfo | null;
};

const MJ_ACCORDION_ROW_SX = {
  "&:before": { display: "none" },
  boxShadow: "none",
  border: "1px solid",
  borderColor: "divider",
  borderRadius: 1,
  mb: 1,
  overflow: "hidden",
  transition: "none !important",
  "& .MuiCollapse-root": { transition: "none !important" },
  "&.Mui-expanded": { margin: 0, mb: 1, mt: 0 },
} as const;

const MJ_ACC_SUMMARY_SX = {
  minHeight: 48,
  py: 0,
  "&.Mui-expanded": { minHeight: 48 },
  "& .MuiAccordionSummary-content": {
    alignItems: "flex-start",
    margin: "10px 0 !important",
    "&.Mui-expanded": { margin: "10px 0 !important" },
  },
} as const;

/**
 * [checkbox][No][status][bot][range][progress][connection][actions]
 *
 * Only the columns that cannot usefully shrink are pinned. Progress and
 * Connection carry no width, so `table-layout: fixed` splits whatever is left
 * between them: the table follows the panel instead of forcing it sideways,
 * and Actions - the column an operator actually clicks - is always on screen.
 */
const MJ_TASKS_PINNED_WIDTHS = {
  checkbox: 44,
  index: 44,
  status: 112,
  // The database id of the task (CAL-6161) - the key the log archive, the
  // browser profile and every refusal message are spelled with.
  taskId: 76,
  bot: 116,
  range: 92,
  actions: 116,
} as const;
/** What the two fluid columns need before the table may start scrolling. */
const MJ_TASKS_FLUID_MIN = 150;
const MJ_TASKS_TABLE_MIN_WIDTH =
  Object.values(MJ_TASKS_PINNED_WIDTHS).reduce((a, b) => a + b, 0) +
  MJ_TASKS_FLUID_MIN * 2;

type ManageTab = "jobs" | "ssn" | "connections";

// Compact 2FA action button so it doesn't tower over the icon buttons next to it.
const MJ_PASS_2FA_BTN_SX = {
  py: 0.15,
  px: 0.85,
  minWidth: 0,
  fontSize: "0.68rem",
  lineHeight: 1.4,
  "& .MuiButton-startIcon": {
    mr: 0.4,
    "& > *": { fontSize: "0.95rem" },
  },
} as const;

function canRunTaskByStatusMj(status: string) {
  return ["PENDING", "STOPPED", "FINISHED", "ERROR", "CRITICAL_ERROR"].includes(
    String(status ?? "").toUpperCase(),
  );
}

/** Run control is swapped for Stop while the worker is actually running workloads. */
function mjTaskShowsStopInsteadOfRun(status: string) {
  const s = String(status ?? "").toUpperCase();
  // QUEUED belongs here: Stop on a queued task means "leave the line".
  return ["ECS_CONTAINER_STARTED", "PROCESSING", "QUEUED"].includes(s);
}

function mjJobAggregateRunning(job: Record<string, unknown>): boolean {
  /** Use stats only: they aggregate live Task rows. Job.task_status can lag after stop/run. */
  return mjJobStatsTotals(job).running > 0;
}

function mjRosterTaskInFlight(job: Record<string, unknown>): boolean {
  const stats = job.stats as { roster_task?: { in_flight?: boolean } | null } | undefined;
  return stats?.roster_task?.in_flight === true;
}

// Anything of the job working, the roster-only task included - what config
// edits, the error-mode switch and Run all have to wait for.
function mjJobAnythingInFlight(job: Record<string, unknown>): boolean {
  return mjJobAggregateRunning(job) || mjRosterTaskInFlight(job);
}

function mjRunHeldBy(job: Record<string, unknown>): RunHeldBy | null {
  const stats = job.stats as { run_held_by?: unknown } | undefined;
  return parseRunHeldBy(stats?.run_held_by);
}

/** The major-job hold of a job that has tasks in line, or null. */
function mjWaitingMajorJobHold(job: Record<string, unknown>): MajorJobHold | null {
  const stats = job.stats as { major_job_hold?: unknown } | undefined;
  const hold = parseMajorJobHold(stats?.major_job_hold);
  return hold?.waiting ? hold : null;
}

/** Why the major job's own ceiling holds this job back, or null (CAL-6156). */
function mjMajorJobHoldReason(job: Record<string, unknown>): string | null {
  const stats = job.stats as { major_job_hold?: unknown } | undefined;
  return majorJobHoldReason(parseMajorJobHold(stats?.major_job_hold));
}

/** Why Run on this task is greyed out, or null when it may start. */
function mjTaskRunBlockedReason(
  job: Record<string, unknown> | null,
  task: { id: number; range_role?: string },
): string | null {
  if (!job) return null;
  // One level up first: the queue refuses every task of this job while a
  // sibling holds the major job's last slot, the roster task included.
  const majorHold = mjMajorJobHoldReason(job);
  if (majorHold) return majorHold;
  if (String(task.range_role ?? "").toUpperCase() === "ROSTER_ONLY") {
    return mjJobAggregateRunning(job) ? ROSTER_RUN_BLOCKED_REASON : null;
  }
  return runHeldReason(mjRunHeldBy(job), task.id, mjIsUploadJob(job));
}

function mjIsUploadJob(job: Record<string, unknown>): boolean {
  return job.bot_kind === "upload_bots" || String(job.bot_name ?? "").startsWith("upload_bots.");
}

// Send rejected files to SFTP is for an upload job at rest whose every employee
// was processed and some still are in ERROR. The backend applies the same rule;
// this one only decides whether the button is worth pressing, and says why not.
function mjSftpExportEligibility(job: Record<string, unknown>): { ok: boolean; reason: string } {
  if (!mjIsUploadJob(job)) return { ok: false, reason: "Only upload jobs export rejected files." };
  if (mjJobAnythingInFlight(job)) return { ok: false, reason: "Stop the job's tasks first." };
  const { success, error, total } = mjJobProgressProps(job);
  if (total == null || total <= 0) {
    return { ok: false, reason: "Total unknown - press Refresh on the job row." };
  }
  if (success + error < total) {
    return {
      ok: false,
      reason: `Not every employee has been processed yet (${success + error} of ${total}).`,
    };
  }
  if (error <= 0) return { ok: false, reason: "No employees in ERROR - nothing to send." };
  return { ok: true, reason: "" };
}

function mjSsnReadinessText(readiness: SsnReadiness): string {
  if (readiness.ready) {
    const source = readiness.source_major_job?.name ?? readiness.source_vendor;
    return `SSN match ready: the SSN files of '${source}' and of this major job hold employees.`;
  }
  return `Names only: ${readiness.reason}. Matching by name is less accurate - two employees with one name can be confused.`;
}

/**
 * The workspace meta as label/value rows - the folded form of the pill strip.
 * One list, so the pills and the rows can never say different things.
 */
function mjWorkspaceMetaRows(
  major: MajorJob,
  userLabel: string | undefined,
  profiles: BrowserProfilesInfo | null,
): WorkspaceMetaRow[] {
  return [
    { title: "User", value: userLabel?.trim() ? userLabel.trim() : "User" },
    {
      title: "Action",
      value: botKindMeta(major.bot_kind).label,
      color: botKindMeta(major.bot_kind).color,
    },
    { title: "Vendor", value: (major.vendor ?? "").trim() || "-" },
    ...(major.jobs_running_at_once != null
      ? [{ title: "Jobs at once", value: mjJobsAtOnceLabel(major.jobs_running_at_once) }]
      : []),
    ...(major.ssn_file_exists != null
      ? [
          {
            title: "SSN",
            value: major.ssn_file_exists ? "ready" : "not ready",
            color: major.ssn_file_exists ? "success.main" : "warning.main",
          },
        ]
      : []),
    ...(profiles
      ? [{ title: "Browser profile", value: mjBrowserProfilesLabel(profiles) }]
      : []),
  ];
}

function mjJobsAtOnceLabel(limit: number): string {
  return `${limit} job${limit === 1 ? "" : "s"} at a time`;
}

/** The same facts on one line, for when even the rows are too tall. */
function mjWorkspaceSummaryLine(
  major: MajorJob,
  userLabel: string | undefined,
  profiles: BrowserProfilesInfo | null,
): string {
  return mjWorkspaceMetaRows(major, userLabel, profiles)
    .map((row) => row.value)
    .join(" - ");
}

function mjBrowserProfilesLabel(info: BrowserProfilesInfo): string {
  if (!info.profiles.length) return "none stored";
  const bytes = info.profiles.reduce((acc, p) => acc + (p.size_bytes ?? 0), 0);
  const latest = info.profiles
    .map((p) => (p.last_modified ? new Date(p.last_modified).getTime() : 0))
    .reduce((a, b) => Math.max(a, b), 0);
  const when = latest ? `, updated ${new Date(latest).toLocaleDateString()}` : "";
  return `${info.profiles.length} file(s), ${Math.round(bytes / 1048576)} MB${when}`;
}

function mjRosterInfoLine(info: JobRosterInfo): string {
  const shared = info.sibling_job_ids.length
    ? ` Job(s) ${info.sibling_job_ids.map((id) => `#${id}`).join(", ")} read this same file.`
    : "";
  if (info.kind === "matches") {
    const matches = info.files.find((f) => f.filename === "s3_to_site_names_matches.csv");
    if (!matches?.exists) return `Matches: not built yet.${shared}`;
    const when = matches.last_modified
      ? `, updated ${new Date(matches.last_modified).toLocaleString()}`
      : "";
    const manual = info.summary?.manual_upload ? ", uploaded by hand" : "";
    return `Matches: ${matches.rows ?? "?"} row(s)${when}${manual}.${shared}`;
  }
  if (!info.exists) return `Roster: not collected yet.${shared}`;
  const when = info.last_modified ? `, updated ${new Date(info.last_modified).toLocaleString()}` : "";
  return `Roster: ${info.rows ?? "?"} employee(s)${when}.${shared}`;
}

function mjRosterInfoExists(info: JobRosterInfo | null): boolean {
  if (!info) return false;
  return info.kind === "matches" ? info.files.some((f) => f.exists) : info.exists;
}

// Rows the stored roster / Matches file names; 0 for a file that is there but
// empty, null when nothing is stored or the count is unknown.
function mjRosterStoredRows(info: JobRosterInfo | null): number | null {
  if (!info) return null;
  if (info.kind === "matches") {
    const matches = info.files.find((f) => f.filename === "s3_to_site_names_matches.csv");
    return matches?.exists ? matches.rows : null;
  }
  return info.exists ? info.rows : null;
}

// The file a run would reuse: the Matches CSV itself (upload) or the roster.
// The Several / No matches reports do not count - they may outlive a deleted
// Matches CSV, and a run over them still matches again.
function mjRosterMainFileExists(info: JobRosterInfo): boolean {
  if (info.kind === "matches") {
    return info.files.some((f) => f.filename === "s3_to_site_names_matches.csv" && f.exists);
  }
  return info.exists;
}

// A roster-only run would reuse a stored file and change nothing, so the button
// is greyed out while one with employees in it exists. An empty file does not
// count - the bot throws it away and collects again, as the backend's own check
// does. Loaded file facts decide; the job's total is only the fallback before
// they arrive (it is set from the file and cleared when the file is deleted).
function mjRosterAlreadyBuilt(job: Record<string, unknown>, info: JobRosterInfo | null): boolean {
  if (info) {
    const rows = mjRosterStoredRows(info);
    return rows != null ? rows > 0 : mjRosterMainFileExists(info);
  }
  return Number(job.actual_total_employees ?? 0) > 0;
}

function mjRosterBuiltTooltip(job: Record<string, unknown>, info: JobRosterInfo | null): string {
  const what = mjIsUploadJob(job) ? "The Matches file is" : "The roster is";
  const line = info && mjRosterInfoExists(info) ? ` ${mjRosterInfoLine(info)}` : "";
  return `${what} already built.${line} Delete it in Manage job - Files to build it again.`;
}

// The stored file exists but names nobody: the run rebuilds it.
function mjRosterStoredEmptyNote(info: JobRosterInfo | null): string {
  return mjRosterStoredRows(info) === 0
    ? " The stored file is empty and is thrown away - this run collects it again."
    : "";
}

function parseConnectionPlansFromJobRow(
  job: Record<string, unknown>,
): { connection_id: number; task_target_count: number }[] {
  const raw = job.connection_plans;
  if (!Array.isArray(raw)) return [];
  const out: { connection_id: number; task_target_count: number }[] = [];
  for (const item of raw) {
    if (item == null || typeof item !== "object") continue;
    const obj = item as Record<string, unknown>;
    const cid = Number(obj.connection_id);
    if (!Number.isFinite(cid) || cid < 1) continue;
    out.push({
      connection_id: cid,
      task_target_count: Math.max(1, Number(obj.task_target_count) || 1),
    });
  }
  return out;
}

function legacyPrimaryConnectionFromJob(job: Record<string, unknown>): number | null {
  const direct = Number(job.connection_id ?? 0);
  if (Number.isFinite(direct) && direct > 0) return direct;
  const cr = job.connection;
  if (typeof cr === "number" && Number.isFinite(cr) && cr > 0) return cr;
  if (cr != null && typeof cr === "object") {
    const pid = Number((cr as Record<string, unknown>).id ?? 0);
    if (Number.isFinite(pid) && pid > 0) return pid;
  }
  return null;
}

function mjTasksCountOnConnection(tasks: MjConnectionTask[], connectionId: number): number {
  return tasks.reduce((acc, t) => acc + (Number(t.connection) === connectionId ? 1 : 0), 0);
}

/** Sums numeric per-row "to add" counts grouped by login (planned add dialog). */
function aggregatePlannedAddsByConnection(rows: ConnPlanEditorRow[]): { cid: number; add: number }[] {
  const m = new Map<number, number>();
  for (const r of rows) {
    const cid = typeof r.connection_id === "number" ? r.connection_id : NaN;
    const d = connPlanTasksDigitsOnly(r.task_target_count);
    const addRaw = d === "" ? Number.NaN : Math.floor(Number(d));
    const add = Number.isFinite(addRaw) ? addRaw : 0;
    if (!Number.isFinite(cid) || cid < 1 || add < 1) continue;
    m.set(cid, (m.get(cid) ?? 0) + add);
  }
  return Array.from(m.entries())
    .map(([cid, add]) => ({ cid, add }))
    .sort((a, b) => a.cid - b.cid);
}

function resolveMjBulkTaskAddPayload(
  planMode: boolean,
  plansSnap: { connection_id: number; task_target_count: number }[],
  poolIds: number[],
  rows: ConnPlanEditorRow[],
  currentTasks: MjConnectionTask[],
  jobTotalRemain: number,
  connections: AdminVendorConnection[],
):
  | { error: string }
  | { planMode: false; legacyCount: number }
  | { planMode: true; batches: { connection_id: number; count: number }[] } {
  if (jobTotalRemain < 1) {
    return { error: `This job already has ${MAX_BATCH_TASKS} tasks (maximum).` };
  }

  if (!planMode) {
    if (rows.length !== 1) {
      return { error: "This job expects a single add row." };
    }
    const d0 = connPlanTasksDigitsOnly(String(rows[0]?.task_target_count ?? ""));
    const n = d0 === "" ? Number.NaN : Math.floor(Number(d0));
    const parsed = Number.isFinite(n) ? n : 0;
    if (parsed < 1) return { error: "Add at least one task." };
    if (parsed > jobTotalRemain) return { error: `At most ${jobTotalRemain} more task(s) fit on this job.` };
    return { planMode: false, legacyCount: parsed };
  }

  // Plan rows are treated as advisory hints in this UI: when a row asks for
  // more tasks than the recorded quota, the backend auto-raises the quota for
  // that connection. The pool set (logins available to this major job) is
  // still enforced so users cannot attach tasks to logins outside the pool.
  const poolOk = poolIds.length ? new Set(poolIds) : null;
  // Mark unused variable to keep the helper signature backwards compatible.
  void plansSnap;
  void currentTasks;
  void connections;

  for (let i = 0; i < rows.length; i++) {
    const r = rows[i];
    const cid = typeof r.connection_id === "number" ? r.connection_id : NaN;
    const d = connPlanTasksDigitsOnly(String(r.task_target_count ?? ""));
    const addRaw = d === "" ? Number.NaN : Math.floor(Number(d));
    const add = Number.isFinite(addRaw) ? addRaw : 0;
    if (!Number.isFinite(cid) || cid < 1) {
      return { error: `Row ${i + 1}: choose a login.` };
    }
    if (poolOk && !poolOk.has(cid)) {
      return { error: `Row ${i + 1}: login outside the mapped set.` };
    }
    if (add < 1) {
      return { error: `Row ${i + 1}: enter at least 1 task to add.` };
    }
  }

  const batches = aggregatePlannedAddsByConnection(rows).map(({ cid, add }) => ({
    connection_id: cid,
    count: add,
  }));
  if (!batches.length) {
    return { error: "Pick logins and how many tasks to add on each row." };
  }

  const sum = batches.reduce((acc, b) => acc + b.count, 0);
  if (sum > jobTotalRemain) {
    return { error: `Total adds (${sum}) exceed the ${MAX_BATCH_TASKS}-task job limit (${jobTotalRemain} left).` };
  }

  return { planMode: true, batches };
}

/** The tasks that carry employees - the roster-only task is not part of the split. */
function mjEmployeeTasks(tasks: MjConnectionTask[]): MjConnectionTask[] {
  return tasks.filter((t) => String(t.range_role ?? "EMPLOYEES") !== "ROSTER_ONLY");
}

/**
 * The server's own order for what a reduce drops (job_services._reduction_order_key):
 * tasks that never ran first, then the ones holding the highest employee numbers.
 */
function mjReductionOrder(tasks: MjConnectionTask[]): MjConnectionTask[] {
  return mjEmployeeTasks(tasks)
    .slice()
    .sort((a, b) => {
      const aRan = a.committed_from != null && a.committed_to != null;
      const bRan = b.committed_from != null && b.committed_to != null;
      if (aRan !== bRan) return aRan ? 1 : -1;
      const aUpper = aRan ? Number(a.committed_to) : a.employee_number_to;
      const bUpper = bRan ? Number(b.committed_to) : b.employee_number_to;
      if (aUpper !== bUpper) return bUpper - aUpper;
      return b.id - a.id;
    });
}

function mjTaskRunLabel(task: MjConnectionTask): string {
  if (task.committed_from == null || task.committed_to == null) return "never run";
  return `ran ${String(task.committed_from)}-${String(task.committed_to)}`;
}

function mjDigitsToCount(value: string): number {
  const digits = connPlanTasksDigitsOnly(value);
  return digits === "" ? Number.NaN : Math.floor(Number(digits));
}

function mjJobDisplayName(job: Record<string, unknown>): string {
  return displayJobNameShortDash(String(job.name ?? ""));
}

function mjEmployeeStatValue(value: unknown): string {
  if (value === undefined || value === null) return "—";
  const n = Number(value);
  return Number.isFinite(n) ? String(n) : "—";
}

function mjEmployeeStatsFromJob(job: Record<string, unknown>) {
  const stats = job.stats as Record<string, unknown> | undefined;
  const total = stats?.actual_total_employees ?? job.actual_total_employees;
  const success = stats?.success_employees_count;
  const error = stats?.error_employees_count;
  const snapshotAt =
    typeof stats?.stats_snapshot_updated_at === "string"
      ? (stats.stats_snapshot_updated_at as string)
      : null;
  const source = countersSource(stats?.counters_source);
  const hasAny =
    total != null ||
    (success != null && Number(success) > 0) ||
    (error != null && Number(error) > 0);
  if (!hasAny) {
    return { total: "—", success: "—", error: "—", snapshotAt, source };
  }
  return {
    total: mjEmployeeStatValue(total),
    success: mjEmployeeStatValue(success ?? 0),
    error: mjEmployeeStatValue(error ?? 0),
    snapshotAt,
    source,
  };
}

/** What the stored roster says about itself; null before it was ever read. */
function mjRosterState(job: Record<string, unknown>): RosterState | null {
  const state = (job as { roster_state?: RosterState | null }).roster_state;
  return state && typeof state === "object" ? state : null;
}

function mjRangeNotice(job: Record<string, unknown>): string {
  const notice = (
    job as { stats?: { range_coverage?: { notice?: string } } }
  ).stats?.range_coverage?.notice;
  return typeof notice === "string" ? notice.trim() : "";
}

// The roster was collected and holds nobody (0 matches). Different from a
// roster not collected yet, which is total == null.
function mjRosterEmpty(job: Record<string, unknown>): boolean {
  const stats = job.stats as
    | { range_coverage?: { roster_empty?: boolean }; actual_total_employees?: unknown }
    | undefined;
  return (
    stats?.range_coverage?.roster_empty === true ||
    stats?.actual_total_employees === 0 ||
    job.actual_total_employees === 0
  );
}

// Bounds that name nobody: the bootstrap guess before the first roster
// collection, or an empty roster. Rendered as "-" everywhere.
function mjRangesPending(job: Record<string, unknown>): boolean {
  return (
    (job.auto_recalculate_ranges !== false && job.actual_total_employees == null) ||
    mjRosterEmpty(job)
  );
}

function mjRosterEmptyWarning(job: Record<string, unknown>): string {
  if (!mjRosterEmpty(job)) return "";
  const warning = (
    job as { stats?: { range_coverage?: { warning?: string } } }
  ).stats?.range_coverage?.warning;
  return typeof warning === "string" && warning.trim()
    ? warning.trim()
    : "Warning: the roster is empty (0 matches) - the job has nobody to process.";
}

function mjMatchesPreflight(job: Record<string, unknown>): MatchesPreflight | null {
  const raw = job.matches_preflight;
  return raw && typeof raw === "object" ? (raw as MatchesPreflight) : null;
}

// The pre-run check matters only while the job has to build its Matches file;
// a stored one with employees in it is reused whatever the SSN files say.
function mjShowMatchesPreflight(job: Record<string, unknown>, info: JobRosterInfo | null): boolean {
  return mjIsUploadJob(job) && !mjRosterAlreadyBuilt(job, info);
}

function mjSsnUpdaterButtonReason(
  job: Record<string, unknown>,
  readiness: SsnReadiness | null,
): { disabled: boolean; reason: string } {
  if (mjJobAnythingInFlight(job)) {
    return {
      disabled: true,
      reason: "The job has a queued or running task - wait for it to finish, or stop it, first.",
    };
  }
  if (readiness && readiness.algorithm !== "ssn") {
    return { disabled: true, reason: `SSN matching is not available: ${readiness.reason}.` };
  }
  return {
    disabled: false,
    reason:
      'Files folders named "<name> - <last 4 of SSN>" (the PDF Splitter pattern) under their SSNs in the source SSN file, so the SSN matching finds them.',
  };
}

function mjMatchesSummaryHeadline(summary: MatchesSummary): string {
  if (summary.algorithm === "manual_upload" || summary.manual_upload) {
    const manual = summary.manual_upload;
    const when = manual?.uploaded_at ? ` on ${new Date(manual.uploaded_at).toLocaleString()}` : "";
    const who = manual?.uploaded_by ? ` by ${manual.uploaded_by}` : "";
    const file = manual?.file_name ? ` (${manual.file_name})` : "";
    return `Matches file uploaded by hand: ${summary.matched_rows} row(s)${when}${who}${file}.`;
  }
  const algorithm = summary.algorithm === "ssn" ? "SSN comparison" : "names comparison";
  const requested = summary.ssn_match_requested ? "SSN match requested" : "SSN match not requested";
  return (
    `${summary.matched_folders} of ${summary.s3_folders} employee folder(s) matched ` +
    `(${summary.matched_rows} row(s)), ${summary.several} with several matches, ` +
    `${summary.no_matches} without a match. Algorithm: ${algorithm} (${requested}).`
  );
}

function mjMatchesSummaryLines(summary: MatchesSummary): string[] {
  const lines: string[] = [];
  if (summary.ssn_fallback_reason) lines.push(`${summary.ssn_fallback_reason}.`);
  if (summary.top_reason_label) lines.push(`Top reason: ${summary.top_reason_label}.`);
  const breakdown = Object.entries(summary.breakdown ?? {})
    .filter(([, count]) => Number(count) > 0)
    .map(([key, count]) => `${key.replace(/_/g, " ")}: ${count}`);
  if (breakdown.length) lines.push(`Breakdown: ${breakdown.join(", ")}.`);
  for (const [role, file] of Object.entries(summary.ssn_files ?? {})) {
    const when = file.last_modified ? new Date(file.last_modified).toLocaleString() : "unknown date";
    const shapes = file.employee_id_shapes?.length
      ? `, employee_id looks like ${file.employee_id_shapes.join(", ")}`
      : "";
    lines.push(
      `${role} SSN file (${file.vendor}): ${file.ssn_count} SSN(s), ${file.account_count} account(s), ${when}${shapes}.`,
    );
  }
  if (summary.folder_id_shapes?.length) {
    lines.push(`Folder ids look like ${summary.folder_id_shapes.join(", ")}.`);
  }
  for (const hint of summary.hints ?? []) lines.push(hint);
  if (summary.created_at) {
    const by = summary.task_id ? ` by task #${summary.task_id}` : "";
    lines.push(`Built ${new Date(summary.created_at).toLocaleString()}${by}.`);
  }
  return lines;
}


function mjUncoveredRows(job: Record<string, unknown>): {
  rows: number;
  suggested: number;
} {
  const coverage = (
    job as {
      stats?: {
        range_coverage?: { uncovered_rows?: number; suggested_new_tasks?: number };
      };
    }
  ).stats?.range_coverage;
  return {
    rows: Number(coverage?.uncovered_rows ?? 0),
    suggested: Math.max(1, Number(coverage?.suggested_new_tasks ?? 1)),
  };
}

// The same fact Run merged EEs is enabled on: rows past every task's range exist
// only once tasks have run. After a roster-only run the new rows are already
// split across the tasks, and Run all is the button to press.
function mjRunHint(job: Record<string, unknown>): RunHint {
  return mjUncoveredRows(job).rows > 0 ? "run_merged_ees" : "run_all";
}


function mjRangeChipLabel(job: Record<string, unknown>): string {
  if (mjRangesPending(job)) {
    return "Range -";
  }
  const from = Number(job.employee_number_from ?? 0);
  const to = Number(job.employee_number_to ?? 0);
  // An upper bound below the lower one is a real state: more tasks than
  // employees leaves the extra ones with nothing to process.
  return from > to ? "Range empty" : `Range ${from}-${to}`;
}


function mjWhenAgo(iso: string): string | null {
  const ts = new Date(iso);
  if (Number.isNaN(ts.getTime())) return null;
  const min = Math.round((Date.now() - ts.getTime()) / 60000);
  let when: string;
  if (min < 1) when = "just now";
  else if (min < 60) when = `${min} min ago`;
  else if (min < 60 * 24) when = `${Math.round(min / 60)} h ago`;
  else when = `${Math.round(min / (60 * 24))} d ago`;
  return `${when} (${ts.toLocaleString()})`;
}

// Where the job's Success / Errors come from right now. Live counters move with
// the bots' events; the stored-log count replaces them when the queue drains.
function mjStatsSnapshotTooltip(
  snapshotAt: string | null | undefined,
  source: CountersSource = "snapshot",
): string {
  const exactAt = snapshotAt ? mjWhenAgo(snapshotAt) : null;
  if (source === "progress") {
    return (
      "Counted from the stored logs, each employee once, plus what the running bots have " +
      "done since - a retry that fails again changes nothing. The stored count is refreshed " +
      "when the queue drains" +
      (exactAt ? ` (last count ${exactAt}).` : ".")
    );
  }
  if (source === "live") {
    return "Live task counters - this job has not been counted from its stored logs yet. Refresh to count.";
  }
  if (!snapshotAt) return "Live task counters. Refresh to recount from the stored logs.";
  if (!exactAt) return "Stats are based on stored logs.";
  return `Counted from the stored logs, each employee once. Refreshed ${exactAt}.`;
}

// The progress bar's tooltip: the bar and the Success / Errors cells share one story.
function mjJobCountersTooltip(employeeStats: {
  snapshotAt: string | null;
  source: CountersSource;
}): string {
  const lead =
    employeeStats.source === "live"
      ? ""
      : "Employees with a status record, each once, over the whole job. ";
  return `${lead}${mjStatsSnapshotTooltip(employeeStats.snapshotAt, employeeStats.source)}`;
}

// A dot the operator can read the socket by: green while events arrive, amber
// while the panel polls instead. The heartbeat behind it is verified through the
// channel layer, so "on" means the whole path works, not just an open socket.
function LiveUpdatesIndicator({ connected }: { connected: boolean }) {
  return (
    <Tooltip
      title={
        connected
          ? "Live updates on: statuses and counters arrive over the websocket as the bots report them."
          : "Live updates off: the websocket is down, so the panel polls every 8 s while tasks run and every 32 s otherwise."
      }
    >
      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ pr: 1, cursor: "help" }}>
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            bgcolor: connected ? "success.main" : "warning.main",
          }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 0.2 }}>
          {connected ? "Live" : "Polling"}
        </Typography>
      </Stack>
    </Tooltip>
  );
}

function mjArtifactRangeWarning(warning: string | undefined | null): string | null {
  const trimmed = (warning ?? "").trim();
  if (!trimmed) return null;
  if (/employees_info\.json has not been created/i.test(trimmed)) return null;
  return trimmed;
}


function mjJobStatsTotals(job: Record<string, unknown>) {
  const stats = job.stats as Record<string, unknown> | undefined;
  // Queued tasks count as running: the job is mid-flight and must offer Stop.
  const running =
    Number(stats?.running_tasks ?? 0) + Number(stats?.queued_tasks ?? 0);
  const total = Number(stats?.total_tasks ?? 0);
  return { running, total };
}

/** "3 of 4 running at once, 2 waiting behind it" - what the ceiling is doing to
 *  this job right now, or null when there is nothing worth saying. */
function mjParallelismSummary(job: Record<string, unknown>): string | null {
  // In line behind a sibling of the major job, not behind this job's ceiling.
  const majorHold = mjWaitingMajorJobHold(job);
  if (majorHold) return majorJobHoldReason(majorHold);
  const stats = job.stats as Record<string, unknown> | undefined;
  const running = Number(stats?.running_tasks ?? 0);
  const waiting = Number(stats?.queued_tasks ?? 0);
  const raw = job.max_parallel_tasks;
  if (raw == null) return waiting > 0 ? `${running} running, ${waiting} queued` : null;
  const tail = waiting > 0 ? `, ${waiting} waiting behind it` : "";
  return `${running} of ${Number(raw)} running at once${tail}`;
}

function normalizeRawTaskToMj(t: Record<string, unknown>, fallbackJobId: number): MjConnectionTask {
  const jobVal = t.job;
  const jid =
    typeof jobVal === "number"
      ? jobVal
      : jobVal != null && typeof jobVal === "object" && "id" in (jobVal as object)
        ? Number((jobVal as { id?: unknown }).id)
        : fallbackJobId;
  const connVal = t.connection;
  const cid =
    typeof connVal === "number"
      ? connVal
      : connVal != null && typeof connVal === "object" && "id" in (connVal as object)
        ? Number((connVal as { id?: unknown }).id)
        : undefined;
  return {
    id: Number(t.id),
    job: Number.isFinite(jid) ? jid : fallbackJobId,
    task_status: String(t.task_status ?? ""),
    bot_work_status: String(t.bot_work_status ?? ""),
    task2fa_status: typeof t.task2fa_status === "string" ? t.task2fa_status : "not_requested",
    task2fa_auto_until: typeof t.task2fa_auto_until === "string" ? t.task2fa_auto_until : null,
    live_view_status: typeof t.live_view_status === "string" ? t.live_view_status : "not_requested",
    live_view_phase: typeof t.live_view_phase === "string" ? t.live_view_phase : "",
    started_by: typeof t.started_by === "number" ? t.started_by : null,
    bot_name: String(t.bot_name ?? ""),
    employee_number_from: Number(t.employee_number_from ?? 0),
    employee_number_to: Number(t.employee_number_to ?? 0),
    connection: cid,
    range_role: typeof t.range_role === "string" ? t.range_role : "EMPLOYEES",
    successfully_processed_employees_count: Number(t.successfully_processed_employees_count ?? 0),
    error_employees_count: Number(t.error_employees_count ?? 0),
    success_employees_snapshot: numberOrNull(t.success_employees_snapshot),
    error_employees_snapshot: numberOrNull(t.error_employees_snapshot),
    success_employees_current: numberOrNull(t.success_employees_current),
    error_employees_current: numberOrNull(t.error_employees_current),
    counters_source:
      typeof t.counters_source === "string" ? countersSource(t.counters_source) : null,
    retry_error_employees: t.retry_error_employees === true,
    additional_options: typeof t.additional_options === "string" ? t.additional_options : null,
    browser_profile_id: numberOrNull(t.browser_profile_id),
    started_by_username: typeof t.started_by_username === "string" ? t.started_by_username : null,
    document_type: typeof t.document_type === "string" ? t.document_type : "",
    path_to_s3_base_folder: typeof t.path_to_s3_base_folder === "string" ? t.path_to_s3_base_folder : "",
    use_ssn_match: t.use_ssn_match !== false,
    upload_as_zip: t.upload_as_zip === true,
    unzip_folder_before_uploading: t.unzip_folder_before_uploading === true,
    committed_from: numberOrNull(t.committed_from),
    committed_to: numberOrNull(t.committed_to),
    queue_run_source: typeof t.queue_run_source === "string" ? t.queue_run_source : "",
    // This mapper is an allow-list: a field left out here never reaches the row.
    scheduled_run: (t.scheduled_run ?? null) as ScheduledRunInfo | null,
  };
}

function numberOrNull(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function mjTimeAgo(iso: string | null | undefined): string {
  if (!iso) return "";
  const ts = new Date(iso);
  if (Number.isNaN(ts.getTime())) return "";
  const min = Math.round((Date.now() - ts.getTime()) / 60000);
  const when =
    min < 1
      ? "just now"
      : min < 60
        ? `${min} min ago`
        : min < 60 * 24
          ? `${Math.round(min / 60)} h ago`
          : `${Math.round(min / (60 * 24))} d ago`;
  return `${when} (${ts.toLocaleString()})`;
}

const MJ_TASK_LIVE_STATUSES = ["ECS_CONTAINER_STARTED", "PROCESSING"];

// Per-task numbers as the backend states them: the stored-log snapshot plus
// this run's progress, or the raw counters for a task never scanned. Older
// payloads without the current fields fall back to snapshot-or-live.
function mjTaskEmployeeProgress(
  task: MjConnectionTask,
  jobSnapshotAt: string | null | undefined,
  rangePending = false,
) {
  const rosterOnly = String(task.range_role ?? "").toUpperCase() === "ROSTER_ONLY";
  // Bootstrap bounds are a guess, not a size: no denominator until the roster is in.
  const rangeSize =
    rosterOnly || task.employee_number_from > task.employee_number_to
      ? 0
      : rangePending
        ? null
        : task.employee_number_to - task.employee_number_from + 1;
  const running = MJ_TASK_LIVE_STATUSES.includes(
    String(task.task_status ?? "").toUpperCase(),
  );
  const hasSnapshot =
    task.success_employees_snapshot != null && task.error_employees_snapshot != null;
  const hasCurrent =
    task.success_employees_current != null && task.error_employees_current != null;
  const source: CountersSource = hasCurrent
    ? countersSource(task.counters_source)
    : hasSnapshot && !running
      ? "snapshot"
      : "live";
  const success = Number(
    hasCurrent
      ? task.success_employees_current
      : source === "snapshot"
        ? task.success_employees_snapshot
        : task.successfully_processed_employees_count ?? 0,
  );
  const error = Number(
    hasCurrent
      ? task.error_employees_current
      : source === "snapshot"
        ? task.error_employees_snapshot
        : task.error_employees_count ?? 0,
  );
  const useSnapshot = source !== "live";
  const tooltip = taskCountersTooltip(source, jobSnapshotAt);
  return { success, error, processed: success + error, rangeSize, rosterOnly, useSnapshot, tooltip };
}

// Employee folders under the job's data folder, as Data Storage knows them
// (the index answers; S3 is listed only for a folder the index does not know).
function mjEmployeeFolders(job: Record<string, unknown>): number | null {
  const stats = job.stats as { employee_folders?: unknown } | undefined;
  return numberOrNull(stats?.employee_folders);
}

// Options stored on a job or task, labelled the way Edit job labels them.
function mjBotOptionTags(raw: unknown, specs: BotOptionSpec[]) {
  return summarizeBotOptions(specs, typeof raw === "string" ? raw : "", MJ_OPTION_SUMMARY_OMIT)
    .filter((line) => line.set)
    .map((line) => {
      const full = botOptionTagText(line);
      const label = full.length > 56 ? `${full.slice(0, 53)}...` : full;
      return (
        <SoftTag
          key={line.key}
          mono={!line.declared}
          icon={<TuneIcon />}
          label={label}
          title={line.help ? `${full}. ${line.help}` : full}
        />
      );
    });
}

// The two switches of Edit job -> Upload options: they decide what actually
// lands on the portal (one archive per employee, and whether a stored .zip is
// unpacked first), so an upload job wears them next to its SSN mode. Both are
// job columns, not additional_options - the option summary never shows them.
function mjUploadFlagTags(job: Record<string, unknown>) {
  if (!mjIsUploadJob(job)) return null;
  const asZip = job.upload_as_zip === true;
  const unzipFirst = job.unzip_folder_before_uploading === true;
  return (
    <>
      <SoftTag
        tone={asZip ? "info" : "neutral"}
        icon={<FolderZipIcon />}
        label={`Upload as ZIP: ${asZip ? "on" : "off"}`}
        title="On: the employee's files go to the portal as one archive. Off: file by file. Edit job -> Upload options."
      />
      <SoftTag
        tone={unzipFirst ? "info" : "neutral"}
        icon={<UnarchiveIcon />}
        label={`Unzip before uploading: ${unzipFirst ? "on" : "off"}`}
        title="On: an archive stored in Data Storage is unpacked and its files are uploaded. Off: the archive is uploaded as it is. Edit job -> Upload options."
      />
    </>
  );
}

/** The booking on a job or task row, next to the rest of its facts. */
function mjScheduledRunTag(row: Record<string, unknown>) {
  const run = row.scheduled_run as ScheduledRunInfo | null | undefined;
  if (!run) return null;
  return (
    <SoftTag
      tone="info"
      icon={<ScheduleIcon />}
      label={scheduledRunTagLabel(run)}
      title={scheduledRunTooltip(run)}
    />
  );
}

/** "Waiting for a sibling job" while the major job is at its ceiling. The job
 * is queued and starts by itself, so this says what it waits for rather than
 * asking anyone to do something (CAL-6156). */
function mjMajorJobWaitingTag(row: Record<string, unknown>) {
  const hold = mjWaitingMajorJobHold(row);
  if (!hold) return null;
  const names = hold.running_jobs.map((job) => job.name).join(", ");
  return (
    <SoftTag
      tone="warning"
      icon={<HourglassEmptyIcon />}
      label={`Waiting: ${names}`}
      title={majorJobHoldReason(hold) ?? ""}
    />
  );
}

/** The job's status chip; a job in line behind a sibling of the major job reads
 * WAITING, where its queued tasks alone would read PROCESSING. */
function mjJobStatusChip(job: Record<string, unknown>) {
  const hold = mjWaitingMajorJobHold(job);
  if (hold) {
    return (
      <StatusChipMj label="WAITING" color="warning" title={majorJobHoldReason(hold) ?? ""} />
    );
  }
  const status = String(job.task_status ?? "");
  return <StatusChipMj label={status} color={taskRunStatusChipColor(status)} />;
}

// What a task is about, for the info tooltip in its row.
function mjTaskDetailLines(task: MjConnectionTask, specs: BotOptionSpec[]): string[] {
  const lines: string[] = [];
  const kind = String(task.bot_name ?? "").split(".")[0];
  const options = String(task.additional_options ?? "");
  if (task.document_type) lines.push(`Document type: ${task.document_type}`);
  if (kind === "upload_bots" && task.path_to_s3_base_folder) {
    lines.push(`Source folder: ${task.path_to_s3_base_folder}`);
  }
  if (kind === "delete_bots") {
    const docs = readListOption(options, "DOCS_TYPE");
    if (docs.length) lines.push(`Document types to delete: ${docs.join(", ")}`);
  }
  if (kind === "upload_bots") {
    lines.push(`SSN match: ${task.use_ssn_match === false ? "off" : "on"}`);
    lines.push(`Upload as ZIP: ${task.upload_as_zip ? "on" : "off"}`);
    lines.push(`Unzip before uploading: ${task.unzip_folder_before_uploading ? "on" : "off"}`);
  }
  const sticky = extractSkipErrorEmployees(options).processErrors;
  lines.push(
    `Error mode: ${
      task.retry_error_employees ? "on (automatic error pass)" : sticky ? "on (job switch)" : "off"
    }`,
  );
  if (task.browser_profile_id != null) lines.push(`Browser profile: ${task.browser_profile_id}`);
  if (task.started_by_username) lines.push(`Started by: ${task.started_by_username}`);
  if (task.queue_run_source) lines.push(`Run source: ${task.queue_run_source}`);
  for (const line of summarizeBotOptions(specs, options, MJ_OPTION_SUMMARY_OMIT)) {
    if (line.set) lines.push(botOptionTagText(line));
  }
  return lines;
}

function botNameFromRegistry(row: BotRegistryItem | { vendor: string; job_kind: string; bot_slug: string }) {
  return `${row.job_kind}.${row.bot_slug}`;
}

/** What the vendor is known to tolerate for this bot, or null when it meters
 *  no sessions at all. Comes straight from the bot registry. */
function recommendedParallelismFor(bots: BotRegistryItem[], botName: string): number | null {
  const row = bots.find((b) => botNameFromRegistry(b) === botName);
  return row?.recommended_parallelism ?? null;
}

/** What the selected bot does with the job's Document type. Straight from the
 *  bot registry; "required" when the bot is unknown, which is what every upload
 *  bot answered before the registry carried this at all. */
function documentTypeUseFor(bots: BotRegistryItem[], botName: string): DocumentTypeUse {
  const row = bots.find((b) => botNameFromRegistry(b) === botName);
  return row?.document_type_use ?? "required";
}

/** The line under "Max tasks running at once" - the only warning an operator gets. */
function parallelismHelpText(recommended: number | null): string {
  return recommended == null
    ? "Empty means no ceiling: this vendor does not limit concurrent sessions."
    : `Recommended for this bot: ${recommended}. Higher puts more bots in the vendor's portal than it is known to tolerate; empty means no ceiling.`;
}

/** The field's value as the API wants it, or undefined when it is not a number. */
function parsedParallelism(raw: string): number | null | undefined {
  const text = raw.trim();
  if (!text) return null;
  const value = Number(text);
  return Number.isInteger(value) && value >= 1 ? value : undefined;
}

/**
 * What a batch job delete actually did, as one sentence. The endpoint answers 200
 * with a per-job verdict, so "it failed" is never the whole truth: some jobs can be
 * gone while others were refused for a reason the operator can act on.
 */
function describeRefusedJobDeletes(
  refused: { job_id: number; error: string }[],
  deletedCount: number,
): string {
  const running = refused.filter((r) => r.error === "running_tasks").map((r) => r.job_id);
  const missing = refused.filter((r) => r.error === "not_found").map((r) => r.job_id);
  const other = refused.filter((r) => r.error !== "running_tasks" && r.error !== "not_found");
  const parts: string[] = [];
  if (deletedCount) {
    parts.push(`Deleted ${deletedCount} task job${deletedCount === 1 ? "" : "s"}.`);
  }
  if (running.length) {
    parts.push(
      `Job${running.length === 1 ? "" : "s"} #${running.join(", #")} still ${
        running.length === 1 ? "has a task" : "have tasks"
      } running - stop the job first, then delete it.`,
    );
  }
  if (missing.length) {
    parts.push(`Job${missing.length === 1 ? "" : "s"} #${missing.join(", #")} no longer exist.`);
  }
  for (const r of other) {
    parts.push(`Job #${r.job_id}: ${r.error}`);
  }
  return parts.join(" ");
}

/** Why a major job cannot be deleted right now; null when it can. The server
 * refuses the same two cases. */
function majorDeleteBlockedReason(major: MajorJob): string | null {
  if (major.has_running_tasks) {
    return "A bot of this automation is running. Stop its tasks first.";
  }
  if (major.has_active_split_jobs) {
    return "A split job of this major job is splitting, saving or linking SSNs. Stop it or wait for it first.";
  }
  return null;
}

function getErr(error: unknown, fallback: string): string {
  const stringify = (data: unknown): string | undefined => {
    if (data == null || data === "") return undefined;
    if (typeof data === "string") return data;
    if (typeof data === "number" || typeof data === "boolean") return String(data);
    if (Array.isArray(data)) {
      const parts = data.map((x) => stringify(x)).filter(Boolean) as string[];
      return parts.length ? parts.join(" ") : undefined;
    }
    if (typeof data === "object") {
      const o = data as Record<string, unknown>;
      for (const key of ["detail", "message", "error"]) {
        const inner = o[key];
        const s = stringify(inner);
        if (s) return s;
      }
      const parts: string[] = [];
      for (const [k, v] of Object.entries(o)) {
        if (k === "detail" || k === "message" || k === "error") continue;
        const s = stringify(v);
        if (s) parts.push(`${k}: ${s}`);
      }
      if (parts.length) return parts.join("; ");
    }
    return undefined;
  };
  const wrapped = error as { data?: unknown } | undefined;
  return stringify(wrapped?.data) || fallback;
}

export type MajorJobsPanelProps = {
  userId: number;
  userLabel?: string;
  connections: AdminVendorConnection[];
  connectionsLoading?: boolean;
  connectionsError?: boolean;
  onRefreshConnections: () => Promise<unknown>;
  /** Full-page workspace for one major job (opened via route). */
  detailMajorJobId?: number | null;
  onExitMajorDetail?: () => void;
  /** Opens major job workspace - typically navigate to `/bots-management/:userId/major-job/:id`. */
  onOpenMajorDetail?: (majorJobId: number) => void;
  /** Switches the page to the standalone Connections workspace for this user. */
  onOpenConnectionsScreen?: () => void;
  /** The one place the notes for DPM are written (CAL-6162). */
  onOpenBotNotesScreen?: () => void;
};

/** Major jobs + task jobs for one Django user (vendor-scoped pools). */
export default function MajorJobsPanel({
  userId,
  userLabel,
  connections,
  connectionsLoading = false,
  connectionsError = false,
  onRefreshConnections,
  detailMajorJobId = null,
  onExitMajorDetail,
  onOpenMajorDetail,
  onOpenConnectionsScreen,
  onOpenBotNotesScreen,
}: MajorJobsPanelProps) {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const { isCardMode, isCompact, isShort } = useResponsive();
  // A laptop window and a phone both run out of height before they run out of
  // room for the workspace meta: fold it away and let the operator ask for it.
  const foldWorkspaceMeta = isCardMode || isShort;
  /** Room the active tab is guaranteed; the workspace scrolls to provide it. */
  const workspaceTabMinHeight = isShort && !isCardMode ? 320 : 0;
  const [workspaceMetaOpen, setWorkspaceMetaOpen] = useState(false);
  const navigate = useAppNavigate();
  const location = useLocation();
  const { profile: botsProfile, readOnly: botsReadOnly, realProfile } = useViewAs();
  const canEditBots = isBotsOperator(botsProfile) && !botsReadOnly;
  const botsReadOnlyReason = botsReadOnly ? CLIENT_VIEW_READ_ONLY_TOOLTIP : null;
  // The initiator comparison and the live-view button follow the REAL
  // operator, and never Client View: the backend URL gate
  // (TaskLiveViewStatusSerializer._viewer_may_control) works the same way.
  const myPlatformUserId = useAppSelector((s) => s.auth.user?.id ?? null);
  const canOpenLiveView = isBotsOperator(realProfile) && !botsReadOnly;
  const { data: majorsRaw, refetch, isFetching: majorsFetching } = useGetMajorJobsQuery(userId, {
    refetchOnMountOrArgChange: true,
  });
  const majors = useMemo(() => {
    if (majorsRaw == null) return [];
    if (Array.isArray(majorsRaw)) return majorsRaw;
    const r = (majorsRaw as { results?: MajorJob[] }).results;
    return Array.isArray(r) ? r : [];
  }, [majorsRaw]);
  const [createMajorJob, { isLoading: isCreating }] = useCreateMajorJobMutation();
  const [updateMajorJob, { isLoading: isSavingMajor }] = useUpdateMajorJobMutation();
  const [deleteMajorJob, { isLoading: isDeletingMajor }] = useDeleteMajorJobMutation();

  const [createConnectionMutation, { isLoading: isMpcCreating }] =
    useCreateBackendUserBotConnectionMutation();
  const [updateConnectionMutation, { isLoading: isMpcUpdating }] =
    useUpdateAdminConnectionMutation();
  const [deleteConnectionMutation, { isLoading: isMpcDeleting }] =
    useDeleteAdminConnectionMutation();

  const [selectedMajorIds, setSelectedMajorIds] = useState<number[]>([]);
  const [activeMajorId, setActiveMajorId] = useState<number | null>(null);

  const [majorEditOpen, setMajorEditOpen] = useState(false);
  const [majorEditTarget, setMajorEditTarget] = useState<MajorJob | null>(null);
  const [majName, setMajName] = useState("");
  const [majVendor, setMajVendor] = useState("");
  const [majKind, setMajKind] = useState("download_bots");
  // Kept as text so the field can be empty, which is what "no limit" is.
  const [majJobsAtOnce, setMajJobsAtOnce] = useState("");
  const [majJobsAtOnceTouched, setMajJobsAtOnceTouched] = useState(false);
  // A number input reports "" for text it cannot read; that is not "no limit".
  const [majJobsAtOnceBadInput, setMajJobsAtOnceBadInput] = useState(false);
  const [majJobsAtOnceError, setMajJobsAtOnceError] = useState<string | null>(null);
  const [majorFormError, setMajorFormError] = useState<string | null>(null);
  const [majorRowDeleteTarget, setMajorRowDeleteTarget] = useState<MajorJob | null>(null);
  const [majorBulkDeleteOpen, setMajorBulkDeleteOpen] = useState(false);
  const majorDeletePreview = useGetMajorJobDeletePreviewQuery(majorRowDeleteTarget?.id ?? 0, {
    skip: majorRowDeleteTarget == null,
    refetchOnMountOrArgChange: true,
  });
  /** Why the server refused the last major-job delete: it declines while a task
   * of any nested job is still running, and that sentence is the whole answer. */
  const [majorDeleteError, setMajorDeleteError] = useState<string | null>(null);

  const { data: majorFormAllRegistry } = useGetBotRegistryQuery(
    { isActive: true, pageSize: 500 },
    { skip: !majorEditOpen },
  );
  const registryVendorsBase = useMemo(() => {
    const rows = normalizeBotRegistryPayload(majorFormAllRegistry);
    return Array.from(new Set(rows.map((r) => r.vendor))).sort((left, right) =>
      left.localeCompare(right),
    );
  }, [majorFormAllRegistry]);

  // A split major job is named after the vendor whose PDFs it splits: the
  // splitters' vendors are its whole choice.
  const majFormIsSplit = isSplitKind(majKind);
  const { data: splitterConfig } = useGetPdfToolsConfigQuery(undefined, {
    skip: !majorEditOpen || !majFormIsSplit,
  });
  const splitterVendors = useMemo(() => splitProvidersOf(splitterConfig), [splitterConfig]);
  const majorFormVendorOptions = useMemo(() => {
    const base = majFormIsSplit ? splitterVendors : registryVendorsBase;
    const v = majVendor.trim();
    if (!v) return base;
    const hit = base.some((x) => x.toLowerCase() === v.toLowerCase());
    if (!hit) return [...base, v].sort((a, b) => a.localeCompare(b));
    return base;
  }, [majFormIsSplit, splitterVendors, registryVendorsBase, majVendor]);
  const majorFormHasSplitJobs = (majorEditTarget?.split_job_count ?? 0) > 0;
  /** Why the vendor cannot carry a split major job; null when it can. */
  const majorFormSplitVendorError =
    majFormIsSplit &&
    splitterConfig != null &&
    majVendor.trim() !== "" &&
    !splitterVendors.some((x) => x.toLowerCase() === majVendor.trim().toLowerCase())
      ? `No splitter reads ${majVendor.trim()} PDFs. Pick one of: ${splitterVendors.join(", ")}.`
      : null;

  /** What this vendor recommends for "Jobs running at once", straight from the
   * registry - the same road Document type and the bot options take. */
  const majorVendorDefaultJobsAtOnce = useMemo(() => {
    const vendors =
      majorFormAllRegistry && !Array.isArray(majorFormAllRegistry)
        ? majorFormAllRegistry.vendors
        : undefined;
    const hit = vendors?.[majVendor.trim().toLowerCase()];
    return hit?.recommended_major_job_parallelism ?? null;
  }, [majorFormAllRegistry, majVendor]);

  // The default prefills a NEW major job when its vendor is picked, and only
  // until the operator types something: it is an offer, not a rule, and an
  // existing major job keeps whatever it was saved with (no backfill).
  useEffect(() => {
    if (!majorEditOpen || majorEditTarget || majJobsAtOnceTouched) return;
    setMajJobsAtOnce(
      majorVendorDefaultJobsAtOnce == null ? "" : String(majorVendorDefaultJobsAtOnce),
    );
  }, [majorEditOpen, majorEditTarget, majJobsAtOnceTouched, majorVendorDefaultJobsAtOnce]);

  const {
    data: childJobsData,
    currentData: childJobsCurrent,
    refetch: refetchChildJobs,
  } = useGetTaskJobsQuery(
    // include=tasks embeds every job's task rows, so this one request also
    // feeds the accordions - there is no per-job detail prefetch any more.
    { userId, majorJobId: activeMajorId ?? undefined, includeTasks: true },
    // Refetch on remount so returning from a task detail shows fresh task-job data.
    { skip: activeMajorId == null, refetchOnMountOrArgChange: true },
  );
  const childJobs = useMemo(() => {
    if (Array.isArray(childJobsData)) return childJobsData;
    const r = (childJobsData as { results?: unknown[] } | undefined)?.results;
    return Array.isArray(r) ? r : [];
  }, [childJobsData]);
  const childJobsLoading = childJobsData === undefined;

  /** Safe refresh — `refetch()` throws if the subscribed query was skipped (no active major yet). */
  const refreshChildJobsList = useCallback(async () => {
    const majorJobId = activeMajorId ?? detailMajorJobId ?? null;
    if (majorJobId == null) return;
    try {
      // The args must stay identical to the hook's above: a different arg
      // object is a different cache entry, and the hook would never see it.
      await dispatch(
        adminUsersApi.endpoints.getTaskJobs.initiate(
          { userId, majorJobId, includeTasks: true },
          { forceRefetch: true },
        ),
      ).unwrap();
    } catch {
      // ignore — list may not be mounted yet
    }
  }, [activeMajorId, detailMajorJobId, userId, dispatch]);

  const safeRefetchChildJobs = useCallback(async () => {
    if (activeMajorId == null) {
      await refreshChildJobsList();
      return;
    }
    try {
      await refetchChildJobs();
    } catch {
      await refreshChildJobsList();
    }
  }, [activeMajorId, refetchChildJobs, refreshChildJobsList]);

  const [createTasksBatch] = useCreateTasksBatchMutation();
  const [updateTaskJob, { isLoading: isMjEditJobSaving }] = useUpdateTaskJobMutation();
  const [refreshTaskJobStats] = useRefreshTaskJobStatsMutation();
  const [mjProcessErrorsSavingJobId, setMjProcessErrorsSavingJobId] = useState<
    number | null
  >(null);
  const [mjRowBatchBusyJobId, setMjRowBatchBusyJobId] = useState<number | null>(null);

  // Sticky mode, not the one-shot pass the Edit job dialog arms: while it is on,
  // every run of this job - Run all or a single task - carries
  // SKIP_ERROR_EMPLOYEES=False, so employees that errored before are retried.
  // The job PATCH copies additional_options down to all of its tasks.
  const toggleJobProcessErrors = async (
    jobId: number,
    rawOptions: string,
    processErrors: boolean,
  ) => {
    setMjProcessErrorsSavingJobId(jobId);
    try {
      const { remaining } = extractSkipErrorEmployees(rawOptions);
      await updateTaskJob({
        jobId,
        body: {
          additional_options: applySkipErrorEmployees(remaining, processErrors),
        },
      }).unwrap();
      // The row renders the list row merged with the cached job detail, and the
      // detail wins - without this the switch snaps back to its old position.
      // Silent: the PATCH already landed, so a failed follow-up read must not
      // claim the change failed.
      await refreshMjJobDetailRef.current(jobId, false, true);
    } catch (e) {
      setMjJobActionAlertsByJobId((prev) => ({
        ...prev,
        [jobId]: getErr(e, "Could not change 'Process error EEs'."),
      }));
    } finally {
      setMjProcessErrorsSavingJobId(null);
    }
  };
  const [runJobNewRows] = useRunJobNewRowsMutation();
  const [runJobRosterTask] = useRunJobRosterTaskMutation();
  const [mjRosterTaskBusyJobId, setMjRosterTaskBusyJobId] = useState<number | null>(null);
  // Which job the "Build roster only?" confirmation is open for.
  const [mjRosterConfirmJobId, setMjRosterConfirmJobId] = useState<number | null>(null);
  const [mjRosterExistsPrompt, setMjRosterExistsPrompt] = useState<{
    jobId: number;
    files: string[];
    message: string;
  } | null>(null);
  // "Tasks of this job have run" - asked before a rebuild renumbers their rows.
  const [mjRosterReindexPrompt, setMjRosterReindexPrompt] = useState<{
    jobId: number;
    message: string;
  } | null>(null);
  const [mjRosterRecoveringJobId, setMjRosterRecoveringJobId] = useState<number | null>(
    null,
  );
  const [fetchJobRoster] = useLazyGetJobRosterQuery();
  const [deleteJobRosterMut] = useDeleteJobRosterMutation();
  const [recoverJobRosterMut] = useRecoverJobRosterMutation();
  const [presignArtifactUpload] = usePresignJobArtifactUploadMutation();
  const [replaceJobMatchesMut] = useReplaceJobMatchesMutation();
  const [replaceJobRosterMut] = useReplaceJobRosterMutation();
  const [fetchJobSsnReadiness] = useLazyGetJobSsnReadinessQuery();
  const [fetchMajorSsnReadiness] = useLazyGetMajorJobSsnReadinessQuery();
  const [fetchBrowserProfiles] = useLazyGetMajorJobBrowserProfilesQuery();
  const [deleteBrowserProfilesMut] = useDeleteMajorJobBrowserProfilesMutation();
  const [runMatchesPreflight] = useRunJobMatchesPreflightMutation();
  // The stored file of the job open in Manage job, and the one-line notice
  // after it was deleted or replaced.
  // Stored roster / Matches file facts per job: Manage job reads them, and
  // the Build roster only buttons are greyed out while such a file exists.
  const [mjRosterInfoByJobId, setMjRosterInfoByJobId] = useState<Record<number, JobRosterInfo>>({});
  const [mjRosterInfoLoading, setMjRosterInfoLoading] = useState(false);
  const [mjRosterNotice, setMjRosterNotice] = useState<string | null>(null);
  const [mjRosterDeletePrompt, setMjRosterDeletePrompt] = useState<{
    jobId: number;
    /** The server's reindex question, once it has been asked. */
    reindex?: string;
  } | null>(null);
  const [mjRosterDeleting, setMjRosterDeleting] = useState(false);
  const [mjArtifactUpload, setMjArtifactUpload] = useState<{
    jobId: number;
    purpose: "matches" | "roster";
    file: File | null;
    step: "idle" | "uploading" | "ready" | "replacing" | "done" | "error";
    stagingKey: string | null;
    error: string | null;
    reindex: { message: string; committed: number[] } | null;
    confirmReindex: boolean;
    result: string | null;
  } | null>(null);
  const [mjBrowserProfiles, setMjBrowserProfiles] = useState<BrowserProfilesInfo | null>(null);
  const [mjBrowserProfilesPrompt, setMjBrowserProfilesPrompt] = useState(false);
  const [mjBrowserProfilesBusy, setMjBrowserProfilesBusy] = useState(false);
  const [mjBrowserProfilesError, setMjBrowserProfilesError] = useState<string | null>(null);
  // SSN readiness per upload job (fetched when a job row is expanded or managed),
  // and the "run by names?" question asked before a run that would build matches.
  const [mjSsnReadinessByJobId, setMjSsnReadinessByJobId] = useState<Record<number, SsnReadiness>>({});
  const mjSsnReadinessRequestedRef = useRef<Set<number>>(new Set());
  const [mjSsnRunPrompt, setMjSsnRunPrompt] = useState<{
    readiness: SsnReadiness;
    label: string;
    proceed: () => Promise<void> | void;
  } | null>(null);
  const [childSsnReadiness, setChildSsnReadiness] = useState<SsnReadiness | null>(null);
  const [mjEditSsnReadiness, setMjEditSsnReadiness] = useState<SsnReadiness | null>(null);
  const [mjNewRowsRunningJobId, setMjNewRowsRunningJobId] = useState<number | null>(null);
  // The vendor's own limit on parallel sessions is the operator's to know, so
  // the number of tasks is asked for rather than derived.
  const [mjNewRowsJob, setMjNewRowsJob] = useState<{
    id: number;
    rows: number;
  } | null>(null);
  const [mjNewRowsTaskCount, setMjNewRowsTaskCount] = useState("1");
  const runNewRosterRows = async (jobId: number, taskCount: number) => {
    setMjNewRowsJob(null);
    setMjNewRowsRunningJobId(jobId);
    try {
      await runJobNewRows({ jobId, taskCount }).unwrap();
      await refreshMjJobDetailRef.current(jobId, false, true);
    } catch (e) {
      setMjJobActionAlertsByJobId((prev) => ({
        ...prev,
        [jobId]: getErr(e, "Could not run the new roster rows."),
      }));
    } finally {
      setMjNewRowsRunningJobId(null);
    }
  };

  const mjRecheckMatches = async (jobId: number) => {
    setMjRecheckingJobIds((prev) => ({ ...prev, [jobId]: true }));
    try {
      await runMatchesPreflight(jobId).unwrap();
    } catch (e) {
      setMjJobActionAlertsByJobId((prev) => ({
        ...prev,
        [jobId]: getErr(e, "Could not check the matches."),
      }));
    } finally {
      setMjRecheckingJobIds((prev) => {
        const next = { ...prev };
        delete next[jobId];
        return next;
      });
    }
  };

  const mjRunRosterTask = async (jobId: number, force = false, confirmReindex = false) => {
    setMjRosterConfirmJobId(null);
    setMjRosterExistsPrompt(null);
    setMjRosterTaskBusyJobId(jobId);
    setMjJobActionAlertsByJobId((prev) => {
      const next = { ...prev };
      delete next[jobId];
      return next;
    });
    try {
      await runJobRosterTask({
        jobId,
        force,
        confirm_reindex: confirmReindex,
      }).unwrap();
      await refreshMjJobDetailRef.current(jobId, true, true);
      await reloadMjAccordionTasksRef.current(jobId);
    } catch (e) {
      const data = (e as { data?: { code?: string; files?: string[]; message?: string } })
        ?.data;
      if (data?.code === "roster_exists") {
        setMjRosterExistsPrompt({
          jobId,
          files: data.files ?? [],
          message: data.message ?? "The roster is already built.",
        });
      } else if (data?.code === "confirm_reindex_required") {
        // Collecting it again puts the employees on other rows, and tasks of
        // this job have already run against the old numbering.
        setMjRosterReindexPrompt({
          jobId,
          message: data.message ?? "",
        });
      } else {
        setMjJobActionAlertsByJobId((prev) => ({
          ...prev,
          [jobId]: getErr(e, "Could not start the roster task."),
        }));
      }
    } finally {
      setMjRosterTaskBusyJobId(null);
    }
  };

  const mjJobRowById = (jobId: number): Record<string, unknown> | null => {
    const raw = childJobs.find((r) => Number((r as Record<string, unknown>).id) === jobId);
    if (!raw) return null;
    return { ...(raw as Record<string, unknown>), ...(mjJobDetailById[jobId] ?? {}) };
  };

  const loadMjRosterInfo = useCallback(
    async (jobId: number) => {
      setMjRosterInfoLoading(true);
      try {
        const info = await fetchJobRoster(jobId).unwrap();
        setMjRosterInfoByJobId((prev) => ({ ...prev, [jobId]: info }));
        if (info.kind === "matches") {
          try {
            const readiness = await fetchJobSsnReadiness(jobId).unwrap();
            setMjSsnReadinessByJobId((prev) => ({ ...prev, [jobId]: readiness }));
          } catch {
            // Readiness is advisory; the file line still renders.
          }
        }
      } catch {
        setMjRosterInfoByJobId((prev) => {
          const next = { ...prev };
          delete next[jobId];
          return next;
        });
      } finally {
        setMjRosterInfoLoading(false);
      }
    },
    [fetchJobRoster, fetchJobSsnReadiness],
  );

  const confirmMjRosterDelete = async () => {
    const jobId = mjRosterDeletePrompt?.jobId;
    if (jobId == null) return;
    setMjRosterDeleting(true);
    try {
      const res = await deleteJobRosterMut({
        jobId,
        confirm_reindex: mjRosterDeletePrompt?.reindex != null,
      }).unwrap();
      setMjRosterDeletePrompt(null);
      setMjManageJobError(null);
      const shared = res.sibling_job_ids.length
        ? ` Job(s) ${res.sibling_job_ids.map((id) => `#${id}`).join(", ")} read the same file and start over too.`
        : "";
      setMjRosterNotice(
        `${res.deleted_keys.length} file(s) deleted. The next run collects the roster again.${shared}`,
      );
      await refreshMjJobDetailRef.current(jobId, true, true);
      await loadMjRosterInfo(jobId);
      if (mjRosterInfo?.info.kind === "matches") await loadMatchesFiles(jobId);
    } catch (e) {
      // Tasks of this job have run: a new file puts the employees on other
      // rows, so the operator says they know before it is collected again.
      const data = (e as { data?: { code?: string; message?: string } })?.data;
      if (data?.code === "confirm_reindex_required") {
        setMjRosterDeletePrompt({ jobId, reindex: data.message ?? "" });
        return;
      }
      setMjRosterDeletePrompt(null);
      setMjManageJobError(getErr(e, "Could not delete the stored file."));
    } finally {
      setMjRosterDeleting(false);
    }
  };

  /** Deal the roster out again across the job's tasks. Starts no bot. */
  const mjRecoverRoster = async (jobId: number) => {
    setMjRosterRecoveringJobId(jobId);
    setMjJobActionAlertsByJobId((prev) => {
      const next = { ...prev };
      delete next[jobId];
      return next;
    });
    try {
      const res = await recoverJobRosterMut(jobId).unwrap();
      setMjJobNoticesByJobId((prev) => ({ ...prev, [jobId]: res.message }));
      await refreshMjJobDetailRef.current(jobId, true, true);
      await reloadMjAccordionTasksRef.current(jobId);
    } catch (e) {
      setMjJobActionAlertsByJobId((prev) => ({
        ...prev,
        [jobId]: getErr(e, "Could not re-cover the roster."),
      }));
    } finally {
      setMjRosterRecoveringJobId(null);
    }
  };

  const openMjArtifactUpload = (jobId: number, purpose: "matches" | "roster") =>
    setMjArtifactUpload({
      jobId,
      purpose,
      file: null,
      step: "idle",
      stagingKey: null,
      error: null,
      reindex: null,
      confirmReindex: false,
      result: null,
    });

  const handleMjArtifactFileSelect = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    e.target.value = "";
    if (!file || !mjArtifactUpload) return;
    const { jobId, purpose } = mjArtifactUpload;
    setMjArtifactUpload((prev) =>
      prev ? { ...prev, file, step: "uploading", error: null, stagingKey: null, result: null } : prev,
    );
    try {
      const presign = await presignArtifactUpload({
        jobId,
        file_name: file.name,
        content_type: file.type || "application/octet-stream",
        purpose,
      }).unwrap();
      const response = await fetch(presign.upload_url, {
        method: "PUT",
        headers: presign.headers,
        body: file,
      });
      if (!response.ok) throw new Error(`Upload failed with status ${response.status}`);
      setMjArtifactUpload((prev) => (prev ? { ...prev, step: "ready", stagingKey: presign.key } : prev));
    } catch (err) {
      setMjArtifactUpload((prev) =>
        prev ? { ...prev, step: "error", error: getErr(err, "Upload failed.") } : prev,
      );
    }
  };

  const confirmMjArtifactReplace = async () => {
    const state = mjArtifactUpload;
    if (!state?.stagingKey) return;
    setMjArtifactUpload((prev) => (prev ? { ...prev, step: "replacing", error: null } : prev));
    try {
      const body = {
        jobId: state.jobId,
        staging_key: state.stagingKey,
        confirm_reindex: state.confirmReindex,
      };
      const result =
        state.purpose === "matches"
          ? await replaceJobMatchesMut({ ...body, file_name: state.file?.name }).unwrap()
          : await replaceJobRosterMut(body).unwrap();
      setMjArtifactUpload((prev) =>
        prev
          ? {
              ...prev,
              step: "done",
              stagingKey: null,
              result: `${result.rows} row(s) written. The job now covers ${
                result.actual_total_employees ?? "-"
              } employee(s).`,
            }
          : prev,
      );
      await refreshMjJobDetailRef.current(state.jobId, true, true);
      await loadMjRosterInfo(state.jobId);
      if (state.purpose === "matches") await loadMatchesFiles(state.jobId);
    } catch (err) {
      const data = (
        err as { data?: { code?: string; message?: string; committed_tasks?: number[] } }
      )?.data;
      if (data?.code === "confirm_reindex_required") {
        setMjArtifactUpload((prev) =>
          prev
            ? {
                ...prev,
                step: "ready",
                reindex: { message: data.message ?? "", committed: data.committed_tasks ?? [] },
              }
            : prev,
        );
        return;
      }
      // The staged upload is consumed by the attempt: a retry needs the file again.
      setMjArtifactUpload((prev) =>
        prev
          ? { ...prev, step: "error", stagingKey: null, error: getErr(err, "Replace failed.") }
          : prev,
      );
    }
  };

  const loadMjBrowserProfiles = useCallback(
    async (majorJobId: number) => {
      try {
        setMjBrowserProfiles(await fetchBrowserProfiles(majorJobId).unwrap());
      } catch {
        setMjBrowserProfiles(null);
      }
    },
    [fetchBrowserProfiles],
  );

  const confirmMjBrowserProfilesDelete = async () => {
    if (!activeMajor) return;
    setMjBrowserProfilesBusy(true);
    try {
      const res = await deleteBrowserProfilesMut({ majorJobId: activeMajor.id }).unwrap();
      setMjBrowserProfiles(res);
      setMjBrowserProfilesPrompt(false);
      setMjBrowserProfilesError(null);
    } catch (e) {
      setMjBrowserProfilesPrompt(false);
      setMjBrowserProfilesError(getErr(e, "Could not delete the browser profile."));
    } finally {
      setMjBrowserProfilesBusy(false);
    }
  };

  // Before a run that would build the matches file by name, ask. A run that
  // reuses an existing Matches file matches nothing, so it is not asked.
  const mjSsnGuardedRun = async (
    job: Record<string, unknown> | null | undefined,
    label: string,
    proceed: () => Promise<void> | void,
  ) => {
    if (!job || !mjIsUploadJob(job)) {
      await proceed();
      return;
    }
    const jobId = Number(job.id);
    let readiness: SsnReadiness | null = mjSsnReadinessByJobId[jobId] ?? null;
    try {
      readiness = await fetchJobSsnReadiness(jobId).unwrap();
      const fresh = readiness;
      setMjSsnReadinessByJobId((prev) => ({ ...prev, [jobId]: fresh }));
    } catch {
      // Unknown readiness must not block a run.
    }
    if (readiness && readiness.algorithm === "names" && readiness.matches_file_exists === false) {
      setMjSsnRunPrompt({ readiness, label, proceed });
      return;
    }
    await proceed();
  };

  const [taskJobsBatchDelete, { isLoading: isDeletingJobsBulk }] =
    useTaskJobsBatchDeleteMutation();

  const [triggerJobLogsArchive, { isFetching: isMjLogsArchiveLoading }] =
    useLazyGetJobLogsArchiveQuery();
  const [clearJobLogsMut, { isLoading: isMjClearLogsLoading }] = useClearJobLogsMutation();
  const [fetchJobLogsInventory] = useLazyGetJobLogsInventoryQuery();
  const [mjClearLogsJobId, setMjClearLogsJobId] = useState<number | null>(null);
  // The job's log folder is shared with every other job of the same document type, so
  // the dialog has to say what is in there before anything is deleted.
  const [mjClearLogsInventory, setMjClearLogsInventory] = useState<{
    own: number;
    shared: number;
  } | null>(null);
  const [mjClearLogsIncludeShared, setMjClearLogsIncludeShared] = useState(false);

  const [deleteTaskJobMut, { isLoading: isMjDeletingJob }] = useDeleteTaskJobMutation();
  const [addTasksToJobMutation, { isLoading: isAddingTasksToJob }] = useAddTasksToJobMutation();
  const [reduceJobTasksMutation, { isLoading: isReducingJobTasks }] = useReduceJobTasksMutation();
  const [runTaskMutation, { isLoading: isRunTaskBusy }] = useRunTaskMutation();
  const [stopTaskMutation, { isLoading: isStopTaskBusy }] = useStopTaskMutation();
  const [updateAdminTaskMut, { isLoading: isUpdatingTaskRow }] = useUpdateAdminTaskMutation();
  const [runTasksBatchMut, { isLoading: isMjBatchRunning }] = useRunTasksBatchMutation();
  const [stopTasksBatchMut, { isLoading: isMjBatchStopping }] = useStopTasksBatchMutation();
  const [submitTask2FACodeMut, { isLoading: isSubmit2FAMj }] = useSubmitTask2FACodeMutation();
  const [expandedMjJobAccordion, setExpandedMjJobAccordion] = useState<Record<number, boolean>>({});
  const [mjAccordionTasksByJob, setMjAccordionTasksByJob] = useState<Record<number, MjConnectionTask[]>>({});
  const [mjAccordionLoadingJobId, setMjAccordionLoadingJobId] = useState<number | null>(null);
  /** Timestamp (ms) of last successful accordion tasks fetch — used for 30-second cache. */
  const [mjAccordionCacheTs, setMjAccordionCacheTs] = useState<Record<number, number>>({});
  // The prefetch below reads freshness from a callback that outlives its render,
  // so it needs the live value, not the one captured back then. Written together
  // with the state, never through an effect: a prefetch answer can land between
  // a reload's setState and the following render.
  const mjAccordionCacheTsRef = useRef<Record<number, number>>({});
  const markMjAccordionFresh = useCallback((jobId: number) => {
    const now = Date.now();
    mjAccordionCacheTsRef.current = { ...mjAccordionCacheTsRef.current, [jobId]: now };
    setMjAccordionCacheTs((prev) => ({ ...prev, [jobId]: now }));
  }, []);
  /** Task ID that is currently being run or stopped (for per-row spinner). */
  const [busyTaskId, setBusyTaskId] = useState<number | null>(null);
  /** Coverage / employees-info warnings keyed by task job id (from API stats). */
  const [mjJobRangeWarningsByJobId, setMjJobRangeWarningsByJobId] = useState<Record<number, string>>({});
  /** Run/stop/delete errors shown inside each job accordion row. */
  const [mjJobActionAlertsByJobId, setMjJobActionAlertsByJobId] = useState<Record<number, string>>({});
  // Advisory answers to a row action, e.g. "queued behind the roster task".
  const [mjJobNoticesByJobId, setMjJobNoticesByJobId] = useState<Record<number, string>>({});
  const [mjRefreshingJobIds, setMjRefreshingJobIds] = useState<Record<number, boolean>>({});
  const [mjJobDetailById, setMjJobDetailById] = useState<Record<number, Record<string, unknown>>>({});
  const [mjWsConnected, setMjWsConnected] = useState(() => botsManagementWs.isConnected());
  const expandedMjJobAccordionRef = useRef<Record<number, boolean>>({});
  const mjAccordionTasksByJobRef = useRef<Record<number, MjConnectionTask[]>>({});
  const reloadMjAccordionTasksRef = useRef<(jobId: number) => Promise<MjConnectionTask[] | null>>(
    async () => null,
  );
  const refreshMjJobDetailRef = useRef<
    (jobId: number, refreshList?: boolean, silent?: boolean) => Promise<void>
  >(
    async () => undefined,
  );
  /** Job IDs with an accordion reload currently in flight — dedupes rapid open/close/open. */
  const mjAccordionInFlightRef = useRef<Set<number>>(new Set());
  // Jobs counted on their first open this session (no stats snapshot yet).
  const mjAutoScannedJobsRef = useRef<Set<number>>(new Set());
  const userIdRef = useRef(userId);
  const activeMajorForWsRef = useRef<number | null>(null);
  // The major jobs list carries the split jobs count and whether one is busy.
  const refetchMajorsRef = useRef(refetch);
  refetchMajorsRef.current = refetch;
  // The first "connected" after the page load is not a reconnect: the data on
  // screen came from REST moments earlier, so it needs no catch-up refetch.
  const hasSeenWsConnectedRef = useRef(botsManagementWs.isConnected());
  /** Latest reload sequence per job, so an out-of-order response is dropped. */
  const mjAccordionSeqRef = useRef<Record<number, number>>({});
  const safeRefetchChildJobsRef = useRef<() => Promise<void>>(async () => undefined);

  const [mjManageJobId, setMjManageJobId] = useState<number | null>(null);
  // The job whose Send rejected files to SFTP dialog is open.
  const [mjSftpExportJobId, setMjSftpExportJobId] = useState<number | null>(null);
  // The job whose SSN Updater dialog is open, and the jobs whose matches
  // re-check was just requested (the result arrives over the websocket).
  const [mjSsnUpdaterJobId, setMjSsnUpdaterJobId] = useState<number | null>(null);
  const [mjRecheckingJobIds, setMjRecheckingJobIds] = useState<Record<number, boolean>>({});
  const mjRosterInfo =
    mjManageJobId != null && mjRosterInfoByJobId[mjManageJobId]
      ? { jobId: mjManageJobId, info: mjRosterInfoByJobId[mjManageJobId] }
      : null;
  const [mjManageJobDetail, setMjManageJobDetail] = useState<Record<string, unknown> | null>(null);
  const [mjManageJobError, setMjManageJobError] = useState<string | null>(null);
  const [mjManageJobNotice, setMjManageJobNotice] = useState<string | null>(null);

  const [mjAddTasksOpen, setMjAddTasksOpen] = useState(false);
  const [mjAddTasksJobId, setMjAddTasksJobId] = useState<number | null>(null);
  const [mjAddTasksLoading, setMjAddTasksLoading] = useState(false);
  const [mjBulkAddRows, setMjBulkAddRows] = useState<ConnPlanEditorRow[]>([
    { connection_id: "", task_target_count: "1" },
  ]);
  const [mjAddPlanConnPool, setMjAddPlanConnPool] = useState<number[]>([]);
  const [mjAddTasksErr, setMjAddTasksErr] = useState<string | null>(null);
  // How many tasks the job should end up split into - the one number the Tasks
  // dialog is about, in both directions.
  const [mjTaskCountTarget, setMjTaskCountTarget] = useState("1");

  const [mjDeleteJobId, setMjDeleteJobId] = useState<number | null>(null);

  const [mjEditJobOpen, setMjEditJobOpen] = useState(false);
  const [mjEditJobId, setMjEditJobId] = useState<number | null>(null);
  const [mjEditJobName, setMjEditJobName] = useState("");
  const [mjEditBotName, setMjEditBotName] = useState("");
  const [mjEditDocumentType, setMjEditDocumentType] = useState("");
  // The job as the dialog found it. Required values are judged as a difference:
  // a job that predates an option must stay renameable, so only what this edit
  // takes away is refused. Mirrors _job_config_invalid_message on the API.
  const [mjEditBaseline, setMjEditBaseline] = useState({
    botName: "",
    documentType: "",
    additionalOptions: "",
  });
  const [mjEditDocumentTypes, setMjEditDocumentTypes] = useState<string[]>([" "]);
  const [mjEditAdditionalOptions, setMjEditAdditionalOptions] = useState("");
  const [mjEditProcessErrors, setMjEditProcessErrors] = useState(false);
  const [mjEditSeparateProfiles, setMjEditSeparateProfiles] = useState(false);
  const [mjEditMaxParallel, setMjEditMaxParallel] = useState("");
  const [mjEditUploadAsZip, setMjEditUploadAsZip] = useState(false);
  const [mjEditUnzipBefore, setMjEditUnzipBefore] = useState(false);
  const [mjEditUseSsnMatch, setMjEditUseSsnMatch] = useState(true);
  const [mjEditSourcePath, setMjEditSourcePath] = useState("");
  const [mjEditSourceSsn, setMjEditSourceSsn] = useState<SourceSsnChoice>(undefined);
  const [mjEditJobError, setMjEditJobError] = useState<string | null>(null);

  // Taking one task out of the split - the soft one, the logs stay.
  const [mjRemoveTaskPrompt, setMjRemoveTaskPrompt] = useState<{
    jobId: number;
    taskId: number;
  } | null>(null);
  const [mjAddOneTaskJobId, setMjAddOneTaskJobId] = useState<number | null>(null);
  /** Task ids selected inside a child job accordion (for batch delete). */
  const [mjNestedTaskSelectionByJob, setMjNestedTaskSelectionByJob] = useState<Record<number, number[]>>({});
  // Everything the ticked rows can be put through, in one place.
  const [mjManageSelectionJobId, setMjManageSelectionJobId] = useState<number | null>(null);
  const [mjSelectionBusyAction, setMjSelectionBusyAction] = useState<string | null>(null);
  const [twoFaMjTask, setTwoFaMjTask] = useState<MjConnectionTask | null>(null);
  // Captcha the bot cannot solve: the operator solves it in the bot's browser.
  const [liveViewMjTaskId, setLiveViewMjTaskId] = useState<number | null>(null);
  // Tasks whose window the operator closed by hand - the derived rule below
  // would otherwise put it straight back up on the next poll.
  const [liveViewMjDismissed, setLiveViewMjDismissed] = useState<number[]>([]);
  const [twoFaMjCode, setTwoFaMjCode] = useState("");
  const [twoFaMjErr, setTwoFaMjErr] = useState<string | null>(null);
  // Task ids where the operator forced manual 2FA entry during the auto phase.
  const [twoFaManualOverrideIds, setTwoFaManualOverrideIds] = useState<Set<number>>(
    () => new Set(),
  );

  // The Logs popup: the viewer owns its own fetching, filters and refreshing, so
  // the panel only has to say whose logs to show.
  const [logsScope, setLogsScope] = useState<LogsScope | null>(null);
  const [logsTitle, setLogsTitle] = useState<string>("");

  // Error popup (clickable CRITICAL_ERROR status chip)
  const [mjErrorPopupTask, setMjErrorPopupTask] = useState<{ jobId: number; task: MjConnectionTask } | null>(null);

  // Matches tab (inside Manage Job dialog, upload_bots only)
  const [mjManageTab, setMjManageTab] = useState(0);
  const [matchesFiles, setMatchesFiles] = useState<MatchesFileInfo[] | null>(null);
  const [matchesSummary, setMatchesSummary] = useState<MatchesSummary | null>(null);
  const [matchesLoading, setMatchesLoading] = useState(false);
  const [matchesError, setMatchesError] = useState<string | null>(null);
  // CSV viewer popup
  const [matchesViewOpen, setMatchesViewOpen] = useState(false);
  const [matchesViewFilename, setMatchesViewFilename] = useState("");
  const [matchesViewRows, setMatchesViewRows] = useState<string[][]>([]);
  const [matchesViewLoading, setMatchesViewLoading] = useState(false);
  // Delete confirmation
  const [matchesDeleteFilename, setMatchesDeleteFilename] = useState<string | null>(null);
  const [matchesDeleting, setMatchesDeleting] = useState(false);
  const [matchesSelectedFiles, setMatchesSelectedFiles] = useState<string[]>([]);
  // Merge reports: one slot per reviewed file; the backend checks them together on Merge.
  const [matchesMerge, setMatchesMerge] = useState<{
    jobId: number;
    slots: Record<MergeReportKind, MergeSlotState>;
    step: "idle" | "merging";
    error: string | null;
  } | null>(null);
  const [matchesMergeSuccess, setMatchesMergeSuccess] = useState<string | null>(null);
  // No matches / Several matches review: the file labels, the viewer's per-employee
  // states, the "No match found" prompt and what came of it.
  const [matchesReview, setMatchesReview] = useState<UnmatchedReview | null>(null);
  const [matchesViewReview, setMatchesViewReview] = useState<UnmatchedReviewDetail | null>(null);
  const [matchesReviewPrompt, setMatchesReviewPrompt] = useState<{
    jobId: number;
    kind: MergeReportKind;
    action: UnmatchedReviewAction;
  } | null>(null);
  const [matchesReviewBusy, setMatchesReviewBusy] = useState<MergeReportKind | null>(null);
  const [matchesReviewNotice, setMatchesReviewNotice] = useState<{
    severity: "success" | "error";
    text: string;
  } | null>(null);
  const [triggerGetUnmatchedReview] = useLazyGetJobUnmatchedReviewQuery();
  const [markUnmatchedReviewMut] = useMarkJobUnmatchedReviewMutation();
  const matchesViewKind = unmatchedKindForFilename(matchesViewFilename);
  // Prefilled search of the Logs popup - a deep link onto one employee.
  const [logsSearch, setLogsSearch] = useState("");
  const [triggerGetMatchesFileContent] = useLazyGetJobMatchesFileContentQuery();
  const [deleteMatchesFileMut] = useDeleteJobMatchesFileMutation();
  const [presignMatchesMergeMut] = usePresignJobMatchesMergeUploadMutation();
  const [mergeMatchesReportsMut] = useMergeJobMatchesReportsMutation();

  // ── SSN Job tab ────────────────────────────────────────────────────────────
  type SsnJobData = {
    job: Record<string, unknown> | null;
    task: Record<string, unknown> | null;
    /** "Holds data": an empty file reads false here, as it does everywhere else. */
    ssn_file_exists: boolean | null;
    ssn_file: SsnFileInfo | null;
    /** Upload jobs of other major jobs reading this file as their source. */
    used_as_source_by?: SsnSourceReader[];
  };
  const [ssnJobData, setSsnJobData] = useState<SsnJobData | null>(null);
  const [ssnJobLoading, setSsnJobLoading] = useState(false);
  const [ssnJobError, setSsnJobError] = useState<string | null>(null);
  const [ssnFileDeleteError, setSsnFileDeleteError] = useState<string | null>(null);
  // Sending an SSN file by hand (CAL-6159): the dialog previews it first.
  const [ssnUploadOpen, setSsnUploadOpen] = useState(false);
  const [ssnJobSelectedConnId, setSsnJobSelectedConnId] = useState<number | null>(null);
  const [ssnJobCreating, setSsnJobCreating] = useState(false);
  const [ssnJobDeleting, setSsnJobDeleting] = useState(false);
  const [ssnJobFileDeleting, setSsnJobFileDeleting] = useState(false);
  const [ssnJobDeleteConfirmOpen, setSsnJobDeleteConfirmOpen] = useState(false);
  const [ssnAccordionExpanded, setSsnAccordionExpanded] = useState(false);
  const [createSsnJobMut] = useCreateMajorJobSsnJobMutation();
  const [updateSsnJobMut] = useUpdateMajorJobSsnJobMutation();
  const [deleteSsnJobMut] = useDeleteMajorJobSsnJobMutation();
  const [ssnAdditionalOptions, setSsnAdditionalOptions] = useState("");
  const [ssnOptionsSaving, setSsnOptionsSaving] = useState(false);
  const [ssnJobDialogOpen, setSsnJobDialogOpen] = useState(false);
  const [ssnJobDialogMode, setSsnJobDialogMode] = useState<"create" | "edit">("create");
  const [ssnJobDialogError, setSsnJobDialogError] = useState<string | null>(null);
  // What the local editor was last filled from, so a silent refresh cannot wipe
  // an edit in progress while a real change on the server still lands.
  const ssnOptionsSourceRef = useRef<string | null>(null);
  const [deleteSsnFileMut] = useDeleteMajorJobSsnFileMutation();
  const [triggerGetSsnFileContent] = useLazyGetMajorJobSsnFileContentQuery();
  const [triggerGetSsnFileDownloadUrl] = useLazyGetMajorJobSsnFileDownloadUrlQuery();
  const [ssnFileViewOpen, setSsnFileViewOpen] = useState(false);
  const [triggerGetRosterDownloadUrl] = useLazyGetJobRosterDownloadUrlQuery();
  const [rosterViewJobId, setRosterViewJobId] = useState<number | null>(null);
  const ssnJobDataRef = useRef<SsnJobData | null>(null);
  const loadSsnJobRef = useRef<(majorJobId: number, opts?: { silent?: boolean }) => Promise<void>>(async () => {});
  const loadMatchesFilesRef = useRef<(jobId: number) => Promise<void>>(async () => {});
  const ssnMajorIdRef = useRef<number | null>(null);
  // A file of '{"ssn_info": {}}' is a failed run, not a result - the bot throws it
  // away and collects again, and the matching calls the job "names only". The tab
  // still offers View / Download / Delete for it: an empty file is a thing to look
  // at, which is why the buttons follow "there is a file" and the status follows
  // "it holds data".
  const ssnFileFacts = ssnJobData?.ssn_file ?? null;
  const ssnFilePresent = ssnFileFacts
    ? ssnFileFacts.exists
    : ssnJobData?.ssn_file_exists === true;
  const ssnFileEmpty = Boolean(ssnFileFacts?.exists && ssnFileFacts.empty);
  const ssnFileStamp = [
    formatFileSize(ssnFileFacts?.size_bytes),
    ssnFileFacts?.last_modified
      ? new Date(ssnFileFacts.last_modified).toLocaleString()
      : "",
  ]
    .filter(Boolean)
    .join(", ");

  const loadSsnJob = async (majorJobId: number, { silent = false }: { silent?: boolean } = {}) => {
    if (!silent) {
      setSsnJobLoading(true);
      setSsnJobError(null);
      setSsnJobData(null);
    }
    try {
      const res = await dispatch(
        adminUsersApi.endpoints.getMajorJobSsnJob.initiate(majorJobId, { subscribe: false, forceRefetch: true }),
      ).unwrap();
      const next = res as SsnJobData;
      setSsnJobData((prev) => {
        if (prev?.ssn_file_exists !== next.ssn_file_exists) void refetch();
        return next;
      });
    } catch (e: unknown) {
      if (!silent) {
        const msg = (e as { data?: { message?: string } })?.data?.message ?? "Failed to load SSN job.";
        setSsnJobError(msg);
      }
    } finally {
      if (!silent) setSsnJobLoading(false);
    }
  };

  /** Inline draft connection IDs for tasks shown in the per-job accordion table.
   *  Shape: { [taskId]: pendingConnectionId }. A row shows a Save button while
   *  the draft differs from the task's persisted connection. */
  const [taskConnDraftById, setTaskConnDraftById] = useState<Record<number, number>>({});
  const [taskConnSavingId, setTaskConnSavingId] = useState<number | null>(null);
  const [taskConnSaveErrById, setTaskConnSaveErrById] = useState<Record<number, string>>({});

  const setTaskConnDraft = useCallback((taskId: number, cid: number) => {
    setTaskConnDraftById((prev) => ({ ...prev, [taskId]: cid }));
    setTaskConnSaveErrById((prev) => {
      if (!prev[taskId]) return prev;
      const next = { ...prev };
      delete next[taskId];
      return next;
    });
  }, []);

  const clearTaskConnDraft = useCallback((taskId: number) => {
    setTaskConnDraftById((prev) => {
      if (prev[taskId] == null) return prev;
      const next = { ...prev };
      delete next[taskId];
      return next;
    });
    setTaskConnSaveErrById((prev) => {
      if (prev[taskId] == null) return prev;
      const next = { ...prev };
      delete next[taskId];
      return next;
    });
  }, []);

  const saveTaskConn = useCallback(
    async (jobId: number, task: MjConnectionTask, newCid: number) => {
      if (!canEditBots) return;
      setTaskConnSavingId(task.id);
      try {
        await updateAdminTaskMut({ taskId: task.id, body: { connection: newCid } }).unwrap();
        setMjAccordionTasksByJob((prev) => {
          const list = prev[jobId];
          if (!list) return prev;
          return {
            ...prev,
            [jobId]: list.map((t) => (t.id === task.id ? { ...t, connection: newCid } : t)),
          };
        });
        clearTaskConnDraft(task.id);
      } catch (e) {
        setTaskConnSaveErrById((prev) => ({
          ...prev,
          [task.id]: getErr(e, "Failed to move the task to the selected connection."),
        }));
      } finally {
        setTaskConnSavingId(null);
      }
    },
    [canEditBots, updateAdminTaskMut, clearTaskConnDraft],
  );

  const mjJobActionBusy =
    isAddingTasksToJob ||
    isRunTaskBusy ||
    isStopTaskBusy ||
    isMjBatchRunning ||
    isMjBatchStopping ||
    isSubmit2FAMj;

  const openJobLogs = (jobId: number, jobName?: string) => {
    setLogsScope({ kind: "job", jobId });
    setLogsSearch("");
    setLogsTitle(`Logs - ${jobName || `Job #${jobId}`}`);
  };

  const openTaskLogs = (taskId: number, opts?: { search?: string }) => {
    setLogsSearch(opts?.search ?? "");
    setLogsScope({ kind: "task", taskId });
    setLogsTitle(`Logs - Task #${taskId}`);
  };

  // --- Matches files helpers ---

  const loadMatchesFiles = async (jobId: number) => {
    setMatchesLoading(true);
    setMatchesError(null);
    setMatchesSelectedFiles([]);
    try {
      const data = await dispatch(
        adminUsersApi.endpoints.getJobMatchesFiles.initiate(jobId, {
          subscribe: false,
          forceRefetch: true,
        }),
      ).unwrap();
      setMatchesFiles(data.files);
      setMatchesSummary(data.summary);
      setMatchesReview(data.review ?? null);
    } catch {
      setMatchesError("Failed to load matches files.");
    } finally {
      setMatchesLoading(false);
    }
  };

  const loadMatchesViewReview = async (jobId: number) => {
    try {
      const detail = await triggerGetUnmatchedReview({ jobId }, false).unwrap();
      setMatchesViewReview(detail);
      setMatchesReview(detail.review);
    } catch {
      setMatchesViewReview(null);
    }
  };

  const openMatchesFileViewer = async (jobId: number, filename: string) => {
    setMatchesViewFilename(filename);
    setMatchesViewRows([]);
    setMatchesViewOpen(true);
    setMatchesViewLoading(true);
    setMatchesViewReview(null);
    if (unmatchedKindForFilename(filename)) void loadMatchesViewReview(jobId);
    try {
      const res = await triggerGetMatchesFileContent({ jobId, filename }, false).unwrap();
      // Parse CSV: split by lines, then by comma
      const lines = res.content.split(/\r?\n/).filter((l) => l.trim() !== "");
      const parsed = lines.map((line) => {
        // Simple CSV parse: handle quoted fields
        const cols: string[] = [];
        let cur = "";
        let inQuotes = false;
        for (let i = 0; i < line.length; i++) {
          const ch = line[i];
          if (ch === '"') {
            inQuotes = !inQuotes;
          } else if (ch === "," && !inQuotes) {
            cols.push(cur);
            cur = "";
          } else {
            cur += ch;
          }
        }
        cols.push(cur);
        return cols;
      });
      setMatchesViewRows(parsed);
    } catch {
      setMatchesViewRows([["(failed to load file content)"]]);
    } finally {
      setMatchesViewLoading(false);
    }
  };

  const downloadMatchesFile = async (jobId: number, filename: string) => {
    try {
      const res = await triggerGetMatchesFileContent({ jobId, filename }, false).unwrap();
      const blob = new Blob([res.content], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      // silently fail — user will see no download
    }
  };

  const downloadSsnFile = async (majorJobId: number) => {
    // S3 hands the file over directly: the API runs on Lambda, whose response is
    // capped at 6 MB, and a large tenant's SSN file is past that. Local
    // development has no bucket, so there the inline endpoint still answers.
    try {
      const link = await triggerGetSsnFileDownloadUrl(majorJobId, false).unwrap();
      if (link.url) {
        window.open(link.url, "_blank", "noopener");
        return;
      }
      const res = await triggerGetSsnFileContent(majorJobId, false).unwrap();
      const blob = new Blob([res.content], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = res.filename || "ssn_info.json";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      // silently fail — user will see no download
    }
  };

  const downloadRoster = async (jobId: number) => {
    // S3 hands the file over directly: a roster reaches tens of megabytes and
    // the API runs on Lambda, whose response is capped at 6 MB.
    try {
      const link = await triggerGetRosterDownloadUrl(jobId, false).unwrap();
      if (link.url) window.open(link.url, "_blank", "noopener");
    } catch {
      // silently fail - the operator sees no download
    }
  };

  const confirmDeleteMatchesFile = async (jobId: number) => {
    if (!canEditBots) return;
    if (!matchesDeleteFilename || jobId == null) return;
    setMatchesDeleting(true);
    try {
      await deleteMatchesFileMut({ jobId, filename: matchesDeleteFilename }).unwrap();
      setMatchesDeleteFilename(null);
      await loadMatchesFiles(jobId);
    } catch {
      // keep dialog open on error
    } finally {
      setMatchesDeleting(false);
    }
  };

  const confirmBatchDeleteMatchesFiles = async (jobId: number) => {
    if (!canEditBots) return;
    if (!matchesSelectedFiles.length) return;
    setMatchesDeleting(true);
    try {
      await Promise.all(
        matchesSelectedFiles.map((filename) => deleteMatchesFileMut({ jobId, filename }).unwrap()),
      );
      setMatchesSelectedFiles([]);
      await loadMatchesFiles(jobId);
    } catch {
      // keep selection visible so user can retry
    } finally {
      setMatchesDeleting(false);
    }
  };

  const resetMatchesMergeState = () => setMatchesMerge(null);

  const openMatchesMergeDialog = () => {
    if (mjManageJobId == null) return;
    setMatchesMerge({
      jobId: mjManageJobId,
      slots: { no_matches: { ...EMPTY_MERGE_SLOT }, several_matches: { ...EMPTY_MERGE_SLOT } },
      step: "idle",
      error: null,
    });
  };

  type MergeReportsState = NonNullable<typeof matchesMerge>;

  const updateMergeSlot = (kind: MergeReportKind, patch: Partial<MergeSlotState>) =>
    setMatchesMerge((prev) =>
      prev ? { ...prev, slots: { ...prev.slots, [kind]: { ...prev.slots[kind], ...patch } } } : prev,
    );

  // A changed slot voids the last check of both slots: the backend checks the files together.
  const withMergeReportsCleared = (state: MergeReportsState): MergeReportsState => ({
    ...state,
    error: null,
    slots: {
      no_matches: { ...state.slots.no_matches, report: null },
      several_matches: { ...state.slots.several_matches, report: null },
    },
  });

  const clearMergeSlot = (kind: MergeReportKind) =>
    setMatchesMerge((prev) => {
      if (!prev) return prev;
      const cleared = withMergeReportsCleared(prev);
      return {
        ...cleared,
        slots: {
          ...cleared.slots,
          [kind]: { ...EMPTY_MERGE_SLOT, showExample: prev.slots[kind].showExample },
        },
      };
    });

  const mergeErrText = (err: unknown, fallback: string) =>
    err instanceof Error ? err.message : getErr(err, fallback);

  const handleMergeSlotFileSelect = async (
    kind: MergeReportKind,
    e: ChangeEvent<HTMLInputElement>,
  ) => {
    if (!canEditBots) return;
    const file = e.target.files?.[0];
    const jobId = matchesMerge?.jobId;
    if (!file || jobId == null) return;
    e.target.value = "";

    setMatchesMerge((prev) => {
      if (!prev) return prev;
      const cleared = withMergeReportsCleared(prev);
      return {
        ...cleared,
        slots: {
          ...cleared.slots,
          [kind]: {
            ...EMPTY_MERGE_SLOT,
            file,
            step: "uploading",
            showExample: prev.slots[kind].showExample,
          },
        },
      };
    });

    try {
      const presign = await presignMatchesMergeMut({
        jobId,
        file_name: file.name,
        content_type: file.type || "application/octet-stream",
      }).unwrap();

      const uploadResponse = await fetch(presign.upload_url, {
        method: "PUT",
        headers: presign.headers,
        body: file,
      });

      if (!uploadResponse.ok) {
        throw new Error(`Upload failed with status ${uploadResponse.status}`);
      }

      updateMergeSlot(kind, { stagingKey: presign.key, step: "ready" });
    } catch (err: unknown) {
      updateMergeSlot(kind, {
        step: "error",
        uploadError: mergeErrText(err, "Failed to upload file."),
      });
    }
  };

  const mergeReportsSummary = (res: MergeReportsResponse): string => {
    const rowWord = (count: number) => `${count} row${count === 1 ? "" : "s"}`;
    const parts = MERGE_REPORT_KINDS.flatMap((kind) => {
      const report = res.files[kind];
      return report ? [`${rowWord(report.rows_added)} from ${mergeSlotTitle(kind)}`] : [];
    });
    const added = parts.length
      ? `Added to Matches: ${parts.join(", ")}.`
      : `${rowWord(res.rows_added)} added to Matches.`;
    const skipped = res.rows_skipped
      ? ` ${rowWord(res.rows_skipped)} already present, skipped.`
      : "";
    const marked = MERGE_REPORT_KINDS.reduce(
      (sum, kind) => sum + (res.no_match_marked?.[kind] ?? 0),
      0,
    );
    const reviewed = marked
      ? ` ${rowWord(marked)} marked No Match - recorded as reviewed, no match found.`
      : "";
    // After a roster-only run the new rows are already split across the tasks
    // and Run merged EEs stays grey - so name both buttons by their case.
    const hint = res.rows_added
      ? " Run all runs the whole job, the new rows included; when tasks have already run, Run merged EEs runs just these."
      : "";
    return `${added}${skipped}${reviewed}${hint}`;
  };

  const confirmUnmatchedReview = async () => {
    const prompt = matchesReviewPrompt;
    if (!prompt || !canEditBots) return;
    setMatchesReviewBusy(prompt.kind);
    try {
      const res = await markUnmatchedReviewMut({
        jobId: prompt.jobId,
        kind: prompt.kind,
        action: prompt.action,
      }).unwrap();
      setMatchesReviewPrompt(null);
      setMatchesReview(res.review);
      setMatchesViewReview((current) =>
        current && current.job_id === prompt.jobId
          ? { ...current, review: res.review, statuses: res.statuses }
          : current,
      );
      setMatchesReviewNotice({
        severity: "success",
        text: describeReviewOutcome(prompt.kind, prompt.action, res),
      });
      await loadMatchesFiles(prompt.jobId);
    } catch (err) {
      setMatchesReviewNotice({ severity: "error", text: describeReviewError(err) });
    } finally {
      setMatchesReviewBusy(null);
    }
  };

  const mergeReportsBusy =
    matchesMerge?.step === "merging" ||
    MERGE_REPORT_KINDS.some((kind) => matchesMerge?.slots[kind].step === "uploading");
  const mergeReportsReady = MERGE_REPORT_KINDS.some(
    (kind) =>
      matchesMerge?.slots[kind].step === "ready" && Boolean(matchesMerge?.slots[kind].stagingKey),
  );

  const confirmMatchesMerge = async () => {
    if (!canEditBots || !matchesMerge) return;
    const files: Partial<Record<MergeReportKind, { staging_key: string }>> = {};
    MERGE_REPORT_KINDS.forEach((kind) => {
      const slot = matchesMerge.slots[kind];
      if (slot.step === "ready" && slot.stagingKey) files[kind] = { staging_key: slot.stagingKey };
    });
    if (!Object.keys(files).length) return;
    const jobId = matchesMerge.jobId;
    setMatchesMerge((prev) => (prev ? { ...prev, step: "merging", error: null } : prev));
    try {
      const res = await mergeMatchesReportsMut({ jobId, files }).unwrap();
      setMatchesMergeSuccess(mergeReportsSummary(res));
      setMatchesMerge(null);
      await loadMatchesFiles(jobId);
    } catch (err: unknown) {
      const data = (
        err as {
          data?: {
            code?: string;
            kind?: MergeReportKind;
            files?: Partial<Record<MergeReportKind, MergeFileReport>>;
          };
        }
      )?.data;
      setMatchesMerge((prev) => {
        if (!prev) return prev;
        if (data?.code === "merge_files_invalid" && data.files) {
          // Nothing was written; each slot shows its own verdict, the uploads stay staged.
          const reports = data.files;
          return {
            ...prev,
            step: "idle",
            error: null,
            slots: {
              no_matches: { ...prev.slots.no_matches, report: reports.no_matches ?? null },
              several_matches: {
                ...prev.slots.several_matches,
                report: reports.several_matches ?? null,
              },
            },
          };
        }
        if (data?.code === "staging_file_missing" && data.kind && prev.slots[data.kind]) {
          return {
            ...prev,
            step: "idle",
            error: null,
            slots: {
              ...prev.slots,
              [data.kind]: {
                ...EMPTY_MERGE_SLOT,
                uploadError: getErr(err, "The uploaded file was not found. Upload it again."),
                showExample: prev.slots[data.kind].showExample,
              },
            },
          };
        }
        return { ...prev, step: "idle", error: getErr(err, "Merge failed.") };
      });
    }
  };

  const mjToggleNestedTaskSelect = useCallback((jobId: number, taskId: number, checked: boolean) => {
    setMjNestedTaskSelectionByJob((prev) => {
      const cur = prev[jobId] ?? [];
      const next = checked ? Array.from(new Set([...cur, taskId])) : cur.filter((x) => x !== taskId);
      const out = { ...prev };
      if (next.length) out[jobId] = next;
      else delete out[jobId];
      return out;
    });
  }, []);

  const mjToggleAllNestedTasks = useCallback((jobId: number, taskRows: MjConnectionTask[], checked: boolean) => {
    setMjNestedTaskSelectionByJob((prev) => {
      const out = { ...prev };
      if (!checked || !taskRows.length) delete out[jobId];
      else out[jobId] = taskRows.map((t) => t.id);
      return out;
    });
  }, []);

  const [childDialogOpen, setChildDialogOpen] = useState(false);
  const [taskJobListName, setTaskJobListName] = useState("");
  const [childPlans, setChildPlans] = useState<ConnPlanEditorRow[]>([]);
  const [childBotName, setChildBotName] = useState("");
  const [childAdditionalOptions, setChildAdditionalOptions] = useState("");
  const [childProcessErrors, setChildProcessErrors] = useState(false);
  const [childSeparateProfiles, setChildSeparateProfiles] = useState(false);
  const [childMaxParallel, setChildMaxParallel] = useState("");
  const [childDocumentType, setChildDocumentType] = useState("");
  const [childDocumentTypes, setChildDocumentTypes] = useState<string[]>([" "]);
  const [childUploadAsZip, setChildUploadAsZip] = useState(false);
  const [childUnzipBefore, setChildUnzipBefore] = useState(false);
  const [childUseSsnMatch, setChildUseSsnMatch] = useState(true);
  // path_to_s3_base_folder for upload bots — chosen via folder picker
  const [childSourcePath, setChildSourcePath] = useState("");
  // The Download major job whose SSN file the new upload job reads (undefined:
  // the server's suggestion for the folder).
  const [childSourceSsn, setChildSourceSsn] = useState<SourceSsnChoice>(undefined);
  const [childError, setChildError] = useState<string | null>(null);

  const [selectedChildJobIds, setSelectedChildJobIds] = useState<number[]>([]);

  /** The jobs the workspace lists - the hidden SSN job is not one of them.
   * The "Task job #N" labels count these, never the raw list: an SSN job
   * sitting before a job in API order used to push every label after it up by
   * one (CAL-6161). */
  const mjVisibleJobs = useMemo(
    () => visibleJobs(childJobs as Array<Record<string, unknown>>),
    [childJobs],
  );

  const taskJobRowIdsOnPage = useMemo(
    () =>
      mjVisibleJobs
        .map((raw) => Number((raw as Record<string, unknown>).id))
        .filter((id) => Number.isFinite(id) && id > 0),
    [mjVisibleJobs],
  );

  const allTaskJobRowsSelected =
    taskJobRowIdsOnPage.length > 0 &&
    taskJobRowIdsOnPage.every((id) => selectedChildJobIds.includes(id));
  const taskJobRowsSelectIndeterminate =
    taskJobRowIdsOnPage.some((id) => selectedChildJobIds.includes(id)) && !allTaskJobRowsSelected;

  const toggleSelectAllTaskJobRows = useCallback(
    (checked: boolean) => {
      setSelectedChildJobIds((prev) => {
        if (checked) {
          return Array.from(new Set([...prev, ...taskJobRowIdsOnPage]));
        }
        const drop = new Set(taskJobRowIdsOnPage);
        return prev.filter((id) => !drop.has(id));
      });
    },
    [taskJobRowIdsOnPage],
  );

  // Folder picker dialog — shared between Create and Edit task job dialogs for upload bots
  const [folderPickerOpen, setFolderPickerOpen] = useState(false);
  const [folderPickerTarget, setFolderPickerTarget] = useState<"create" | "edit">("create");
  const [folderPickerInitialPath, setFolderPickerInitialPath] = useState("");

  const [majConnCreateOpen, setMajConnCreateOpen] = useState(false);
  const [mcUsername, setMcUsername] = useState("");
  const [mcHost, setMcHost] = useState("");
  const [mcClientCode, setMcClientCode] = useState("");
  const [mcPassword, setMcPassword] = useState("");
  const [mcShowPw, setMcShowPw] = useState(false);
  const [mcError, setMcError] = useState<string | null>(null);

  const [majConnEdit, setMajConnEdit] = useState<AdminVendorConnection | null>(null);
  const [meUsername, setMeUsername] = useState("");
  const [meHost, setMeHost] = useState("");
  const [meClientCode, setMeClientCode] = useState("");
  const [mePassword, setMePassword] = useState("");
  const [meShowPw, setMeShowPw] = useState(false);
  const [meError, setMeError] = useState<string | null>(null);

  const [selectedPoolConnectionIds, setSelectedPoolConnectionIds] = useState<number[]>([]);
  const [deleteConnConfirm, setDeleteConnConfirm] = useState<number | null>(null);
  /** Per-major-job pool: selected connection rows for batch delete. */
  const [poolConnSelectedIds, setPoolConnSelectedIds] = useState<number[]>([]);
  const [poolConnBulkDeleteOpen, setPoolConnBulkDeleteOpen] = useState(false);
  const [poolConnBulkDeleting, setPoolConnBulkDeleting] = useState(false);

  // Active manage-dialog tab. Stable string ids (not positional indices) so the
  // absent SSN tab on delete_bots can't collide with Connections. Persisted in URL.
  const tabFromUrl = useMemo<ManageTab>(() => {
    const t = new URLSearchParams(location.search).get("tab");
    return t === "connections" ? "connections" : t === "ssn" ? "ssn" : "jobs";
  }, [location.search]);
  const [manageTab, setManageTab] = useState<ManageTab>(tabFromUrl);
  useEffect(() => {
    setManageTab(tabFromUrl);
  }, [tabFromUrl]);

  const handleManageTabChange = (_e: React.SyntheticEvent, v: ManageTab) => {
    const params = new URLSearchParams(location.search);
    if (v === "jobs") {
      params.delete("tab");
    } else {
      params.set("tab", v);
    }
    navigate({ search: params.toString() }, { replace: true });
    // SSN data loads from the tab's own effect, so every way in behaves alike.
  };

  const activeMajor = useMemo(
    () => majors.find((m) => m.id === activeMajorId) ?? null,
    [majors, activeMajorId],
  );

  // The tab the major job actually has: a delete_bots one has no SSN tab, a
  // split one neither an SSN nor a Connections tab, so a link naming one shows
  // the jobs tab and the address drops it.
  const shownTab = useMemo<ManageTab>(() => {
    if (isSplitKind(activeMajor?.bot_kind)) return "jobs";
    if (activeMajor?.bot_kind === "delete_bots" && manageTab === "ssn") return "jobs";
    return manageTab;
  }, [activeMajor?.bot_kind, manageTab]);
  useEffect(() => {
    if (shownTab === manageTab) return;
    const params = new URLSearchParams(location.search);
    params.delete("tab");
    navigate({ search: params.toString() }, { replace: true });
  }, [shownTab, manageTab, location.search, navigate]);

  /** True if at least one child task of this major job is running OR already downloaded >0 employees.
   *  Used to lock destructive edits (vendor / bot kind / job rewiring). */
  const majorHasRunningOrProgress = useMemo(() => {
    if (!activeMajor) return false;
    for (const raw of childJobs) {
      const row = raw as Record<string, unknown>;
      if (mjJobAnythingInFlight(row)) return true;
      const stats = row.stats as Record<string, unknown> | undefined;
      const processed =
        Number(stats?.success_employees_count ?? 0) +
        Number(stats?.error_employees_count ?? 0);
      if (processed > 0) return true;
    }
    return false;
  }, [activeMajor, childJobs]);

  /** Whether the major job being edited has a task in line or in a portal.
   *
   * Narrower than majorHasRunningOrProgress on purpose: past progress does not
   * lock "Jobs running at once", only work the queue has already dealt out
   * against the current limit. The workspace reads its child jobs; the list-row
   * Edit reads the list's in-flight flag, refetched when the dialog opens. */
  const majorEditTargetInFlight = useMemo(() => {
    if (!majorEditTarget) return false;
    if (activeMajor?.id === majorEditTarget.id) {
      return childJobs.some((raw) => mjJobAnythingInFlight(raw as Record<string, unknown>));
    }
    const fresh = majors.find((m) => m.id === majorEditTarget.id) ?? majorEditTarget;
    return Boolean(fresh.has_tasks_in_flight ?? fresh.has_running_tasks);
  }, [majorEditTarget, activeMajor, childJobs, majors]);

  /** The ticked automations a delete would be refused for, by name. */
  const selectedMajorsRunning = useMemo(
    () =>
      majors
        .filter((m) => selectedMajorIds.includes(m.id) && majorDeleteBlockedReason(m) != null)
        .map((m) => m.name),
    [majors, selectedMajorIds],
  );

  /** True if any child job currently has at least one running task. */
  const majorHasRunningTasks = useMemo(() => {
    if (!activeMajor) return false;
    return childJobs.some((raw) =>
      mjJobAnythingInFlight(raw as Record<string, unknown>),
    );
  }, [activeMajor, childJobs]);

  /** Why the open major job cannot be deleted now: its rows say so before the
   * list catches up. */
  const activeMajorDeleteBlocked = majorHasRunningTasks
    ? "A bot of this automation is running. Stop its tasks first."
    : activeMajor
      ? majorDeleteBlockedReason(activeMajor)
      : null;

  /** Vendor and scope of the major job in the form cannot change now. */
  const majorFormCriticalLocked =
    Boolean(majorEditTarget) && (majorHasRunningOrProgress || majorFormHasSplitJobs);

  /** Auto-close the 2FA dialog as soon as the underlying task no longer needs a code.
   *  This handles WS updates: when the bot acknowledges the code, `task2fa_status`
   *  flips away from pending/ready and the dialog disappears immediately. */
  useEffect(() => {
    if (twoFaMjTask == null) return;
    const jobId =
      typeof twoFaMjTask.job === "number" && Number.isFinite(twoFaMjTask.job) ? twoFaMjTask.job : null;
    if (jobId == null) return;
    const tasksInJob = mjAccordionTasksByJob[jobId] ?? [];
    const fresh = tasksInJob.find((t) => t.id === twoFaMjTask.id);
    if (fresh && !shouldShowPass2FAMj(fresh)) {
      setTwoFaMjTask(null);
      setTwoFaMjCode("");
      setTwoFaMjErr(null);
    }
  }, [mjAccordionTasksByJob, twoFaMjTask]);

  const isDetailMode = detailMajorJobId != null;
  const majorsStillLoading = majorsRaw === undefined || majorsFetching;
  const detailMajorNotFound =
    isDetailMode &&
    !majorsStillLoading &&
    detailMajorJobId != null &&
    !majors.some((m) => m.id === detailMajorJobId);

  /** All bot connections for this user that match the active major's vendor (pool is kept in sync on the server). */
  const poolConnIdsForMajor = useMemo(() => {
    if (!activeMajor) return [];
    const want = normVendor(activeMajor.vendor);
    return connections
      .filter((c) => normVendor(c.vendor) === want)
      .map((c) => c.id)
      .sort((a, b) => a - b);
  }, [activeMajor, connections]);

  /**
   * Logins explicitly attached to this automation (`MajorJobConnection` / included_connection_ids), vendor-filtered.
   * Task job PATCH only accepts plan rows whose connection_id is listed here unless we merge first — see save flows.
   */
  const majorIncludedConnIds = useMemo(() => {
    if (!activeMajor) return [];
    const want = normVendor(activeMajor.vendor);
    const vendorIds = new Set(
      connections.filter((c) => normVendor(c.vendor) === want).map((c) => c.id),
    );
    return Array.from(new Set((activeMajor.included_connection_ids ?? []).filter((id) => id >= 1)))
      .filter((id) => vendorIds.has(id))
      .sort((a, b) => a - b);
  }, [activeMajor, connections]);

  /**
   * Dropdown pool for allocating tasks to logins: every vendor-matching login for this user is
   * eligible. Picking one that isn't yet attached to the major job triggers
   * ``ensureMajorIncludesConnectionIds`` on save, which adds it to the pool.
   *
   * Linked rows from ``MajorJobConnection`` come first so operators see the "primary" ones at
   * the top of the dropdown, and we append the rest sorted by id.
   */
  const majorConnPickerIds = useMemo(() => {
    if (!activeMajor) return [];
    const linked = majorIncludedConnIds;
    const rest = poolConnIdsForMajor.filter((cid) => !linked.includes(cid));
    return [...linked, ...rest];
  }, [activeMajor, majorIncludedConnIds, poolConnIdsForMajor]);

  const nextUnusedChildPlanConnId = useMemo(
    () => nextUnusedPoolConnectionId(majorConnPickerIds, childPlans),
    [majorConnPickerIds, childPlans],
  );

  const ensureMajorIncludesConnectionIds = useCallback(
    async (connectionIds: number[]) => {
      if (!canEditBots) return;
      if (!activeMajor) return;
      const uniq = Array.from(new Set(connectionIds.filter((id) => id >= 1))).sort((a, b) => a - b);
      if (!uniq.length) return;
      const cur = activeMajor.included_connection_ids ?? [];
      const missing = uniq.filter((id) => !cur.includes(id));
      if (!missing.length) return;
      const merged = Array.from(new Set([...cur, ...uniq])).sort((a, b) => a - b);
      await updateMajorJob({
        majorJobId: activeMajor.id,
        invalidateUserId: userId,
        body: { included_connection_ids: merged },
      }).unwrap();
      await refetch();
    },
    [canEditBots, activeMajor, updateMajorJob, userId, refetch],
  );

  /** Stale IDs still stored on the major (e.g. before the next PATCH) or wrong-vendor rows. */
  const poolOrphans = useMemo(() => {
    if (!activeMajor) return [];
    const want = normVendor(activeMajor.vendor);
    const vendorMatchIds = new Set(
      connections.filter((c) => normVendor(c.vendor) === want).map((c) => c.id),
    );
    return (activeMajor.included_connection_ids ?? []).filter((cid) => !vendorMatchIds.has(cid));
  }, [activeMajor, connections]);

  /** 1-based index of the task job in this major job's list (API order); not Django task job id. */
  const mjEditJobOrdinal = useMemo(
    () => jobOrdinal(childJobs as Array<Record<string, unknown>>, mjEditJobId),
    [mjEditJobId, childJobs],
  );

  /** 1-based index of a deleted job target (for confirmation copy). */
  const mjDeleteJobOrdinal = useMemo(
    () => jobOrdinal(childJobs as Array<Record<string, unknown>>, mjDeleteJobId),
    [mjDeleteJobId, childJobs],
  );

  /** 1-based index of a task within its parent job's task list. */
  const taskOrdinalInJob = useCallback(
    (jobId: number, taskId: number) => {
      const tasks = mjAccordionTasksByJob[jobId] ?? [];
      const idx = tasks.findIndex((t) => t.id === taskId);
      return idx >= 0 ? idx + 1 : null;
    },
    [mjAccordionTasksByJob],
  );

  const { data: childRegistryBots } = useGetBotRegistryQuery(
    {
      vendor: activeMajor?.vendor ?? undefined,
      jobKind: activeMajor?.bot_kind ?? undefined,
      isActive: true,
      pageSize: 120,
    },
    // Fetched as soon as a major job is active: the Add/Edit dialogs render
    // their option fields from this registry, and fetching it only on open
    // left the first open without specs. A split major job has no bot of its
    // own kind to configure.
    { skip: !activeMajor?.vendor || isSplitKind(activeMajor?.bot_kind) },
  );

  const normalizedChildBots = useMemo(
    () => normalizeBotRegistryPayload(childRegistryBots),
    [childRegistryBots],
  );
  /** The vendor's note (CAL-6162), from whichever registry read has it: the
   * workspace loads its own vendor's bots, the major job form loads them all. */
  const mjVendorNote = useMemo(() => {
    const vendor = (activeMajor?.vendor ?? "").toLowerCase();
    const of = (data: unknown) => {
      const payload = data && !Array.isArray(data) ? (data as { vendors?: Record<string, { note?: BotNote | null }> }) : undefined;
      return payload?.vendors?.[vendor]?.note ?? null;
    };
    return of(childRegistryBots) ?? of(majorFormAllRegistry) ?? null;
  }, [childRegistryBots, majorFormAllRegistry, activeMajor]);
  /** The note of the bot picked in New / Edit task job. */
  const mjPickedBotNote = useMemo(
    () =>
      normalizedChildBots.find((bot) => botNameFromRegistry(bot) === childBotName)
        ?.note ?? null,
    [normalizedChildBots, childBotName],
  );
  /** A job's own bot note, for the expanded job and Manage job. */
  const mjBotNoteFor = useCallback(
    (botName: string | null | undefined) =>
      normalizedChildBots.find((bot) => botNameFromRegistry(bot) === (botName ?? ""))
        ?.note ?? null,
    [normalizedChildBots],
  );
  const botOptionSpecs = (botName: string) =>
    normalizedChildBots.find((b) => botNameFromRegistry(b) === botName)?.metadata
      ?.options ?? [];
  // The child registry is scoped to the major job's own kind, which is never
  // ssn_bots - the SSN bot's options need their own lookup.
  const { data: ssnRegistryBots } = useGetBotRegistryQuery(
    {
      vendor: activeMajor?.vendor ?? undefined,
      jobKind: "ssn_bots",
      isActive: true,
      pageSize: 50,
    },
    { skip: !activeMajor?.vendor || shownTab !== "ssn" },
  );
  const ssnOptionSpecs = useMemo(
    () =>
      normalizeBotRegistryPayload(ssnRegistryBots).find(
        (b) => botNameFromRegistry(b) === SSN_BOT_NAME,
      )?.metadata?.options ?? [],
    [ssnRegistryBots],
  );
  // The SSN bot's own note: the job kind of the major job is never ssn_bots,
  // so without this lookup its note showed only on the Notes screen.
  const ssnBotNote = useMemo(
    () =>
      normalizeBotRegistryPayload(ssnRegistryBots).find(
        (b) => botNameFromRegistry(b) === SSN_BOT_NAME,
      )?.note ?? null,
    [ssnRegistryBots],
  );
  const ssnServerOptions = String(ssnJobData?.job?.additional_options ?? "");
  const ssnOptionsDirty = ssnAdditionalOptions !== ssnServerOptions;
  // Advisory only: the backend decides whether the edit is allowed and answers
  // 409 with the reason, so this warns without trying to mirror that rule.
  const ssnTaskLooksBusy = mjTaskShowsStopInsteadOfRun(
    String(ssnJobData?.task?.task_status ?? ""),
  );

  const openSsnJobDialog = (
    mode: "create" | "edit",
    connectionId?: number | null,
  ) => {
    setSsnJobDialogMode(mode);
    setSsnJobDialogError(null);
    if (mode === "create" && connectionId != null) {
      setSsnJobSelectedConnId(connectionId);
    }
    setSsnJobDialogOpen(true);
  };

  /** Create or save from the dialog; the 409 the backend answers when the job
   *  has tasks in flight explains itself, so it is shown as it comes. */
  const submitSsnJobDialog = async () => {
    if (!activeMajor || !canEditBots) return;
    const creating = ssnJobDialogMode === "create";
    if (creating) setSsnJobCreating(true);
    else setSsnOptionsSaving(true);
    setSsnJobDialogError(null);
    try {
      const res = creating
        ? await createSsnJobMut({
            majorJobId: activeMajor.id,
            connection_id: ssnJobSelectedConnId ?? undefined,
            additional_options: ssnAdditionalOptions,
          }).unwrap()
        : await updateSsnJobMut({
            majorJobId: activeMajor.id,
            additional_options: ssnAdditionalOptions,
          }).unwrap();
      setSsnJobData(res as SsnJobData);
      setSsnJobDialogOpen(false);
      if (creating) {
        setSsnAccordionExpanded(true);
        // Creating it may have linked the login to the major job.
        void refetch();
      }
    } catch (e: unknown) {
      setSsnJobDialogError(
        (e as { data?: { message?: string } })?.data?.message ??
          (creating ? "Failed to create SSN job." : "Failed to save SSN job."),
      );
    } finally {
      setSsnJobCreating(false);
      setSsnOptionsSaving(false);
    }
  };
  const mjEffectiveManagedJob = useMemo((): Record<string, unknown> | null => {
    if (mjManageJobId == null) return null;
    if (
      mjManageJobDetail != null &&
      Number((mjManageJobDetail as Record<string, unknown>).id) === mjManageJobId
    ) {
      return mjManageJobDetail as Record<string, unknown>;
    }
    const fromList = childJobs.find((raw) => Number((raw as Record<string, unknown>).id) === mjManageJobId);
    return (fromList as Record<string, unknown> | undefined) ?? null;
  }, [mjManageJobId, mjManageJobDetail, childJobs]);

  // The last rejected-files export of the job open in Manage job - one line
  // under its button. The dialog reads the same cache entry.
  const { data: mjManageSftpExport } = useGetJobSftpExportQuery(
    { jobId: mjManageJobId ?? 0 },
    { skip: mjManageJobId == null || !mjEffectiveManagedJob || !mjIsUploadJob(mjEffectiveManagedJob) },
  );

  const openFolderPicker = (target: "create" | "edit", currentPath?: string) => {
    setFolderPickerTarget(target);
    // The dialog opens on this path whatever its depth; no parsing here.
    setFolderPickerInitialPath(currentPath ?? "");
    setFolderPickerOpen(true);
  };

  const confirmFolderPicker = (path: string) => {
    // Another folder can come from another vendor: its Source SSN file starts over.
    if (folderPickerTarget === "create") {
      if (path !== childSourcePath) setChildSourceSsn(undefined);
      setChildSourcePath(path);
    } else {
      if (path !== mjEditSourcePath) setMjEditSourceSsn(undefined);
      setMjEditSourcePath(path);
    }
  };

  /** 1-based index of the currently managed job in this major's list (UI-only label). */
  const mjEffectiveManagedJobOrdinal = useMemo(
    () =>
      jobOrdinal(
        childJobs as Array<Record<string, unknown>>,
        mjEffectiveManagedJob ? Number(mjEffectiveManagedJob.id) : null,
      ),
    [mjEffectiveManagedJob, childJobs],
  );

  /** True when the currently-being-edited task job has any task running OR any
   *  downloaded employees. Used to lock destructive edits on the job. */
  const mjEditJobHasRunningOrProgress = useMemo(() => {
    if (mjEditJobId == null) return false;
    const raw = childJobs.find(
      (r) => Number((r as Record<string, unknown>).id) === mjEditJobId,
    ) as Record<string, unknown> | undefined;
    if (!raw) return false;
    if (mjJobAnythingInFlight(raw)) return true;
    const stats = raw.stats as Record<string, unknown> | undefined;
    const processed =
      Number(stats?.success_employees_count ?? 0) + Number(stats?.error_employees_count ?? 0);
    return processed > 0;
  }, [mjEditJobId, childJobs]);

  const mjEditJobHasRunningTasks = useMemo(() => {
    if (mjEditJobId == null) return false;
    const raw = childJobs.find(
      (r) => Number((r as Record<string, unknown>).id) === mjEditJobId,
    ) as Record<string, unknown> | undefined;
    return Boolean(raw && mjJobAnythingInFlight(raw));
  }, [mjEditJobId, childJobs]);

  const mjEditJobLockedReason = useMemo(() => {
    const raw = childJobs.find(
      (r) => Number((r as Record<string, unknown>).id) === mjEditJobId,
    ) as Record<string, unknown> | undefined;
    const stats = raw?.stats as Record<string, unknown> | undefined;
    return editJobLockedReason(parseInFlightTasks(stats?.in_flight_tasks));
  }, [mjEditJobId, childJobs]);

  /** Employees already processed (success + error) - any progress locks bot type. */
  const mjEditJobProcessedEmployees = useMemo(() => {
    if (mjEditJobId == null) return 0;
    const raw = childJobs.find(
      (r) => Number((r as Record<string, unknown>).id) === mjEditJobId,
    ) as Record<string, unknown> | undefined;
    const stats = raw?.stats as Record<string, unknown> | undefined;
    const fromTotal = Number(stats?.processed_employees_count);
    if (Number.isFinite(fromTotal)) return Math.max(0, fromTotal);
    const suc = Number(stats?.success_employees_count ?? 0) || 0;
    const err = Number(stats?.error_employees_count ?? 0) || 0;
    return suc + err;
  }, [mjEditJobId, childJobs]);

  const mjEditJobBotLocked = mjEditJobProcessedEmployees > 0;

  // While the bot fetches the code automatically (auto_until in the future) the
  // manual button stays hidden; the "Auto 2FA" chip lets the operator force it.
  const twoFaOf = (task: MjConnectionTask) => ({
    status: task.task2fa_status,
    auto_until: task.task2fa_auto_until,
  });

  const isAuto2FAPhaseMj = (task: MjConnectionTask) =>
    isTwoFaAutoPhase(twoFaOf(task), twoFaManualOverrideIds.has(task.id));

  const shouldShowAuto2FAMj = (task: MjConnectionTask) =>
    task.bot_work_status === "2FA" && isAuto2FAPhaseMj(task);

  // A served live view means the operator can step into this bot's browser -
  // shown only to those the backend would actually hand the URL to, so Client
  // View is not offered a window that never connects.
  const shouldShowLiveViewMj = (task: MjConnectionTask) =>
    canOpenLiveView && isLiveViewReady(task.live_view_status, task.bot_work_status);

  // Derived from the rows rather than from the websocket event: the bot
  // publishes live_view.updated on every change of its session, so opening on
  // the event put the window up during the login and again after it was over.
  useEffect(() => {
    const rows = Object.values(mjAccordionTasksByJob).flat();
    if (liveViewMjTaskId != null) {
      const open = rows.find((task) => task.id === liveViewMjTaskId);
      if (
        open &&
        !isLiveViewReady(open.live_view_status, open.bot_work_status)
      ) {
        setLiveViewMjTaskId(null);
      }
      return;
    }
    if (!canOpenLiveView) return;
    const needsHuman = rows.find(
      (task) =>
        shouldAutoOpenLiveView(
          task.live_view_status,
          task.live_view_phase,
          task.bot_work_status,
          task.started_by,
          myPlatformUserId,
        ) && !liveViewMjDismissed.includes(task.id),
    );
    if (needsHuman) setLiveViewMjTaskId(needsHuman.id);
  }, [
    mjAccordionTasksByJob,
    liveViewMjTaskId,
    liveViewMjDismissed,
    canOpenLiveView,
    myPlatformUserId,
  ]);

  // A finished session lets the next one open by itself again.
  useEffect(() => {
    if (liveViewMjDismissed.length === 0) return;
    const rows = Object.values(mjAccordionTasksByJob).flat();
    const stillOpen = liveViewMjDismissed.filter((id) =>
      rows.some(
        (task) =>
          task.id === id &&
          isLiveViewReady(task.live_view_status, task.bot_work_status),
      ),
    );
    if (stillOpen.length !== liveViewMjDismissed.length) {
      setLiveViewMjDismissed(stillOpen);
    }
  }, [mjAccordionTasksByJob, liveViewMjDismissed]);

  // Only while the code is genuinely missing: a stored code ('ready') means a
  // reader already found it and the bot is typing it in.
  const shouldShowPass2FAMj = (task: MjConnectionTask) =>
    isTwoFaCodeNeeded(
      task.bot_work_status,
      twoFaOf(task),
      twoFaManualOverrideIds.has(task.id),
    );

  const shouldShowCodeSentMj = (task: MjConnectionTask) =>
    task.bot_work_status === "2FA" && isTwoFaCodeSubmitted(twoFaOf(task));

  const reloadMjAccordionTasks = useCallback(
    async (jobId: number) => {
      // Reloads overlap (poll + websocket event + the action the operator just
      // took), and a slow older response used to overwrite the fresh statuses -
      // the row then looked unchanged until a manual page refresh.
      const seq = (mjAccordionSeqRef.current[jobId] ?? 0) + 1;
      mjAccordionSeqRef.current[jobId] = seq;
      try {
        const raw = await dispatch(
          adminUsersApi.endpoints.getTaskJob.initiate(jobId, { subscribe: false, forceRefetch: true }),
        ).unwrap();
        if (mjAccordionSeqRef.current[jobId] !== seq) return null;
        setMjJobDetailById((prev) => ({
          ...prev,
          [jobId]: raw as Record<string, unknown>,
        }));
        const warnRaw = (
          raw as { stats?: { range_coverage?: { warning?: string } } }
        ).stats?.range_coverage?.warning;
        const warn = mjArtifactRangeWarning(typeof warnRaw === "string" ? warnRaw : null);
        setMjJobRangeWarningsByJobId((prev) => {
          const n = { ...prev };
          if (warn) n[jobId] = warn;
          else delete n[jobId];
          return n;
        });
        const tasksUnknown = raw.tasks as unknown;
        const list = Array.isArray(tasksUnknown)
          ? (tasksUnknown as Record<string, unknown>[])
          : [];
        const mapped = list.map((t) => normalizeRawTaskToMj(t, jobId));
        setMjAccordionTasksByJob((prev) => ({ ...prev, [jobId]: mapped }));
        markMjAccordionFresh(jobId);
        return mapped;
      } catch {
        return null;
      }
    },
    [dispatch, markMjAccordionFresh],
  );

  const ACCORDION_CACHE_TTL_MS = 30_000; // 30 seconds

  // Seed the accordion caches from the list response: with include=tasks the
  // list carries every job's task rows, so one request feeds the rows AND the
  // accordions (the old per-job detail prefetch fanned out N parallel GETs on
  // every load). Bumping the sequence drops any older in-flight detail read,
  // and mjJobDetailById must be written too - the row renders the detail over
  // the list row wherever one exists, so a stale detail would shadow this.
  useEffect(() => {
    for (const raw of childJobs) {
      const row = raw as Record<string, unknown>;
      const jobId = Number(row.id);
      if (!jobId) continue;
      const tasksUnknown = row.tasks as unknown;
      if (!Array.isArray(tasksUnknown)) continue;
      mjAccordionSeqRef.current[jobId] = (mjAccordionSeqRef.current[jobId] ?? 0) + 1;
      const mapped = (tasksUnknown as Record<string, unknown>[]).map((t) =>
        normalizeRawTaskToMj(t, jobId),
      );
      setMjJobDetailById((prev) => ({ ...prev, [jobId]: row }));
      setMjAccordionTasksByJob((prev) => ({ ...prev, [jobId]: mapped }));
      markMjAccordionFresh(jobId);
      if (mjManageJobId === jobId) setMjManageJobDetail(row);

      const warnRaw = (row as { stats?: { range_coverage?: { warning?: string } } })
        .stats?.range_coverage?.warning;
      const warn = mjArtifactRangeWarning(typeof warnRaw === "string" ? warnRaw : null);
      setMjJobRangeWarningsByJobId((prev) => {
        const n = { ...prev };
        if (warn) n[jobId] = warn;
        else delete n[jobId];
        return n;
      });
    }
  }, [childJobs, markMjAccordionFresh, mjManageJobId]);

  const handleMjAccordionExpanded = (_event: SyntheticEvent, jobId: number, expanded: boolean) => {
    setExpandedMjJobAccordion((prev) => ({ ...prev, [jobId]: expanded }));
    if (!expanded) return;

    const cachedAt = mjAccordionCacheTs[jobId] ?? 0;
    const hasData = Boolean(mjAccordionTasksByJob[jobId]);
    const isFresh = hasData && Date.now() - cachedAt < ACCORDION_CACHE_TTL_MS;

    void loadMjRosterInfo(jobId);
    // A job that was never counted (no stats snapshot) is counted on its first
    // open, so the S3 folder count and the log-based numbers show up without a
    // Refresh click. Later refreshes stay manual: they read every archive.
    const row = mjJobRowById(jobId);
    if (
      row &&
      mjEmployeeStatsFromJob(row).snapshotAt == null &&
      !mjAutoScannedJobsRef.current.has(jobId)
    ) {
      mjAutoScannedJobsRef.current.add(jobId);
      void refreshMjJobRow(jobId);
    }
    // Fresh cache — render instantly, nothing to fetch.
    if (isFresh) return;
    // A reload is already running for this job — don't stack another (rapid open/close/open).
    if (mjAccordionInFlightRef.current.has(jobId)) return;

    // Block with the spinner ONLY when there is nothing to show yet. When stale
    // data exists we render it immediately and refresh silently in the background.
    if (!hasData) setMjAccordionLoadingJobId(jobId);

    mjAccordionInFlightRef.current.add(jobId);
    void reloadMjAccordionTasks(jobId).finally(() => {
      mjAccordionInFlightRef.current.delete(jobId);
      setMjAccordionLoadingJobId((cur) => (cur === jobId ? null : cur));
    });
  };

  /** `silent` suppresses the error banner: used by callers whose own write already
   *  succeeded, so a failed follow-up read must not claim the change failed. Two
   *  quick toggles also race two `subscribe: false` reads of the same job and the
   *  loser rejects, which is exactly the case that used to raise a false alarm. */
  const refreshMjJobDetail = async (
    jobId: number,
    refreshList = false,
    silent = false,
  ) => {
    setMjRefreshingJobIds((prev) => ({ ...prev, [jobId]: true }));
    try {
      const details = (await dispatch(
        adminUsersApi.endpoints.getTaskJob.initiate(jobId, { subscribe: false, forceRefetch: true }),
      ).unwrap()) as Record<string, unknown> | undefined;
      // unwrap() of an unsubscribed initiate resolves to undefined (not an error)
      // when a concurrent tag invalidation evicts the entry before the state read.
      // The GET itself succeeded, so keep the previous detail and fall through to
      // the list refetch instead of raising a false "failed" banner.
      if (!details) {
        if (refreshList) await safeRefetchChildJobs();
        return;
      }
      setMjJobDetailById((prev) => ({
        ...prev,
        [jobId]: details,
      }));
      const statsRaw = (
        details as { stats?: { range_coverage?: { warning?: string } } }
      ).stats?.range_coverage?.warning;
      const stats = mjArtifactRangeWarning(typeof statsRaw === "string" ? statsRaw : null);
      setMjJobRangeWarningsByJobId((prev) => {
        const n = { ...prev };
        if (stats) n[jobId] = stats;
        else delete n[jobId];
        return n;
      });
      if (mjManageJobId === jobId) setMjManageJobDetail(details as Record<string, unknown>);
      const tasksUnknown = (details as { tasks?: unknown }).tasks;
      const list = Array.isArray(tasksUnknown) ? (tasksUnknown as Record<string, unknown>[]) : [];
      const mapped = list.map((t) => normalizeRawTaskToMj(t, jobId));
      setMjAccordionTasksByJob((prev) => ({ ...prev, [jobId]: mapped }));
      markMjAccordionFresh(jobId);
      if (refreshList) await safeRefetchChildJobs();
    } catch (e) {
      if (silent) return;
      setMjJobActionAlertsByJobId((prev) => ({
        ...prev,
        [jobId]: getErr(e, "Failed to refresh task job."),
      }));
    } finally {
      setMjRefreshingJobIds((prev) => {
        const next = { ...prev };
        delete next[jobId];
        return next;
      });
    }
  };

  /** The row's Refresh button: rescan the stored logs, then show the result.
   *
   * Reading the job no longer scans S3 - that is what makes the panel's polling
   * affordable - so Refresh is the one place that asks for a rescan. It is also
   * what the Success/Error tooltip already promises ("Refreshed from stored logs
   * N min ago"). refresh-stats persists the snapshot and answers with the
   * serialized job, so its response is what the row should render.
   */
  const refreshMjJobRow = async (jobId: number) => {
    setMjRefreshingJobIds((prev) => ({ ...prev, [jobId]: true }));
    try {
      const rescanned = await refreshTaskJobStats(jobId).unwrap();
      // While bots run the backend leaves the stored count alone and says so;
      // the rows keep moving on the live progress until the last bot finishes.
      const countersRefreshed =
        (rescanned as { counters_refreshed?: unknown }).counters_refreshed !== false;
      setMjJobNoticesByJobId((prev) => {
        const next = { ...prev };
        if (countersRefreshed) delete next[jobId];
        else
          next[jobId] =
            "Bots are still running: the numbers follow the live progress, and the stored count is refreshed when the last bot finishes.";
        return next;
      });
      setMjJobDetailById((prev) => ({
        ...prev,
        [jobId]: { ...(prev[jobId] ?? {}), ...(rescanned as Record<string, unknown>) },
      }));
      if (mjManageJobId === jobId) {
        setMjManageJobDetail((prev) => ({
          ...(prev ?? {}),
          ...(rescanned as Record<string, unknown>),
        }));
      }
      await safeRefetchChildJobs();
      await reloadMjAccordionTasks(jobId);
    } catch (e) {
      setMjJobActionAlertsByJobId((prev) => ({
        ...prev,
        [jobId]: getErr(e, "Failed to refresh task job."),
      }));
    } finally {
      setMjRefreshingJobIds((prev) => {
        const next = { ...prev };
        delete next[jobId];
        return next;
      });
    }
  };

  useEffect(() => {
    expandedMjJobAccordionRef.current = expandedMjJobAccordion;
  }, [expandedMjJobAccordion]);

  useEffect(() => {
    userIdRef.current = userId;
  }, [userId]);

  useEffect(() => {
    activeMajorForWsRef.current = activeMajorId ?? detailMajorJobId ?? null;
  }, [activeMajorId, detailMajorJobId]);

  // Deep link: /major-job/N?job=M expands job M once and leaves the URL.
  // Waits for this major job's own list (`currentData`: `data` may still be
  // the previous one's), so the mount-time reset below cannot wipe it.
  useEffect(() => {
    const link = takeJobDeepLink(
      location.search,
      childJobsCurrent === undefined
        ? null
        : childJobs.map((row) => Number((row as Record<string, unknown>).id)),
    );
    if (link == null) return;
    const { expandJobId } = link;
    if (expandJobId != null) {
      setExpandedMjJobAccordion((prev) =>
        prev[expandJobId] ? prev : { ...prev, [expandJobId]: true },
      );
    }
    navigate({ search: link.search }, { replace: true });
  }, [location.search, childJobs, childJobsCurrent]);

  useEffect(() => {
    mjAccordionTasksByJobRef.current = mjAccordionTasksByJob;
  }, [mjAccordionTasksByJob]);

  useEffect(() => {
    reloadMjAccordionTasksRef.current = reloadMjAccordionTasks;
  }, [reloadMjAccordionTasks]);

  useEffect(() => {
    refreshMjJobDetailRef.current = refreshMjJobDetail;
  });

  useEffect(() => {
    safeRefetchChildJobsRef.current = safeRefetchChildJobs;
  }, [safeRefetchChildJobs]);

  useEffect(() => { ssnJobDataRef.current = ssnJobData; }, [ssnJobData]);

  // Refill the editor only when the stored value actually changed: the SSN tab
  // reloads silently on every websocket event, and refilling on each of those
  // would delete whatever the operator is typing.
  useEffect(() => {
    const jobId = ssnJobData?.job?.id ?? "none";
    const source = `${jobId}|${ssnServerOptions}`;
    if (ssnOptionsSourceRef.current === source) return;
    ssnOptionsSourceRef.current = source;
    setSsnAdditionalOptions(ssnServerOptions);
  }, [ssnJobData, ssnServerOptions]);
  useEffect(() => { ssnMajorIdRef.current = activeMajorId ?? detailMajorJobId ?? null; }, [activeMajorId, detailMajorJobId]);
  useEffect(() => { loadSsnJobRef.current = loadSsnJob; });

  // The SSN tab owns its data: a click is not the only way to land on it (a
  // shared ?tab=ssn link, a reload, or switching major jobs while it is open).
  // Goes through the refs so a re-created loadSsnJob cannot re-trigger this.
  useEffect(() => {
    if (shownTab !== "ssn" || !activeMajor) return;
    void loadSsnJobRef.current(activeMajor.id, {
      silent: ssnJobDataRef.current?.job?.id != null,
    });
  }, [shownTab, activeMajor?.id]);

  useEffect(() => { loadMatchesFilesRef.current = loadMatchesFiles; });

  // Same for the Matches tab of the manage dialog: it used to load only from
  // the Tabs onChange and only when the list was still null, so reopening the
  // dialog after a delete or a merge showed the previous file list.
  useEffect(() => {
    if (mjManageTab !== 1 || mjManageJobId == null) return;
    void loadMatchesFilesRef.current(mjManageJobId);
  }, [mjManageTab, mjManageJobId]);

  useEffect(() => {
    // One timer per key, fired on the leading edge of its window
    // (panelWsEvents.createCoalescer).
    const coalescer = createCoalescer();

    const reloadSsnIfMatch = (matchJobId: number | null, matchTaskId: number | null) => {
      const ssnData = ssnJobDataRef.current;
      const majorId = ssnMajorIdRef.current;
      if (!ssnData || majorId == null) return;
      const ssnJobId = ssnData.job != null ? Number((ssnData.job as Record<string, unknown>).id ?? 0) : null;
      const ssnTaskId = ssnData.task != null ? Number((ssnData.task as Record<string, unknown>).id ?? 0) : null;
      const matches =
        (matchJobId != null && ssnJobId != null && matchJobId === ssnJobId) ||
        (matchTaskId != null && ssnTaskId != null && matchTaskId === ssnTaskId);
      if (matches) {
        coalescer.schedule(`ssn-job-reload-${majorId}`, () => void loadSsnJobRef.current(majorId, { silent: true }), 600);
      }
    };

    // Every refresh funnels into ONE coalesced list refetch: with
    // include=tasks the list response updates the job rows and the accordion
    // task rows alike, so a burst of N events costs one request per window
    // instead of a per-job detail fan-out plus a list refetch.
    // A split job's progress events repeat its state; only a change of it can
    // change the list (the Jobs count, the Delete lock).
    const splitStatesSeen: Record<string, string> = {};
    const unsubscribeEvents = botsManagementWs.subscribe((payload: BotsManagementWsEvent) => {
      if (payload.event === SPLIT_JOB_EVENT) {
        const split = payload.split_job;
        if (!split || !splitEventConcerns(payload, { userId: userIdRef.current })) return;
        const state = [
          split.status,
          split.publish_status,
          split.ssn_check_status,
          split.ssn_updater_status,
          split.discarded,
        ].join("|");
        const key = String(split.job_id ?? "");
        if (splitStatesSeen[key] === state) return;
        splitStatesSeen[key] = state;
        coalescer.schedule("majors", () => void refetchMajorsRef.current(), 1000);
        return;
      }
      const route = routePanelWsEvent(payload, {
        userId: userIdRef.current,
        shownMajorId: activeMajorForWsRef.current,
      });
      if (route.ssnJobId != null || route.ssnTaskId != null) {
        reloadSsnIfMatch(route.ssnJobId, route.ssnTaskId);
      }
      if (route.refreshList) {
        coalescer.schedule("child-jobs", () => void safeRefetchChildJobsRef.current(), 700);
      }
    });

    const unsubscribeStatus = botsManagementWs.subscribeStatus((connected) => {
      setMjWsConnected(connected);
      if (!connected) return;
      if (!hasSeenWsConnectedRef.current) {
        // The first open right after the page load: the data on screen came
        // from REST moments ago, so refetching it now would be the duplicate
        // the ticket measured. Only a REconnect needs a catch-up refetch.
        hasSeenWsConnectedRef.current = true;
        return;
      }
      coalescer.schedule("ws-reconnect", () => void safeRefetchChildJobsRef.current(), 1000);
    });

    return () => {
      unsubscribeEvents();
      unsubscribeStatus();
      coalescer.cancelAll();
    };
  }, []);

  /**
   * Polling exists for one case only: the WebSocket is not delivering. The
   * client reports "connected" solely while its heartbeat is answered, and
   * the pong rides the same channel layer as the events - so a socket that is
   * open but mute (stale Daphne, dead Redis) flips to disconnected within the
   * heartbeat timeout and this fallback takes over. While the socket is
   * healthy there is NO polling at all: the backend publishes throttled
   * progress events, so even the employee counters move without one.
   */
  useEffect(() => {
    if (mjWsConnected) return undefined;
    if (activeMajorId == null && detailMajorJobId == null) return undefined;
    const TERMINAL_STATUSES = ["FINISHED", "STOPPED", "ERROR", "CRITICAL_ERROR"];
    // One list request per tick (include=tasks carries the accordions too).
    // A page with a running task refreshes every tick; a fully idle one only
    // needs to notice that something started, every 4th tick. An empty tasks
    // map means the first load has not landed yet - treat it as active.
    let tick = 0;
    const interval = window.setInterval(() => {
      tick += 1;
      const jobLists = Object.values(mjAccordionTasksByJobRef.current);
      const anyActive =
        !jobLists.length ||
        jobLists.some((tasks) =>
          tasks.some(
            (t) => !TERMINAL_STATUSES.includes((t.task_status || "").toUpperCase()),
          ),
        );
      if (anyActive || tick % 4 === 0) void safeRefetchChildJobsRef.current();
    }, 8000);
    return () => window.clearInterval(interval);
  }, [mjWsConnected, activeMajorId, detailMajorJobId]);

  const openMjManage = (jobId: number, tab: 0 | 1 = 0) => {
    setMjManageJobError(null);
    setMjManageJobId(jobId);
    // Reset Matches tab state
    setMjManageTab(tab);
    setMatchesFiles(null);
    setMatchesSummary(null);
    setMatchesReview(null);
    setMatchesReviewNotice(null);
    setMatchesReviewPrompt(null);
    setMatchesError(null);
    setMatchesSelectedFiles([]);
    setMatchesMergeSuccess(null);
    resetMatchesMergeState();
    const snap = childJobs.find((raw) => Number((raw as Record<string, unknown>).id) === jobId);
    setMjManageJobDetail(snap ? (snap as Record<string, unknown>) : null);
    void reloadMjAccordionTasks(jobId);
    void dispatch(
      adminUsersApi.endpoints.getTaskJob.initiate(jobId, { subscribe: false, forceRefetch: true }),
    )
      .unwrap()
      .then((raw) => setMjManageJobDetail(raw as Record<string, unknown>))
      .catch(() => {});
  };

  // Coming back from Data Storage: ?manage=<jobId> reopens the dialog the
  // operator left from, then drops out of the URL. Waits for the job list -
  // the mount-time reset effects would otherwise wipe the dialog right back.
  const mjManageRestoredRef = useRef(false);
  useEffect(() => {
    if (mjManageRestoredRef.current) return;
    const params = new URLSearchParams(location.search);
    const manage = params.get("manage");
    if (!manage) {
      mjManageRestoredRef.current = true;
      return;
    }
    const jobId = Number(manage);
    const known = childJobs.some(
      (raw) => Number((raw as Record<string, unknown>).id) === jobId,
    );
    if (!Number.isFinite(jobId) || jobId <= 0 || !known) return;
    mjManageRestoredRef.current = true;
    openMjManage(jobId);
    params.delete("manage");
    navigate({ search: params.toString() }, { replace: true });
  }, [childJobs]);

  const openMjAddTasksDialog = async () => {
    if (mjManageJobId == null) return;
    const jid = mjManageJobId;
    let snap: Record<string, unknown> | null = null;
    setMjAddTasksLoading(true);
    try {
      const raw = await dispatch(
        adminUsersApi.endpoints.getTaskJob.initiate(jid, { subscribe: false, forceRefetch: true }),
      ).unwrap();
      setMjManageJobDetail(raw as Record<string, unknown>);
      snap = raw as Record<string, unknown>;
    } catch {
      snap =
        (mjManageJobDetail != null && Number((mjManageJobDetail as Record<string, unknown>).id) === jid
          ? (mjManageJobDetail as Record<string, unknown>)
          : null) ??
        ((childJobs.find((r) => Number((r as Record<string, unknown>).id) === jid) ?? null) as Record<
          string,
          unknown
        > | null);
    }

    if (snap != null) {
      const plans = parseConnectionPlansFromJobRow(snap);
      if (plans.length) {
        const pool = Array.from(new Set(plans.map((p) => p.connection_id))).sort((a, b) => a - b);
        setMjAddPlanConnPool(pool);
        const head = pool[0];
        setMjBulkAddRows([{ connection_id: head != null ? head : "", task_target_count: "1" }]);
      } else {
        setMjAddPlanConnPool([]);
        const lc =
          legacyPrimaryConnectionFromJob(snap) ?? (majorConnPickerIds[0] != null ? majorConnPickerIds[0] : "");
        const cid = typeof lc === "number" && lc >= 1 ? lc : "";
        setMjBulkAddRows([{ connection_id: cid, task_target_count: "1" }]);
      }
    } else {
      setMjAddPlanConnPool([]);
      const fb = majorConnPickerIds[0];
      setMjBulkAddRows([{ connection_id: fb != null ? fb : "", task_target_count: "1" }]);
    }

    const snapRows =
      snap != null && Array.isArray(snap.tasks) ? (snap.tasks as Record<string, unknown>[]) : [];
    const seed = snapRows.length
      ? mjEmployeeTasks(snapRows.map((t) => normalizeRawTaskToMj(t, jid))).length
      : mjEmployeeTasks(mjAccordionTasksByJob[jid] ?? []).length;
    setMjTaskCountTarget(String(Math.max(seed, 1)));

    setMjAddTasksJobId(jid);
    setMjAddTasksErr(null);
    void reloadMjAccordionTasks(jid);
    setMjAddTasksOpen(true);
    setMjAddTasksLoading(false);
  };

  const handleMjAddTasksConfirmed = async () => {
    if (!canEditBots) return;
    if (mjAddTasksJobId == null) return;

    let currentTasks = mjAccordionTasksByJob[mjAddTasksJobId] ?? [];

    const manageDetailSnap =
      mjManageJobDetail != null && Number(mjManageJobDetail.id) === mjAddTasksJobId
        ? mjManageJobDetail
        : null;

    const rawTasksOnDetail = manageDetailSnap
      ? ((manageDetailSnap as Record<string, unknown>).tasks as unknown)
      : undefined;
    const arrFromDetail =
      rawTasksOnDetail &&
      Array.isArray(rawTasksOnDetail) &&
      (currentTasks.length === 0 || (rawTasksOnDetail as unknown[]).length >= currentTasks.length)
        ? (rawTasksOnDetail as Record<string, unknown>[])
        : null;

    if (arrFromDetail && arrFromDetail.length > 0) {
      currentTasks = arrFromDetail.map((t) => normalizeRawTaskToMj(t, mjAddTasksJobId));
    }

    const rawJobSnapshot =
      (mjEffectiveManagedJob != null && Number(mjEffectiveManagedJob.id) === mjAddTasksJobId
        ? mjEffectiveManagedJob
        : null) ??
      (manageDetailSnap as Record<string, unknown> | null) ??
      ((childJobs.find((r) => Number((r as Record<string, unknown>).id) === mjAddTasksJobId) ??
        null) as Record<string, unknown> | null);

    const plansSnap = rawJobSnapshot ? parseConnectionPlansFromJobRow(rawJobSnapshot) : [];
    const planMode = plansSnap.length > 0;
    const jobTotalRemain = Math.max(0, MAX_BATCH_TASKS - currentTasks.length);

    const resolved = resolveMjBulkTaskAddPayload(
      planMode,
      plansSnap,
      mjAddPlanConnPool,
      mjBulkAddRows,
      currentTasks,
      jobTotalRemain,
      connections,
    );

    if ("error" in resolved) {
      setMjAddTasksErr(resolved.error);
      return;
    }

    setMjAddTasksErr(null);
    const jid = mjAddTasksJobId;
    try {
      if (resolved.planMode) {
        for (const batch of resolved.batches) {
          await addTasksToJobMutation({
            jobId: jid,
            count: batch.count,
            connection_id: batch.connection_id,
          }).unwrap();
        }
      } else {
        await addTasksToJobMutation({
          jobId: jid,
          count: resolved.legacyCount,
        }).unwrap();
      }

      if (mjAddPlanConnPool.length) {
        const h = mjAddPlanConnPool[0];
        setMjBulkAddRows([{ connection_id: h!, task_target_count: "1" }]);
      } else {
        setMjBulkAddRows((prev) => [{ connection_id: prev[0]?.connection_id ?? "", task_target_count: "1" }]);
      }

      setMjAddTasksOpen(false);
      await safeRefetchChildJobs();
      if (expandedMjJobAccordion[jid]) await reloadMjAccordionTasks(jid);
      void dispatch(adminUsersApi.endpoints.getTaskJob.initiate(jid, { subscribe: false, forceRefetch: true }))
        .unwrap()
        .then((raw) => setMjManageJobDetail(raw as Record<string, unknown>))
        .catch(() => {});
    } catch (e) {
      setMjAddTasksErr(getErr(e, "Could not add tasks."));
    }
  };

  const mjRunTaskRow = async (jobId: number, task: MjConnectionTask) => {
    if (!canEditBots) return;
    setBusyTaskId(task.id);
    try {
      setMjJobActionAlertsByJobId((prev) => {
        const n = { ...prev };
        delete n[jobId];
        return n;
      });
      // One call: the backend resets a finished run itself. Two calls used to
      // move the row first, so a refused start left it reset under an error.
      await runTaskMutation(task.id).unwrap();
      await safeRefetchChildJobs();
      await reloadMjAccordionTasks(jobId);
    } catch (e) {
      setMjJobActionAlertsByJobId((prev) => ({
        ...prev,
        [jobId]: getErr(e, "Could not run task."),
      }));
    } finally {
      setBusyTaskId((cur) => (cur === task.id ? null : cur));
    }
  };

  const mjStopTaskRow = async (jobId: number, taskId: number) => {
    if (!canEditBots) return;
    setBusyTaskId(taskId);
    try {
      setMjJobActionAlertsByJobId((prev) => {
        const n = { ...prev };
        delete n[jobId];
        return n;
      });
      const report = await stopTaskMutation(taskId).unwrap();
      if (report.outcome === "failed") {
        setMjJobActionAlertsByJobId((prev) => ({ ...prev, [jobId]: report.message }));
      }
      if (twoFaMjTask?.id === taskId) {
        setTwoFaMjTask(null);
        setTwoFaMjCode("");
        setTwoFaMjErr(null);
      }
      await safeRefetchChildJobs();
      await reloadMjAccordionTasks(jobId);
    } catch (e) {
      setMjJobActionAlertsByJobId((prev) => ({
        ...prev,
        [jobId]: getErr(e, "Could not stop task."),
      }));
    } finally {
      setBusyTaskId((cur) => (cur === taskId ? null : cur));
    }
  };

  /**
   * The one task row of this panel: used by the job accordions and by the SSN
   * job, which used to carry its own ~210-line copy. The copy had already
   * fallen behind - a CRITICAL_ERROR status was clickable (opens the error and
   * a rerun) in the accordion but dead in the SSN row.
   *
   * A local renderer rather than a separate component on purpose: the row reads
   * a dozen pieces of this panel's state (connection drafts, busy ids, 2FA
   * visibility, permissions) and threading all of it through props would just
   * move the duplication into a prop list.
   */
  useEffect(() => {
    setMjRosterNotice(null);
    if (mjManageJobId == null) return;
    void loadMjRosterInfo(mjManageJobId);
  }, [mjManageJobId, loadMjRosterInfo]);

  useEffect(() => {
    setMjBrowserProfilesError(null);
    // A split major job logs into nothing, so it has no browser profile.
    if (activeMajor?.id && isDetailMode && !isSplitKind(activeMajor.bot_kind)) {
      void loadMjBrowserProfiles(activeMajor.id);
    } else setMjBrowserProfiles(null);
  }, [activeMajor?.id, activeMajor?.bot_kind, isDetailMode, loadMjBrowserProfiles]);

  // SSN readiness of the upload jobs whose rows are open.
  useEffect(() => {
    for (const raw of childJobs) {
      const row = raw as Record<string, unknown>;
      const id = Number(row.id);
      if (
        !expandedMjJobAccordion[id] ||
        !mjIsUploadJob(row) ||
        mjSsnReadinessRequestedRef.current.has(id)
      ) {
        continue;
      }
      mjSsnReadinessRequestedRef.current.add(id);
      fetchJobSsnReadiness(id)
        .unwrap()
        .then((readiness) =>
          setMjSsnReadinessByJobId((prev) => ({ ...prev, [id]: readiness })),
        )
        .catch(() => mjSsnReadinessRequestedRef.current.delete(id));
    }
  }, [expandedMjJobAccordion, childJobs, fetchJobSsnReadiness]);

  // The dialogs say up front whether the job as configured would match by SSN.
  useEffect(() => {
    if (!childDialogOpen || !activeMajor || activeMajor.bot_kind !== "upload_bots") {
      setChildSsnReadiness(null);
      return;
    }
    const path = childSourcePath.trim();
    if (!path) {
      setChildSsnReadiness(null);
      return;
    }
    const majorJobId = activeMajor.id;
    const timer = setTimeout(() => {
      fetchMajorSsnReadiness({
        majorJobId,
        path,
        useSsnMatch: childUseSsnMatch,
        sourceMajorJobId: childSourceSsn,
      })
        .unwrap()
        .then(setChildSsnReadiness)
        .catch(() => setChildSsnReadiness(null));
    }, 300);
    return () => clearTimeout(timer);
  }, [
    childDialogOpen,
    childSourcePath,
    childUseSsnMatch,
    childSourceSsn,
    activeMajor,
    fetchMajorSsnReadiness,
  ]);

  useEffect(() => {
    if (mjEditJobId == null || !activeMajor || activeMajor.bot_kind !== "upload_bots") {
      setMjEditSsnReadiness(null);
      return;
    }
    const path = mjEditSourcePath.trim();
    if (!path) {
      setMjEditSsnReadiness(null);
      return;
    }
    const majorJobId = activeMajor.id;
    const timer = setTimeout(() => {
      fetchMajorSsnReadiness({
        majorJobId,
        path,
        useSsnMatch: mjEditUseSsnMatch,
        sourceMajorJobId: mjEditSourceSsn,
      })
        .unwrap()
        .then(setMjEditSsnReadiness)
        .catch(() => setMjEditSsnReadiness(null));
    }, 300);
    return () => clearTimeout(timer);
  }, [
    mjEditJobId,
    mjEditSourcePath,
    mjEditUseSsnMatch,
    mjEditSourceSsn,
    activeMajor,
    fetchMajorSsnReadiness,
  ]);

  // The Source SSN file shown and sent: the choice, else the server's suggestion.
  const childSourceSsnValue = resolveSourceChoice(
    childSourceSsn,
    childSsnReadiness?.source_candidates,
    childSsnReadiness?.suggested_source_major_job_id,
  );
  const mjEditSourceSsnValue = mjEditSsnReadiness
    ? resolveSourceChoice(
        mjEditSourceSsn,
        mjEditSsnReadiness.source_candidates,
        mjEditSsnReadiness.suggested_source_major_job_id,
      )
    : (mjEditSourceSsn ?? null);

  /**
   * The job actions that are not what an operator reaches for every time.
   *
   * Ten labelled buttons need 1751px; a Full HD workspace has 1134 and a
   * 1366 one has 910, so the row wrapped into two ragged lines at every real
   * resolution. The frequent five stay in the row - the error-mode switch,
   * Run all / Stop all, Schedule, Logs, Manage job - and these move behind
   * one "More" menu, which is also where their explanations finally fit.
   */
  /**
   * Everything under an opened task job: notices, the progress card, the row
   * of controls and the task list. It used to live inside a colSpan cell of
   * the jobs table, propped up by `width: calc(100vw - 100px)` so it would not
   * scroll away sideways with the table. As a block of its own it simply
   * follows the panel.
   */
  const renderMjJobExpanded = (raw: Record<string, unknown>) => {
    const baseRow = raw;
    const id = Number(baseRow.id);
    const row = { ...baseRow, ...(mjJobDetailById[id] ?? {}) };
    const employeeStats = mjEmployeeStatsFromJob(row);
    const tasks = mjAccordionTasksByJob[id] ?? [];
    const nestSel = mjNestedTaskSelectionByJob[id] ?? [];
    const allNestIds = tasks.map((t) => t.id);
    const allNestSelected =
      allNestIds.length > 0 && allNestIds.every((tid) => nestSel.includes(tid));
    return (
      <Box sx={{ pt: 1, pb: 2, px: { xs: 0, md: 2 }, boxSizing: "border-box" }}>
                      {baseRow.auto_recalculate_ranges !== false &&
                      baseRow.actual_total_employees == null &&
                      !mjRosterEmpty(baseRow) ? (
                        <Alert severity="info" sx={{ mb: 1 }}>
                          Ranges will be set for all tasks after the first task
                          finishes collecting the roster.
                        </Alert>
                      ) : null}
                      {mjRangeNotice(baseRow) ? (
                        <Alert severity="info" sx={{ mb: 1 }}>
                          {mjRangeNotice(baseRow)}
                        </Alert>
                      ) : null}
                      {mjJobRangeWarningsByJobId[id] ? (
                        <Alert
                          severity="warning"
                          sx={{ mb: 1 }}
                          onClose={() =>
                            setMjJobRangeWarningsByJobId((p) => {
                              const n = { ...p };
                              delete n[id];
                              return n;
                            })
                          }
                        >
                          {mjJobRangeWarningsByJobId[id]}
                        </Alert>
                      ) : null}
                      {mjJobActionAlertsByJobId[id] ? (
                        <Alert
                          severity="error"
                          sx={{ mb: 1 }}
                          onClose={() =>
                            setMjJobActionAlertsByJobId((p) => {
                              const n = { ...p };
                              delete n[id];
                              return n;
                            })
                          }
                        >
                          {mjJobActionAlertsByJobId[id]}
                        </Alert>
                      ) : null}
                      {mjJobNoticesByJobId[id] ? (
                        <Alert
                          severity="info"
                          sx={{ mb: 1 }}
                          onClose={() =>
                            setMjJobNoticesByJobId((p) => {
                              const n = { ...p };
                              delete n[id];
                              return n;
                            })
                          }
                        >
                          {mjJobNoticesByJobId[id]}
                        </Alert>
                      ) : null}
                      {mjBotNoteFor(String(row.bot_name ?? "")) ? (
                        <Box sx={{ mb: 1 }}>
                          <BotNoteView
                            note={mjBotNoteFor(String(row.bot_name ?? ""))}
                            label={String(row.bot_name ?? "")}
                            dense
                          />
                        </Box>
                      ) : null}
                      <RosterStateAlert
                        roster={mjRosterState(row)}
                        canEdit={canEditBots}
                        recovering={mjRosterRecoveringJobId === id}
                        onRecover={() => void mjRecoverRoster(id)}
                        readOnlyReason={botsReadOnlyReason}
                      />
                      {mjShowMatchesPreflight(row, mjRosterInfoByJobId[id] ?? null) ? (
                        <MatchesPreflightAlert
                          preflight={mjMatchesPreflight(row)}
                          canEdit={canEditBots}
                          rechecking={!!mjRecheckingJobIds[id]}
                          onRecheck={() => void mjRecheckMatches(id)}
                          onRunUpdater={() => setMjSsnUpdaterJobId(id)}
                          readOnlyReason={botsReadOnlyReason}
                        />
                      ) : null}
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          alignItems: "center",
                          gap: 1,
                          mb: 1,
                          justifyContent: { xs: "flex-start", sm: "space-between" },
                        }}
                      >
                        <Box sx={MJ_PROGRESS_CARD_SX}>
                          <EmployeeProgressBar
                            {...mjJobProgressProps(row)}
                            pendingLabel="ranges not set yet"
                            tooltip={mjJobCountersTooltip(employeeStats)}
                            leading={
                              <>
                                <SoftTag
                                  mono
                                  icon={<SmartToyIcon />}
                                  label={String(row.bot_name ?? "-")}
                                  title="Bot"
                                />
                                <SoftTag
                                  icon={<StraightenIcon />}
                                  label={mjRangeChipLabel(row)}
                                  title="Employee range of the job"
                                />
                                {mjBotOptionTags(row.additional_options, botOptionSpecs(String(row.bot_name ?? "")))}
                                {mjUploadFlagTags(row)}
                                {mjIsUploadJob(row) && mjSsnReadinessByJobId[id] ? (
                                  <SoftTag
                                    tone={mjSsnReadinessByJobId[id].ready ? "success" : "warning"}
                                    icon={
                                      mjSsnReadinessByJobId[id].ready ? <VerifiedUserIcon /> : <WarningAmberIcon />
                                    }
                                    label={mjSsnReadinessByJobId[id].ready ? "SSN ready" : "SSN: names only"}
                                    title={mjSsnReadinessText(mjSsnReadinessByJobId[id])}
                                  />
                                ) : null}
                                <UnmatchedReviewTags
                                  review={mjIsUploadJob(row) ? jobUnmatchedReview(row) : null}
                                  onOpen={() => openMjManage(id, 1)}
                                />
                                {String(row.error_pass_state ?? "") === "RUNNING" && (
                                  <SoftTag tone="warning" icon={<ReplayTagIcon />} label="Error pass running" />
                                )}
                                {mjMajorJobWaitingTag(row)}
                                {mjScheduledRunTag(row)}
                              </>
                            }
                          />
                        </Box>
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          flexWrap="wrap"
                          useFlexGap
                          sx={{
                            // Left-aligned, always: the row wraps onto two lines
                            // often enough that pushing it right just makes the
                            // second line look like a mistake.
                            width: "100%",
                            justifyContent: "flex-start",
                            rowGap: 0.75,
                            // One height for every control in the row, the
                            // error-mode toggle included.
                            "& .MuiButton-root": { minHeight: { xs: 40, sm: 32 } },
                          }}
                        >
                          {(() => {
                            const errorModeOn = extractSkipErrorEmployees(
                              String(row.additional_options ?? ""),
                            ).processErrors;
                            const locked = mjJobAnythingInFlight(row);
                            return (
                              <ErrorModeToggle
                                checked={errorModeOn}
                                disabled={!canEditBots || mjProcessErrorsSavingJobId === id || locked}
                                onChange={(next) =>
                                  void toggleJobProcessErrors(
                                    id,
                                    String(row.additional_options ?? ""),
                                    next,
                                  )
                                }
                                tooltip={
                                  locked
                                    ? "The job has queued or running tasks - a running bot keeps the settings it started with, so this switch is locked until they stop."
                                    : errorModeOn
                                      ? "Error mode is on: every run of this job retries only the employees whose current status is ERROR, and Run all starts only the tasks that hold them. Employees no run has attempted yet are skipped - turn it off to run them."
                                      : "Off: a run processes the employees it has not attempted yet and skips the ones that errored. Turn on to retry only the employees whose current status is ERROR."
                                }
                              />
                            );
                          })()}
                          {mjJobAggregateRunning(row) ? (
                            <Button
                              size="small"
                              variant="contained"
                              disableElevation
                              color="error"
                              startIcon={
                                mjRowBatchBusyJobId === id ? (
                                  <CircularProgress size={14} color="inherit" />
                                ) : (
                                  <StopIcon fontSize="small" />
                                )
                              }
                              disabled={mjJobActionBusy || mjRowBatchBusyJobId === id || !canEditBots}
                              onClick={() => void mjStopJob(id)}
                            >
                              {mjRowBatchBusyJobId === id ? "Stopping..." : "Stop all"}
                            </Button>
                          ) : (
                            <Tooltip
                              title={
                                mjRosterTaskInFlight(row) ? rosterBuildReason(mjIsUploadJob(row)) : ""
                              }
                            >
                              <span>
                                <Button
                                  size="small"
                                  variant="contained"
                                  disableElevation
                                  color="success"
                                  startIcon={
                                    mjRowBatchBusyJobId === id ? (
                                      <CircularProgress size={14} color="inherit" />
                                    ) : (
                                      <PlayArrowIcon fontSize="small" />
                                    )
                                  }
                                  disabled={
                                    mjJobActionBusy ||
                                    mjRowBatchBusyJobId === id ||
                                    !canEditBots ||
                                    mjRosterTaskInFlight(row)
                                  }
                                  onClick={() => void mjSsnGuardedRun(row, "Run all", () => mjRunJob(id))}
                                >
                                  {mjRowBatchBusyJobId === id ? "Running..." : "Run all"}
                                </Button>
                              </span>
                            </Tooltip>
                          )}
                          {(() => {
                            const isUpload = mjIsUploadJob(row);
                            const label = isUpload
                              ? "Build matches only"
                              : "Build roster only";
                            const rosterBusy = mjRosterTaskBusyJobId === id;
                            const blocked = mjJobAnythingInFlight(row);
                            const rosterInfo = mjRosterInfoByJobId[id] ?? null;
                            const built =
                              !rosterBusy && !blocked && mjRosterAlreadyBuilt(row, rosterInfo);
                            return (
                              <Tooltip
                                title={
                                  blocked
                                    ? ROSTER_RUN_BLOCKED_REASON
                                    : built
                                      ? mjRosterBuiltTooltip(row, rosterInfo)
                                      : `${label}: the bot logs in, ${
                                          isUpload
                                            ? "builds the Matches / Several matches / No matches files"
                                            : "collects the roster"
                                        }, sets the ranges and processes nobody.${mjRosterStoredEmptyNote(rosterInfo)}`
                                }
                              >
                                <span>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    disabled={
                                      !canEditBots ||
                                      rosterBusy ||
                                      blocked ||
                                      built ||
                                      mjJobActionBusy
                                    }
                                    startIcon={
                                      rosterBusy ? (
                                        <CircularProgress size={14} color="inherit" />
                                      ) : undefined
                                    }
                                    onClick={() => setMjRosterConfirmJobId(id)}
                                  >
                                    {label}
                                  </Button>
                                </span>
                              </Tooltip>
                            );
                          })()}
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<ArticleOutlinedIcon fontSize="small" />}
                            onClick={() => openJobLogs(id, String(baseRow.name ?? ""))}
                          >
                            Logs
                          </Button>
                          <Button size="small" variant="outlined" onClick={() => openMjManage(id)}>
                            Manage job
                          </Button>
                          <ActionsMenu
                            label="More"
                            ariaLabel="More job actions"
                            actions={mjJobOverflowActions(row, id, nestSel.length)}
                          />
                        </Stack>
                      </Box>
                      {mjAccordionLoadingJobId === id ? (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                          <CircularProgress size={22} />
                        </Box>
                      ) : (
                        (() => {
                          // The roster-only task sits first, outside the
                          // employee numbering: it processes nobody.
                          const rosterTask = tasks.find(
                            (t) =>
                              String(t.range_role ?? "").toUpperCase() === "ROSTER_ONLY",
                          );
                          const employeeTasks = tasks.filter((t) => t !== rosterTask);
                          const entryFor = (
                            task: MjConnectionTask,
                            index: number | string,
                            variant: "row" | "card",
                          ) =>
                            renderMjTaskRow({
                              task,
                              jobId: id,
                              index,
                              showCheckbox: true,
                              showRange: true,
                              rangePending: mjRangesPending(baseRow),
                              showProgress: true,
                              jobSnapshotAt: employeeStats.snapshotAt,
                              showDelete: true,
                              selected: nestSel.includes(task.id),
                              onToggleSelect: (checked) =>
                                mjToggleNestedTaskSelect(id, task.id, checked),
                              variant,
                            });
                          const entries = (variant: "row" | "card") => (
                            <>
                              {rosterTask ? entryFor(rosterTask, "R", variant) : null}
                              {employeeTasks.map((task, ti) =>
                                entryFor(task, ti + 1, variant),
                              )}
                            </>
                          );
                          const employeeCount = mjEmployeeTasks(tasks).length;
                          const busyAdding = mjAddOneTaskJobId === id;
                          const blockedBy = !canEditBots
                            ? CLIENT_VIEW_READ_ONLY_TOOLTIP
                            : mjJobAnythingInFlight(baseRow)
                              ? "Stop the job's queued and running tasks first."
                              : employeeCount >= MAX_BATCH_TASKS
                                ? `A job runs on at most ${MAX_BATCH_TASKS} tasks.`
                                : null;
                          const addTaskButton = (
                            <Tooltip
                              title={
                                blockedBy ??
                                "One more task; the roster is split again across all of them."
                              }
                            >
                              <span style={{ display: "block" }}>
                                <Button
                                  fullWidth
                                  size="small"
                                  startIcon={
                                    busyAdding ? (
                                      <CircularProgress size={14} />
                                    ) : (
                                      <Add fontSize="small" />
                                    )
                                  }
                                  disabled={Boolean(blockedBy) || busyAdding}
                                  onClick={() => void handleMjAddOneTask(id)}
                                  sx={{ color: "text.secondary" }}
                                >
                                  Add task
                                </Button>
                              </span>
                            </Tooltip>
                          );

                          const cardsView = () => (
                            <Box
                              sx={{
                                border: 1,
                                borderColor: "divider",
                                borderRadius: 1,
                                px: 1.25,
                                minWidth: 0,
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                  minHeight: 40,
                                }}
                              >
                                <Checkbox
                                  size="small"
                                  sx={{ p: 0.5 }}
                                  indeterminate={nestSel.length > 0 && !allNestSelected}
                                  checked={allNestSelected}
                                  onChange={(e) =>
                                    mjToggleAllNestedTasks(id, tasks, e.target.checked)
                                  }
                                  inputProps={{
                                    "aria-label": "Select all tasks in this job",
                                  }}
                                />
                                <Typography variant="body2" color="text.secondary">
                                  {nestSel.length > 0
                                    ? `${nestSel.length} selected`
                                    : "Select all"}
                                </Typography>
                              </Box>
                              <Divider />
                              {tasks.length ? (
                                <Stack divider={<Divider />}>{entries("card")}</Stack>
                              ) : (
                                <Typography
                                  color="text.secondary"
                                  sx={{ py: 2, textAlign: "center" }}
                                >
                                  No tasks loaded.
                                </Typography>
                              )}
                              <Divider sx={{ borderStyle: "dashed" }} />
                              <Box sx={{ py: 0.5 }}>{addTaskButton}</Box>
                            </Box>
                          );

                          const tableView = () => (
                        <BotsTableScroll>
                          <Table
                            size="small"
                            stickyHeader
                            sx={{
                              tableLayout: "fixed",
                              width: "100%",
                              minWidth: MJ_TASKS_TABLE_MIN_WIDTH,
                            }}
                          >
                            <colgroup>
                              <col style={{ width: MJ_TASKS_PINNED_WIDTHS.checkbox }} />
                              <col style={{ width: MJ_TASKS_PINNED_WIDTHS.index }} />
                              <col style={{ width: MJ_TASKS_PINNED_WIDTHS.status }} />
                              <col style={{ width: MJ_TASKS_PINNED_WIDTHS.taskId }} />
                              <col style={{ width: MJ_TASKS_PINNED_WIDTHS.bot }} />
                              <col style={{ width: MJ_TASKS_PINNED_WIDTHS.range }} />
                              <col />
                              <col />
                              <col style={{ width: MJ_TASKS_PINNED_WIDTHS.actions }} />
                            </colgroup>
                            <TableHead>
                              <TableRow>
                                <TableCell padding="checkbox" sx={BOTS_HEADER_CELL_SX}>
                                  <Checkbox
                                    size="small"
                                    indeterminate={nestSel.length > 0 && !allNestSelected}
                                    checked={allNestSelected}
                                    onChange={(e) =>
                                      mjToggleAllNestedTasks(id, tasks, e.target.checked)
                                    }
                                    inputProps={{
                                      "aria-label": "Select all tasks in this job",
                                    }}
                                  />
                                </TableCell>
                                <TableCell sx={BOTS_HEADER_CELL_SX}>No</TableCell>
                                <TableCell sx={BOTS_HEADER_CELL_SX}>Status</TableCell>
                                <TableCell sx={BOTS_HEADER_CELL_SX}>Task ID</TableCell>
                                <TableCell sx={BOTS_HEADER_CELL_SX}>Bot</TableCell>
                                <TableCell sx={BOTS_HEADER_CELL_SX}>Range</TableCell>
                                <TableCell sx={BOTS_HEADER_CELL_SX}>Progress</TableCell>
                                <TableCell sx={BOTS_HEADER_CELL_SX}>Connection</TableCell>
                                <TableCell sx={BOTS_HEADER_CELL_SX}>Actions</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {entries("row")}
                              {!tasks.length && (
                                <TableRow>
                                  <TableCell colSpan={9}>
                                    <Typography color="text.secondary">
                                      No tasks loaded.
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              )}
                              <TableRow>
                                <TableCell
                                  colSpan={9}
                                  sx={{
                                    borderTop: "1px dashed",
                                    borderTopColor: "divider",
                                    py: 0.25,
                                  }}
                                >
                                  {addTaskButton}
                                </TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </BotsTableScroll>
                          );
                          return (
                            <BotsFitLayout
                              minWidth={MJ_TASKS_TABLE_MIN_WIDTH}
                              cards={cardsView}
                              table={tableView}
                            />
                          );
                        })()
                      )}
      </Box>
    );
  };

  const mjJobOverflowActions = (
    row: Record<string, unknown>,
    id: number,
    selectedTaskCount: number,
  ): ActionsMenuItem[] => {
    const actions: ActionsMenuItem[] = [];
    const isUpload = mjIsUploadJob(row);
    const jobRunning = mjJobAnythingInFlight(row);
    const errorModeOn = extractSkipErrorEmployees(
      String(row.additional_options ?? ""),
    ).processErrors;

    if (isUpload) {
      const { rows, suggested } = mjUncoveredRows(row);
      // Only the rows past every task's range are this action's; the notice can
      // also be the roster's "no record yet", which Run all answers.
      const uncovered = rows > 0 ? mjRangeNotice(row) : "";
      const busy = mjNewRowsRunningJobId === id;
      actions.push({
        key: "merged",
        label: "Run merged EEs",
        icon: <PlayArrowIcon fontSize="small" />,
        disabled: !canEditBots || busy || jobRunning || errorModeOn || !uncovered,
        hint: jobRunning
          ? "The job has queued or running tasks. Wait for them to finish, or stop them, before running the merged employees."
          : errorModeOn
            ? "Turn off Process error EEs first: in error mode the tasks for the merged employees would process nobody."
            : mjRosterEmpty(row)
              ? "The Matches file has no employees - nothing to run."
              : uncovered
                ? `${uncovered} They get tasks of their own; the tasks that already ran keep their employees and their logs and are not restarted.`
                : "Every employee in the Matches file already belongs to a task. Employees added through Merge reports show up here once the file has them.",
        onClick: () => {
          setMjNewRowsTaskCount(String(suggested));
          setMjNewRowsJob({ id, rows });
        },
      });
    }

    {
      const booking = (row.scheduled_run ?? null) as ScheduledRunInfo | null;
      actions.push({
        key: "schedule",
        label: booking ? "Reschedule the run" : "Schedule a run",
        icon: <ScheduleIcon fontSize="small" color={booking ? "info" : undefined} />,
        disabled: mjJobActionBusy || !canEditBots,
        hint: booking
          ? scheduledRunTooltip(booking)
          : "Run all at a time you pick. Slack is told 30 minutes before, and it can be cancelled until it starts.",
        onClick: () =>
          void mjSsnGuardedRun(row, "Schedule", () =>
            setMjScheduleTarget({ kind: "job", jobId: id, existing: booking }),
          ),
      });
      if (booking) {
        actions.push({
          key: "unschedule",
          label: "Cancel the scheduled run",
          icon: <UnscheduleIcon fontSize="small" />,
          disabled: mjJobActionBusy || !canEditBots,
          onClick: () =>
            setMjUnscheduleTarget({ kind: "job", jobId: id, run: booking }),
        });
      }
    }

    if (isUpload) {
      const gate = mjSsnUpdaterButtonReason(row, mjSsnReadinessByJobId[id] ?? null);
      actions.push({
        key: "ssnUpdater",
        label: "SSN Updater",
        icon: <VerifiedUserIcon fontSize="small" />,
        disabled: !canEditBots || gate.disabled || mjJobActionBusy,
        hint: gate.reason,
        onClick: () => setMjSsnUpdaterJobId(id),
      });
    }

    actions.push({
      key: "manageSelected",
      label: `Manage selected (${selectedTaskCount})`,
      icon: <ReadMoreIcon fontSize="small" />,
      separatorBefore: true,
      disabled: selectedTaskCount === 0 || !canEditBots,
      hint:
        selectedTaskCount === 0
          ? "Tick the tasks in the table below first."
          : "Remove, run, stop or schedule the ticked tasks.",
      onClick: () => selectedTaskCount > 0 && setMjManageSelectionJobId(id),
    });

    if (isUpload) {
      const eligible = mjSftpExportEligibility(row);
      actions.push({
        key: "sftpExport",
        label: "Send rejected files to SFTP",
        icon: <SendIcon fontSize="small" />,
        disabled: !eligible.ok,
        hint: eligible.ok
          ? "Copies the files the vendor refused into the client's SFTP folder and hands out the SFTP login."
          : eligible.reason,
        onClick: () => setMjSftpExportJobId(id),
      });
    }

    return actions;
  };

  const renderMjTaskRow = ({
    task,
    jobId,
    index,
    showCheckbox = false,
    showRange = false,
    rangePending = false,
    showProgress = false,
    jobSnapshotAt = null,
    showDelete = false,
    selected = false,
    onToggleSelect,
    variant = "row",
  }: {
    task: MjConnectionTask;
    jobId: number;
    index: number | string;
    showCheckbox?: boolean;
    showRange?: boolean;
    /** Bootstrap ranges: the real slices arrive with the first roster. */
    rangePending?: boolean;
    /** Processed/Success/Errors cell; jobSnapshotAt dates the stored-log numbers. */
    showProgress?: boolean;
    jobSnapshotAt?: string | null;
    showDelete?: boolean;
    selected?: boolean;
    onToggleSelect?: (checked: boolean) => void;
    /** "card" stacks the same nodes for a phone; "row" is the desktop table. */
    variant?: "row" | "card";
  }) => {
    const rosterOnlyTask = String(task.range_role ?? "").toUpperCase() === "ROSTER_ONLY";
    const currentCid = task.connection ?? null;
    const draftCid = taskConnDraftById[task.id];
    const selectVal =
      typeof draftCid === "number" ? draftCid : typeof currentCid === "number" ? currentCid : "";
    const dirty = typeof draftCid === "number" && draftCid !== currentCid;
    const taskIsRunning = mjTaskShowsStopInsteadOfRun(task.task_status);
    const saving = taskConnSavingId === task.id;
    const saveErr = taskConnSaveErrById[task.id];
    const busy = busyTaskId === task.id;
    const statusText = String(task.task_status ?? "").toUpperCase();
    const isErrorChip = statusText === "CRITICAL_ERROR";
    const booking = task.scheduled_run ?? null;
    const jobRow = mjJobRowById(jobId);
    const jobInFlight = jobRow ? mjJobAnythingInFlight(jobRow) : false;
    const runBlockedBy = mjTaskRunBlockedReason(jobRow, task);
    const employeeTaskCount = mjEmployeeTasks(mjAccordionTasksByJob[jobId] ?? []).length;
    const removeBlockedBy = !canEditBots
      ? (botsReadOnlyReason ?? "Read-only access.")
      : jobInFlight
        ? "Stop the job's queued and running tasks first."
        : employeeTaskCount <= 1
          ? "A job keeps at least one task - delete the whole job instead."
          : null;
    const rowMenuActions: ActionsMenuItem[] = [
      {
        key: "workspace",
        label: "Open task workspace",
        icon: <ReadMoreIcon fontSize="small" />,
        onClick: () => {
          const back = encodeURIComponent(location.pathname + location.search);
          navigate(
            `/bots-management/${userId}/${task.connection ?? ""}/task/${task.id}?back=${back}`,
          );
        },
      },
      {
        key: "logs",
        label: "Logs of this task",
        icon: <ArticleOutlinedIcon fontSize="small" />,
        onClick: () => openTaskLogs(task.id),
      },
      {
        key: "schedule",
        label: booking ? "Move the scheduled run" : "Schedule a run",
        hint: booking ? scheduledRunTooltip(booking) : undefined,
        icon: <ScheduleIcon fontSize="small" color={booking ? "info" : undefined} />,
        disabled: mjJobActionBusy || !canEditBots,
        onClick: () =>
          void mjSsnGuardedRun(mjJobRowById(jobId), "Schedule", () =>
            setMjScheduleTarget({
              kind: "task",
              jobId,
              taskId: task.id,
              existing: booking,
            }),
          ),
      },
    ];
    if (booking) {
      rowMenuActions.push({
        key: "unschedule",
        label: "Cancel the scheduled run",
        icon: <UnscheduleIcon fontSize="small" />,
        disabled: mjJobActionBusy || !canEditBots,
        onClick: () =>
          setMjUnscheduleTarget({ kind: "task", jobId, taskId: task.id, run: booking }),
      });
    }
    if (showDelete && !rosterOnlyTask) {
      rowMenuActions.push({
        key: "remove",
        label: "Remove task",
        hint:
          removeBlockedBy ??
          "Takes the task out of the split. Its logs stay, so its employees stay processed, and the roster is split again across the tasks that stay.",
        icon: <RemoveCircleOutlineIcon fontSize="small" />,
        separatorBefore: true,
        disabled: Boolean(removeBlockedBy),
        onClick: () => setMjRemoveTaskPrompt({ jobId, taskId: task.id }),
      });
    }

    // One model, two layouts: the desktop row and the phone card are built
    // from the same nodes, so a change to an action cannot reach only one.
    const statusNode = (
      <StatusChipMj
        label={task.task_status}
        color={taskRunStatusChipColor(task.task_status)}
        onClick={isErrorChip ? () => setMjErrorPopupTask({ jobId, task }) : undefined}
        icon={isErrorChip ? <ErrorOutlineIcon /> : undefined}
        title={isErrorChip ? "Click to view the error and rerun the task" : undefined}
      />
    );
    // The row's database id (CAL-6161). The No column beside it is an ordinal;
    // this is what the task URL, the log archive logs-task-<id>.zip, the
    // browser profile number and every "Task #id ..." refusal are keyed by, so
    // it is worth a click to copy rather than a read off the address bar.
    const taskIdNode = (
      <Tooltip title="Copy the task ID">
        <Typography
          component="button"
          type="button"
          onClick={() => {
            navigator.clipboard?.writeText(String(task.id));
            dispatch(pushNotification({ severity: "success", message: "Copied." }));
          }}
          sx={{
            border: 0,
            background: "none",
            p: 0,
            cursor: "pointer",
            font: "inherit",
            color: "text.secondary",
            fontVariantNumeric: "tabular-nums",
            "&:hover": { color: "primary.main", textDecoration: "underline" },
          }}
        >
          {`#${task.id}`}
        </Typography>
      </Tooltip>
    );
    const botStatusNode = (
      <StatusChipMj
        label={task.bot_work_status}
        color={botWorkStatusChipColor(task.bot_work_status)}
      />
    );
    const rangeNode = (
            <Stack direction="row" spacing={0.5} justifyContent="center" alignItems="center">
              <Typography variant="body2" color="text.secondary" component="span">
                {rosterOnlyTask
                  ? "roster only"
                  : rangePending
                    ? "-"
                    : task.employee_number_from > task.employee_number_to
                      ? "empty"
                      : `${task.employee_number_from}-${task.employee_number_to}`}
              </Typography>
              <Tooltip
                title={
                  <Box component="span" sx={{ whiteSpace: "pre-line" }}>
                    {mjTaskDetailLines(task, botOptionSpecs(String(task.bot_name ?? ""))).join("\n")}
                  </Box>
                }
              >
                <InfoOutlinedIcon
                  sx={{ fontSize: 16, color: "text.disabled", cursor: "help" }}
                  aria-label="Task details"
                />
              </Tooltip>
            </Stack>
    );
    const progressNode = (() => {
      const progress = mjTaskEmployeeProgress(task, jobSnapshotAt, rangePending);
      return (
        <EmployeeProgressBar
          success={progress.success}
          error={progress.error}
          total={progress.rosterOnly ? null : progress.rangeSize}
          pendingLabel={
            progress.rosterOnly ? "roster only - processes nobody" : "range pending"
          }
          tooltip={progress.tooltip}
          onErrorsClick={() => openTaskLogs(task.id)}
        />
      );
    })();
    // An SSN task logs in with one of the major job's own logins only.
    const pickerConnIds = String(task.bot_name ?? "").startsWith("ssn_bots.")
      ? majorIncludedConnIds
      : majorConnPickerIds;
    const connectionNode = (
          <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
            <Stack
              direction="row"
              spacing={0.75}
              alignItems="flex-start"
              justifyContent="center"
              flexWrap="wrap"
              sx={{ maxWidth: "100%" }}
            >
              <Tooltip
                title={taskIsRunning ? "Stop the task first to move it to another login" : ""}
              >
                <TextField
                  select
                  size="small"
                  value={selectVal}
                  onChange={(ev) => {
                    const v = Number(ev.target.value);
                    if (Number.isFinite(v) && v > 0) setTaskConnDraft(task.id, v);
                  }}
                  disabled={taskIsRunning || saving || !pickerConnIds.length || !canEditBots}
                  sx={{
                    width: "100%",
                    maxWidth: 260,
                    minWidth: 0,
                    "& .MuiInputBase-input": { textAlign: "left" },
                    "& .MuiSelect-select": {
                      textAlign: "left",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-start",
                    },
                  }}
                  error={Boolean(saveErr)}
                  helperText={saveErr ?? ""}
                  FormHelperTextProps={{ sx: { mx: 0, textAlign: "left" } }}
                  // A narrow column cuts the login short; the whole one on hover.
                  SelectProps={{
                    SelectDisplayProps: {
                      title:
                        typeof selectVal === "number"
                          ? connectionPoolRowLabel(connections, selectVal) || undefined
                          : undefined,
                    },
                  }}
                >
                  {pickerConnIds.map((cid, ix) => (
                    <MenuItem key={cid} value={cid}>
                      {`${ix + 1}. ${connectionPoolRowLabel(connections, cid) || "-"}`}
                    </MenuItem>
                  ))}
                  {typeof currentCid === "number" && !pickerConnIds.includes(currentCid) && (
                    <MenuItem value={currentCid}>
                      {connectionPoolRowLabel(connections, currentCid) || "-"}
                    </MenuItem>
                  )}
                </TextField>
              </Tooltip>
              {dirty ? (
                <Stack direction="row" spacing={0.25}>
                  <Tooltip title="Save new connection">
                    <span>
                      <Button
                        size="small"
                        variant="contained"
                        disabled={saving || taskIsRunning || !canEditBots}
                        onClick={() => void saveTaskConn(jobId, task, draftCid)}
                      >
                        {saving ? "..." : "Save"}
                      </Button>
                    </span>
                  </Tooltip>
                  <Tooltip title="Discard">
                    <span>
                      <Button
                        size="small"
                        onClick={() => clearTaskConnDraft(task.id)}
                        disabled={saving}
                      >
                        Cancel
                      </Button>
                    </span>
                  </Tooltip>
                </Stack>
              ) : null}
            </Stack>
          </Box>
    );
    const actionsNode = (
          <Stack
            direction="row"
            spacing={0.5}
            justifyContent="center"
            alignItems="center"
            sx={{ width: "100%", flexWrap: "wrap", rowGap: 0.5 }}
          >
            {shouldShowAuto2FAMj(task) && (
              <Tooltip title="The bot is entering the 2FA code automatically. Click to enter it manually.">
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    color="warning"
                    sx={MJ_PASS_2FA_BTN_SX}
                    startIcon={<CircularProgress size={12} color="warning" />}
                    onClick={() =>
                      setTwoFaManualOverrideIds((prev) => new Set(prev).add(task.id))
                    }
                  >
                    Auto 2FA...
                  </Button>
                </span>
              </Tooltip>
            )}
            {shouldShowCodeSentMj(task) && (
              <Tooltip title="The code is in - the bot is entering it">
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    color="warning"
                    sx={MJ_PASS_2FA_BTN_SX}
                    startIcon={<CircularProgress size={12} color="warning" />}
                    disabled
                  >
                    Code sent
                  </Button>
                </span>
              </Tooltip>
            )}
            {shouldShowLiveViewMj(task) && (
              <Tooltip title="Open the bot's browser - watch it, or solve a captcha in it">
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    color="warning"
                    sx={MJ_PASS_2FA_BTN_SX}
                    startIcon={<ImageSearchIcon fontSize="small" />}
                    onClick={() => setLiveViewMjTaskId(task.id)}
                  >
                    Live browser
                  </Button>
                </span>
              </Tooltip>
            )}
            {shouldShowPass2FAMj(task) && (
              <Tooltip title="Enter the 2FA code">
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    color="warning"
                    sx={MJ_PASS_2FA_BTN_SX}
                    startIcon={<VpnKeyOutlinedIcon fontSize="small" />}
                    onClick={() => {
                      setMjAccordionTasksByJob((prev) =>
                        prev[jobId] ? prev : { ...prev, [jobId]: [task] },
                      );
                      setTwoFaMjTask(task);
                      setTwoFaMjCode("");
                      setTwoFaMjErr(null);
                    }}
                  >
                    Pass 2FA
                  </Button>
                </span>
              </Tooltip>
            )}
            {taskIsRunning ? (
              <Tooltip title="Stop task">
                <span>
                  <IconButton
                    size="small"
                    color="error"
                    disabled={mjJobActionBusy || !canEditBots}
                    onClick={() => void mjStopTaskRow(jobId, task.id)}
                    aria-label="Stop task"
                  >
                    {busy ? <CircularProgress size={14} color="error" /> : <StopIcon fontSize="small" />}
                  </IconButton>
                </span>
              </Tooltip>
            ) : (
              <Tooltip title={runBlockedBy ?? "Run task"}>
                <span>
                  <IconButton
                    size="small"
                    color="success"
                    disabled={
                      mjJobActionBusy ||
                      !canRunTaskByStatusMj(task.task_status) ||
                      !canEditBots ||
                      runBlockedBy != null
                    }
                    onClick={() =>
                      void mjSsnGuardedRun(jobRow, "Run task", () => mjRunTaskRow(jobId, task))
                    }
                    aria-label="Run task"
                  >
                    {busy ? (
                      <CircularProgress size={14} color="success" />
                    ) : (
                      <PlayArrowIcon fontSize="small" />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            )}
            <ActionsMenu actions={rowMenuActions} />
          </Stack>
    );

    if (variant === "card") {
      return (
        <Box key={task.id} sx={{ py: 1.25, minWidth: 0 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              minWidth: 0,
              flexWrap: "wrap",
            }}
          >
            {showCheckbox && (
              <Checkbox
                size="small"
                sx={{ p: 0.5 }}
                checked={selected}
                onChange={(e) => onToggleSelect?.(e.target.checked)}
                inputProps={{ "aria-label": `Select task ${index}` }}
              />
            )}
            <Typography fontWeight={800} sx={{ flexShrink: 0 }}>
              {typeof index === "number" ? `Task ${index}` : "Roster task"}
            </Typography>
            {taskIdNode}
            {statusNode}
            {botStatusNode}
          </Box>
          {showRange && (
            <Box
              sx={{
                mt: 0.75,
                display: "flex",
                alignItems: "center",
                gap: 1,
                minWidth: 0,
              }}
            >
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ lineHeight: 1.7, fontWeight: 600 }}
              >
                Range
              </Typography>
              {rangeNode}
            </Box>
          )}
          {showProgress && <Box sx={{ mt: 0.75 }}>{progressNode}</Box>}
          <Box sx={{ mt: 1, minWidth: 0 }}>{connectionNode}</Box>
          <Box sx={{ mt: 1, minWidth: 0 }}>{actionsNode}</Box>
        </Box>
      );
    }

    return (
      <TableRow key={task.id}>
        {showCheckbox && (
          <TableCell padding="checkbox" align="center">
            <Checkbox
              size="small"
              checked={selected}
              onChange={(e) => onToggleSelect?.(e.target.checked)}
            />
          </TableCell>
        )}
        <TableCell align="center">{index}</TableCell>
        <TableCell align="center" sx={{ verticalAlign: "middle" }}>
          <Box sx={{ display: "flex", justifyContent: "center" }}>{statusNode}</Box>
        </TableCell>
        <TableCell align="center" sx={{ verticalAlign: "middle" }}>
          <Box sx={{ display: "flex", justifyContent: "center" }}>{taskIdNode}</Box>
        </TableCell>
        <TableCell align="center" sx={{ verticalAlign: "middle" }}>
          <Box sx={{ display: "flex", justifyContent: "center" }}>{botStatusNode}</Box>
        </TableCell>
        {showRange && (
          <TableCell
            align="center"
            sx={{ verticalAlign: "middle", fontVariantNumeric: "tabular-nums" }}
          >
            {rangeNode}
          </TableCell>
        )}
        {showProgress && (
          <TableCell align="left" sx={{ verticalAlign: "middle", px: 1.5 }}>
            {progressNode}
          </TableCell>
        )}
        <TableCell align="center" sx={{ verticalAlign: "top", py: 1.25 }}>
          {connectionNode}
        </TableCell>
        <TableCell align="center" sx={{ verticalAlign: "middle" }}>
          {actionsNode}
        </TableCell>
      </TableRow>
    );
  };

  /** `jobIdArg` comes from the task-jobs list; without it this drives the Manage job dialog. */
  const mjRunJob = async (jobIdArg?: number) => {
    if (!canEditBots) return;
    const jobId = jobIdArg ?? mjManageJobId;
    if (jobId == null) return;
    const inline = jobIdArg != null;
    if (inline) {
      setMjRowBatchBusyJobId(jobId);
      setMjJobActionAlertsByJobId((prev) => {
        const n = { ...prev };
        delete n[jobId];
        return n;
      });
      setMjJobNoticesByJobId((prev) => {
        const n = { ...prev };
        delete n[jobId];
        return n;
      });
    } else {
      setMjManageJobError(null);
      setMjManageJobNotice(null);
    }
    try {
      const report = await runTasksBatchMut({ job_id: jobId }).unwrap();
      await refreshMjJobDetail(jobId, true);
      // 200 with nothing started: the batch waits behind a task that holds
      // the queue (typically the roster task) - say so, it is not an error.
      // Error mode leaving tasks alone is worth a line too: the operator sees
      // fewer tasks start than the job has.
      if (
        (!report.started_task_ids?.length && report.queued_task_ids?.length) ||
        report.left_alone_task_ids?.length
      ) {
        if (inline) setMjJobNoticesByJobId((prev) => ({ ...prev, [jobId]: report.message }));
        else setMjManageJobNotice(report.message);
      }
    } catch (e) {
      const message = getErr(e, "Failed to run job.");
      if (inline) setMjJobActionAlertsByJobId((prev) => ({ ...prev, [jobId]: message }));
      else setMjManageJobError(message);
    } finally {
      if (inline) setMjRowBatchBusyJobId(null);
    }
  };

  // Booking a run for later. The dialog owns the moment; this only says what is
  // being booked and where to put the refusal.
  const [mjScheduleTarget, setMjScheduleTarget] = useState<
    | { kind: "job"; jobId: number; existing: ScheduledRunInfo | null }
    | { kind: "task"; jobId: number; taskId: number; existing: ScheduledRunInfo | null }
    | { kind: "tasks"; jobId: number; taskIds: number[] }
    | null
  >(null);
  const [mjUnscheduleTarget, setMjUnscheduleTarget] = useState<
    | { kind: "job"; jobId: number; run: ScheduledRunInfo }
    | { kind: "task"; jobId: number; taskId: number; run: ScheduledRunInfo }
    | null
  >(null);
  const [scheduleJobRunMut, { isLoading: isSchedulingJob }] = useScheduleJobRunMutation();
  const [scheduleTaskRunMut, { isLoading: isSchedulingTask }] = useScheduleTaskRunMutation();
  const [cancelJobScheduleMut] = useCancelJobRunScheduleMutation();
  const [cancelTaskScheduleMut] = useCancelTaskRunScheduleMutation();

  const mjRefreshAfterSchedule = async (jobId: number, taskScoped: boolean) => {
    await safeRefetchChildJobs();
    await refreshMjJobDetail(jobId, true);
    if (taskScoped) await reloadMjAccordionTasks(jobId);
  };

  const mjSubmitSchedule = async (isoInstant: string, zone: string) => {
    const target = mjScheduleTarget;
    if (!target) return;
    if (target.kind === "job") {
      await scheduleJobRunMut({
        jobId: target.jobId,
        scheduled_at: isoInstant,
        timezone: zone,
      }).unwrap();
    } else if (target.kind === "tasks") {
      // One booking per task: the schedule is per target, so several tasks mean
      // several rows - and several Slack reminders.
      for (const taskId of target.taskIds) {
        await scheduleTaskRunMut({
          taskId,
          scheduled_at: isoInstant,
          timezone: zone,
        }).unwrap();
      }
    } else {
      await scheduleTaskRunMut({
        taskId: target.taskId,
        scheduled_at: isoInstant,
        timezone: zone,
      }).unwrap();
    }
    await mjRefreshAfterSchedule(target.jobId, target.kind !== "job");
  };

  const mjCancelSchedule = async () => {
    const target = mjUnscheduleTarget;
    if (!target) return;
    try {
      if (target.kind === "job") await cancelJobScheduleMut(target.jobId).unwrap();
      else await cancelTaskScheduleMut(target.taskId).unwrap();
      await mjRefreshAfterSchedule(target.jobId, target.kind === "task");
    } catch (e) {
      setMjJobActionAlertsByJobId((prev) => ({
        ...prev,
        [target.jobId]: getErr(e, "Could not cancel the scheduled run."),
      }));
    } finally {
      setMjUnscheduleTarget(null);
    }
  };

  const mjStopJob = async (jobIdArg?: number) => {
    if (!canEditBots) return;
    const jobId = jobIdArg ?? mjManageJobId;
    if (jobId == null) return;
    const inline = jobIdArg != null;
    if (inline) {
      setMjRowBatchBusyJobId(jobId);
      setMjJobActionAlertsByJobId((prev) => {
        const n = { ...prev };
        delete n[jobId];
        return n;
      });
    } else setMjManageJobError(null);
    try {
      const report = await stopTasksBatchMut({ job_id: jobId }).unwrap();
      await refreshMjJobDetail(jobId, true);
      // A 200 no longer means every container obeyed: the ones ECS refused are
      // named in the report, and staying silent about them is what had
      // operators pressing Stop again (CAL-5949).
      if (report.failed?.length) {
        if (inline) setMjJobActionAlertsByJobId((prev) => ({ ...prev, [jobId]: report.message }));
        else setMjManageJobError(report.message);
      }
    } catch (e) {
      const message = getErr(e, "Failed to stop job.");
      if (inline) setMjJobActionAlertsByJobId((prev) => ({ ...prev, [jobId]: message }));
      else setMjManageJobError(message);
    } finally {
      if (inline) setMjRowBatchBusyJobId(null);
    }
  };

  const handleMjSubmit2FACode = async () => {
    if (!canEditBots) return;
    if (twoFaMjTask == null) return;
    const submittedTaskId = twoFaMjTask.id;
    const trimmed = twoFaMjCode.trim();
    if (!trimmed) {
      setTwoFaMjErr("2FA code is required.");
      return;
    }
    let jobIdMaybe: number | null = null;
    if (typeof twoFaMjTask.job === "number" && Number.isFinite(twoFaMjTask.job) && twoFaMjTask.job > 0) {
      jobIdMaybe = twoFaMjTask.job;
    }
    setTwoFaMjErr(null);
    try {
      await submitTask2FACodeMut({ taskId: twoFaMjTask.id, code: trimmed }).unwrap();
      setTwoFaMjTask(null);
      setTwoFaMjCode("");
      await safeRefetchChildJobs();
      if (jobIdMaybe != null && expandedMjJobAccordion[jobIdMaybe]) {
        for (let attempt = 0; attempt < 8; attempt += 1) {
          const mapped = await reloadMjAccordionTasks(jobIdMaybe);
          const currentTask = mapped?.find((task) => task.id === submittedTaskId);
          if (!currentTask || !shouldShowPass2FAMj(currentTask)) {
            break;
          }
          await new Promise((resolve) => setTimeout(resolve, 700));
        }
      }
    } catch (e) {
      setTwoFaMjErr(getErr(e, "Could not submit 2FA code."));
    }
  };

  /** The ticked rows of a job, in table order. */
  const mjSelectedTasks = (jobId: number): MjConnectionTask[] => {
    const picked = new Set(mjNestedTaskSelectionByJob[jobId] ?? []);
    return (mjAccordionTasksByJob[jobId] ?? []).filter((task) => picked.has(task.id));
  };

  /**
   * What a Remove over the ticked rows would really take. The roster-only task
   * is not part of the split, and a job keeps at least one task - so ticking
   * everything removes everything but the one worth keeping most.
   */
  const mjSelectionRemovalPlan = (jobId: number) => {
    const selected = mjEmployeeTasks(mjSelectedTasks(jobId));
    const all = mjEmployeeTasks(mjAccordionTasksByJob[jobId] ?? []);
    if (all.length > 0 && selected.length >= all.length) {
      const ordered = mjReductionOrder(selected);
      return { removed: ordered.slice(0, -1), survivor: ordered[ordered.length - 1] ?? null };
    }
    return { removed: selected, survivor: null };
  };

  const mjClearTaskSelection = (jobId: number) =>
    setMjNestedTaskSelectionByJob((prev) => {
      const next = { ...prev };
      delete next[jobId];
      return next;
    });

  /** Run one selection action, then put the panel and the selection back in order. */
  const mjRunSelectionAction = async (
    jobId: number,
    key: string,
    action: () => Promise<void>,
    { keepSelection = false }: { keepSelection?: boolean } = {},
  ) => {
    if (!canEditBots) return;
    setMjSelectionBusyAction(key);
    try {
      await action();
      setMjManageSelectionJobId(null);
      if (!keepSelection) mjClearTaskSelection(jobId);
      await mjRefreshJobAndTasks(jobId);
    } catch (e) {
      setMjJobActionAlertsByJobId((prev) => ({
        ...prev,
        [jobId]: getErr(e, "Could not apply the action to the selected tasks."),
      }));
    } finally {
      setMjSelectionBusyAction(null);
    }
  };
  const mjConfirmDeleteJob = async () => {
    if (!canEditBots) return;
    const delId = mjDeleteJobId;
    if (delId == null) return;
    setMjManageJobError(null);
    try {
      const res = await deleteTaskJobMut({ jobId: delId }).unwrap();
      const kept = rosterKeptNotice(res?.roster_kept_for_job_ids);
      if (kept) dispatch(pushNotification({ severity: "info", message: kept }));
      setMjDeleteJobId(null);
      setMjManageJobId(null);
      setMjManageJobDetail(null);
      setMjAccordionTasksByJob((prev) => {
        const next = { ...prev };
        delete next[delId];
        return next;
      });
      await safeRefetchChildJobs();
      await refetch();
    } catch (e) {
      setMjManageJobError(getErr(e, "Failed to delete job."));
    }
  };

  const handleMjViewJobFiles = (job: Record<string, unknown>) => {
    const prefix = String(job.data_storage_prefix ?? job.path_to_s3_base_folder ?? "").trim();
    const rootPrefix = String(job.data_storage_root_prefix ?? "").trim();
    const rootDisplay = String(job.data_storage_root_display_name ?? "").trim();
    const segs = prefix.split("/").filter(Boolean);
    let leaf = segs.length ? (segs[segs.length - 1] ?? "") : "";
    if (!leaf || leaf === "data") {
      leaf = extractDefaultFolderSlugFromBotName(String(job.bot_name ?? ""));
    }
    const folderMappingSeed =
      rootPrefix && rootDisplay
        ? buildDataStorageFolderMappingSeed(rootDisplay, rootPrefix, prefix)
        : undefined;
    // The header's "Back to Automations" button returns here; ?manage=
    // reopens the same Manage job dialog.
    window.sessionStorage.setItem(
      "botsReturnTo",
      `${location.pathname}${mjManageJobId != null ? `?manage=${mjManageJobId}` : location.search}`,
    );
    navigate("/data-storage", {
      state: {
        initialPrefix: prefix,
        initialFname: leaf || mjJobDisplayName(job),
        ...(folderMappingSeed ? { folderMappingSeed } : {}),
        fromBotsManagement: true,
      },
    });
  };

  const handleMjDownloadJobLogs = async (jobId: number, job?: Record<string, unknown>) => {
    setMjManageJobError(null);
    try {
      const blob = await triggerJobLogsArchive(jobId).unwrap();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const sanitize = (s: string) =>
        s.replace(/^@/, "").replace(/[^A-Za-z0-9._-]+/g, "_").replace(/^_+|_+$/g, "");
      const client = sanitize((userLabel ?? "").trim() || `user-${userId}`);
      const jobName = sanitize(job ? mjJobDisplayName(job) : `job-${jobId}`);
      a.download = `${client}_${jobName}.zip` || `job-${jobId}-logs.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setMjManageJobError(getErr(e, "No logs found for this job yet."));
    }
  };

  const openMjClearLogs = async (jobId: number) => {
    setMjClearLogsJobId(jobId);
    setMjClearLogsIncludeShared(false);
    setMjClearLogsInventory(null);
    try {
      const inventory = await fetchJobLogsInventory(jobId).unwrap();
      setMjClearLogsInventory({
        own: inventory.own,
        shared: inventory.shared,
      });
    } catch {
      // The counts are advice, not a gate - the dialog still works without them.
      setMjClearLogsInventory(null);
    }
  };

  const handleMjClearJobLogs = async () => {
    if (!canEditBots) return;
    if (mjClearLogsJobId == null) return;
    const jobId = mjClearLogsJobId;
    setMjManageJobError(null);
    try {
      await clearJobLogsMut({
        jobId,
        includeShared: mjClearLogsIncludeShared,
      }).unwrap();
      setMjClearLogsJobId(null);
      if (expandedMjJobAccordionRef.current[jobId]) {
        void reloadMjAccordionTasksRef.current(jobId);
      }
      // Stats are reset server-side together with the logs — refresh the rows.
      await safeRefetchChildJobs();
    } catch (e) {
      setMjClearLogsJobId(null);
      setMjManageJobError(getErr(e, "Failed to clear logs."));
    }
  };

  const openMjEditJobDialogFor = (j: Record<string, unknown> | null) => {
    if (j == null || activeMajor == null) return;
    const jobId = Number(j.id);
    if (!jobId) return;
    setMjEditJobError(null);
    setMjEditJobId(jobId);
    setMjEditJobName(mjJobDisplayName(j));
    setMjEditBotName(String(j.bot_name ?? ""));
    setMjEditDocumentType(String(j.document_type ?? "") || "");
    const rawAddOpts = String(j.additional_options ?? "") || "";
    if (activeMajor.bot_kind === "delete_bots") {
      const { types, remaining } = extractDocsTypeFromOptions(rawAddOpts);
      setMjEditDocumentTypes(types);
      setMjEditAdditionalOptions(remaining);
      setMjEditProcessErrors(Boolean(j.auto_process_error_employees));
    } else {
      // SKIP_ERROR_EMPLOYEES is kept as it is: it carries the job's sticky
      // "Process error employees" mode, and dropping it here used to switch that
      // mode off on any save. BotOptionFields already hides it from the raw list.
      setMjEditAdditionalOptions(rawAddOpts);
      setMjEditProcessErrors(Boolean(j.auto_process_error_employees));
    }
    setMjEditBaseline({
      botName: String(j.bot_name ?? ""),
      documentType: String(j.document_type ?? "") || "",
      additionalOptions: rawAddOpts,
    });
    setMjEditSeparateProfiles(Boolean(j.use_separate_browser_profiles));
    setMjEditMaxParallel(
      j.max_parallel_tasks == null ? "" : String(j.max_parallel_tasks),
    );
    setMjEditUploadAsZip(Boolean(j.upload_as_zip));
    setMjEditUnzipBefore(Boolean(j.unzip_folder_before_uploading));
    setMjEditUseSsnMatch(j.use_ssn_match !== false);
    setMjEditSourcePath(String(j.path_to_s3_base_folder ?? ""));
    setMjEditSourceSsn(
      j.source_ssn_major_job == null ? null : Number(j.source_ssn_major_job),
    );
    setMjEditJobOpen(true);
  };

  const openMjEditJobDialog = () => openMjEditJobDialogFor(mjEffectiveManagedJob);

  const saveMjEditJob = async () => {
    if (!canEditBots) return;
    if (mjEditJobId == null || activeMajor == null) return;
    setMjEditJobError(null);
    const snapshotId = mjEditJobId;
    const updatedBot = mjEditBotName.trim();
    if (!updatedBot) {
      setMjEditJobError("Bot is required.");
      return;
    }
    const isUploadEdit = activeMajor.bot_kind === "upload_bots";
    const isDeleteEdit = activeMajor.bot_kind === "delete_bots";
    const editDocTypeUse = documentTypeUseFor(normalizedChildBots, updatedBot);
    // Until the registry answers, every bot looks like it needs a document type;
    // blocking a save on that would be a guess, and the API checks it anyway.
    const editRegistryKnown = normalizedChildBots.length > 0;
    const editWasRequired =
      documentTypeUseFor(normalizedChildBots, mjEditBaseline.botName) === "required";
    if (
      isUploadEdit &&
      editRegistryKnown &&
      editDocTypeUse === "required" &&
      !mjEditDocumentType.trim() &&
      (mjEditBaseline.documentType.trim() || !editWasRequired)
    ) {
      setMjEditJobError("Document type is required.");
      return;
    }
    const editWasMissing = missingRequiredOptions(
      botOptionSpecs(mjEditBaseline.botName),
      mjEditBaseline.additionalOptions,
    );
    const editMissingOptions = missingRequiredOptions(
      botOptionSpecs(updatedBot),
      mjEditAdditionalOptions,
    ).filter((label) => !editWasMissing.includes(label));
    if (editMissingOptions.length) {
      setMjEditJobError(`This bot cannot run without ${editMissingOptions.join(", ")}.`);
      return;
    }
    // Only what this edit broke, as with the required options above.
    const editWasMalformed = malformedOptions(
      botOptionSpecs(mjEditBaseline.botName),
      mjEditBaseline.additionalOptions,
    );
    const editMalformedOptions = malformedOptions(
      botOptionSpecs(updatedBot),
      mjEditAdditionalOptions,
    ).filter((problem) => !editWasMalformed.includes(problem));
    if (editMalformedOptions.length) {
      setMjEditJobError(editMalformedOptions.join(" "));
      return;
    }
    if (isDeleteEdit) {
      // Validate what the save would actually store, not the raw inputs: a
      // row holding only "[]" is non-empty text but an empty list.
      if (!cleanDocTypes(mjEditDocumentTypes).length) {
        setMjEditJobError("At least one document type is required.");
        return;
      }
    }
    const maxParallelEdit = parsedParallelism(mjEditMaxParallel);
    if (maxParallelEdit === undefined) {
      setMjEditJobError("Max tasks running at once must be 1 or more, or empty for no ceiling.");
      return;
    }
    const editAddOpts = isDeleteEdit
      ? buildDeleteAdditionalOptions(mjEditAdditionalOptions.trim(), mjEditDocumentTypes) || null
      : mjEditAdditionalOptions.trim() || null;
    try {
      await updateTaskJob({
        jobId: snapshotId,
        body: {
          name: mjEditJobName.trim(),
          major_job: activeMajor.id,
          bot_name: updatedBot,
          additional_options: editAddOpts,
          auto_process_error_employees: mjEditProcessErrors,
          use_separate_browser_profiles: mjEditSeparateProfiles,
          max_parallel_tasks: maxParallelEdit,
          ...(isUploadEdit
            ? {
                // A bot that never reads the value must not be the reason a
                // document type stored by an earlier job is wiped, so the key
                // is left out of the PATCH entirely rather than blanked.
                ...(editDocTypeUse === "unused"
                  ? {}
                  : { document_type: mjEditDocumentType.trim() }),
                upload_as_zip: mjEditUploadAsZip,
                unzip_folder_before_uploading: mjEditUnzipBefore,
                path_to_s3_base_folder: mjEditSourcePath.trim() || undefined,
                // Not known yet for a new folder: the server keeps or suggests.
                ...(mjEditSsnReadiness || mjEditSourceSsn !== undefined
                  ? { source_ssn_major_job: mjEditSourceSsnValue }
                  : {}),
              }
            : isDeleteEdit
              ? { document_type: "", path_to_s3_base_folder: "" }
              : { document_type: "" }),
          use_ssn_match: mjEditUseSsnMatch,
        },
      }).unwrap();

      setMjAccordionTasksByJob((prev) => {
        delete prev[snapshotId];
        return { ...prev };
      });
      await safeRefetchChildJobs();
      await refetch();
      if (mjManageJobId === snapshotId) {
        void dispatch(
          adminUsersApi.endpoints.getTaskJob.initiate(snapshotId, { subscribe: false, forceRefetch: true }),
        )
          .unwrap()
          .then((r) => setMjManageJobDetail(r as Record<string, unknown>))
          .catch(() => {});
      }
      setMjEditJobOpen(false);
      if (expandedMjJobAccordion[snapshotId]) await reloadMjAccordionTasks(snapshotId);
    } catch (e) {
      setMjEditJobError(getErr(e, "Failed to save task job."));
    }
  };

  useEffect(() => {
    setSelectedMajorIds([]);
    setActiveMajorId(null);
    setSelectedChildJobIds([]);
    setExpandedMjJobAccordion({});
    setMjAccordionTasksByJob({});
    setMjJobRangeWarningsByJobId({});
    setMjJobActionAlertsByJobId({});
    setMjNestedTaskSelectionByJob({});
    setMjManageSelectionJobId(null);
    setMjManageJobId(null);
    setMjManageJobDetail(null);
    setMjManageJobError(null);
  }, [userId]);

  useEffect(() => {
    setSelectedChildJobIds([]);
    setPoolConnSelectedIds([]);
    if (activeMajorId != null && !majors.some((m) => m.id === activeMajorId)) setActiveMajorId(null);
  }, [majors, activeMajorId]);

  // Drop any selected connection ids that no longer belong to this major job's
  // pool (e.g. a connection was deleted elsewhere or vendor switched).
  useEffect(() => {
    setPoolConnSelectedIds((prev) => prev.filter((id) => poolConnIdsForMajor.includes(id)));
  }, [poolConnIdsForMajor]);

  useEffect(() => {
    if (detailMajorJobId == null) return;
    if (majorsStillLoading) return;
    if (!majors.some((m) => m.id === detailMajorJobId)) return;
    setActiveMajorId(detailMajorJobId);
  }, [detailMajorJobId, majors, majorsStillLoading]);

  const toggleMajorCheckbox = (id: number, checked: boolean) => {
    setSelectedMajorIds((prev) => (checked ? [...prev, id] : prev.filter((x) => x !== id)));
  };

  const majorIdsOnPage = useMemo(() => majors.map((m) => m.id), [majors]);
  const allMajorsOnPageSelected =
    majorIdsOnPage.length > 0 && majorIdsOnPage.every((id) => selectedMajorIds.includes(id));
  const someMajorsOnPageSelected = majorIdsOnPage.some((id) => selectedMajorIds.includes(id));

  const toggleSelectAllMajorsOnPage = (checked: boolean) => {
    setSelectedMajorIds((prev) => {
      if (checked) {
        return Array.from(new Set([...prev, ...majorIdsOnPage]));
      }
      const drop = new Set(majorIdsOnPage);
      return prev.filter((id) => !drop.has(id));
    });
  };

  const openCreateMajor = () => {
    setMajorEditTarget(null);
    setMajName("");
    setMajVendor("");
    setMajKind("download_bots");
    setMajJobsAtOnce("");
    setMajJobsAtOnceTouched(false);
    setMajJobsAtOnceBadInput(false);
    setMajJobsAtOnceError(null);
    setMajorFormError(null);
    setMajorEditOpen(true);
  };

  const openEditMajor = (m: MajorJob) => {
    const src = majors.find((x) => x.id === m.id) ?? m;
    setMajorEditTarget(src);
    setMajName(src.name);
    setMajVendor(src.vendor);
    setMajKind(src.bot_kind);
    setMajJobsAtOnce(
      src.jobs_running_at_once == null ? "" : String(src.jobs_running_at_once),
    );
    setMajJobsAtOnceTouched(false);
    setMajJobsAtOnceBadInput(false);
    setMajJobsAtOnceError(null);
    setMajorFormError(null);
    setMajorEditOpen(true);
    // The list is not refetched on websocket events; the lock reads it fresh.
    void refetch();
  };

  const saveMajorForm = async () => {
    if (!canEditBots) return;
    if (!majName.trim()) {
      setMajorFormError("Name is required.");
      return;
    }
    if (!majVendor.trim()) {
      setMajorFormError("Vendor is required.");
      return;
    }
    // The Vendor field says why already.
    if (majorFormSplitVendorError) return;
    setMajorFormError(null);
    // Empty is a real answer - no limit - so only a typed value is parsed.
    const jobsAtOnceRaw = majJobsAtOnce.trim();
    let jobsAtOnce: number | null = null;
    const jobsAtOnceInvalid =
      "Jobs running at once must be a whole number of 1 or more. Leave it empty for no limit.";
    if (majJobsAtOnceBadInput) {
      setMajJobsAtOnceError(jobsAtOnceInvalid);
      return;
    }
    if (jobsAtOnceRaw) {
      const parsed = Number(jobsAtOnceRaw);
      if (!Number.isInteger(parsed) || parsed < 1) {
        setMajJobsAtOnceError(jobsAtOnceInvalid);
        return;
      }
      jobsAtOnce = parsed;
    }
    try {
      if (majorEditTarget) {
        await updateMajorJob({
          majorJobId: majorEditTarget.id,
          invalidateUserId: userId,
          body: {
            name: majName.trim(),
            vendor: majVendor.trim(),
            bot_kind: majKind,
            jobs_running_at_once: jobsAtOnce,
          },
        }).unwrap();
      } else {
        await createMajorJob({
          userId,
          name: majName.trim(),
          vendor: majVendor.trim(),
          bot_kind: majKind,
          jobs_running_at_once: jobsAtOnce,
          included_connection_ids: [],
        }).unwrap();
      }
      await refetch();
      setMajorEditOpen(false);
    } catch (e) {
      const fieldError = (e as { data?: { jobs_running_at_once?: unknown } })?.data
        ?.jobs_running_at_once;
      if (fieldError) {
        setMajJobsAtOnceError(String(Array.isArray(fieldError) ? fieldError[0] : fieldError));
        return;
      }
      // The server says which rule the form broke (a split major job keeps its
      // vendor while it has split jobs, for one); field names stay out of it.
      setMajorFormError(
        getErr(e, "Failed to save major job.").replace(
          /(^|; )(name|vendor|bot_kind|non_field_errors): /g,
          "$1",
        ),
      );
    }
  };

  const confirmRemoveMajor = async (m: MajorJob) => {
    if (!canEditBots) return;
    setMajorDeleteError(null);
    try {
      await deleteMajorJob({ majorJobId: m.id, invalidateUserId: userId }).unwrap();
      if (activeMajorId === m.id) setActiveMajorId(null);
      if (detailMajorJobId === m.id) onExitMajorDetail?.();
      setMajorRowDeleteTarget(null);
      await refetch();
    } catch (e) {
      setMajorDeleteError(getErr(e, "Failed to delete the major job."));
    }
  };

  const confirmBulkDeleteMajors = async () => {
    if (!canEditBots) return;
    if (!selectedMajorIds.length) return;
    setMajorDeleteError(null);
    const done: number[] = [];
    try {
      for (const mid of selectedMajorIds) {
        await deleteMajorJob({ majorJobId: mid, invalidateUserId: userId }).unwrap();
        done.push(mid);
        if (detailMajorJobId === mid) onExitMajorDetail?.();
      }
      setSelectedMajorIds([]);
      setActiveMajorId(null);
      setMajorBulkDeleteOpen(false);
      await refetch();
    } catch (e) {
      // Keep the ones that are still there ticked, so a retry after stopping the
      // bots does not ask the operator to select them again.
      setSelectedMajorIds((prev) => prev.filter((mid) => !done.includes(mid)));
      setMajorDeleteError(
        getErr(
          e,
          done.length
            ? `Deleted ${done.length} major job(s); the next one could not be deleted.`
            : "Failed to delete the selected major jobs.",
        ),
      );
    } finally {
      if (done.length) await refetch();
    }
  };

  const applyPool = async (nextIds: number[]) => {
    if (!canEditBots) return;
    if (!activeMajor) return;
    await updateMajorJob({
      majorJobId: activeMajor.id,
      invalidateUserId: userId,
      body: { included_connection_ids: Array.from(new Set(nextIds)).sort((a, b) => a - b) },
    }).unwrap();
    await refetch();
  };

  const linkExistingToMajor = async (connectionId: number) => {
    if (!activeMajor) return;
    const c = connections.find((x) => x.id === connectionId);
    if (!c || normVendor(c.vendor) !== normVendor(activeMajor.vendor)) return;
    const next = Array.from(new Set([...(activeMajor.included_connection_ids ?? []), connectionId]));
    await applyPool(next);
  };

  const openMajConnCreate = () => {
    if (!activeMajor) return;
    setMcUsername("");
    setMcHost("");
    setMcClientCode("");
    setMcPassword("");
    setMcShowPw(false);
    setMcError(null);
    setMajConnCreateOpen(true);
  };

  const submitMajConnCreate = async () => {
    if (!canEditBots) return;
    if (!activeMajor) return;
    setMcError(null);
    if (!mcUsername.trim()) {
      setMcError("Username is required.");
      return;
    }
    try {
      const raw = await createConnectionMutation({
        userId,
        body: {
          vendor: activeMajor.vendor.trim(),
          username: mcUsername.trim(),
          host: mcHost.trim(),
          client_code: mcClientCode.trim(),
          password: mcPassword,
          port: "9223",
        },
      }).unwrap();
      const newId = typeof raw.id === "number" ? raw.id : null;
      await onRefreshConnections();
      setMajConnCreateOpen(false);
      if (newId != null) await linkExistingToMajor(newId);
      // Created from inside the New task job form: slot the fresh login in.
      if (newId != null && childDialogOpen) {
        setChildError(null);
        // An unpicked row holds "" (ConnPlanEditorRow), which == null missed.
        setChildPlans((prev) =>
          prev.length === 0 || !prev[0].connection_id
            ? [{ connection_id: newId, task_target_count: "1" }, ...prev.slice(1)]
            : prev,
        );
      }
    } catch (e) {
      setMcError(getErr(e, "Failed to create connection."));
    }
  };

  const openMajConnEdit = (c: AdminVendorConnection) => {
    setMajConnEdit(c);
    setMeUsername(c.username || "");
    setMeHost(c.host || "");
    setMeClientCode(c.client_code || "");
    setMePassword(c.password || "");
    setMeShowPw(false);
    setMeError(null);
  };

  /** Connections linked to this automation: one description, table or cards. */
  const mjPoolConnectionColumns: BotsColumn<number>[] = useMemo(
    () => {
      const connOf = (cid: number) => connections.find((c) => c.id === cid);
      const loginOf = (cid: number) => {
        const row = connOf(cid);
        if (!row) return "...";
        return row.username?.trim() ? `@${row.username.trim()}` : "-";
      };
      const hostOf = (cid: number) => {
        const row = connOf(cid);
        return row?.host?.trim() || `default ${row?.vendor?.trim() || "vendor"} host`;
      };
      return [
        {
          key: "ordinal",
          header: "No",
          width: 60,
          compactWidth: 48,
          card: "hidden",
          cell: (cid) => poolConnIdsForMajor.indexOf(cid) + 1,
        },
        {
          key: "login",
          header: "Login",
          width: 180,
          compactWidth: 150,
          card: "title",
          cell: (cid) => (
            <Typography variant="body2" noWrap title={loginOf(cid)}>
              {loginOf(cid)}
            </Typography>
          ),
          cardCell: (cid) => (
            <Typography fontWeight={700} sx={{ overflowWrap: "anywhere" }}>
              {`${poolConnIdsForMajor.indexOf(cid) + 1}. ${loginOf(cid)}`}
            </Typography>
          ),
        },
        {
          key: "vendor",
          header: "Vendor",
          width: 130,
          // The major job's own vendor on every row.
          compactHidden: true,
          cell: (cid) => (
            <Typography variant="body2" noWrap title={connOf(cid)?.vendor ?? ""}>
              {connOf(cid)?.vendor ?? "-"}
            </Typography>
          ),
        },
        {
          key: "host",
          header: "Host",
          cell: (cid) => (
            <Typography
              variant="body2"
              noWrap
              title={hostOf(cid)}
              color={connOf(cid)?.host?.trim() ? "text.primary" : "text.secondary"}
            >
              {hostOf(cid)}
            </Typography>
          ),
          cardCell: (cid) => (
            <Typography
              variant="body2"
              sx={{ overflowWrap: "anywhere" }}
              color={connOf(cid)?.host?.trim() ? "text.primary" : "text.secondary"}
            >
              {hostOf(cid)}
            </Typography>
          ),
        },
        {
          key: "clientCode",
          header: "Client code",
          width: 140,
          compactWidth: 120,
          cell: (cid) => (
            <Typography variant="body2" noWrap title={connOf(cid)?.client_code ?? ""}>
              {connOf(cid)?.client_code?.trim() || "-"}
            </Typography>
          ),
        },
        {
          key: "actions",
          header: "Actions",
          width: 150,
          compactWidth: 136,
          card: "actions",
          cell: (cid) => {
            const row = connOf(cid);
            return (
              <Stack
                direction="row"
                spacing={0.5}
                justifyContent="center"
                alignItems="center"
              >
                {row && (
                  <Button
                    size="small"
                    disabled={!canEditBots}
                    onClick={() => openMajConnEdit(row)}
                  >
                    Edit
                  </Button>
                )}
                <Tooltip title="Delete connection">
                  <span>
                    <IconButton
                      size="small"
                      color="error"
                      aria-label="Delete connection"
                      disabled={!canEditBots}
                      onClick={() => setDeleteConnConfirm(cid)}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            );
          },
        },
      ];
    },
    [connections, poolConnIdsForMajor, canEditBots, openMajConnEdit],
  );

  /** The automations list: a row on a desktop, a card on a phone. */
  const mjMajorJobColumns: BotsColumn<MajorJob>[] = useMemo(
    () => [
      {
        key: "name",
        header: "Name",
        card: "title",
        cell: (m) => (
          <Typography variant="body2" noWrap title={m.name}>
            {m.name}
          </Typography>
        ),
        cardCell: (m) => (
          <Typography fontWeight={700} sx={{ overflowWrap: "anywhere" }}>
            {m.name}
          </Typography>
        ),
      },
      {
        key: "vendor",
        header: "Vendor",
        width: 160,
        compactWidth: 128,
        card: "badge",
        cell: (m) => (
          <Typography variant="body2" noWrap title={m.vendor}>
            {m.vendor}
          </Typography>
        ),
        cardCell: (m) => (
          <SoftTag icon={<BusinessIcon />} label={m.vendor || "-"} title="Vendor" />
        ),
      },
      {
        key: "kind",
        header: "Kind",
        width: 110,
        compactWidth: 96,
        card: "badge",
        cell: (m) => botKindMeta(m.bot_kind).label,
        cardCell: (m) => {
          const kind = botKindMeta(m.bot_kind);
          return <SoftTag tone={kind.tone} icon={kind.icon} label={kind.label} title="Action" />;
        },
      },
      {
        key: "connections",
        header: "Connections",
        width: 120,
        // Derived from the vendor and shown in full on the workspace screen.
        card: "hidden",
        compactHidden: true,
        cell: (m) =>
          isSplitKind(m.bot_kind)
            ? "-"
            : connections.filter((c) => normVendor(c.vendor) === normVendor(m.vendor))
                .length,
      },
      {
        key: "jobs",
        header: "Jobs",
        width: 90,
        compactWidth: 72,
        cardLabel: "Jobs",
        // A split major job counts its split jobs.
        cell: (m) =>
          isSplitKind(m.bot_kind) ? (
            <Tooltip title="Split jobs">
              <span>{m.split_job_count ?? 0}</span>
            </Tooltip>
          ) : (
            m.child_job_ids?.length ?? 0
          ),
      },
      {
        key: "ssn",
        header: "SSN",
        width: 80,
        compactWidth: 64,
        card: "badge",
        cell: (m) =>
          m.ssn_file_exists === true ? (
            <Tooltip title="This major job's SSN file is in storage and holds employees">
              <CheckCircleOutlineIcon fontSize="small" color="success" />
            </Tooltip>
          ) : null,
        cardCell: (m) =>
          m.ssn_file_exists === true ? (
            <SoftTag
              tone="success"
              icon={<VerifiedUserIcon />}
              label="SSN ready"
              title="SSN file"
            />
          ) : null,
      },
      {
        key: "actions",
        header: "Actions",
        width: 130,
        card: "actions",
        cell: (m) => (
          <Stack
            direction="row"
            spacing={0.5}
            justifyContent="center"
            alignItems="center"
          >
            <Button
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                openEditMajor(m);
              }}
              disabled={isSavingMajor || !canEditBots}
            >
              Edit
            </Button>
            <Tooltip title={majorDeleteBlockedReason(m) ?? "Delete"}>
              <span>
                <IconButton
                  size="small"
                  color="error"
                  aria-label="Delete major job"
                  disabled={!canEditBots || majorDeleteBlockedReason(m) != null}
                  onClick={(e) => {
                    e.stopPropagation();
                    setMajorRowDeleteTarget(m);
                  }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        ),
      },
    ],
    [connections, canEditBots, isSavingMajor, openEditMajor],
  );

  const submitMajConnEdit = async () => {
    if (!canEditBots) return;
    if (!majConnEdit) return;
    setMeError(null);
    if (!meUsername.trim()) {
      setMeError("Username is required.");
      return;
    }
    try {
      await updateConnectionMutation({
        connectionId: majConnEdit.id,
        body: {
          vendor: majConnEdit.vendor,
          username: meUsername.trim(),
          host: meHost.trim() || null,
          client_code: meClientCode.trim() || null,
          password: mePassword,
          port: "9223",
        },
      }).unwrap();
      await onRefreshConnections();
      setMajConnEdit(null);
    } catch (e) {
      setMeError(getErr(e, "Failed to update connection."));
    }
  };

  const deleteConnectionHard = async (cid: number) => {
    if (!canEditBots) return;
    try {
      await deleteConnectionMutation(cid).unwrap();
      await onRefreshConnections();
      setDeleteConnConfirm(null);
      await refetch();
    } catch {
      /* ignore */
    }
  };

  const toggleChildCheckbox = (id: number, checked: boolean) => {
    setSelectedChildJobIds((prev) => (checked ? [...prev, id] : prev.filter((x) => x !== id)));
  };

  /**
   * A task job: a row on a desktop, a card on a phone. The three counter
   * columns become one progress line on a card - the same three numbers, but
   * legible at 390px instead of three digits behind a sideways scroll.
   */
  const mjTaskJobColumns: BotsColumn<Record<string, unknown>>[] = useMemo(
    () => {
      const merged = (raw: Record<string, unknown>) => ({
        ...raw,
        ...(mjJobDetailById[Number(raw.id)] ?? {}),
      });
      return [
        {
          key: "name",
          header: "Name",
          align: "left",
          card: "title",
          cell: (raw) => (
            <Typography fontWeight={700} noWrap title={mjJobDisplayName(merged(raw))}>
              {mjJobDisplayName(merged(raw))}
            </Typography>
          ),
          cardCell: (raw) => (
            <Typography fontWeight={700} sx={{ overflowWrap: "anywhere" }}>
              {mjJobDisplayName(merged(raw))}
            </Typography>
          ),
        },
        {
          key: "status",
          header: "Status",
          width: 130,
          compactWidth: 116,
          card: "badge",
          cell: (raw) => (
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              {mjJobStatusChip(merged(raw))}
            </Box>
          ),
          cardCell: (raw) => mjJobStatusChip(merged(raw)),
        },
        {
          key: "tasks",
          header: "Tasks",
          width: 90,
          compactWidth: 72,
          cardLabel: "Running",
          cell: (raw) => {
            const { running, total } = mjJobStatsTotals(merged(raw));
            return (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontVariantNumeric: "tabular-nums" }}
              >
                {running}/{total}
              </Typography>
            );
          },
        },
        {
          key: "total",
          header: "Total",
          width: 80,
          compactWidth: 64,
          card: "hidden",
          cell: (raw) => (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontVariantNumeric: "tabular-nums" }}
            >
              {mjEmployeeStatsFromJob(merged(raw)).total}
            </Typography>
          ),
        },
        {
          key: "success",
          header: "Success",
          width: 90,
          compactWidth: 76,
          card: "hidden",
          cell: (raw) => {
            const stats = mjEmployeeStatsFromJob(merged(raw));
            return (
              <Tooltip title={mjStatsSnapshotTooltip(stats.snapshotAt, stats.source)}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontVariantNumeric: "tabular-nums" }}
                >
                  {stats.success}
                </Typography>
              </Tooltip>
            );
          },
        },
        {
          key: "error",
          header: "Error",
          width: 80,
          compactWidth: 64,
          card: "hidden",
          cell: (raw) => {
            const stats = mjEmployeeStatsFromJob(merged(raw));
            return (
              <Tooltip title={mjStatsSnapshotTooltip(stats.snapshotAt, stats.source)}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontVariantNumeric: "tabular-nums" }}
                >
                  {stats.error}
                </Typography>
              </Tooltip>
            );
          },
        },
        {
          key: "progress",
          header: "",
          // Desktop reads Total / Success / Error as columns; a card reads the
          // same three numbers off the bar, which needs no sideways room.
          card: "block",
          cell: () => null,
          cardCell: (raw) => (
            <EmployeeProgressBar
              {...mjJobProgressProps(merged(raw))}
              pendingLabel="ranges not set yet"
              tooltip={mjJobCountersTooltip(mjEmployeeStatsFromJob(merged(raw)))}
            />
          ),
        },
        {
          key: "actions",
          header: "Actions",
          width: 176,
          compactWidth: 152,
          card: "actions",
          cell: (raw) => {
            const row = merged(raw);
            const id = Number(raw.id);
            const busyRow = Boolean(mjRefreshingJobIds[id]);
            return (
              <Stack
                direction="row"
                spacing={0.5}
                justifyContent="center"
                alignItems="center"
                sx={{ flexWrap: { xs: "wrap", md: "nowrap" } }}
              >
                <Tooltip title="Refresh job row - recounts Total / Success / Errors from the stored logs">
                  <span>
                    <IconButton
                      size="small"
                      color="primary"
                      disabled={busyRow}
                      onClick={(ev) => {
                        ev.stopPropagation();
                        void refreshMjJobRow(id);
                      }}
                    >
                      {busyRow ? (
                        <CircularProgress size={18} color="primary" />
                      ) : (
                        <ReplayIcon sx={{ fontSize: 20 }} />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
                {activeMajor?.bot_kind !== "delete_bots" && (
                  <Button
                    size="small"
                    onClick={(ev) => {
                      ev.stopPropagation();
                      handleMjViewJobFiles(row);
                    }}
                  >
                    Files
                  </Button>
                )}
                {activeMajor?.bot_kind === "upload_bots" &&
                  (() => {
                    const eligible = mjSftpExportEligibility(row);
                    return (
                      <Tooltip
                        title={
                          eligible.ok
                            ? "Send rejected files to SFTP"
                            : eligible.reason
                        }
                      >
                        <span>
                          <IconButton
                            size="small"
                            color="primary"
                            disabled={!eligible.ok}
                            aria-label="Send rejected files to SFTP"
                            onClick={(ev) => {
                              ev.stopPropagation();
                              setMjSftpExportJobId(id);
                            }}
                          >
                            <SendIcon sx={{ fontSize: 20 }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    );
                  })()}
              </Stack>
            );
          },
        },
      ];
    },
    [mjJobDetailById, mjRefreshingJobIds, activeMajor, handleMjViewJobFiles],
  );

  /** Confirmation dialog state for the bulk "Delete selected task jobs" action. */
  const [bulkChildDeleteOpen, setBulkChildDeleteOpen] = useState(false);
  const [bulkChildDeleteError, setBulkChildDeleteError] = useState<string | null>(null);

  const openBulkDeleteChildren = () => {
    if (!selectedChildJobIds.length) return;
    setBulkChildDeleteError(null);
    setBulkChildDeleteOpen(true);
  };

  const confirmBulkDeleteChildren = async () => {
    if (!canEditBots) return;
    if (!selectedChildJobIds.length) {
      setBulkChildDeleteOpen(false);
      return;
    }
    setBulkChildDeleteError(null);
    try {
      const res = await taskJobsBatchDelete({ job_ids: selectedChildJobIds }).unwrap();
      // The endpoint answers 200 even when it refused a job: what it did is in the
      // body. Reporting only the transport would close the dialog over a job that
      // is still there - which is what made the button look like it did nothing.
      const refused = res?.errors ?? [];
      const deletedIds = res?.deleted_job_ids ?? [];
      const kept = rosterKeptNotice(res?.roster_kept_for_job_ids);
      if (kept) dispatch(pushNotification({ severity: "info", message: kept }));
      setExpandedMjJobAccordion({});
      setMjAccordionTasksByJob({});
      await safeRefetchChildJobs();
      await refetch();
      if (refused.length) {
        setSelectedChildJobIds(refused.map((r) => r.job_id));
        setBulkChildDeleteError(describeRefusedJobDeletes(refused, deletedIds.length));
        return;
      }
      setSelectedChildJobIds([]);
      setBulkChildDeleteOpen(false);
    } catch (e) {
      // The dialog stays open with the reason: the server declines while a task
      // of one of the selected jobs is still running, and closing would hide it.
      setBulkChildDeleteError(getErr(e, "Failed to delete the selected task jobs."));
    }
  };

  const openCreateChildJob = () => {
    if (!activeMajor) return;
    // Both ways in start from an empty form. The one without a login of the
    // vendor used to skip this and open over what the previous job's form had
    // left - its name, its bot's options, its connection and task count.
    setTaskJobListName("");
    setChildBotName("");
    setChildAdditionalOptions("");
    setChildProcessErrors(false);
    setChildSeparateProfiles(false);
    setChildMaxParallel("");
    setChildDocumentType("");
    setChildDocumentTypes([""]);
    setChildUploadAsZip(false);
    setChildUnzipBefore(false);
    setChildUseSsnMatch(true);
    setChildSourcePath("");
    setChildSourceSsn(undefined);
    if (!majorConnPickerIds.length) {
      // No row yet: the login made with "New connection" takes the first one.
      setChildPlans([]);
      setChildError(
        `No bot logins exist for vendor "${activeMajor.vendor}" on this user yet. ` +
          'Use the "New connection" button below to create one without leaving this form.',
      );
      setChildDialogOpen(true);
      return;
    }
    setChildError(null);
    const firstConn = majorConnPickerIds[0];
    setChildPlans([{ connection_id: firstConn, task_target_count: "1" }]);
    setChildDialogOpen(true);
  };

  const createChildSubmit = async () => {
    if (!canEditBots) return;
    if (!activeMajor) return;
    if (!childBotName.trim()) {
      setChildError("Bot is required.");
      return;
    }
    if (!taskJobListName.trim()) {
      setChildError("Task Job Name is required.");
      return;
    }
    const newTaskJobName = taskJobListName.trim().slice(0, 255);
    const connPlans = normalizePlans(childPlans);
    if (!connPlans.length) {
      setChildError("Add at least one connection row with a task count.");
      return;
    }
    const maxParallelCreate = parsedParallelism(childMaxParallel);
    if (maxParallelCreate === undefined) {
      setChildError("Max tasks running at once must be 1 or more, or empty for no ceiling.");
      return;
    }
    const totalTasks = connPlans.reduce((acc, r) => acc + r.task_target_count, 0);
    if (totalTasks < 1) {
      setChildError("Total tasks must be >= 1.");
      return;
    }
    if (totalTasks > MAX_BATCH_TASKS) {
      setChildError(`Total tasks (${totalTasks}) exceeds the per-job limit of ${MAX_BATCH_TASKS}.`);
      return;
    }
    const vnormCreate = normVendor(activeMajor.vendor);
    for (const r of connPlans) {
      const c = connections.find((x) => x.id === r.connection_id);
      if (!c || normVendor(c.vendor) !== vnormCreate) {
        setChildError(`Connection vendor mismatch for ${activeMajor.vendor}.`);
        return;
      }
    }
    const isUploadKind = activeMajor.bot_kind === "upload_bots";
    const isDeleteKind = activeMajor.bot_kind === "delete_bots";
    // Whether a document type is needed is the bot's answer, not the kind's:
    // some upload bots never read it, and for Rippling a blank one means the
    // Top folder itself.
    const createDocTypeUse = documentTypeUseFor(normalizedChildBots, childBotName.trim());
    if (
      isUploadKind &&
      normalizedChildBots.length > 0 &&
      createDocTypeUse === "required" &&
      !childDocumentType.trim()
    ) {
      setChildError("Document type is required.");
      return;
    }
    const createMissingOptions = missingRequiredOptions(
      botOptionSpecs(childBotName.trim()),
      childAdditionalOptions,
    );
    if (createMissingOptions.length) {
      setChildError(`This bot cannot run without ${createMissingOptions.join(", ")}.`);
      return;
    }
    const createMalformedOptions = malformedOptions(
      botOptionSpecs(childBotName.trim()),
      childAdditionalOptions,
    );
    if (createMalformedOptions.length) {
      setChildError(createMalformedOptions.join(" "));
      return;
    }
    if (isDeleteKind) {
      // Validate what the save would actually store, not the raw inputs: a
      // row holding only "[]" is non-empty text but an empty list.
      if (!cleanDocTypes(childDocumentTypes).length) {
        setChildError("At least one document type is required.");
        return;
      }
    }
    if (isUploadKind && !childSourcePath.trim()) {
      setChildError("Source folder is required for upload bots. Click Browse to select one.");
      return;
    }
    // A value typed before the operator switched to a bot that ignores it must
    // not travel with the job.
    const docType =
      isUploadKind && createDocTypeUse !== "unused" ? childDocumentType.trim() : "";
    const folderSlug = extractDefaultFolderSlugFromBotName(childBotName.trim());
    const defaultPath = isUploadKind
      ? childSourcePath.trim()
      : isDeleteKind
        ? ""
        : `users/${userId}/${activeMajor.vendor}/data/${folderSlug}`;
    const addOpts = isDeleteKind
      ? buildDeleteAdditionalOptions(childAdditionalOptions.trim(), childDocumentTypes) || null
      : childAdditionalOptions.trim() || null;
    try {
      await ensureMajorIncludesConnectionIds(connPlans.map((r) => r.connection_id));
      const primaryConn = connPlans[0].connection_id;
      const primaryCount = connPlans[0].task_target_count;
      const res = await createTasksBatch({
        connection: primaryConn,
        bot_name: childBotName.trim(),
        document_type: docType,
        task_status: "PENDING",
        bot_work_status: "NOT_LOGGED_IN",
        path_to_s3_base_folder: defaultPath,
        browser_profile_id: 1,
        upload_as_zip: isUploadKind ? childUploadAsZip : false,
        unzip_folder_before_uploading: isUploadKind ? childUnzipBefore : false,
        use_ssn_match: childUseSsnMatch,
        tasks_number: primaryCount,
        employee_number_from: 0,
        use_separate_profiles: childSeparateProfiles,
        max_parallel_tasks: maxParallelCreate,
        create_job: true,
        job_name: newTaskJobName,
        major_job_id: activeMajor.id,
        additional_options: addOpts,
        auto_process_error_employees: childProcessErrors,
        // Left out until the folder was checked: the server then suggests.
        ...(isUploadKind && childSsnReadiness
          ? { source_ssn_major_job: childSourceSsnValue }
          : {}),
      }).unwrap();
      const jobId = Number((res as { job?: { id?: unknown } }).job?.id);
      if (!jobId || Number.isNaN(jobId)) throw new Error("missing job id");
      await updateTaskJob({
        jobId,
        body: {
          name: newTaskJobName,
          major_job: activeMajor.id,
          connection_plans: connPlans,
          additional_options: addOpts,
        },
      }).unwrap();
      for (let i = 1; i < connPlans.length; i++) {
        const extra = connPlans[i];
        if (extra.task_target_count < 1) continue;
        await addTasksToJobMutation({
          jobId,
          count: extra.task_target_count,
          connection_id: extra.connection_id,
        }).unwrap();
      }
      await safeRefetchChildJobs();
      await refetch();
      setChildDialogOpen(false);
    } catch (e) {
      setChildError(getErr(e, "Unable to create task job. Check inputs and try again."));
    }
  };

  const mjAddTasksDialogStats = useMemo(() => {
    if (!mjAddTasksOpen || mjAddTasksJobId == null) return null;

    let currentTasks = mjAccordionTasksByJob[mjAddTasksJobId] ?? [];
    const det =
      mjManageJobDetail != null && Number((mjManageJobDetail as Record<string, unknown>).id) === mjAddTasksJobId
        ? mjManageJobDetail
        : null;

    const rawDt = det ? ((det as Record<string, unknown>).tasks as unknown) : undefined;
    if (
      rawDt &&
      Array.isArray(rawDt) &&
      rawDt.length > 0 &&
      rawDt.length >= currentTasks.length
    ) {
      currentTasks = (rawDt as Record<string, unknown>[]).map((t) =>
        normalizeRawTaskToMj(t, mjAddTasksJobId),
      );
    }

    const jRec =
      (mjEffectiveManagedJob != null && Number(mjEffectiveManagedJob.id) === mjAddTasksJobId
        ? mjEffectiveManagedJob
        : null) ??
      (det ? (det as Record<string, unknown>) : null) ??
      (((childJobs.find((r) => Number((r as Record<string, unknown>).id) === mjAddTasksJobId) ??
        null) as Record<string, unknown> | null));

    const plansSnap = jRec ? parseConnectionPlansFromJobRow(jRec) : [];
    const planMode = plansSnap.length > 0;
    // The roster-only task is outside the split, here as on the server.
    const employeeTasks = mjEmployeeTasks(currentTasks);
    const currentCount = employeeTasks.length;
    const jobTotalRemain = Math.max(0, MAX_BATCH_TASKS - currentCount);

    const resolved = resolveMjBulkTaskAddPayload(
      planMode,
      plansSnap,
      mjAddPlanConnPool,
      mjBulkAddRows,
      currentTasks,
      jobTotalRemain,
      connections,
    );

    // Block adding only on global 30-cap or running tasks. Plan-quota errors
    // surfaced by the resolver are no longer enforced - the backend auto-raises
    // per-connection quotas, so the only hard frontend rule is "<=30 total".
    const jobHasRunningTasks = jRec ? mjJobAnythingInFlight(jRec) : false;
    const overGlobalLimit =
      "error" in resolved && /total adds .* exceed/i.test(resolved.error);
    const planLevelOnly = "error" in resolved && !overGlobalLimit;

    const addBlockingReason = overGlobalLimit
      ? resolved.error
      : jobHasRunningTasks
        ? "Stop all tasks of this task job before adding new ones."
        : null;

    const planLevelHint = planLevelOnly ? (resolved as { error: string }).error : null;

    const totalHelp = `This job is split into ${currentCount} task(s), out of ${MAX_BATCH_TASKS} allowed.`;

    const countHint =
      jobTotalRemain < 1
        ? `This task job is at its ${MAX_BATCH_TASKS} task limit.`
        : `Up to ${jobTotalRemain} more task(s) can be added.`;

    const parsedTarget = mjDigitsToCount(mjTaskCountTarget);
    const target = Number.isFinite(parsedTarget) ? parsedTarget : currentCount;
    const delta = target - currentCount;
    const removalPreview =
      delta < 0 ? mjReductionOrder(employeeTasks).slice(0, -delta) : [];
    const rowsSum = mjBulkAddRows.reduce((acc, row) => {
      const parsed = mjDigitsToCount(String(row.task_target_count ?? ""));
      return acc + (Number.isFinite(parsed) ? parsed : 0);
    }, 0);
    const deltaLabel =
      delta === 0
        ? "no change"
        : delta > 0
          ? `adding ${delta} task(s)`
          : `removing ${-delta} task(s)`;

    const applyBlockingReason = jobHasRunningTasks
      ? "Stop the queued and running tasks of this job before changing how many tasks it runs on."
      : !Number.isFinite(parsedTarget) || target < 1
        ? "Enter how many tasks this job should run on."
        : target > MAX_BATCH_TASKS
          ? `A job runs on at most ${MAX_BATCH_TASKS} tasks.`
          : delta === 0
            ? `This job already runs on ${currentCount} task(s).`
            : delta > 0
              ? addBlockingReason ??
                (planMode && rowsSum !== delta
                  ? `The logins below add up to ${rowsSum} task(s), not ${delta}.`
                  : null)
              : null;

    return {
      plansSnap,
      totalHelp,
      planMode,
      legacyMultiLoginHint: null as string | null,
      addBlockingReason,
      applyBlockingReason,
      planLevelHint,
      countHint,
      jobHasRunningTasks,
      currentCount,
      target,
      delta,
      deltaLabel,
      removalPreview,
    };
  }, [
    mjAddTasksOpen,
    mjAddTasksJobId,
    mjAccordionTasksByJob,
    mjEffectiveManagedJob,
    mjManageJobDetail,
    childJobs,
    mjBulkAddRows,
    mjTaskCountTarget,
    mjAddPlanConnPool,
    majorConnPickerIds,
    connections,
  ]);

  /** The number field owns the split; changing it re-seeds the per-login rows. */
  const mjSetTaskCountTarget = (next: string) => {
    setMjTaskCountTarget(next);
    const stats = mjAddTasksDialogStats;
    if (stats == null) return;
    const parsed = mjDigitsToCount(next);
    const add = Number.isFinite(parsed) ? parsed - stats.currentCount : 0;
    if (add <= 0) return;
    const pool = mjAddPlanConnPool.length > 0
      ? mjAddPlanConnPool
      : stats.plansSnap.map((plan) => plan.connection_id);
    const head = stats.planMode ? pool[0] : undefined;
    setMjBulkAddRows((prev) => [
      {
        connection_id: head != null ? head : prev[0]?.connection_id ?? "",
        task_target_count: String(add),
      },
    ]);
  };

  /** Editing a login row moves the number with it, so the two never disagree. */
  const mjSetBulkAddRows = (rows: ConnPlanEditorRow[]) => {
    setMjBulkAddRows(rows);
    const stats = mjAddTasksDialogStats;
    if (stats == null) return;
    const sum = rows.reduce((acc, row) => {
      const parsed = mjDigitsToCount(String(row.task_target_count ?? ""));
      return acc + (Number.isFinite(parsed) ? parsed : 0);
    }, 0);
    setMjTaskCountTarget(String(stats.currentCount + sum));
  };

  /** Everything the panel has to re-read after the task count of a job changes. */
  const mjRefreshJobAndTasks = async (jobId: number) => {
    await safeRefetchChildJobs();
    await reloadMjAccordionTasks(jobId);
    void dispatch(
      adminUsersApi.endpoints.getTaskJob.initiate(jobId, { subscribe: false, forceRefetch: true }),
    )
      .unwrap()
      .then((raw) => {
        if (mjManageJobId === jobId) setMjManageJobDetail(raw as Record<string, unknown>);
      })
      .catch(() => {});
  };

  /** Apply the number: add tasks, or take some away and deal the roster again. */
  const handleMjTaskCountApply = async () => {
    if (!canEditBots) return;
    const stats = mjAddTasksDialogStats;
    if (stats == null || mjAddTasksJobId == null) return;
    if (stats.delta > 0) {
      await handleMjAddTasksConfirmed();
      return;
    }
    if (stats.delta === 0) return;
    const jid = mjAddTasksJobId;
    setMjAddTasksErr(null);
    try {
      await reduceJobTasksMutation({ jobId: jid, count: -stats.delta }).unwrap();
      setMjAddTasksOpen(false);
      await safeRefetchChildJobs();
      if (expandedMjJobAccordion[jid]) await reloadMjAccordionTasks(jid);
      void dispatch(
        adminUsersApi.endpoints.getTaskJob.initiate(jid, { subscribe: false, forceRefetch: true }),
      )
        .unwrap()
        .then((raw) => setMjManageJobDetail(raw as Record<string, unknown>))
        .catch(() => {});
    } catch (e) {
      setMjAddTasksErr(getErr(e, "Could not change the number of tasks."));
    }
  };

  /** One task out of the split, the soft way: its log archive stays put. */
  const handleMjRemoveOneTask = async () => {
    if (!canEditBots || mjRemoveTaskPrompt == null) return;
    const { jobId, taskId } = mjRemoveTaskPrompt;
    try {
      await reduceJobTasksMutation({ jobId, task_ids: [taskId] }).unwrap();
      setMjRemoveTaskPrompt(null);
      await mjRefreshJobAndTasks(jobId);
    } catch (e) {
      setMjRemoveTaskPrompt(null);
      setMjJobActionAlertsByJobId((prev) => ({
        ...prev,
        [jobId]: getErr(e, "Could not remove the task."),
      }));
    }
  };

  /** The placeholder row under the task table: one more task, roster re-split. */
  const handleMjAddOneTask = async (jobId: number) => {
    if (!canEditBots) return;
    setMjAddOneTaskJobId(jobId);
    try {
      await addTasksToJobMutation({ jobId, count: 1 }).unwrap();
      await mjRefreshJobAndTasks(jobId);
    } catch (e) {
      setMjJobActionAlertsByJobId((prev) => ({
        ...prev,
        [jobId]: getErr(e, "Could not add a task."),
      }));
    } finally {
      setMjAddOneTaskJobId(null);
    }
  };

  const closeMjManage = () => {
    setMjManageJobId(null);
    setMjManageJobDetail(null);
    setMjManageJobError(null);
    setMatchesMergeSuccess(null);
    setMatchesReviewNotice(null);
    setMatchesReviewPrompt(null);
    resetMatchesMergeState();
  };

  if (isDetailMode && detailMajorNotFound) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, p: 1 }}>
        <Alert severity="warning">This automation was not found (or is not visible for this user).</Alert>
        {onExitMajorDetail && (
          <Button variant="outlined" onClick={onExitMajorDetail}>
            Back to Major Jobs list
          </Button>
        )}
      </Box>
    );
  }

  if (!isDetailMode && majorsStillLoading && majors.length === 0) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        ...(isCardMode ? {} : { flex: 1, minHeight: 0 }),
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
        <Box>
          <Typography variant="h6" fontWeight={700}>
            {isDetailMode ? "Major job workspace" : "Major jobs"}
          </Typography>
          {!isDetailMode && (
            // Hidden on phones — the mobile user switcher above already names the user.
            <Box sx={{ mt: 0.75, display: { xs: "none", sm: "block" } }}>
              <MajorJobStripItem
                title="User"
                value={userLabel?.trim() ? userLabel.trim() : `ID ${userId}`}
                avatar
              />
            </Box>
          )}
        </Box>
        {/* Mobile: full-width stacked buttons (drawer-like); desktop: inline row. */}
        <Box
          sx={{
            display: "flex",
            gap: 1,
            flexWrap: "wrap",
            flexDirection: { xs: "column", sm: "row" },
            width: { xs: "100%", sm: "auto" },
            "& .MuiButton-root": { minHeight: { xs: 40, sm: 0 } },
          }}
        >
          {!isDetailMode && (
            <>
              <Button size="small" variant="contained" onClick={openCreateMajor} disabled={isCreating || !canEditBots}>
                New Major Job
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => onOpenConnectionsScreen?.()}
                disabled={!onOpenConnectionsScreen}
              >
                Connections
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => onOpenBotNotesScreen?.()}
                disabled={!onOpenBotNotesScreen}
              >
                Notes
              </Button>
              <Tooltip
                title={
                  selectedMajorsRunning.length
                    ? `Bots or split jobs are running in ${selectedMajorsRunning.join(", ")}. Stop them first.`
                    : ""
                }
              >
                <span>
                  <Button
                    size="small"
                    color="error"
                    variant="outlined"
                    onClick={() => {
                      setMajorBulkDeleteOpen(true);
                    }}
                    disabled={
                      !selectedMajorIds.length ||
                      isDeletingMajor ||
                      selectedMajorsRunning.length > 0 ||
                      !canEditBots
                    }
                  >
                    {isCardMode ? "Delete" : "Delete selected"}
                  </Button>
                </span>
              </Tooltip>
            </>
          )}
          {isDetailMode && onExitMajorDetail && (
            <Button size="small" variant="outlined" onClick={onExitMajorDetail}>
              Back to Major Jobs list
            </Button>
          )}
        </Box>
      </Box>

      {!isDetailMode && (
        <BotsRecordTable<MajorJob>
          rows={majors}
          rowKey={(m) => m.id}
          fill
          onRowClick={(m) => onOpenMajorDetail?.(m.id)}
          empty={
            <Typography color="text.secondary">
              No automations for this user yet.
            </Typography>
          }
          selection={{
            selectedIds: selectedMajorIds,
            onToggle: (id, checked) => toggleMajorCheckbox(Number(id), checked),
            onToggleAll: toggleSelectAllMajorsOnPage,
            allLabel: "Select all major jobs on this page",
            disabled: !majors.length,
          }}
          columns={mjMajorJobColumns}
        />
      )}

      <DeleteJobConfirmDialog
        open={majorRowDeleteTarget != null}
        title="Delete major job?"
        message={
          majorRowDeleteTarget ? (
            <>
              Delete &quot;{majorRowDeleteTarget.name}&quot; and everything in it?{" "}
              {majorDeletePreview.isFetching
                ? "Counting what goes with it..."
                : majorDeletePreview.currentData
                  ? isSplitKind(majorRowDeleteTarget.bot_kind)
                    ? splitMajorJobDeleteSummary(majorDeletePreview.currentData)
                    : majorJobDeleteSummary(majorDeletePreview.currentData)
                  : "What goes with it could not be counted just now; its task jobs, tasks and their S3 data are deleted all the same."}
            </>
          ) : null
        }
        onCancel={() => {
          setMajorRowDeleteTarget(null);
          setMajorDeleteError(null);
        }}
        onConfirm={() => {
          if (majorRowDeleteTarget) void confirmRemoveMajor(majorRowDeleteTarget);
        }}
        confirmDisabled={majorRowDeleteTarget == null || !canEditBots || majorDeletePreview.isFetching}
        confirmPhrase={majorRowDeleteTarget?.name}
        busy={isDeletingMajor}
        error={majorDeleteError}
      />

      <DeleteJobConfirmDialog
        open={majorBulkDeleteOpen}
        title="Delete selected major jobs?"
        message={
          <>
            Delete {selectedMajorIds.length} selected major job{selectedMajorIds.length === 1 ? "" : "s"} and
            everything in them? Each goes with all of its task jobs and tasks.
          </>
        }
        onCancel={() => {
          setMajorBulkDeleteOpen(false);
          setMajorDeleteError(null);
        }}
        onConfirm={() => void confirmBulkDeleteMajors()}
        confirmDisabled={!selectedMajorIds.length || !canEditBots}
        confirmPhrase={BULK_DELETE_PHRASE}
        busy={isDeletingMajor}
        error={majorDeleteError}
      />

      {isDetailMode && !activeMajor && !detailMajorNotFound && <MajorJobWorkspaceSkeleton />}

      {activeMajor && isDetailMode && (
        <Paper
          variant="outlined"
          sx={{
            p: 1.5,
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
            [VIEWPORT_SHORT_MAX_MEDIA]: { p: 1 },
            // When the header plus the tab content cannot both fit, the
            // workspace scrolls. Before this the last flex child was simply
            // crushed - at 1366x625 the task list was three pixels tall. On
            // touch the page itself scrolls, so no inner scroller here.
            ...(isShort && !isCardMode ? { overflowY: "auto" } : {}),
            ...(isCardMode ? { flex: "0 0 auto" } : {}),
          }}
        >
          <Box
            sx={{
              mb: 1.5,
              display: "flex",
              justifyContent: "space-between",
              gap: 1,
              alignItems: "flex-start",
              flexWrap: "wrap",
              flexShrink: 0,
              [VIEWPORT_SHORT_MAX_MEDIA]: { mb: 1 },
            }}
          >
            <Box sx={{ minWidth: 0, flex: "1 1 auto" }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  minWidth: 0,
                }}
              >
                <Typography
                  variant="subtitle1"
                  fontWeight={800}
                  sx={{ minWidth: 0, overflowWrap: "anywhere" }}
                >
                  {activeMajor.name}
                </Typography>
                {isCardMode && <LiveUpdatesIndicator connected={mjWsConnected} />}
                {foldWorkspaceMeta && (
                  <Button
                    size="small"
                    onClick={() => setWorkspaceMetaOpen((prev) => !prev)}
                    endIcon={
                      <ExpandMore
                        sx={{
                          transition: "transform 150ms",
                          transform: workspaceMetaOpen ? "rotate(180deg)" : "none",
                        }}
                      />
                    }
                    sx={{ flexShrink: 0, textTransform: "none" }}
                    aria-expanded={workspaceMetaOpen}
                  >
                    Details
                  </Button>
                )}
              </Box>
              {foldWorkspaceMeta ? (
                <>
                  {!workspaceMetaOpen && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mt: 0.25,
                        overflowWrap: "anywhere",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {mjWorkspaceSummaryLine(
                        activeMajor,
                        userLabel,
                        mjBrowserProfiles,
                      )}
                    </Typography>
                  )}
                  {workspaceMetaOpen && (
                    <WorkspaceMetaList
                      rows={mjWorkspaceMetaRows(
                        activeMajor,
                        userLabel,
                        mjBrowserProfiles,
                      )}
                    />
                  )}
                </>
              ) : (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "stretch", mt: 0.75 }}>
                <MetaPill
                  title="User"
                  tone="primary"
                  icon={<PersonOutline />}
                  value={userLabel?.trim() ? userLabel.trim() : "User"}
                />
                <MetaPill
                  title="Action"
                  tone={botKindMeta(activeMajor.bot_kind).tone}
                  icon={botKindMeta(activeMajor.bot_kind).icon}
                  value={botKindMeta(activeMajor.bot_kind).label}
                />
                <MetaPill
                  title="Vendor"
                  tone="secondary"
                  icon={<BusinessIcon />}
                  value={(activeMajor.vendor ?? "").trim() || "-"}
                />
                {activeMajor.jobs_running_at_once != null && (
                  <MetaPill
                    title="Jobs at once"
                    tone="info"
                    icon={<HourglassEmptyIcon />}
                    value={mjJobsAtOnceLabel(activeMajor.jobs_running_at_once)}
                  />
                )}
                {activeMajor.ssn_file_exists != null && (
                  <MetaPill
                    title="SSN file"
                    tone={activeMajor.ssn_file_exists ? "success" : "warning"}
                    icon={activeMajor.ssn_file_exists ? <VerifiedUserIcon /> : <WarningAmberIcon />}
                    value={activeMajor.ssn_file_exists ? "ready" : "not ready"}
                  />
                )}
                {mjBrowserProfiles && (
                  <MetaPill
                    title="Browser profile"
                    tone={mjBrowserProfiles.profiles.length ? "info" : "neutral"}
                    icon={<LanguageIcon />}
                    value={mjBrowserProfilesLabel(mjBrowserProfiles)}
                    action={
                      mjBrowserProfiles.profiles.length > 0 && canEditBots ? (
                        <Tooltip title="Delete the stored Chrome profile(s) of this vendor - every bot of this user on this vendor logs in from scratch next time (2FA / captcha).">
                          <IconButton
                            size="small"
                            color="warning"
                            aria-label="Delete browser profile"
                            onClick={() => setMjBrowserProfilesPrompt(true)}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : undefined
                    }
                  />
                )}
              </Box>
              )}
              {mjBrowserProfilesError && (
                <Alert severity="error" sx={{ mt: 1 }} onClose={() => setMjBrowserProfilesError(null)}>
                  {mjBrowserProfilesError}
                </Alert>
              )}
            </Box>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ width: { xs: "100%", sm: "auto" }, flexShrink: 0 }}
            >
              <Button
                size="small"
                variant="outlined"
                onClick={() => openEditMajor(activeMajor)}
                disabled={isSavingMajor || !canEditBots}
                sx={{ flex: { xs: 1, sm: "0 0 auto" }, minHeight: { xs: 40, sm: 0 }, whiteSpace: "nowrap" }}
              >
                {foldWorkspaceMeta ? "Edit" : "Edit Major Job"}
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="error"
                startIcon={<DeleteOutlineIcon fontSize="small" />}
                onClick={() => {
                  setMajorRowDeleteTarget(activeMajor);
                }}
                disabled={isDeletingMajor || activeMajorDeleteBlocked != null || !canEditBots}
                title={activeMajorDeleteBlocked ?? undefined}
                sx={{ flex: { xs: 1, sm: "0 0 auto" }, minHeight: { xs: 40, sm: 0 }, whiteSpace: "nowrap" }}
              >
                {foldWorkspaceMeta ? "Delete" : "Delete Major Job"}
              </Button>
            </Stack>
          </Box>
          {poolOrphans.length > 0 && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              {poolOrphans.length} saved pool id(s) do not match current connections for this vendor.
            </Alert>
          )}
          {(() => {
            const isDeleteKind = activeMajor.bot_kind === "delete_bots";
            const isSplitMajor = isSplitKind(activeMajor.bot_kind);
            return (
              <>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    borderBottom: 1,
                    borderColor: "divider",
                    flexShrink: 0,
                  }}
                >
                  <Tabs
                    value={shownTab}
                    onChange={handleManageTabChange}
                    // Three tabs fit a phone edge to edge; scroll arrows there
                    // only steal the width the labels need.
                    variant={isCardMode ? "fullWidth" : "scrollable"}
                    allowScrollButtonsMobile
                    sx={{
                      minHeight: 40,
                      flex: 1,
                      minWidth: 0,
                      [VIEWPORT_SHORT_MAX_MEDIA]: { minHeight: 36 },
                      // Three labels across a 390px phone: trim the tracking
                      // and the gutters rather than clip "Connections".
                      ...(isCardMode
                        ? {
                            "& .MuiTab-root": {
                              minWidth: 0,
                              px: 0.5,
                              fontSize: 11,
                              letterSpacing: 0,
                            },
                          }
                        : {}),
                    }}
                  >
                    <Tab label={isSplitMajor ? "Split jobs" : "Task jobs"} value="jobs" />
                    {/* Splitting logs into nothing and reads the vendor's SSN file
                        the client's Download major job collects: no SSN Job, no logins. */}
                    {!isDeleteKind && !isSplitMajor && <Tab label="SSN Job" value="ssn" />}
                    {!isSplitMajor && <Tab label="Connections" value="connections" />}
                  </Tabs>
                  {!isCardMode && <LiveUpdatesIndicator connected={mjWsConnected} />}
                </Box>
                {/* What this vendor's bots need from whoever runs them
                    (CAL-6162) - read once for the whole workspace. */}
                {mjVendorNote ? (
                  <Box sx={{ mt: 1 }}>
                    <BotNoteView note={mjVendorNote} label={activeMajor?.vendor} dense />
                  </Box>
                ) : null}
                {/* ── SSN Job TabPanel ─────────────────────────────────── */}
                {!isDeleteKind && !isSplitMajor && <TabPanel
            value={shownTab}
            index="ssn"
            minHeight={workspaceTabMinHeight}
            flow={isCardMode}
          >
            {ssnJobLoading && <LinearProgress sx={{ mb: 1 }} />}
            {ssnJobError && (
              <Alert severity="warning" sx={{ mb: 1 }}>
                {ssnJobError}
              </Alert>
            )}
            {!ssnJobLoading && !ssnJobError && ssnJobData !== null && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
                {/* SSN File status */}
                <Box sx={{ p: 1.5, border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
                  <Stack direction="row" alignItems="center" sx={{ mb: 0.5 }}>
                    <Typography variant="subtitle2" fontWeight={700}>SSN file</Typography>
                    <Box sx={{ flex: 1 }} />
                    {ssnFilePresent && (
                      <>
                        <Tooltip title="View">
                          <IconButton
                            size="small"
                            onClick={() => setSsnFileViewOpen(true)}
                          >
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Download">
                          <IconButton
                            size="small"
                            sx={{ mr: 0.5 }}
                            onClick={() => activeMajor && void downloadSsnFile(activeMajor.id)}
                          >
                            <FileDownloadIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                    <Button
                      size="small"
                      variant="outlined"
                      sx={{ mr: 0.5 }}
                      disabled={!canEditBots}
                      onClick={() => setSsnUploadOpen(true)}
                    >
                      Upload SSN file
                    </Button>
                    {ssnFilePresent && (
                      <Button
                        size="small"
                        color="error"
                        variant="outlined"
                        startIcon={ssnJobFileDeleting ? <CircularProgress size={14} /> : <DeleteOutlineIcon fontSize="small" />}
                        disabled={ssnJobFileDeleting || !canEditBots}
                        onClick={async () => {
                          if (!activeMajor || !canEditBots) return;
                          setSsnJobFileDeleting(true);
                          setSsnFileDeleteError(null);
                          try {
                            await deleteSsnFileMut(activeMajor.id).unwrap();
                            await loadSsnJob(activeMajor.id);
                          } catch (e) {
                            // Refused while the SSN job, SSN Updater or a job reading
                            // the file runs - the server says which.
                            setSsnFileDeleteError(getErr(e, "Could not delete the SSN file."));
                          } finally {
                            setSsnJobFileDeleting(false);
                          }
                        }}
                      >
                        Delete file
                      </Button>
                    )}
                  </Stack>
                  {ssnFileEmpty ? (
                    <Alert severity="warning" sx={{ py: 0.25 }}>
                      {EMPTY_SSN_FILE_MESSAGE}
                      {ssnFileStamp ? ` (${ssnFileStamp})` : ""}
                    </Alert>
                  ) : ssnFilePresent ? (
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <CheckCircleOutlineIcon fontSize="small" color="success" />
                      <Typography variant="body2" color="success.main">
                        ssn_info.json exists in S3
                        {ssnFileStamp ? ` - ${ssnFileStamp}` : ""}
                      </Typography>
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {ssnJobData.ssn_file_exists === null
                        ? "SSN file status unknown (S3 not configured)"
                        : "No SSN file yet - this major job's SSN job collects it"}
                    </Typography>
                  )}
                  {(ssnJobData.used_as_source_by ?? []).length > 0 && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                      The Source SSN file of {describeSourceReaders(ssnJobData.used_as_source_by)}.
                    </Typography>
                  )}
                  {ssnFileDeleteError && (
                    <Alert severity="error" sx={{ mt: 1, py: 0.25 }} onClose={() => setSsnFileDeleteError(null)}>
                      {ssnFileDeleteError}
                    </Alert>
                  )}
                </Box>

                {/* SSN Job */}
                {ssnJobData.job == null ? (() => {
                  // The SSN job collects the employees of the login it runs with.
                  // This major job's logins come first; another login of the client
                  // is linked to the major job when the SSN job is created with it.
                  const vendorConns = connections.filter((c) => c.vendor?.toLowerCase() === activeMajor?.vendor?.toLowerCase());
                  const effectiveSsnConnId =
                    ssnJobSelectedConnId != null && majorConnPickerIds.includes(ssnJobSelectedConnId)
                      ? ssnJobSelectedConnId
                      : (majorConnPickerIds[0] ?? null);
                  return (
                  <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1.5, py: 0.5 }}>
                    <Typography variant="subtitle2" fontWeight={700}>SSN Job</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {vendorConns.length === 0
                        ? `No SSN Job yet - and no bot logins exist for vendor "${activeMajor?.vendor ?? ""}" on this user, so one cannot be created.`
                        : "No SSN Job created yet."}
                    </Typography>
                    <Box sx={{ flex: 1 }} />
                    {vendorConns.length === 0 && (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Add fontSize="small" />}
                        onClick={openMajConnCreate}
                        disabled={!activeMajor || !canEditBots}
                      >
                        New connection
                      </Button>
                    )}
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<Add fontSize="small" />}
                      disabled={vendorConns.length === 0 || !canEditBots}
                      onClick={() => openSsnJobDialog("create", effectiveSsnConnId)}
                    >
                      Create
                    </Button>
                  </Box>
                  );
                })() : (() => {
                  const ssnTask = ssnJobData.task;
                  const ssnTaskId = ssnTask ? Number(ssnTask.id) : null;
                  const ssnIsRunning = mjTaskShowsStopInsteadOfRun(String(ssnTask?.task_status ?? ""));
                  const ssnCurrentCid = typeof ssnTask?.connection === "number" ? ssnTask.connection : null;
                  const ssnJobId = Number(ssnJobData.job?.id ?? 0);
                  const ssnAsMjTask: MjConnectionTask = {
                    id: ssnTaskId ?? 0,
                    job: ssnJobId,
                    task_status: String(ssnTask?.task_status ?? ""),
                    bot_work_status: String(ssnTask?.bot_work_status ?? ""),
                    task2fa_status: typeof ssnTask?.task2fa_status === "string" ? ssnTask.task2fa_status : "not_requested",
                    task2fa_auto_until: typeof ssnTask?.task2fa_auto_until === "string" ? ssnTask.task2fa_auto_until : null,
                    live_view_status: typeof ssnTask?.live_view_status === "string" ? ssnTask.live_view_status : "not_requested",
                    live_view_phase: typeof ssnTask?.live_view_phase === "string" ? ssnTask.live_view_phase : "",
                    bot_name: String(ssnTask?.bot_name ?? ""),
                    employee_number_from: Number(ssnTask?.employee_number_from ?? 0),
                    employee_number_to: Number(ssnTask?.employee_number_to ?? 0),
                    connection: ssnCurrentCid ?? undefined,
                  };
                  return (
                    <Accordion
                      expanded={ssnAccordionExpanded}
                      disableGutters
                      elevation={0}
                      TransitionProps={{ timeout: 0 }}
                      onChange={() => setSsnAccordionExpanded((v) => !v)}
                      sx={MJ_ACCORDION_ROW_SX}
                    >
                      <AccordionSummary
                        sx={{
                          ...MJ_ACC_SUMMARY_SX,
                          px: 2,
                          boxSizing: "border-box",
                          "& .MuiAccordionSummary-content": {
                            ...MJ_ACC_SUMMARY_SX["& .MuiAccordionSummary-content"],
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            width: "100%",
                            margin: "0 !important",
                          },
                        }}
                      >
                        <Typography fontWeight={700} noWrap>SSN Job</Typography>
                        <StatusChipMj
                          label={String(ssnJobData.job?.task_status ?? "PENDING")}
                          color={taskRunStatusChipColor(String(ssnJobData.job?.task_status ?? "PENDING"))}
                        />
                        {ssnFileEmpty && (
                          <SoftTag
                            label="SSN file empty"
                            tone="warning"
                            title={EMPTY_SSN_FILE_MESSAGE}
                          />
                        )}
                        <Box sx={{ flex: 1 }} />
                        <Stack direction="row" spacing={0.5} alignItems="center" onClick={(e) => e.stopPropagation()}>
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={!canEditBots}
                            onClick={(e) => { e.stopPropagation(); openSsnJobDialog("edit"); }}
                          >
                            Edit job
                          </Button>
                          <Tooltip title="Refresh">
                            <span>
                              <IconButton size="small" color="primary" onClick={async (e) => { e.stopPropagation(); if (activeMajor) await loadSsnJob(activeMajor.id); }}>
                                {ssnJobLoading ? <CircularProgress size={18} color="primary" /> : <ReplayIcon sx={{ fontSize: 20 }} />}
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Delete SSN job">
                            <span>
                              <IconButton size="small" color="error" disabled={ssnJobDeleting || ssnIsRunning || !canEditBots}
                                onClick={(e) => { e.stopPropagation(); setSsnJobDeleteConfirmOpen(true); }}>
                                {ssnJobDeleting ? <CircularProgress size={16} color="error" /> : <DeleteOutlineIcon fontSize="small" />}
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title={ssnAccordionExpanded ? "Collapse" : "Expand"}>
                            <IconButton size="small" color="primary" onClick={(e) => { e.stopPropagation(); setSsnAccordionExpanded((v) => !v); }}>
                              <ExpandMore sx={{ fontSize: 24, transition: "transform 0.2s", transform: ssnAccordionExpanded ? "rotate(180deg)" : "rotate(0deg)" }} />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </AccordionSummary>
                      <AccordionDetails sx={{ pt: 1, pb: 2, px: 2, borderTop: 1, borderColor: "divider" }}>
                        {ssnTask &&
                          (isCardMode ? (
                            renderMjTaskRow({
                              task: ssnAsMjTask,
                              jobId: ssnJobId,
                              index: 1,
                              variant: "card",
                            })
                          ) : (
                          <BotsTableScroll>
                          <Table size="small" sx={{ tableLayout: "fixed", width: "100%", minWidth: 660 + MJ_TASKS_PINNED_WIDTHS.taskId }}>
                            <colgroup>
                              <col style={{ width: "48px" }} />
                              <col style={{ width: "132px" }} />
                              <col style={{ width: `${MJ_TASKS_PINNED_WIDTHS.taskId}px` }} />
                              <col style={{ width: "132px" }} />
                              <col />
                              <col style={{ width: "150px" }} />
                            </colgroup>
                            <TableHead>
                              <TableRow>
                                <TableCell align="center" sx={{ fontWeight: 700 }}>No</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 700 }}>Status</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 700 }}>Task ID</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 700 }}>Bot</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 700 }}>Connection</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 700 }}>Actions</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {renderMjTaskRow({
                                task: ssnAsMjTask,
                                jobId: ssnJobId,
                                index: 1,
                              })}
                            </TableBody>
                          </Table>
                          </BotsTableScroll>
                          ))}
                      </AccordionDetails>
                    </Accordion>
                  );
                })()}
              </Box>
            )}
            {ssnJobError && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <Button variant="outlined" size="small" onClick={() => activeMajor && loadSsnJob(activeMajor.id)}>
                  Retry
                </Button>
              </Box>
            )}
          </TabPanel>}
                {!isSplitMajor && <TabPanel
            value={shownTab}
            index="connections"
            minHeight={workspaceTabMinHeight}
            flow={isCardMode}
          >
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Available connections
                  </Typography>
            {connectionsError && (
              <Alert severity="error" sx={{ mb: 1 }}>
                Failed to load connections.
              </Alert>
            )}
            <Box
              sx={{
                display: "flex",
                gap: 1,
                flexWrap: "wrap",
                mb: 1.5,
                "& .MuiButton-root": {
                  flex: { xs: 1, sm: "0 0 auto" },
                  minHeight: { xs: 38, sm: 0 },
                  whiteSpace: "nowrap",
                  px: { xs: 1, sm: 2 },
                },
              }}
            >
              <Button size="small" variant="contained" onClick={openMajConnCreate} disabled={!activeMajor || !canEditBots}>
                New connection
              </Button>
              <Button
                size="small"
                color="error"
                variant="outlined"
                onClick={() => setPoolConnBulkDeleteOpen(true)}
                disabled={!poolConnSelectedIds.length || poolConnBulkDeleting || !canEditBots}
              >
                {isCardMode ? "Delete" : "Delete selected"}
              </Button>
            </Box>
            {connectionsLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                <CircularProgress size={22} />
              </Box>
            ) : (
              <BotsRecordTable<number>
                  rows={poolConnIdsForMajor}
                  rowKey={(cid) => cid}
                  empty={
                    <Typography color="text.secondary">
                      No vendor-matching bot connections linked to this automation yet.
                    </Typography>
                  }
                  selection={{
                    selectedIds: poolConnSelectedIds,
                    onToggle: (cid, checked) =>
                      setPoolConnSelectedIds((prev) =>
                        checked
                          ? [...prev, Number(cid)]
                          : prev.filter((x) => x !== Number(cid)),
                      ),
                    onToggleAll: (checked) =>
                      setPoolConnSelectedIds((prev) => {
                        if (checked) {
                          return Array.from(new Set([...prev, ...poolConnIdsForMajor]));
                        }
                        const drop = new Set(poolConnIdsForMajor);
                        return prev.filter((id) => !drop.has(id));
                      }),
                    allLabel: "Select all linked connections",
                    disabled: !poolConnIdsForMajor.length,
                  }}
                  columns={mjPoolConnectionColumns}
                />
              )}
                </TabPanel>}
              </>
            );
          })()}
          {isSplitKind(activeMajor.bot_kind) ? (
            <TabPanel
              value={shownTab}
              index="jobs"
              minHeight={workspaceTabMinHeight}
              flow={isCardMode}
            >
              <SplitJobsTab
                userId={userId}
                userLabel={userLabel?.trim() ? userLabel.trim() : "User"}
                majorJob={{
                  id: activeMajor.id,
                  name: activeMajor.name,
                  vendor: activeMajor.vendor,
                }}
                canEdit={canEditBots}
                readOnlyReason={botsReadOnlyReason}
              />
            </TabPanel>
          ) : (
          <TabPanel
            value={shownTab}
            index="jobs"
            minHeight={workspaceTabMinHeight}
            flow={isCardMode}
          >
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1, gap: 1, flexWrap: "wrap" }}>
              <Typography variant="subtitle2" fontWeight={700}>
                Task jobs
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  ml: "auto",
                  width: { xs: "100%", sm: "auto" },
                  "& .MuiButton-root": {
                    flex: { xs: 1, sm: "0 0 auto" },
                    minHeight: { xs: 38, sm: 0 },
                    whiteSpace: "nowrap",
                    px: { xs: 1, sm: 2 },
                  },
                }}
              >
                <Button size="small" variant="contained" onClick={openCreateChildJob} disabled={!canEditBots}>
                  Add Task Job
                </Button>
                <Button
                  size="small"
                  color="warning"
                  variant="outlined"
                  onClick={openBulkDeleteChildren}
                  disabled={selectedChildJobIds.length === 0 || isDeletingJobsBulk || !canEditBots}
                >
                  {isCardMode ? "Delete" : "Delete selected"}
                </Button>
              </Box>
            </Box>

            <BotsRecordTable<Record<string, unknown>>
              rows={childJobs.filter(
                (raw) => !isSSNJob(raw as Record<string, unknown>),
              )}
              rowKey={(raw) => Number(raw.id)}
              loading={!childJobs.length && childJobsLoading}
              skeletonRows={4}
              empty={
                <Typography color="text.secondary">No child jobs yet.</Typography>
              }
              selection={{
                selectedIds: selectedChildJobIds,
                onToggle: (id, checked) => toggleChildCheckbox(Number(id), checked),
                onToggleAll: toggleSelectAllTaskJobRows,
                allLabel: "Select all task jobs in this list",
                disabled: !taskJobRowIdsOnPage.length,
              }}
              columns={mjTaskJobColumns}
              expandedIds={Object.entries(expandedMjJobAccordion)
                .filter(([, open]) => open)
                .map(([jobId]) => Number(jobId))}
              onToggleExpand={(raw) => {
                const id = Number(raw.id);
                handleMjAccordionExpanded(
                  {} as SyntheticEvent,
                  id,
                  !expandedMjJobAccordion[id],
                );
              }}
              expandAdornment={(raw, expanded) => (
                <IconButton
                  size="small"
                  aria-label={expanded ? "Collapse task job" : "Expand task job"}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleMjAccordionExpanded(
                      event as unknown as SyntheticEvent,
                      Number(raw.id),
                      !expanded,
                    );
                  }}
                >
                  <ExpandMore
                    sx={{
                      color: "primary.main",
                      fontSize: 24,
                      transition: "transform 150ms",
                      transform: expanded ? "rotate(180deg)" : "none",
                    }}
                  />
                </IconButton>
              )}
              renderExpanded={(raw) => renderMjJobExpanded(raw)}
            />
          </TabPanel>
          )}
        </Paper>
      )}

      {!activeMajor && !isDetailMode && (
        <Typography color="text.secondary">Select an automation row to manage connections and jobs.</Typography>
      )}

      <AppDialog
        open={majorEditOpen}
        onClose={() => setMajorEditOpen(false)}
        maxWidth="sm"
        title={<>{majorEditTarget ? "Edit Major Job" : "New Major Job"}</>}
        actions={
          <>
            <Button onClick={() => setMajorEditOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={() => void saveMajorForm()}
              disabled={
                isSavingMajor || isCreating || !canEditBots || majorFormSplitVendorError != null
              }
            >
              {isSavingMajor || isCreating ? "Saving..." : "Save"}
            </Button>
          </>
        }
      >
        {/* Critical fields (vendor + bot kind) are locked once any task of this major job
            has downloaded any employees - changing them would cause data drift. A split
            major job keeps them while it has split jobs: their results were saved under
            its vendor. */}
        {majorEditTarget && majorHasRunningOrProgress ? (
          <Alert severity="warning" sx={{ mt: 1 }}>
            This major job has running tasks or downloaded employees. Vendor and bot scope cannot be
            changed; stop all tasks and clear progress to unlock them.
          </Alert>
        ) : majorEditTarget && majorFormHasSplitJobs ? (
          <Alert severity="info" sx={{ mt: 1 }}>
            This major job has split jobs, so its vendor and bot scope stay as they are. Delete
            its split jobs to change them.
          </Alert>
        ) : null}
        <TextField
          sx={{ mt: 1 }}
          fullWidth
          label="Display name"
          required
          value={majName}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setMajName(e.target.value)}
        />
        <TextField
          sx={{ mt: 1 }}
          fullWidth
          select
          label="Bot scope"
          value={majKind}
          disabled={majorFormCriticalLocked}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setMajKind(e.target.value)}
          helperText={BOT_KIND_OPTIONS.find((option) => option.value === majKind)?.hint}
        >
          {BOT_KIND_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
        <Autocomplete
          ListboxComponent={AutocompleteScrollListbox}
          componentsProps={autocompleteScrollPaperProps}
          freeSolo
          sx={{ mt: 1 }}
          options={majorFormVendorOptions}
          inputValue={majVendor}
          disabled={majorFormCriticalLocked}
          onInputChange={(_e, v) => setMajVendor(v)}
          ListboxProps={MAJOR_FORM_LISTBOX}
          filterOptions={filterMajorFormVendors}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Vendor"
              required
              error={majorFormSplitVendorError != null}
              helperText={
                majorFormSplitVendorError ??
                (majFormIsSplit
                  ? "The vendor whose PDFs this major job splits; the results are saved to its data folder."
                  : "Task jobs use connections with this vendor.")
              }
            />
          )}
        />
        {majFormIsSplit ? null : (
          <TextField
            sx={{ mt: 1 }}
            fullWidth
            type="number"
            label="Jobs running at once"
            value={majJobsAtOnce}
            disabled={majorEditTargetInFlight}
            inputProps={{ min: 1, step: 1 }}
            error={Boolean(majJobsAtOnceError)}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              setMajJobsAtOnceTouched(true);
              setMajJobsAtOnce(e.target.value);
              setMajJobsAtOnceBadInput(Boolean(e.target.validity?.badInput));
              setMajJobsAtOnceError(null);
            }}
            helperText={
              majJobsAtOnceError
                ? majJobsAtOnceError
                : majorEditTargetInFlight
                  ? "This major job has a queued or running task. Stop it before changing the limit."
                  : majorVendorDefaultJobsAtOnce != null && !majorEditTarget
                    ? `How many task jobs of this major job may run at the same time. ${majVendor.trim()} is set up to run ${majorVendorDefaultJobsAtOnce} at a time; leave empty for no limit.`
                    : "How many task jobs of this major job may run at the same time. Empty means no limit. Creating jobs is never limited, only running them."
            }
          />
        )}
        {majorFormError && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {majorFormError}
          </Alert>
        )}
      </AppDialog>

      <AppDialog
        open={majConnCreateOpen}
        onClose={() => setMajConnCreateOpen(false)}
        maxWidth="sm"
        title={<>New connection ({activeMajor?.vendor ?? "…"})</>}
        actions={
          <>
            <Button onClick={() => setMajConnCreateOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={() => void submitMajConnCreate()} disabled={isMpcCreating || !canEditBots}>
              Create
            </Button>
          </>
        }
      >
        <TextField fullWidth label="Vendor" value={activeMajor?.vendor ?? ""} disabled sx={{ mt: 1, mb: 1.5 }} />
        <TextField
          required
          fullWidth
          label="Username"
          value={mcUsername}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setMcUsername(e.target.value)}
          sx={{ mb: 1.5 }}
        />
        <TextField
          fullWidth
          label="Host"
          value={mcHost}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setMcHost(e.target.value)}
          sx={{ mb: 1.5 }}
        />
        <TextField
          fullWidth
          label="Client code"
          value={mcClientCode}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setMcClientCode(e.target.value)}
          sx={{ mb: 1.5 }}
        />
        <TextField
          fullWidth
          label="Password"
          type={mcShowPw ? "text" : "password"}
          value={mcPassword}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setMcPassword(e.target.value)}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton edge="end" onClick={() => setMcShowPw((v) => !v)} aria-label="toggle password">
                  {mcShowPw ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        {mcError && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {mcError}
          </Alert>
        )}
      </AppDialog>

      <AppDialog
        open={majConnEdit != null}
        onClose={() => setMajConnEdit(null)}
        maxWidth="sm"
        title="Edit connection"
        actions={
          <>
            <Button onClick={() => setMajConnEdit(null)}>Cancel</Button>
            <Button variant="contained" onClick={() => void submitMajConnEdit()} disabled={isMpcUpdating || !canEditBots}>
              Save
            </Button>
          </>
        }
      >
        <TextField fullWidth label="Vendor" value={majConnEdit?.vendor ?? ""} disabled sx={{ mt: 1, mb: 1.5 }} />
        <TextField
          required
          fullWidth
          label="Username"
          value={meUsername}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setMeUsername(e.target.value)}
          sx={{ mb: 1.5 }}
        />
        <TextField
          fullWidth
          label="Host"
          value={meHost}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setMeHost(e.target.value)}
          sx={{ mb: 1.5 }}
        />
        <TextField
          fullWidth
          label="Client code"
          value={meClientCode}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setMeClientCode(e.target.value)}
          sx={{ mb: 1.5 }}
        />
        <TextField
          fullWidth
          label="Password"
          type={meShowPw ? "text" : "password"}
          value={mePassword}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setMePassword(e.target.value)}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton edge="end" onClick={() => setMeShowPw((v) => !v)} aria-label="toggle password">
                  {meShowPw ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        {meError && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {meError}
          </Alert>
        )}
      </AppDialog>

      <AppDialog
        open={deleteConnConfirm != null}
        onClose={() => setDeleteConnConfirm(null)}
        maxWidth="xs"
        title="Delete connection"
        actions={
          <>
            <Button onClick={() => setDeleteConnConfirm(null)}>Cancel</Button>
            <Button
              color="error"
              variant="contained"
              disabled={isMpcDeleting || !canEditBots}
              onClick={() => deleteConnConfirm != null && void deleteConnectionHard(deleteConnConfirm)}
            >
              Delete
            </Button>
          </>
        }
      >
        <Typography>
          Remove this connection from the user? This cannot be undone.
        </Typography>
      </AppDialog>

      <AppDialog
        open={poolConnBulkDeleteOpen}
        onClose={() => !poolConnBulkDeleting && setPoolConnBulkDeleteOpen(false)}
        maxWidth="xs"
        title="Delete selected connections?"
        actions={
          <>
            <Button
              onClick={() => setPoolConnBulkDeleteOpen(false)}
              disabled={poolConnBulkDeleting}
            >
              Cancel
            </Button>
            <Button
              color="error"
              variant="contained"
              disabled={!poolConnSelectedIds.length || poolConnBulkDeleting || !canEditBots}
              onClick={async () => {
                if (!poolConnSelectedIds.length || !canEditBots) return;
                setPoolConnBulkDeleting(true);
                try {
                  for (const cid of poolConnSelectedIds) {
                    await deleteConnectionMutation(cid).unwrap();
                  }
                  setPoolConnSelectedIds([]);
                  setPoolConnBulkDeleteOpen(false);
                  await onRefreshConnections();
                  await refetch();
                } catch {
                  /* keep dialog open so the user can retry; backend message
                     surfaces via the connection list refresh */
                } finally {
                  setPoolConnBulkDeleting(false);
                }
              }}
            >
              Delete
            </Button>
          </>
        }
      >
        <Typography variant="body2">
          Delete {poolConnSelectedIds.length} selected connection
          {poolConnSelectedIds.length === 1 ? "" : "s"}? This cannot be undone.
        </Typography>
      </AppDialog>

      <AppDialog
        open={childDialogOpen}
        onClose={() => setChildDialogOpen(false)}
        maxWidth="md"
        title="New task job"
        actions={
          <>
            <Button onClick={() => setChildDialogOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={() => void createChildSubmit()} disabled={!canEditBots}>
              Create
            </Button>
          </>
        }
      >
        <TextField
          sx={{ mt: 1, mb: 1 }}
          label="Task Job Name"
          required
          fullWidth
          value={taskJobListName}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setTaskJobListName(e.target.value)}
        />
        <Autocomplete
          ListboxComponent={AutocompleteScrollListbox}
          componentsProps={autocompleteScrollPaperProps}
          sx={{ mb: 1 }}
          options={normalizedChildBots}
          value={
            normalizedChildBots.find((b) => botNameFromRegistry(b) === childBotName) ?? null
          }
          onChange={(_e, v) => {
            const picked = v ? botNameFromRegistry(v) : "";
            setChildBotName(picked);
            // Each bot carries its own ceiling; re-picking re-proposes it rather
            // than leaving the previous bot's number behind.
            const suggested = picked
              ? recommendedParallelismFor(normalizedChildBots, picked)
              : null;
            setChildMaxParallel(suggested == null ? "" : String(suggested));
            // The same for the options: the previous bot's (SCOPE=all of the
            // background checks bot on an M3 job) would be saved with the job
            // under "options this bot does not declare".
            setChildAdditionalOptions((current) =>
              keepOptions(current, [
                ...botOptionSpecs(picked).map((o) => o.key),
                ...DOCS_TYPE_OMIT,
                OPTION_KEYS.skipErrorEmployees,
              ]),
            );
          }}
          filterOptions={filterChildDialogBots}
          getOptionLabel={(o) => o.bot_slug}
          renderOption={(props, option) => (
            <li {...props} key={`${option.vendor}.${option.job_kind}.${option.bot_slug}`}>
              {option.bot_slug}
            </li>
          )}
          renderInput={(params) => (
            <TextField {...params} label="Bot" required error={!childBotName && Boolean(childError)} />
          )}
        />
        {/* What a DPM needs before running this (CAL-6162): the vendor's note
            and the picked bot's, read where the choice is made. */}
        {mjVendorNote ? (
          <Box sx={{ mb: 1 }}>
            <BotNoteView note={mjVendorNote} label={activeMajor?.vendor} dense />
          </Box>
        ) : null}
        {mjPickedBotNote ? (
          <Box sx={{ mb: 1 }}>
            <BotNoteView note={mjPickedBotNote} label={childBotName} dense />
          </Box>
        ) : null}
        <FormControlLabel
          sx={{ mb: 1 }}
          control={
            <Switch
              checked={childProcessErrors}
              onChange={(e) => setChildProcessErrors(e.target.checked)}
            />
          }
          label="Auto process error employees (one retry pass after the job finishes)"
        />
        <FormControlLabel
          sx={{ mb: 1 }}
          control={
            <Switch
              checked={childSeparateProfiles}
              onChange={(e) => setChildSeparateProfiles(e.target.checked)}
            />
          }
          label="Separate browser profile per task (on: each task runs on its own profile, numbered by its task id, so no two tasks share a portal session; off: every task reuses the prepared profile 1)"
        />
        <TextField
          label="Max tasks running at once"
          size="small"
          fullWidth
          inputProps={{ inputMode: "numeric" }}
          value={childMaxParallel}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            setChildMaxParallel(connPlanTasksDigitsOnly(e.target.value))
          }
          sx={{ mb: 1 }}
          helperText={parallelismHelpText(
            recommendedParallelismFor(normalizedChildBots, childBotName),
          )}
        />
        {activeMajor?.bot_kind === "upload_bots" &&
          documentTypeUseFor(normalizedChildBots, childBotName) !== "unused" && (
            <TextField
              label={
                documentTypeUseFor(normalizedChildBots, childBotName) === "required"
                  ? "Document type *"
                  : "Document type"
              }
              size="small"
              fullWidth
              value={childDocumentType}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setChildDocumentType(e.target.value)}
              sx={{ mb: 1 }}
              placeholder="e.g. w2, paystubs, 1095"
              helperText={
                documentTypeUseFor(normalizedChildBots, childBotName) === "optional"
                  ? "Optional for this bot; leave it empty when there is no type to pick."
                  : undefined
              }
            />
          )}
        <BotOptionFields
          key={childBotName}
          options={botOptionSpecs(childBotName)}
          value={childAdditionalOptions}
          onChange={setChildAdditionalOptions}
          omitKeys={DOCS_TYPE_OMIT}
        />
        {activeMajor?.bot_kind === "delete_bots" && (
          <Box sx={{ mb: 1 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
              Document types to delete *
            </Typography>
            {childDocumentTypes.map((dt, i) => (
              <Stack key={i} direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.75 }}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="e.g. Paylocity Historical W2s"
                  value={dt}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setChildDocumentTypes((prev) => prev.map((v, j) => j === i ? e.target.value : v))
                  }
                />
                <IconButton size="small" color="error" disabled={childDocumentTypes.length === 1}
                  onClick={() => setChildDocumentTypes((prev) => prev.filter((_, j) => j !== i))}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Stack>
            ))}
            <Button size="small" startIcon={<Add fontSize="small" />}
              onClick={() => setChildDocumentTypes((prev) => [...prev, ""])}>
              Add type
            </Button>
          </Box>
        )}
        {activeMajor?.bot_kind === "upload_bots" && (
          <Box sx={{ mb: 1, mt: 0.5 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
              Upload options
            </Typography>
            <Stack direction="column" spacing={0.5}>
              <FormControlLabel
                control={
                  <Switch
                    checked={childUploadAsZip}
                    onChange={(e) => setChildUploadAsZip(e.target.checked)}
                    size="small"
                  />
                }
                label="Upload as ZIP"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={childUnzipBefore}
                    onChange={(e) => setChildUnzipBefore(e.target.checked)}
                    size="small"
                  />
                }
                label="Unzip before uploading"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={childUseSsnMatch}
                    onChange={(e) => setChildUseSsnMatch(e.target.checked)}
                    size="small"
                  />
                }
                label="Use SSN match"
              />
            </Stack>
            {childSsnReadiness && (
              <Typography
                variant="caption"
                display="block"
                sx={{ mt: 0.5 }}
                color={childSsnReadiness.ready ? "success.main" : "warning.main"}
              >
                {mjSsnReadinessText(childSsnReadiness)}
                {!childSsnReadiness.ready && childUseSsnMatch
                  ? " Run the SSN job on this major job's SSN Job tab, and on the major job chosen as the Source SSN file, before the first run."
                  : ""}
              </Typography>
            )}
          </Box>
        )}
        {activeMajor?.bot_kind === "upload_bots" && (
          <Box sx={{ mb: 1.5, mt: 0.5 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
              Source folder (files to upload)
            </Typography>
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <TextField
                fullWidth
                size="small"
                placeholder="Select a folder from Data Storage…"
                value={childSourcePath}
                InputProps={{ readOnly: true }}
                error={!childSourcePath && Boolean(childError)}
                sx={{ "& .MuiInputBase-input": { color: childSourcePath ? "text.primary" : "text.disabled" } }}
              />
              <Button
                variant="outlined"
                size="small"
                startIcon={<FolderOpenIcon />}
                onClick={() => openFolderPicker("create", childSourcePath)}
                sx={{ whiteSpace: "nowrap", minWidth: 90 }}
              >
                Browse
              </Button>
            </Stack>
          </Box>
        )}
        {activeMajor?.bot_kind === "upload_bots" && (
          <SourceSsnFileField
            readiness={childSsnReadiness}
            value={childSourceSsnValue}
            onChange={setChildSourceSsn}
            useSsnMatch={childUseSsnMatch}
            hasFolder={Boolean(childSourcePath.trim())}
          />
        )}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
          <Typography variant="subtitle2" fontWeight={700}>
            Connections (tasks per connection)
          </Typography>
          <Button
            size="small"
            variant="text"
            startIcon={<Add fontSize="small" />}
            onClick={openMajConnCreate}
            disabled={!activeMajor || !canEditBots}
            sx={{ ml: "auto" }}
          >
            New connection
          </Button>
          <Tooltip title="Add connection row">
            <span>
              <IconButton
                size="small"
                color="primary"
                aria-label="add connection row"
                onClick={() => {
                  const n = nextUnusedChildPlanConnId;
                  if (n != null) setChildPlans([...childPlans, { connection_id: n, task_target_count: "1" }]);
                }}
                disabled={nextUnusedChildPlanConnId == null}
                sx={{
                  bgcolor: "primary.main",
                  color: "primary.contrastText",
                  "&:hover": { bgcolor: "primary.dark" },
                  "&.Mui-disabled": { bgcolor: "action.disabledBackground", color: "action.disabled" },
                }}
              >
                <Add fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        {childPlans.map((row, i) => (
          <Box key={`${String(i)}-${String(row.connection_id)}`} sx={{ display: "flex", gap: 1, mb: 1, alignItems: "flex-start" }}>
            <TextField
              select
              label="Connection"
              value={String(row.connection_id)}
              sx={{ flex: 1 }}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                const next = childPlans.slice();
                next[i] = { ...row, connection_id: Number(e.target.value) };
                setChildPlans(next);
              }}
            >
              {connectionIdsForPlanRow(majorConnPickerIds, childPlans, i).map((cid) => {
                const ordinal = (majorConnPickerIds.indexOf(cid) ?? -1) + 1;
                return (
                  <MenuItem key={cid} value={String(cid)}>
                    {`${ordinal}. ${connectionPoolRowLabel(connections, cid) || "-"}`}
                  </MenuItem>
                );
              })}
            </TextField>
            <TextField
              label="Tasks"
              inputProps={{ inputMode: "numeric" }}
              value={row.task_target_count}
              sx={{ width: 112 }}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                const next = childPlans.slice();
                next[i] = {
                  ...row,
                  task_target_count: connPlanTasksDigitsOnly(e.target.value),
                };
                setChildPlans(next);
              }}
            />
            <Tooltip title="Remove row">
              <span>
                <IconButton
                  size="small"
                  aria-label="remove connection row"
                  onClick={() => {
                    if (childPlans.length <= 1) return;
                    setChildPlans(childPlans.filter((_r, ri) => ri !== i));
                  }}
                  disabled={childPlans.length <= 1}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        ))}
        {childError && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {childError}
          </Alert>
        )}
      </AppDialog>

      <AppDialog
        open={mjManageJobId != null}
        onClose={closeMjManage}
        maxWidth="sm"
        title={
          <>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1}>
            <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" fontWeight={700} noWrap> {mjEffectiveManagedJob ? mjJobDisplayName(mjEffectiveManagedJob) : "Manage job"} </Typography> {mjEffectiveManagedJob ? ( <Typography variant="caption" color="text.secondary"> Task job{mjEffectiveManagedJobOrdinal != null ? ` #${mjEffectiveManagedJobOrdinal}` : ""} {" · "} {String(mjEffectiveManagedJob.bot_name ?? "-")} </Typography> ) : null} </Box> {mjEffectiveManagedJob ? ( <StatusChipMj label={String(mjEffectiveManagedJob.task_status ?? "")} color={taskRunStatusChipColor(String(mjEffectiveManagedJob.task_status ?? ""))} /> ) : null} </Stack>
          </>
        }
        actions={
          <>
            <Button onClick={closeMjManage}>Close</Button>
          </>
        }
      >
        {/* The bot's own note, where the job is managed (CAL-6162). */}
        {mjEffectiveManagedJob &&
        mjBotNoteFor(String(mjEffectiveManagedJob.bot_name ?? "")) ? (
          <Box sx={{ mt: 1 }}>
            <BotNoteView
              note={mjBotNoteFor(String(mjEffectiveManagedJob.bot_name ?? ""))}
              label={String(mjEffectiveManagedJob.bot_name ?? "")}
              dense
            />
          </Box>
        ) : null}
        {activeMajor?.bot_kind === "upload_bots" && (
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs
              value={mjManageTab}
              onChange={(_e: React.SyntheticEvent, v: number) => setMjManageTab(v)}
            >
              <Tab label="Overview" />
              <Tab label="Matches" />
            </Tabs>
          </Box>
        )}
        {(activeMajor?.bot_kind !== "upload_bots" || mjManageTab === 0) && mjManageJobError && (
          <Alert severity="error" sx={{ mb: 1.5, mt: 1 }}>
            {mjManageJobError}
          </Alert>
        )}
        {(activeMajor?.bot_kind !== "upload_bots" || mjManageTab === 0) && mjManageJobNotice && (
          <Alert severity="info" sx={{ mt: 1 }} onClose={() => setMjManageJobNotice(null)}>
            {mjManageJobNotice}
          </Alert>
        )}
        {mjRosterNotice && (
          <Alert severity="success" sx={{ mt: 1 }} onClose={() => setMjRosterNotice(null)}>
            {mjRosterNotice}
          </Alert>
        )}
        {(activeMajor?.bot_kind !== "upload_bots" || mjManageTab === 0) && mjEffectiveManagedJob && (
          <Stack spacing={1.25} sx={{ mt: 1 }}>
            <Box sx={MJ_PROGRESS_CARD_SX}>
              <EmployeeProgressBar
                {...mjJobProgressProps(mjEffectiveManagedJob)}
                pendingLabel="ranges not set yet"
                tooltip={mjJobCountersTooltip(mjEmployeeStatsFromJob(mjEffectiveManagedJob))}
                leading={
                  <>
                    <SoftTag
                      icon={<StraightenIcon />}
                      label={mjRangeChipLabel(mjEffectiveManagedJob)}
                      title="Employee range of the job"
                    />
                    {mjBotOptionTags(
                      mjEffectiveManagedJob.additional_options,
                      botOptionSpecs(String(mjEffectiveManagedJob.bot_name ?? "")),
                    )}
                    {mjUploadFlagTags(mjEffectiveManagedJob)}
                    {mjManageJobId != null &&
                    mjIsUploadJob(mjEffectiveManagedJob) &&
                    mjSsnReadinessByJobId[mjManageJobId] ? (
                      <SoftTag
                        tone={mjSsnReadinessByJobId[mjManageJobId].ready ? "success" : "warning"}
                        icon={
                          mjSsnReadinessByJobId[mjManageJobId].ready ? (
                            <VerifiedUserIcon />
                          ) : (
                            <WarningAmberIcon />
                          )
                        }
                        label={mjSsnReadinessByJobId[mjManageJobId].ready ? "SSN ready" : "SSN: names only"}
                        title={mjSsnReadinessText(mjSsnReadinessByJobId[mjManageJobId])}
                      />
                    ) : null}
                    <UnmatchedReviewTags
                      review={
                        mjIsUploadJob(mjEffectiveManagedJob) ? jobUnmatchedReview(mjEffectiveManagedJob) : null
                      }
                      onOpen={() => setMjManageTab(1)}
                    />
                    {(() => {
                      const employeeStats = mjEmployeeStatsFromJob(mjEffectiveManagedJob);
                      const stats = mjJobStatsTotals(mjEffectiveManagedJob);
                      return (
                        <>
                          <SoftTag icon={<GroupsIcon />} label={`Total ${employeeStats.total}`} title="Employees in the roster" />
                          <SoftTag
                            tone={Number(employeeStats.success) > 0 ? "success" : "neutral"}
                            icon={<CheckCircleIcon />}
                            label={`Success ${employeeStats.success}`}
                            title={mjStatsSnapshotTooltip(employeeStats.snapshotAt, employeeStats.source)}
                          />
                          <SoftTag
                            tone={Number(employeeStats.error) > 0 ? "error" : "neutral"}
                            icon={<ErrorCircleIcon />}
                            label={`Errors ${employeeStats.error}`}
                            title={mjStatsSnapshotTooltip(employeeStats.snapshotAt, employeeStats.source)}
                          />
                          <SoftTag
                            icon={<LayersIcon />}
                            label={`Tasks ${stats.running}/${stats.total}`}
                            title="Running (incl. queued) / total tasks"
                          />
                          {String(mjEffectiveManagedJob.error_pass_state ?? "") === "RUNNING" && (
                            <SoftTag tone="warning" icon={<ReplayTagIcon />} label="Error pass running" />
                          )}
                          {mjScheduledRunTag(mjEffectiveManagedJob)}
                        </>
                      );
                    })()}
                  </>
                }
              />
            </Box>
            {mjEffectiveManagedJob.auto_recalculate_ranges !== false &&
            mjEffectiveManagedJob.actual_total_employees == null &&
            !mjRosterEmpty(mjEffectiveManagedJob) ? (
              <Alert severity="info">
                Ranges will be set for all tasks after the first task finishes
                collecting the roster.
              </Alert>
            ) : null}
            {mjRosterEmptyWarning(mjEffectiveManagedJob) ? (
              <Alert severity="warning">{mjRosterEmptyWarning(mjEffectiveManagedJob)}</Alert>
            ) : null}
            {mjManageJobId != null ? (
              <RosterStateAlert
                roster={mjRosterState(mjEffectiveManagedJob)}
                canEdit={canEditBots}
                recovering={mjRosterRecoveringJobId === mjManageJobId}
                onRecover={() => mjManageJobId != null && void mjRecoverRoster(mjManageJobId)}
                readOnlyReason={botsReadOnlyReason}
              />
            ) : null}
            {mjManageJobId != null &&
            mjShowMatchesPreflight(mjEffectiveManagedJob, mjRosterInfo?.info ?? null) ? (
              <MatchesPreflightAlert
                preflight={mjMatchesPreflight(mjEffectiveManagedJob)}
                canEdit={canEditBots}
                rechecking={!!mjRecheckingJobIds[mjManageJobId]}
                onRecheck={() => mjManageJobId != null && void mjRecheckMatches(mjManageJobId)}
                onRunUpdater={() => setMjSsnUpdaterJobId(mjManageJobId)}
                readOnlyReason={botsReadOnlyReason}
              />
            ) : null}
            {(() => {
              const lines = summarizeBotOptions(
                botOptionSpecs(String(mjEffectiveManagedJob.bot_name ?? "")),
                String(mjEffectiveManagedJob.additional_options ?? ""),
                MJ_OPTION_SUMMARY_OMIT,
              );
              return lines.length ? (
                <Box sx={MJ_PROGRESS_CARD_SX}>
                  <Tooltip title="What Edit job holds for this bot; unset options run at their default.">
                    <Typography
                      variant="overline"
                      sx={{
                        display: "inline-block",
                        lineHeight: 1.2,
                        letterSpacing: 0.06,
                        fontWeight: 700,
                        fontSize: 10,
                        color: "text.secondary",
                        mb: 0.75,
                        cursor: "help",
                      }}
                    >
                      Bot options
                    </Typography>
                  </Tooltip>
                  <BotOptionSummary lines={lines} dense />
                </Box>
              ) : null;
            })()}
            <Divider sx={{ my: 0.5 }} />
            {mjJobAggregateRunning(mjEffectiveManagedJob) ? (
              <Button
                fullWidth
                variant="contained"
                color="error"
                startIcon={
                  isMjBatchStopping ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <StopIcon fontSize="small" />
                  )
                }
                onClick={() => void mjStopJob()}
                disabled={mjJobActionBusy || isMjBatchStopping || !canEditBots}
              >
                {isMjBatchStopping ? "Stopping..." : "Stop all"}
              </Button>
            ) : (
              <Tooltip
                title={
                  mjRosterTaskInFlight(mjEffectiveManagedJob)
                    ? rosterBuildReason(mjIsUploadJob(mjEffectiveManagedJob))
                    : ""
                }
              >
                <span style={{ display: "block" }}>
                  <Button
                    fullWidth
                    variant="contained"
                    color="success"
                    startIcon={
                      isMjBatchRunning ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        <PlayArrowIcon fontSize="small" />
                      )
                    }
                    onClick={() =>
                      void mjSsnGuardedRun(mjEffectiveManagedJob, "Run all", () => mjRunJob())
                    }
                    disabled={
                      mjJobActionBusy ||
                      isMjBatchRunning ||
                      !canEditBots ||
                      mjRosterTaskInFlight(mjEffectiveManagedJob)
                    }
                  >
                    {isMjBatchRunning ? "Running..." : "Run all"}
                  </Button>
                </span>
              </Tooltip>
            )}
            {mjManageJobId != null &&
              (() => {
                const booking = (mjEffectiveManagedJob.scheduled_run ??
                  null) as ScheduledRunInfo | null;
                return (
                  <Stack direction="row" spacing={0.75}>
                    <Button
                      fullWidth
                      size="small"
                      variant="outlined"
                      startIcon={<ScheduleIcon fontSize="small" />}
                      disabled={mjJobActionBusy || !canEditBots}
                      onClick={() =>
                        void mjSsnGuardedRun(mjEffectiveManagedJob, "Schedule", () =>
                          setMjScheduleTarget({
                            kind: "job",
                            jobId: mjManageJobId,
                            existing: booking,
                          }),
                        )
                      }
                    >
                      {booking ? "Reschedule Run all" : "Schedule Run all"}
                    </Button>
                    {booking && (
                      <Tooltip title="Cancel the scheduled run">
                        <span>
                          <IconButton
                            size="small"
                            color="warning"
                            disabled={mjJobActionBusy || !canEditBots}
                            onClick={() =>
                              setMjUnscheduleTarget({
                                kind: "job",
                                jobId: mjManageJobId,
                                run: booking,
                              })
                            }
                            aria-label="Cancel the scheduled run"
                          >
                            <UnscheduleIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </Stack>
                );
              })()}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 1.25,
              }}
            >
              <Box sx={MJ_MANAGE_GROUP_SX}>
                <Typography variant="caption" sx={MJ_MANAGE_GROUP_LABEL_SX}>
                  Job
                </Typography>
                <Stack spacing={0.75}>
                  <Tooltip title="How many tasks this job is split into - add tasks or take some away">
                    <span style={{ display: "block" }}>
                      <Button
                        fullWidth
                        size="small"
                        variant="outlined"
                        startIcon={
                          mjAddTasksLoading ? (
                            <CircularProgress size={14} />
                          ) : (
                            <LayersIcon fontSize="small" />
                          )
                        }
                        onClick={() => void openMjAddTasksDialog()}
                        disabled={mjJobActionBusy || mjAddTasksLoading || !canEditBots}
                      >
                        {mjAddTasksLoading
                          ? "Loading…"
                          : `Tasks (${mjJobStatsTotals(mjEffectiveManagedJob).total})`}
                      </Button>
                    </span>
                  </Tooltip>
                  {mjParallelismSummary(mjEffectiveManagedJob) && (
                    <Typography variant="caption" color="text.secondary" sx={{ px: 0.5 }}>
                      {mjParallelismSummary(mjEffectiveManagedJob)}
                    </Typography>
                  )}
                  <Button
                    fullWidth
                    size="small"
                    variant="outlined"
                    onClick={openMjEditJobDialog}
                    disabled={!canEditBots}
                  >
                    Edit job
                  </Button>
                  <Tooltip
                    title={
                      mjJobAnythingInFlight(mjEffectiveManagedJob)
                        ? ROSTER_RUN_BLOCKED_REASON
                        : mjRosterAlreadyBuilt(mjEffectiveManagedJob, mjRosterInfo?.info ?? null)
                          ? mjRosterBuiltTooltip(mjEffectiveManagedJob, mjRosterInfo?.info ?? null)
                          : `Logs in, builds the roster (or the matches files), sets the ranges and processes nobody - so No matches can be resolved before the real run.${mjRosterStoredEmptyNote(mjRosterInfo?.info ?? null)}`
                    }
                  >
                    <span style={{ display: "block" }}>
                      <Button
                        fullWidth
                        size="small"
                        variant="outlined"
                        disabled={
                          !canEditBots ||
                          mjJobActionBusy ||
                          mjRosterTaskBusyJobId === mjManageJobId ||
                          mjJobAnythingInFlight(mjEffectiveManagedJob) ||
                          mjRosterAlreadyBuilt(mjEffectiveManagedJob, mjRosterInfo?.info ?? null)
                        }
                        onClick={() => mjManageJobId != null && setMjRosterConfirmJobId(mjManageJobId)}
                      >
                        {mjIsUploadJob(mjEffectiveManagedJob) ? "Build matches only" : "Build roster only"}
                      </Button>
                    </span>
                  </Tooltip>
                  {mjIsUploadJob(mjEffectiveManagedJob) &&
                    (() => {
                      const gate = mjSsnUpdaterButtonReason(
                        mjEffectiveManagedJob,
                        mjManageJobId != null ? mjSsnReadinessByJobId[mjManageJobId] ?? null : null,
                      );
                      return (
                        <Tooltip title={gate.reason}>
                          <span style={{ display: "block" }}>
                            <Button
                              fullWidth
                              size="small"
                              variant="outlined"
                              disabled={!canEditBots || gate.disabled || mjJobActionBusy}
                              onClick={() => mjManageJobId != null && setMjSsnUpdaterJobId(mjManageJobId)}
                            >
                              SSN Updater
                            </Button>
                          </span>
                        </Tooltip>
                      );
                    })()}
                </Stack>
              </Box>
              <Box sx={MJ_MANAGE_GROUP_SX}>
                <Typography variant="caption" sx={MJ_MANAGE_GROUP_LABEL_SX}>
                  Logs
                </Typography>
                <Stack spacing={0.75}>
                  <Tooltip title="Download all stored logs of this job as one .zip">
                    <span style={{ display: "block" }}>
                      <Button
                        fullWidth
                        size="small"
                        variant="outlined"
                        startIcon={
                          isMjLogsArchiveLoading ? (
                            <CircularProgress size={14} />
                          ) : (
                            <FileDownloadIcon fontSize="small" />
                          )
                        }
                        disabled={isMjLogsArchiveLoading || mjEffectiveManagedJob == null}
                        onClick={() =>
                          mjEffectiveManagedJob &&
                          void handleMjDownloadJobLogs(
                            Number(mjEffectiveManagedJob.id),
                            mjEffectiveManagedJob,
                          )
                        }
                      >
                        Download logs
                      </Button>
                    </span>
                  </Tooltip>
                  <Tooltip title="Delete all stored logs so the next run re-processes every employee">
                    <span style={{ display: "block" }}>
                      <Button
                        fullWidth
                        size="small"
                        variant="outlined"
                        color="warning"
                        startIcon={<DeleteSweepIcon fontSize="small" />}
                        disabled={
                          isMjClearLogsLoading ||
                          mjEffectiveManagedJob == null ||
                          !canEditBots
                        }
                        onClick={() =>
                          mjEffectiveManagedJob &&
                          void openMjClearLogs(Number(mjEffectiveManagedJob.id))
                        }
                      >
                        Clear logs
                      </Button>
                    </span>
                  </Tooltip>
                </Stack>
              </Box>
              <Box sx={MJ_MANAGE_GROUP_SX}>
                <Typography variant="caption" sx={MJ_MANAGE_GROUP_LABEL_SX}>
                  Files
                </Typography>
                <Stack spacing={0.75}>
                  {activeMajor?.bot_kind !== "delete_bots" && (
                    <Button
                      fullWidth
                      size="small"
                      variant="outlined"
                      startIcon={<FolderOpenIcon fontSize="small" />}
                      onClick={() =>
                        mjEffectiveManagedJob && handleMjViewJobFiles(mjEffectiveManagedJob)
                      }
                    >
                      Open job files
                    </Button>
                  )}
                  {(() => {
                    const info =
                      mjRosterInfo && mjRosterInfo.jobId === mjManageJobId ? mjRosterInfo.info : null;
                    const isUpload = mjIsUploadJob(mjEffectiveManagedJob);
                    const inFlight = mjJobAnythingInFlight(mjEffectiveManagedJob);
                    const exists = mjRosterInfoExists(info);
                    return (
                      <>
                        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.3 }}>
                          {mjRosterInfoLoading
                            ? "Checking the stored file..."
                            : info
                              ? mjRosterInfoLine(info)
                              : ""}
                        </Typography>
                        <Stack direction="row" spacing={0.75}>
                          <Tooltip
                            title={`Every row of the ${
                              isUpload ? "Matches file" : "roster"
                            } with what the job's logs say happened to it.`}
                          >
                            <span style={{ display: "block", flexGrow: 1 }}>
                              <Button
                                fullWidth
                                size="small"
                                variant="outlined"
                                startIcon={<Visibility fontSize="small" />}
                                disabled={!info || !mjRosterMainFileExists(info)}
                                onClick={() =>
                                  mjManageJobId != null && setRosterViewJobId(mjManageJobId)
                                }
                              >
                                {isUpload ? "View matches rows" : "View roster"}
                              </Button>
                            </span>
                          </Tooltip>
                          <Tooltip title="Download the file itself, straight from S3.">
                            <span style={{ display: "block" }}>
                              <Button
                                size="small"
                                variant="outlined"
                                aria-label="Download the stored file"
                                disabled={!info || !mjRosterMainFileExists(info)}
                                onClick={() =>
                                  mjManageJobId != null && void downloadRoster(mjManageJobId)
                                }
                              >
                                <FileDownloadIcon fontSize="small" />
                              </Button>
                            </span>
                          </Tooltip>
                        </Stack>
                        <Tooltip
                          title={
                            inFlight
                              ? "Stop the job's tasks first."
                              : isUpload
                                ? "Deletes the Matches, Several matches and No matches files (and the summary), so the next run matches again."
                                : "Deletes this job's roster file, so the next run collects it again."
                          }
                        >
                          <span style={{ display: "block" }}>
                            <Button
                              fullWidth
                              size="small"
                              variant="outlined"
                              color="warning"
                              disabled={!canEditBots || !exists || inFlight || mjRosterDeleting}
                              onClick={() =>
                                mjManageJobId != null && setMjRosterDeletePrompt({ jobId: mjManageJobId })
                              }
                            >
                              {isUpload ? "Delete all matches files" : "Delete roster"}
                            </Button>
                          </span>
                        </Tooltip>
                        <Tooltip
                          title={
                            inFlight
                              ? "Stop the job's tasks first."
                              : isUpload
                                ? "Replace the Matches file with a reviewed one (CSV, XLS or XLSX: s3_name, site_name, employee_id)."
                                : "Replace this job's roster with your own JSON list of employees."
                          }
                        >
                          <span style={{ display: "block" }}>
                            <Button
                              fullWidth
                              size="small"
                              variant="outlined"
                              startIcon={<UploadFileIcon fontSize="small" />}
                              disabled={!canEditBots || inFlight}
                              onClick={() =>
                                mjManageJobId != null &&
                                openMjArtifactUpload(mjManageJobId, isUpload ? "matches" : "roster")
                              }
                            >
                              {isUpload ? "Upload matches file" : "Upload roster file"}
                            </Button>
                          </span>
                        </Tooltip>
                        {isUpload && (
                          <Tooltip
                            title={(() => {
                              const eligible = mjSftpExportEligibility(mjEffectiveManagedJob ?? {});
                              return eligible.ok
                                ? "Copies the files the vendor refused into the client's SFTP folder and hands out the SFTP login."
                                : `${eligible.reason} An earlier export, if any, is still shown.`;
                            })()}
                          >
                            <span style={{ display: "block" }}>
                              <Button
                                fullWidth
                                size="small"
                                variant="outlined"
                                startIcon={<SendIcon fontSize="small" />}
                                onClick={() => mjManageJobId != null && setMjSftpExportJobId(mjManageJobId)}
                              >
                                Send rejected files to SFTP
                              </Button>
                            </span>
                          </Tooltip>
                        )}
                        {isUpload && mjManageSftpExport?.export && (
                          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.3 }}>
                            {(() => {
                              const last = mjManageSftpExport.export;
                              const when = last.finished_at ?? last.started_at;
                              const date = new Date(when);
                              const stamp = Number.isNaN(date.getTime()) ? when : date.toLocaleString();
                              if (last.state === "running") {
                                return `Export running: ${last.counts.done} of ${last.counts.planned}...`;
                              }
                              if (last.state === "interrupted") {
                                return `Export started ${stamp} stopped reporting - open the dialog to run it again.`;
                              }
                              if (last.state === "failed") return `Export failed ${stamp}.`;
                              return `Sent to SFTP ${stamp}: ${last.counts.copied} file(s)${
                                last.counts.failed ? `, ${last.counts.failed} failed` : ""
                              }.`;
                            })()}
                          </Typography>
                        )}
                      </>
                    );
                  })()}
                </Stack>
              </Box>
              <Box sx={{ ...MJ_MANAGE_GROUP_SX, borderColor: "error.light" }}>
                <Typography
                  variant="caption"
                  sx={{ ...MJ_MANAGE_GROUP_LABEL_SX, color: "error.main" }}
                >
                  Danger zone
                </Typography>
                <Button
                  fullWidth
                  size="small"
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteOutlineIcon fontSize="small" />}
                  disabled={!canEditBots}
                  onClick={() => mjManageJobId != null && setMjDeleteJobId(mjManageJobId)}
                >
                  Delete job
                </Button>
              </Box>
            </Box>
          </Stack>
        )}
        {/* Matches tab — upload bots only */}
        {activeMajor?.bot_kind === "upload_bots" && mjManageTab === 1 && (
          <Box sx={{ pt: 1 }}>
            <Stack
              gap={1}
              sx={{
                mb: 1.5,
                py: 1,
                px: 1,
                borderRadius: 1,
                bgcolor: "action.hover",
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Append the reviewed No matches and Several matches files to the Matches file, or
                replace it whole.
              </Typography>
              <UnmatchedReviewInstructions hint={mjRunHint(mjEffectiveManagedJob ?? {})} />
              <Stack direction="row" gap={1} flexWrap="wrap">
                <Tooltip
                  title={
                    mjJobAnythingInFlight(mjEffectiveManagedJob ?? {})
                      ? "Stop the job's tasks first."
                      : "Append the reviewed No matches and Several matches files to the Matches file."
                  }
                >
                  <span>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<MergeTypeIcon fontSize="small" />}
                      disabled={
                        matchesLoading ||
                        matchesDeleting ||
                        !canEditBots ||
                        mjJobAnythingInFlight(mjEffectiveManagedJob ?? {})
                      }
                      onClick={openMatchesMergeDialog}
                    >
                      Merge reports
                    </Button>
                  </span>
                </Tooltip>
              </Stack>
            </Stack>
            {matchesSummary?.manual_upload && (
              <Alert severity="info" sx={{ mb: 1 }}>
                Matches file uploaded by hand on{" "}
                {new Date(matchesSummary.manual_upload.uploaded_at).toLocaleString()}
                {matchesSummary.manual_upload.uploaded_by
                  ? ` by ${matchesSummary.manual_upload.uploaded_by}`
                  : ""}{" "}
                ({matchesSummary.manual_upload.rows} row(s)). The Several matches and No matches
                files below come from the bot&apos;s earlier run and are outdated.
              </Alert>
            )}
            {matchesMergeSuccess && (
              <Alert
                severity="success"
                sx={{ mb: 1 }}
                onClose={() => setMatchesMergeSuccess(null)}
              >
                {matchesMergeSuccess}
              </Alert>
            )}
            {matchesReviewNotice && (
              <Alert
                severity={matchesReviewNotice.severity}
                sx={{ mb: 1 }}
                onClose={() => setMatchesReviewNotice(null)}
              >
                {matchesReviewNotice.text}
              </Alert>
            )}
            {matchesLoading && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 2, px: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">Loading…</Typography>
              </Box>
            )}
            {matchesError && (
              <Alert
                severity="error"
                sx={{ mb: 1 }}
                action={
                  <Button
                    size="small"
                    onClick={() => mjManageJobId != null && void loadMatchesFiles(mjManageJobId)}
                  >
                    Retry
                  </Button>
                }
              >
                {matchesError}
              </Alert>
            )}
            {!matchesLoading && matchesFiles && (() => {
              const existingFiles = matchesFiles.filter((f) => f.exists);
              const allSelected =
                existingFiles.length > 0 &&
                matchesSelectedFiles.length === existingFiles.length;
              const someSelected =
                matchesSelectedFiles.length > 0 && !allSelected;
              return (
                <>
                  {matchesSelectedFiles.length > 0 && (
                    <Stack direction="row" gap={1} alignItems="center" sx={{ mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        {matchesSelectedFiles.length} selected
                      </Typography>
                      <Button
                        size="small"
                        variant="contained"
                        color="error"
                        disabled={matchesDeleting || !canEditBots}
                        startIcon={
                          matchesDeleting ? (
                            <CircularProgress size={14} color="inherit" />
                          ) : (
                            <DeleteOutlineIcon fontSize="small" />
                          )
                        }
                        onClick={() =>
                          mjManageJobId != null &&
                          void confirmBatchDeleteMatchesFiles(mjManageJobId)
                        }
                      >
                        {matchesDeleting ? "Deleting…" : "Delete selected"}
                      </Button>
                      <Button
                        size="small"
                        disabled={matchesDeleting}
                        onClick={() => setMatchesSelectedFiles([])}
                      >
                        Clear
                      </Button>
                    </Stack>
                  )}
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ pl: 0, width: 44, pr: 0 }}>
                          <Checkbox
                            size="small"
                            indeterminate={someSelected}
                            checked={allSelected}
                            disabled={existingFiles.length === 0 || matchesDeleting}
                            onChange={(e) =>
                              setMatchesSelectedFiles(
                                e.target.checked
                                  ? existingFiles.map((f) => f.filename)
                                  : [],
                              )
                            }
                            sx={{ p: 0.5 }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, pl: 0 }}>File</TableCell>
                        <TableCell sx={{ fontWeight: 700, textAlign: "right" }}>Rows</TableCell>
                        <TableCell sx={{ fontWeight: 700, textAlign: "right" }}>Size</TableCell>
                        <TableCell sx={{ fontWeight: 700, textAlign: "center" }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {matchesFiles.map((f) => {
                        const label = matchesFileLabel(f.filename);
                        const reviewKind = unmatchedKindForFilename(f.filename);
                        const reviewSummary = reviewKind ? (matchesReview?.[reviewKind] ?? null) : null;
                        const sizeLabel =
                          f.size_bytes != null
                            ? f.size_bytes >= 1024
                              ? `${(f.size_bytes / 1024).toFixed(1)} KB`
                              : `${f.size_bytes} B`
                            : "—";
                        const isSelected = matchesSelectedFiles.includes(f.filename);
                        return (
                          <TableRow
                            key={f.filename}
                            selected={isSelected}
                            sx={{ opacity: matchesDeleting && isSelected ? 0.5 : 1 }}
                          >
                            <TableCell sx={{ pl: 0, pr: 0 }}>
                              <Checkbox
                                size="small"
                                checked={isSelected}
                                disabled={!f.exists || matchesDeleting}
                                onChange={(e) =>
                                  setMatchesSelectedFiles((prev) =>
                                    e.target.checked
                                      ? [...prev, f.filename]
                                      : prev.filter((n) => n !== f.filename),
                                  )
                                }
                                sx={{ p: 0.5 }}
                              />
                            </TableCell>
                            <TableCell sx={{ pl: 0 }}>
                              <Typography
                                variant="body2"
                                fontWeight={f.exists ? 600 : 400}
                                color={f.exists ? "text.primary" : "text.disabled"}
                              >
                                {label}
                              </Typography>
                              {!f.exists && (
                                <Typography variant="caption" color="text.disabled">
                                  not found
                                </Typography>
                              )}
                              {f.exists && reviewKind ? (
                                <UnmatchedReviewFileLabel
                                  kind={reviewKind}
                                  summary={reviewSummary}
                                  rows={f.rows}
                                />
                              ) : null}
                              {f.exists &&
                                matchesSummary?.manual_upload &&
                                f.filename !== "s3_to_site_names_matches.csv" && (
                                  <Typography variant="caption" color="warning.main" display="block">
                                    outdated - written before the manual upload
                                  </Typography>
                                )}
                            </TableCell>
                            <TableCell sx={{ textAlign: "right" }}>
                              <Typography
                                variant="caption"
                                color={f.exists && f.rows === 0 ? "warning.main" : "text.secondary"}
                              >
                                {f.exists && f.rows != null ? f.rows : "—"}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ textAlign: "right" }}>
                              <Typography variant="caption" color="text.secondary">
                                {f.exists ? sizeLabel : "—"}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ textAlign: "center", pr: 0 }}>
                              {f.exists ? (
                                <Stack direction="row" gap={0.5} justifyContent="center">
                                  <Tooltip title="View">
                                    <IconButton
                                      size="small"
                                      disabled={matchesDeleting}
                                      onClick={() =>
                                        mjManageJobId != null &&
                                        void openMatchesFileViewer(mjManageJobId, f.filename)
                                      }
                                    >
                                      <Visibility fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Download">
                                    <IconButton
                                      size="small"
                                      disabled={matchesDeleting}
                                      onClick={() =>
                                        mjManageJobId != null &&
                                        void downloadMatchesFile(mjManageJobId, f.filename)
                                      }
                                    >
                                      <FileDownloadIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Delete">
                                    <IconButton
                                      size="small"
                                      color="error"
                                      disabled={matchesDeleting || !canEditBots}
                                      onClick={() => setMatchesDeleteFilename(f.filename)}
                                    >
                                      <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  {reviewKind ? (
                                    <UnmatchedReviewActions
                                      summary={reviewSummary}
                                      canEdit={canEditBots}
                                      readOnlyReason={botsReadOnlyReason}
                                      busy={matchesReviewBusy === reviewKind}
                                      disabled={matchesDeleting || matchesLoading}
                                      onAct={(action) =>
                                        mjManageJobId != null &&
                                        setMatchesReviewPrompt({ jobId: mjManageJobId, kind: reviewKind, action })
                                      }
                                    />
                                  ) : null}
                                </Stack>
                              ) : (
                                <Typography variant="caption" color="text.disabled">
                                  —
                                </Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                  {matchesSummary ? (
                    <Box sx={{ mt: 1.5 }}>
                      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                        Matching summary
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {mjMatchesSummaryHeadline(matchesSummary)}
                      </Typography>
                      {mjMatchesSummaryLines(matchesSummary).map((line, index) => (
                        <Typography
                          key={`${index}-${line}`}
                          variant="caption"
                          display="block"
                          color="text.secondary"
                        >
                          {line}
                        </Typography>
                      ))}
                    </Box>
                  ) : null}
                </>
              );
            })()}
            {!matchesLoading && !matchesError && matchesFiles === null && (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
                No data loaded.
              </Typography>
            )}
          </Box>
        )}
      </AppDialog>

      <AppDialog
        open={mjAddTasksOpen}
        onClose={() => setMjAddTasksOpen(false)}
        maxWidth="md"
        title="Tasks in this job"
        actions={
          <>
            <Button onClick={() => setMjAddTasksOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={() => void handleMjTaskCountApply()}
              disabled={
                isAddingTasksToJob ||
                isReducingJobTasks ||
                mjAddTasksDialogStats == null ||
                Boolean(mjAddTasksDialogStats.applyBlockingReason) ||
                !canEditBots
              }
            >
              Apply
            </Button>
          </>
        }
      >
        {mjAddTasksJobId != null && mjAddTasksDialogStats && (
          <Stack sx={{ mb: 1.5 }}>
            <Typography variant="caption" color="text.secondary">
              {mjAddTasksDialogStats.totalHelp}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.25 }}>
              <Tooltip title="One task fewer">
                <span>
                  <IconButton
                    size="small"
                    aria-label="one task fewer"
                    disabled={mjAddTasksDialogStats.target <= 1}
                    onClick={() =>
                      mjSetTaskCountTarget(String(mjAddTasksDialogStats.target - 1))
                    }
                  >
                    <RemoveIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <TextField
                sx={{ width: 156 }}
                label="Number of tasks"
                inputProps={{ inputMode: "numeric" }}
                value={mjTaskCountTarget}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  mjSetTaskCountTarget(connPlanTasksDigitsOnly(e.target.value))
                }
              />
              <Tooltip title="One task more">
                <span>
                  <IconButton
                    size="small"
                    aria-label="one task more"
                    disabled={mjAddTasksDialogStats.target >= MAX_BATCH_TASKS}
                    onClick={() =>
                      mjSetTaskCountTarget(String(mjAddTasksDialogStats.target + 1))
                    }
                  >
                    <Add fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Typography variant="body2" color="text.secondary">
                {mjAddTasksDialogStats.deltaLabel}
              </Typography>
            </Stack>
            {mjAddTasksDialogStats.delta > 0 && mjAddTasksDialogStats.planMode ? (
              <>
                {mjBulkAddRows.map((row, ri) => {
                  const pickPool =
                    mjAddPlanConnPool.length > 0
                      ? mjAddPlanConnPool
                      : mjAddTasksDialogStats.plansSnap.map((p) => p.connection_id);
                  return (
                    <Box
                      key={`mj-add-${String(ri)}`}
                      sx={{
                        display: "flex",
                        gap: 1,
                        mt: ri === 0 ? 1.25 : 0.75,
                        mb: 0.25,
                        alignItems: "flex-start",
                      }}
                    >
                      <TextField
                        select
                        label="Connection"
                        sx={{ flex: 1 }}
                        value={
                          typeof row.connection_id === "number" && row.connection_id >= 1
                            ? String(row.connection_id)
                            : ""
                        }
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                          const next = mjBulkAddRows.slice();
                          next[ri] = { ...row, connection_id: Number(e.target.value) };
                          setMjBulkAddRows(next);
                        }}
                      >
                        {connectionIdsForPlanRow(pickPool, mjBulkAddRows, ri).map((cid) => (
                          <MenuItem key={cid} value={String(cid)}>
                            {mjConnLabelVerbose(connections, cid)}
                          </MenuItem>
                        ))}
                      </TextField>
                      <TextField
                        sx={{ width: 124 }}
                        label="Tasks"
                        inputProps={{ inputMode: "numeric" }}
                        value={row.task_target_count}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                          const next = mjBulkAddRows.slice();
                          next[ri] = {
                            ...row,
                            task_target_count: connPlanTasksDigitsOnly(e.target.value),
                          };
                          mjSetBulkAddRows(next);
                        }}
                      />
                      <Tooltip title="Remove row">
                        <span>
                          <IconButton
                            size="small"
                            aria-label="remove row"
                            onClick={() => {
                              if (mjBulkAddRows.length <= 1) return;
                              mjSetBulkAddRows(mjBulkAddRows.filter((_r, ji) => ji !== ri));
                            }}
                            disabled={mjBulkAddRows.length <= 1}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  );
                })}
                <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 0.75 }}>
                  <Tooltip title="Add row for another login">
                    <span>
                      <IconButton
                        size="small"
                        aria-label="add row"
                        onClick={() => {
                          const pool =
                            mjAddPlanConnPool.length > 0
                              ? mjAddPlanConnPool
                              : mjAddTasksDialogStats.plansSnap.map((p) => p.connection_id);
                          const n = nextUnusedPoolConnectionId(pool, mjBulkAddRows);
                          if (n != null) {
                            mjSetBulkAddRows([
                              ...mjBulkAddRows,
                              { connection_id: n, task_target_count: "1" },
                            ]);
                          }
                        }}
                        disabled={
                          nextUnusedPoolConnectionId(
                            mjAddPlanConnPool.length > 0
                              ? mjAddPlanConnPool
                              : mjAddTasksDialogStats.plansSnap.map((p) => p.connection_id),
                            mjBulkAddRows,
                          ) == null
                        }
                      >
                        <Add fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              </>
            ) : null}
            {mjAddTasksDialogStats.removalPreview.length > 0 ? (
              <Box sx={{ mt: 1.5 }}>
                <Typography variant="caption" color="text.secondary">
                  Will be removed:
                </Typography>
                <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                  {mjAddTasksDialogStats.removalPreview.map((task) => (
                    <Typography key={`mj-reduce-${String(task.id)}`} variant="body2">
                      {`#${String(task.id)} - employees ${String(task.employee_number_from)}-${String(
                        task.employee_number_to,
                      )} - ${mjTaskRunLabel(task)}`}
                    </Typography>
                  ))}
                </Stack>
                <Alert severity="info" sx={{ mt: 1 }}>
                  {`Their logs stay stored, so the employees they already processed keep counting and the next run does not repeat them. The roster is split again across the ${mjAddTasksDialogStats.target} task(s) that stay.`}
                </Alert>
              </Box>
            ) : null}
          </Stack>
        )}
        {mjAddTasksDialogStats?.countHint?.trim() ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
            {mjAddTasksDialogStats.countHint}
          </Typography>
        ) : null}
        {!mjAddTasksErr && mjAddTasksDialogStats?.applyBlockingReason?.trim() ? (
          <Alert severity="warning" sx={{ mt: 1 }}>
            {mjAddTasksDialogStats.applyBlockingReason}
          </Alert>
        ) : null}
        {mjAddTasksDialogStats?.planLevelHint?.trim() ? (
          <Alert severity="info" sx={{ mt: 1 }}>
            {mjAddTasksDialogStats.planLevelHint} The job&apos;s per-connection plan will be raised
            automatically when these tasks are added.
          </Alert>
        ) : null}
        {mjAddTasksErr ? (
          <Alert severity="error" sx={{ mt: 1 }}>
            {mjAddTasksErr}
          </Alert>
        ) : null}
      </AppDialog>

      <AppDialog
        open={ssnJobDialogOpen}
        onClose={() => setSsnJobDialogOpen(false)}
        maxWidth="sm"
        title={ssnJobDialogMode === "create" ? "Create SSN Job" : "Edit SSN Job"}
        actions={
          <>
            <Button onClick={() => setSsnJobDialogOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              disabled={
                !canEditBots ||
                ssnJobCreating ||
                ssnOptionsSaving ||
                (ssnJobDialogMode === "create"
                  ? !ssnJobSelectedConnId
                  : !ssnOptionsDirty)
              }
              onClick={() => void submitSsnJobDialog()}
            >
              {ssnJobCreating || ssnOptionsSaving
                ? "Saving..."
                : ssnJobDialogMode === "create"
                  ? "Create"
                  : "Save"}
            </Button>
          </>
        }
      >
        {ssnJobDialogMode === "edit" && ssnTaskLooksBusy ? (
          <Alert severity="warning" sx={{ mt: 1, mb: 1 }}>
            This SSN job has a running task. It keeps the options it started
            with; stop it before editing.
          </Alert>
        ) : null}
        {ssnJobDialogMode === "create" ? (
          <FormControl size="small" fullWidth sx={{ mt: 1, mb: 2 }}>
            <InputLabel>Connection</InputLabel>
            <Select
              value={ssnJobSelectedConnId ?? ""}
              label="Connection"
              onChange={(e) => setSsnJobSelectedConnId(Number(e.target.value) || null)}
            >
              {majorConnPickerIds
                .map((cid) => connections.find((c) => c.id === cid))
                .filter((c): c is (typeof connections)[number] => c != null)
                .map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {(c.username ?? "").trim() ? `@${c.username}` : c.host || String(c.id)}
                  </MenuItem>
                ))}
            </Select>
            {ssnJobSelectedConnId != null &&
              !majorIncludedConnIds.includes(ssnJobSelectedConnId) && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  This login is linked to the major job when the SSN Job is created.
                </Typography>
              )}
          </FormControl>
        ) : null}
        <Divider sx={{ mb: 1.5 }} />
        {ssnBotNote ? (
          <Box sx={{ mb: 1.5 }}>
            <BotNoteView note={ssnBotNote} label={`ssn_bots.${SSN_BOT_NAME}`} dense />
          </Box>
        ) : null}
        <Typography variant="overline" color="text.secondary" sx={{ display: "block", mb: 1 }}>
          Bot options
        </Typography>
        {ssnOptionSpecs.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            This vendor&apos;s SSN bot declares no options.
          </Typography>
        ) : (
          <BotOptionFields
            options={ssnOptionSpecs}
            value={ssnAdditionalOptions}
            onChange={setSsnAdditionalOptions}
            omitKeys={SSN_DERIVED_OMIT}
            disabled={!canEditBots}
          />
        )}
        {ssnJobDialogError ? (
          <Alert severity="error" sx={{ mt: 1 }}>
            {ssnJobDialogError}
          </Alert>
        ) : null}
      </AppDialog>

      <AppDialog
        open={mjEditJobOpen}
        onClose={() => setMjEditJobOpen(false)}
        maxWidth="md"
        title={<>Edit Task Job{mjEditJobOrdinal != null ? ` #${mjEditJobOrdinal}` : ""}</>}
        actions={
          <>
            <Button onClick={() => setMjEditJobOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              disabled={isMjEditJobSaving || mjEditJobHasRunningTasks || !canEditBots}
              onClick={() => void saveMjEditJob()}
            >
              {isMjEditJobSaving ? "Saving..." : "Save"}
            </Button>
          </>
        }
      >
        {mjEditJobHasRunningTasks ? (
          <Alert severity="warning" sx={{ mt: 1, mb: 1 }}>
            {mjEditJobLockedReason}
          </Alert>
        ) : null}
        {mjEditJobBotLocked ? (
          <Alert severity="info" sx={{ mt: mjEditJobHasRunningTasks ? 0 : 1, mb: 1 }}>
            This task job already has progress ({mjEditJobProcessedEmployees} employee
            {mjEditJobProcessedEmployees === 1 ? "" : "s"} processed). The bot cannot be changed.
          </Alert>
        ) : null}
        <TextField
          sx={{ mt: 1, mb: 1 }}
          fullWidth
          label="Task Job Name"
          required
          value={mjEditJobName}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setMjEditJobName(e.target.value)}
          disabled={mjEditJobHasRunningTasks}
        />
        {mjEditJobBotLocked || mjEditJobHasRunningTasks ? (
          <TextField
            sx={{ mb: 1 }}
            fullWidth
            label="Bot"
            value={
              normalizedChildBots.find((b) => botNameFromRegistry(b) === mjEditBotName)?.bot_slug ??
              (mjEditBotName.includes(".") ? mjEditBotName.slice(mjEditBotName.indexOf(".") + 1) : mjEditBotName)
            }
            disabled
            helperText={
              mjEditJobBotLocked
                ? "Cannot change bot while this task job has employee progress."
                : ""
            }
          />
        ) : (
          <Autocomplete
            ListboxComponent={AutocompleteScrollListbox}
            componentsProps={autocompleteScrollPaperProps}
            sx={{ mb: 1 }}
            options={normalizedChildBots}
            value={
              normalizedChildBots.find((b) => botNameFromRegistry(b) === mjEditBotName) ?? null
            }
            onChange={(_e, v) => setMjEditBotName(v ? botNameFromRegistry(v) : "")}
            filterOptions={filterChildDialogBots}
            getOptionLabel={(o) => o.bot_slug}
            renderOption={(props, option) => (
              <li {...props} key={`${option.vendor}.${option.job_kind}.${option.bot_slug}`}>
                {option.bot_slug}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Bot"
                required
                error={!mjEditBotName.trim() && Boolean(mjEditJobError)}
              />
            )}
          />
        )}
        <FormControlLabel
          sx={{ mb: 1 }}
          control={
            <Switch
              checked={mjEditProcessErrors}
              onChange={(e) => setMjEditProcessErrors(e.target.checked)}
              disabled={mjEditJobHasRunningTasks}
            />
          }
          label="Auto process error employees (one retry pass after the job finishes)"
        />
        <FormControlLabel
          sx={{ mb: 1 }}
          control={
            <Switch
              checked={mjEditSeparateProfiles}
              onChange={(e) => setMjEditSeparateProfiles(e.target.checked)}
              disabled={mjEditJobHasRunningTasks}
            />
          }
          label="Separate browser profile per task (on: each task runs on its own profile, numbered by its task id, and its first run logs in from scratch; off: every task reuses the prepared profile 1). Saving a change moves the job's existing tasks too."
        />
        <TextField
          label="Max tasks running at once"
          size="small"
          fullWidth
          inputProps={{ inputMode: "numeric" }}
          value={mjEditMaxParallel}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            setMjEditMaxParallel(connPlanTasksDigitsOnly(e.target.value))
          }
          sx={{ mb: 1 }}
          disabled={mjEditJobHasRunningTasks}
          helperText={parallelismHelpText(
            recommendedParallelismFor(normalizedChildBots, mjEditBotName),
          )}
        />
        {activeMajor?.bot_kind === "upload_bots" &&
          documentTypeUseFor(normalizedChildBots, mjEditBotName) !== "unused" && (
            <TextField
              label={
                documentTypeUseFor(normalizedChildBots, mjEditBotName) === "required"
                  ? "Document type *"
                  : "Document type"
              }
              size="small"
              fullWidth
              value={mjEditDocumentType}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setMjEditDocumentType(e.target.value)}
              sx={{ mb: 1 }}
              placeholder="e.g. w2, paystubs, 1095"
              disabled={mjEditJobHasRunningTasks}
              helperText={
                documentTypeUseFor(normalizedChildBots, mjEditBotName) === "optional"
                  ? "Optional for this bot; leave it empty when there is no type to pick."
                  : undefined
              }
            />
          )}
        <BotOptionFields
          key={mjEditBotName}
          options={botOptionSpecs(mjEditBotName)}
          value={mjEditAdditionalOptions}
          onChange={setMjEditAdditionalOptions}
          disabled={mjEditJobHasRunningTasks}
          omitKeys={DOCS_TYPE_OMIT}
        />
        {activeMajor?.bot_kind === "delete_bots" && (
          <Box sx={{ mb: 1 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
              Document types to delete *
            </Typography>
            {mjEditDocumentTypes.map((dt, i) => (
              <Stack key={i} direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.75 }}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="e.g. Paylocity Historical W2s"
                  value={dt}
                  disabled={mjEditJobHasRunningTasks}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setMjEditDocumentTypes((prev) => prev.map((v, j) => j === i ? e.target.value : v))
                  }
                />
                <IconButton size="small" color="error"
                  disabled={mjEditDocumentTypes.length === 1 || mjEditJobHasRunningTasks}
                  onClick={() => setMjEditDocumentTypes((prev) => prev.filter((_, j) => j !== i))}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Stack>
            ))}
            <Button size="small" startIcon={<Add fontSize="small" />}
              disabled={mjEditJobHasRunningTasks}
              onClick={() => setMjEditDocumentTypes((prev) => [...prev, ""])}>
              Add type
            </Button>
          </Box>
        )}
        {activeMajor?.bot_kind === "upload_bots" && (
          <Box sx={{ mb: 1, mt: 0.5 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
              Upload options
            </Typography>
            <Stack direction="column" spacing={0.5}>
              <FormControlLabel
                control={
                  <Switch
                    checked={mjEditUploadAsZip}
                    onChange={(e) => setMjEditUploadAsZip(e.target.checked)}
                    size="small"
                    disabled={mjEditJobHasRunningTasks}
                  />
                }
                label="Upload as ZIP"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={mjEditUnzipBefore}
                    onChange={(e) => setMjEditUnzipBefore(e.target.checked)}
                    size="small"
                    disabled={mjEditJobHasRunningTasks}
                  />
                }
                label="Unzip before uploading"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={mjEditUseSsnMatch}
                    onChange={(e) => setMjEditUseSsnMatch(e.target.checked)}
                    size="small"
                    disabled={mjEditJobHasRunningTasks}
                  />
                }
                label="Use SSN match"
              />
            </Stack>
            {mjEditSsnReadiness && (
              <Typography
                variant="caption"
                display="block"
                sx={{ mt: 0.5 }}
                color={mjEditSsnReadiness.ready ? "success.main" : "warning.main"}
              >
                {mjSsnReadinessText(mjEditSsnReadiness)}
              </Typography>
            )}
          </Box>
        )}
        {activeMajor?.bot_kind === "upload_bots" && (
          <Box sx={{ mb: 1.5, mt: 0.5 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
              Source folder (files to upload)
            </Typography>
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <TextField
                fullWidth
                size="small"
                placeholder="Select a folder from Data Storage…"
                value={mjEditSourcePath}
                InputProps={{ readOnly: true }}
                disabled={mjEditJobHasRunningTasks}
                sx={{ "& .MuiInputBase-input": { color: mjEditSourcePath ? "text.primary" : "text.disabled" } }}
              />
              <Button
                variant="outlined"
                size="small"
                startIcon={<FolderOpenIcon />}
                disabled={mjEditJobHasRunningTasks}
                onClick={() => openFolderPicker("edit", mjEditSourcePath)}
                sx={{ whiteSpace: "nowrap", minWidth: 90 }}
              >
                Browse
              </Button>
            </Stack>
          </Box>
        )}
        {activeMajor?.bot_kind === "upload_bots" && (
          <SourceSsnFileField
            readiness={mjEditSsnReadiness}
            value={mjEditSourceSsnValue}
            onChange={setMjEditSourceSsn}
            useSsnMatch={mjEditUseSsnMatch}
            hasFolder={Boolean(mjEditSourcePath.trim())}
            disabled={mjEditJobHasRunningTasks}
          />
        )}
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
          Tasks and their connections are managed directly in the tasks table (Add tasks / row
          actions).
        </Typography>
        {mjEditJobError ? (
          <Alert severity="error" sx={{ mt: 1 }}>
            {mjEditJobError}
          </Alert>
        ) : null}
      </AppDialog>

      <DeleteJobConfirmDialog
        open={mjDeleteJobId != null}
        title="Delete task job?"
        message={
          <>This permanently removes job{mjDeleteJobOrdinal != null ? ` #${mjDeleteJobOrdinal}` : ""}.</>
        }
        onCancel={() => setMjDeleteJobId(null)}
        onConfirm={() => void mjConfirmDeleteJob()}
        confirmDisabled={!canEditBots}
        busy={isMjDeletingJob}
        error={mjDeleteJobId != null ? mjManageJobError : null}
      />

      <DeleteJobConfirmDialog
        open={bulkChildDeleteOpen}
        title="Delete selected task jobs?"
        message={
          <>
            Delete {selectedChildJobIds.length} selected task job
            {selectedChildJobIds.length === 1 ? "" : "s"} and every task inside them?
          </>
        }
        onCancel={() => {
          setBulkChildDeleteOpen(false);
          setBulkChildDeleteError(null);
        }}
        onConfirm={() => void confirmBulkDeleteChildren()}
        confirmDisabled={!selectedChildJobIds.length || !canEditBots}
        busy={isDeletingJobsBulk}
        error={bulkChildDeleteError}
      />

      <ConfirmDialog
        open={mjRosterConfirmJobId != null}
        title={
          mjRosterConfirmJobId != null &&
          mjIsUploadJob(
            (childJobs.find(
              (r) => Number((r as Record<string, unknown>).id) === mjRosterConfirmJobId,
            ) as Record<string, unknown> | undefined) ?? {},
          )
            ? "Build the matches files only?"
            : "Build the roster only?"
        }
        message="One bot logs in, builds the roster (for an upload job: the Matches, Several matches and No matches files), sets the task ranges from it and processes nobody. Review the result on the Matches tab, merge what you resolved, then press Run all."
        confirmLabel="Build"
        onConfirm={() => {
          const jobId = mjRosterConfirmJobId;
          if (jobId == null) return;
          setMjRosterConfirmJobId(null);
          void mjSsnGuardedRun(mjJobRowById(jobId), "Build matches only", () =>
            mjRunRosterTask(jobId),
          );
        }}
        onCancel={() => setMjRosterConfirmJobId(null)}
        loading={mjRosterTaskBusyJobId != null}
      />
      <ConfirmDialog
        open={mjRemoveTaskPrompt != null}
        title="Remove this task?"
        message={(() => {
          if (mjRemoveTaskPrompt == null) return "";
          const rows = mjAccordionTasksByJob[mjRemoveTaskPrompt.jobId] ?? [];
          const task = rows.find((row) => row.id === mjRemoveTaskPrompt.taskId) ?? null;
          const staying = Math.max(mjEmployeeTasks(rows).length - 1, 1);
          const range = task
            ? ` (employees ${String(task.employee_number_from)}-${String(task.employee_number_to)})`
            : "";
          return `Task #${String(mjRemoveTaskPrompt.taskId)}${range} leaves the job, and the roster is split again across the ${staying} task(s) that stay. Its logs are kept, so the employees it already processed keep counting and the next run does not repeat them.`;
        })()}
        confirmLabel="Remove"
        onConfirm={() => void handleMjRemoveOneTask()}
        onCancel={() => setMjRemoveTaskPrompt(null)}
        loading={isReducingJobTasks}
      />
      <ConfirmDialog
        open={mjSsnRunPrompt != null}
        title="Run without SSN matching?"
        message={
          mjSsnRunPrompt
            ? `${mjSsnRunPrompt.label}: SSN matching is not available - ${mjSsnRunPrompt.readiness.reason}. The bot will match employees by name only, which is less accurate: two employees with the same name can be confused. Create the SSN job(s) and let them finish first, or continue by names.`
            : ""
        }
        confirmLabel="Continue by names"
        onConfirm={() => {
          const prompt = mjSsnRunPrompt;
          setMjSsnRunPrompt(null);
          if (prompt) void prompt.proceed();
        }}
        onCancel={() => setMjSsnRunPrompt(null)}
      />
      <ConfirmDialog
        open={matchesReviewPrompt != null}
        title={(() => {
          const prompt = matchesReviewPrompt;
          if (!prompt) return "";
          if (prompt.action === "clear") return "Clear the reviewed marks?";
          const pending = matchesReview?.[prompt.kind]?.pending ?? 0;
          return `Mark ${pending} employee${pending === 1 ? "" : "s"} as reviewed - no match found?`;
        })()}
        message={
          matchesReviewPrompt ? (
            <UnmatchedReviewConfirmBody
              kind={matchesReviewPrompt.kind}
              action={matchesReviewPrompt.action}
              summary={matchesReview?.[matchesReviewPrompt.kind] ?? null}
              hint={mjRunHint(mjEffectiveManagedJob ?? {})}
            />
          ) : (
            ""
          )
        }
        confirmLabel={matchesReviewPrompt?.action === "clear" ? "Clear marks" : "No match found"}
        onConfirm={() => void confirmUnmatchedReview()}
        onCancel={() => matchesReviewBusy == null && setMatchesReviewPrompt(null)}
        loading={matchesReviewBusy != null}
      />
      <ConfirmDialog
        open={mjRosterDeletePrompt != null}
        title={
          mjRosterInfo?.info.kind === "matches" ? "Delete all matches files?" : "Delete the roster?"
        }
        message={(() => {
          const info = mjRosterInfo?.info;
          const shared = info?.sibling_job_ids.length
            ? ` Job(s) ${info.sibling_job_ids.map((id) => `#${id}`).join(", ")} read the same file and lose it too.`
            : "";
          // The reindex question, once the server has asked it: tasks of this
          // job have run, and a new file puts their employees on other rows.
          const reindex = mjRosterDeletePrompt?.reindex
            ? ` ${mjRosterDeletePrompt.reindex}`
            : "";
          return info?.kind === "matches"
            ? `The Matches, Several matches and No matches files of this job (and the matching summary) are deleted - the job's own files and the legacy shared ones. The next run matches again from the source folder.${shared} A rebuilt file puts the employees on other rows; their logs follow them.${reindex}`
            : `The roster of this job is deleted - its own file and the shared copy it may still fall back on. The next run collects it again from the vendor.${shared} A rebuilt roster puts the employees on other rows; their logs follow them.${reindex}`;
        })()}
        confirmLabel={mjRosterDeletePrompt?.reindex ? "Delete anyway" : "Delete"}
        onConfirm={() => void confirmMjRosterDelete()}
        onCancel={() => setMjRosterDeletePrompt(null)}
        loading={mjRosterDeleting}
      />
      <ConfirmDialog
        open={mjBrowserProfilesPrompt}
        title="Delete the browser profile?"
        message={`The stored Chrome profile(s) of ${activeMajor?.vendor ?? "this vendor"} for this user are deleted (${mjBrowserProfiles?.profiles.length ?? 0} file(s)). Every bot of this user on this vendor logs in from scratch on its next run - with 2FA or a captcha - and a fresh profile is stored after that login. ${mjBrowserProfiles?.jobs_using ?? 0} job(s) use it.`}
        confirmLabel="Delete"
        onConfirm={() => void confirmMjBrowserProfilesDelete()}
        onCancel={() => setMjBrowserProfilesPrompt(false)}
        loading={mjBrowserProfilesBusy}
      />
      <AppDialog
        open={mjArtifactUpload != null}
        onClose={() =>
          mjArtifactUpload?.step !== "uploading" &&
          mjArtifactUpload?.step !== "replacing" &&
          setMjArtifactUpload(null)
        }
        title={mjArtifactUpload?.purpose === "roster" ? "Upload roster file" : "Upload matches file"}
        actions={
          <>
            <Button
              onClick={() => setMjArtifactUpload(null)}
              disabled={mjArtifactUpload?.step === "uploading" || mjArtifactUpload?.step === "replacing"}
            >
              Close
            </Button>
            {mjArtifactUpload?.step !== "done" && (
              <Button
                variant="contained"
                disabled={
                  mjArtifactUpload?.step !== "ready" ||
                  !canEditBots ||
                  Boolean(mjArtifactUpload?.reindex && !mjArtifactUpload.confirmReindex)
                }
                onClick={() => void confirmMjArtifactReplace()}
              >
                Replace
              </Button>
            )}
          </>
        }
      >
        <Stack spacing={1.25}>
          <Typography variant="body2" color="text.secondary">
            {mjArtifactUpload?.purpose === "roster"
              ? "A JSON list of employees in the shape the bot writes (first_name, last_name, employee_id). It replaces the roster of this job only; the next run walks this list."
              : "A reviewed matches file (CSV, XLS or XLSX) with s3_name, site_name, employee_id - the header row is optional, two columns are read as s3_name and employee_id. It replaces the Matches file whole; the Several matches and No matches files stay as they are and are marked outdated."}
          </Typography>
          <Button
            component="label"
            variant="outlined"
            size="small"
            disabled={mjArtifactUpload?.step === "uploading" || mjArtifactUpload?.step === "replacing"}
          >
            {mjArtifactUpload?.file ? mjArtifactUpload.file.name : "Choose file"}
            <input
              type="file"
              hidden
              accept={mjArtifactUpload?.purpose === "roster" ? ".json" : ".csv,.xls,.xlsx"}
              onChange={(e) => void handleMjArtifactFileSelect(e)}
            />
          </Button>
          {mjArtifactUpload?.step === "uploading" && <LinearProgress />}
          {mjArtifactUpload?.step === "replacing" && <LinearProgress />}
          {mjArtifactUpload?.reindex && (
            <Alert severity="warning">
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={mjArtifactUpload.confirmReindex}
                    onChange={(e) =>
                      setMjArtifactUpload((prev) =>
                        prev ? { ...prev, confirmReindex: e.target.checked } : prev,
                      )
                    }
                  />
                }
                label={mjArtifactUpload.reindex.message}
              />
            </Alert>
          )}
          {mjArtifactUpload?.error && <Alert severity="error">{mjArtifactUpload.error}</Alert>}
          {mjArtifactUpload?.result && <Alert severity="success">{mjArtifactUpload.result}</Alert>}
        </Stack>
      </AppDialog>
      <ConfirmDialog
        open={mjRosterReindexPrompt != null}
        title="Collect the roster again?"
        message={mjRosterReindexPrompt?.message ?? ""}
        confirmLabel="Build anyway"
        onConfirm={() => {
          const jobId = mjRosterReindexPrompt?.jobId;
          setMjRosterReindexPrompt(null);
          if (jobId != null) void mjRunRosterTask(jobId, false, true);
        }}
        onCancel={() => setMjRosterReindexPrompt(null)}
        loading={mjRosterTaskBusyJobId != null}
      />
      <AppDialog
        open={mjRosterExistsPrompt != null}
        onClose={() => setMjRosterExistsPrompt(null)}
        title="The roster is already built"
        actions={
          <>
            <Button onClick={() => setMjRosterExistsPrompt(null)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={() =>
                mjRosterExistsPrompt && void mjRunRosterTask(mjRosterExistsPrompt.jobId, true)
              }
            >
              Run anyway
            </Button>
          </>
        }
      >
        <Stack spacing={1}>
          <Typography variant="body2">{mjRosterExistsPrompt?.message}</Typography>
          {mjRosterExistsPrompt?.files.length ? (
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {mjRosterExistsPrompt.files.map((file) => (
                <Typography key={file} component="li" variant="caption" sx={{ wordBreak: "break-all" }}>
                  {file}
                </Typography>
              ))}
            </Box>
          ) : null}
          <Typography variant="body2" color="text.secondary">
            Delete the file from Manage job to rebuild it, or run anyway - the bot will log in and reuse the stored file.
          </Typography>
        </Stack>
      </AppDialog>
      <AppDialog
        open={mjNewRowsJob != null}
        onClose={() => setMjNewRowsJob(null)}
        maxWidth="xs"
        title="Run merged employees"
        actions={
          <>
            <Button onClick={() => setMjNewRowsJob(null)}>Cancel</Button>
            <Button
              variant="contained"
              disabled={
                !canEditBots ||
                mjNewRowsRunningJobId != null ||
                !(Number(mjNewRowsTaskCount) >= 1)
              }
              onClick={() => {
                if (!mjNewRowsJob) return;
                void runNewRosterRows(
                  mjNewRowsJob.id,
                  Math.max(1, Math.trunc(Number(mjNewRowsTaskCount) || 1)),
                );
              }}
            >
              Run
            </Button>
          </>
        }
      >
        <Stack spacing={1.5}>
          <Typography variant="body2">
            {mjNewRowsJob?.rows ?? 0} employee(s) in the Matches file have never
            been run - normally the ones added through Merge reports. They get
            tasks of their own; the tasks that already ran keep their employees
            and their logs, and are not restarted.
          </Typography>
          <TextField
            size="small"
            type="number"
            label="Split across how many tasks"
            value={mjNewRowsTaskCount}
            onChange={(event) => setMjNewRowsTaskCount(event.target.value)}
            inputProps={{ min: 1, max: 30 }}
            helperText="One bot container per task. Keep it within what the vendor tolerates at once."
          />
        </Stack>
      </AppDialog>

      <AppDialog
        open={mjClearLogsJobId != null}
        onClose={() => setMjClearLogsJobId(null)}
        maxWidth="xs"
        title="Clear all logs?"
        actions={
          <>
            <Button onClick={() => setMjClearLogsJobId(null)}>Cancel</Button>
            <Button
              color="warning"
              variant="contained"
              disabled={isMjClearLogsLoading || !canEditBots}
              onClick={() => void handleMjClearJobLogs()}
            >
              Clear logs
            </Button>
          </>
        }
      >
        <Stack spacing={1.5}>
          <Typography variant="body2">
            This permanently deletes this job's stored logs
            {mjClearLogsInventory ? ` (${mjClearLogsInventory.own} archive(s))` : ""} and the
            error screenshots their records name. The next run will re-process every employee
            from scratch. Continue?
          </Typography>
          {mjClearLogsInventory && mjClearLogsInventory.shared > 0 ? (
            <>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={mjClearLogsIncludeShared}
                    onChange={(event) =>
                      setMjClearLogsIncludeShared(event.target.checked)
                    }
                  />
                }
                label={
                  <Typography variant="body2">
                    {`Also clear the archives of other jobs in this folder (${mjClearLogsInventory.shared})`}
                  </Typography>
                }
              />
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                The folder is shared by every job of this document type, and a bot
                reads all of it when it starts. Leave this off to keep the other
                jobs' history - the next run will then still skip the employees
                those archives cover.
              </Typography>
            </>
          ) : null}
        </Stack>
      </AppDialog>

      <AppDialog
        open={ssnJobDeleteConfirmOpen}
        onClose={() => setSsnJobDeleteConfirmOpen(false)}
        maxWidth="xs"
        title="Delete SSN job?"
        actions={
          <>
            <Button onClick={() => setSsnJobDeleteConfirmOpen(false)}>Cancel</Button>
            <Button
              color="error"
              variant="contained"
              disabled={ssnJobDeleting || !canEditBots}
              onClick={async () => {
                if (!activeMajor || !canEditBots) return;
                setSsnJobDeleting(true);
                setSsnJobDeleteConfirmOpen(false);
                try {
                  await deleteSsnJobMut(activeMajor.id).unwrap();
                  await loadSsnJob(activeMajor.id);
                } catch (e: unknown) {
                  setSsnJobError(
                    (e as { data?: { message?: string } })?.data?.message ??
                      "Failed to delete SSN job.",
                  );
                } finally { setSsnJobDeleting(false); }
              }}
            >
              Delete
            </Button>
          </>
        }
      >
        <Typography variant="body2">
          This will permanently remove the SSN job and its task. This major job&apos;s SSN file stays; Delete file removes it.
        </Typography>
      </AppDialog>

      <AppDialog
        open={twoFaMjTask != null}
        onClose={() => {
          setTwoFaMjTask(null);
          setTwoFaMjCode("");
          setTwoFaMjErr(null);
        }}
        maxWidth="xs"
        title="2FA code"
        actions={
          <>
            <Button
              onClick={() => {
                setTwoFaMjTask(null);
                setTwoFaMjCode("");
                setTwoFaMjErr(null);
              }}
            >
              Cancel
            </Button>
            <Button variant="contained" disabled={isSubmit2FAMj || !canEditBots} onClick={() => void handleMjSubmit2FACode()}>
              Submit
            </Button>
          </>
        }
      >
        {twoFaMjTask ? (
          <Typography variant="body2" sx={{ mb: 1 }}>
            Task{(() => {
              const jid = typeof twoFaMjTask.job === "number" ? twoFaMjTask.job : null;
              if (jid == null) return "";
              const ord = taskOrdinalInJob(jid, twoFaMjTask.id);
              return ord != null ? ` #${ord}` : "";
            })()}
          </Typography>
        ) : null}
        <TextField
          fullWidth
          autoFocus
          label="Code"
          value={twoFaMjCode}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setTwoFaMjCode(e.target.value)}
          onKeyDown={(ev) => {
            if (
              ev.key === "Enter" &&
              !ev.shiftKey &&
              twoFaMjCode.trim().length > 0 &&
              !isSubmit2FAMj &&
              canEditBots
            ) {
              ev.preventDefault();
              void handleMjSubmit2FACode();
            }
          }}
          inputProps={{ inputMode: "numeric", autoComplete: "one-time-code" }}
        />
        {twoFaMjErr ? (
          <Alert severity="error" sx={{ mt: 1 }}>
            {twoFaMjErr}
          </Alert>
        ) : null}
      </AppDialog>

      <AppDialog
        open={mjManageSelectionJobId != null}
        onClose={() => setMjManageSelectionJobId(null)}
        maxWidth="sm"
        title="Manage selected tasks"
        actions={<Button onClick={() => setMjManageSelectionJobId(null)}>Close</Button>}
      >
        {mjManageSelectionJobId != null &&
          (() => {
            const jobId = mjManageSelectionJobId;
            const selected = mjSelectedTasks(jobId);
            const jobRow = mjJobRowById(jobId);
            const inFlight = jobRow ? mjJobAnythingInFlight(jobRow) : false;
            const rosterBuilding = jobRow ? mjRosterTaskInFlight(jobRow) : false;
            const plan = mjSelectionRemovalPlan(jobId);
            // A batch run leaves the roster task out, so counting it would offer a
            // Run the backend answers with "no tasks".
            const runnable = mjEmployeeTasks(selected).filter((task) =>
              canRunTaskByStatusMj(task.task_status),
            );
            const stoppable = selected.filter((task) =>
              mjTaskShowsStopInsteadOfRun(task.task_status),
            );
            const rosterOnlyPicked = selected.length - mjEmployeeTasks(selected).length;
            const busy = mjSelectionBusyAction;
            const actions = [
              {
                key: "remove",
                label: `Remove ${plan.removed.length || ""} task(s)`.replace("  ", " "),
                hint: inFlight
                  ? "Stop the job's queued and running tasks first."
                  : plan.removed.length === 0
                    ? "Nothing here can be removed."
                    : plan.survivor
                      ? `Everything is ticked, so task #${String(plan.survivor.id)} stays and takes the whole roster. Their logs are kept and their employees stay processed.`
                      : "Their logs are kept and their employees stay processed. The roster is split again across the tasks that stay.",
                disabled: inFlight || plan.removed.length === 0,
                run: async () => {
                  await reduceJobTasksMutation({
                    jobId,
                    task_ids: plan.removed.map((task) => task.id),
                  }).unwrap();
                },
              },
              {
                key: "run",
                label: `Run ${runnable.length || ""} task(s)`.replace("  ", " "),
                hint:
                  rosterBuilding && jobRow
                    ? rosterBuildReason(mjIsUploadJob(jobRow))
                    : runnable.length === 0
                      ? "None of the ticked tasks is in a state that can be started."
                      : "Only these tasks are started; the rest of the job is left alone.",
                disabled: runnable.length === 0 || rosterBuilding,
                run: async () => {
                  const report = await runTasksBatchMut({
                    task_ids: runnable.map((task) => task.id),
                  }).unwrap();
                  // Nothing started because a task still logs in: say so, as Run all does.
                  if (!report.started_task_ids?.length && report.queued_task_ids?.length) {
                    setMjJobNoticesByJobId((prev) => ({ ...prev, [jobId]: report.message }));
                  }
                },
              },
              {
                key: "stop",
                label: `Stop ${stoppable.length || ""} task(s)`.replace("  ", " "),
                hint:
                  stoppable.length === 0
                    ? "None of the ticked tasks is running."
                    : "Only these containers are stopped.",
                disabled: stoppable.length === 0,
                run: async () => {
                  await stopTasksBatchMut({
                    task_ids: stoppable.map((task) => task.id),
                  }).unwrap();
                },
              },
            ];
            return (
              <Stack spacing={1.25}>
                <Typography variant="body2">
                  {`${selected.length} task(s) selected: ${selected
                    .map((task) => `#${String(task.id)}`)
                    .join(", ")}`}
                </Typography>
                {rosterOnlyPicked > 0 ? (
                  <Alert severity="info">
                    The roster-only task is not part of the split, so Remove leaves it
                    where it is.
                  </Alert>
                ) : null}
                {actions.map((action) => (
                  <Box key={action.key}>
                    <Button
                      fullWidth
                      variant="outlined"
                      disabled={action.disabled || busy != null || !canEditBots}
                      startIcon={
                        busy === action.key ? <CircularProgress size={14} /> : undefined
                      }
                      onClick={() =>
                        void mjRunSelectionAction(jobId, action.key, action.run)
                      }
                    >
                      {action.label}
                    </Button>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mt: 0.5 }}
                    >
                      {action.hint}
                    </Typography>
                  </Box>
                ))}
                <Box>
                  <Button
                    fullWidth
                    variant="outlined"
                    disabled={selected.length === 0 || busy != null || !canEditBots}
                    onClick={() => {
                      setMjManageSelectionJobId(null);
                      setMjScheduleTarget({
                        kind: "tasks",
                        jobId,
                        taskIds: selected.map((task) => task.id),
                      });
                    }}
                  >
                    {`Schedule ${selected.length} task(s)`}
                  </Button>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mt: 0.5 }}
                  >
                    One booking per task, so Slack is warned once per task 30 minutes
                    before.
                  </Typography>
                </Box>
              </Stack>
            );
          })()}
      </AppDialog>

      <DataStorageFolderPickerDialog
        open={folderPickerOpen}
        onClose={() => setFolderPickerOpen(false)}
        onConfirm={confirmFolderPicker}
        userId={userId}
        initialPath={folderPickerInitialPath}
      />

      {/* ── SSN File Viewer Dialog ────────────────────────────────────────────── */}
      <AppDialog
        open={ssnFileViewOpen}
        onClose={() => setSsnFileViewOpen(false)}
        maxWidth="lg"
        title="ssn_info.json"
        actions={
          <>
            <Button
              startIcon={<FileDownloadIcon fontSize="small" />}
              onClick={() => activeMajor && void downloadSsnFile(activeMajor.id)}
            >
              Download
            </Button>
            <Button onClick={() => setSsnFileViewOpen(false)}>Close</Button>
          </>
        }
      >
        {ssnFileViewOpen && activeMajor ? (
          <SsnFileView
            majorJobId={activeMajor.id}
            onDownload={() => void downloadSsnFile(activeMajor.id)}
          />
        ) : null}
      </AppDialog>

      {activeMajor ? (
        <SsnFileUploadDialog
          open={ssnUploadOpen}
          majorJobId={activeMajor.id}
          majorJobName={activeMajor.name}
          onClose={() => setSsnUploadOpen(false)}
          onReplaced={async () => {
            // The tile, the header's SSN check and the matching preflight all
            // read the file: they are re-read before the operator sees them.
            await loadSsnJob(activeMajor.id);
            await refetch();
          }}
        />
      ) : null}

      {/* ── Roster Viewer Dialog ──────────────────────────────────────────────── */}
      <AppDialog
        open={rosterViewJobId != null}
        onClose={() => setRosterViewJobId(null)}
        maxWidth="lg"
        title={mjIsUploadJob(mjEffectiveManagedJob ?? {}) ? "Matches rows" : "Roster"}
        actions={
          <>
            <Button
              startIcon={<FileDownloadIcon fontSize="small" />}
              onClick={() =>
                rosterViewJobId != null && void downloadRoster(rosterViewJobId)
              }
            >
              Download
            </Button>
            <Button onClick={() => setRosterViewJobId(null)}>Close</Button>
          </>
        }
      >
        {rosterViewJobId != null ? (
          <RosterFileView
            jobId={rosterViewJobId}
            onDownload={() => void downloadRoster(rosterViewJobId)}
          />
        ) : null}
      </AppDialog>

      {/* ── Matches CSV Viewer Dialog ─────────────────────────────────────────── */}
      <AppDialog
        open={matchesViewOpen}
        onClose={() => setMatchesViewOpen(false)}
        maxWidth="lg"
        title={matchesFileLabel(matchesViewFilename)}
        actions={
          <>
            <Typography variant="caption" color="text.secondary" sx={{ flex: 1, ml: 1 }}>
              {(() => {
                const count = Math.max(matchesViewRows.length - 1, 0);
                if (!count) return "";
                if (!matchesViewKind) return `${count} row${count === 1 ? "" : "s"}`;
                return unmatchedReviewLegend(
                  matchesViewReview?.review[matchesViewKind] ?? matchesReview?.[matchesViewKind],
                  count,
                );
              })()}
            </Typography>
            <Button
              startIcon={<FileDownloadIcon fontSize="small" />}
              onClick={() => mjManageJobId != null && void downloadMatchesFile(mjManageJobId, matchesViewFilename)}
              disabled={matchesViewLoading}
            >
              Download
            </Button>
            <Button onClick={() => setMatchesViewOpen(false)}>Close</Button>
          </>
        }
      >
        {matchesViewRows.length === 0 && !matchesViewLoading && (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: "center" }}>
            (empty)
          </Typography>
        )}
        {matchesViewRows.length > 0 && (
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {matchesViewRows[0].map((col, ci) => (
                  <TableCell key={ci} sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                    {col}
                  </TableCell>
                ))}
                {matchesViewKind && matchesViewReview ? (
                  <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>Status</TableCell>
                ) : null}
              </TableRow>
            </TableHead>
            <TableBody>
              {matchesViewRows.slice(1).map((row, ri) => (
                <TableRow key={ri} hover>
                  {row.map((cell, ci) => (
                    <TableCell key={ci} sx={{ fontSize: "0.8rem", maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {cell}
                    </TableCell>
                  ))}
                  {matchesViewKind && matchesViewReview ? (
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      <UnmatchedEmployeeStateChip
                        status={matchesViewReview.statuses[matchesViewKind]?.[(row[0] ?? "").trim()] ?? null}
                        onOpenTaskLogs={(taskId, employeeId) => openTaskLogs(taskId, { search: employeeId })}
                      />
                    </TableCell>
                  ) : null}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </AppDialog>

      {/* ── Matches Merge Reports Dialog ──────────────────────────────────────── */}
      <AppDialog
        open={matchesMerge != null}
        onClose={() => {
          if (!mergeReportsBusy) resetMatchesMergeState();
        }}
        maxWidth="md"
        title="Merge reports"
        actions={
          <>
            <Button onClick={resetMatchesMergeState} disabled={mergeReportsBusy}>
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={!mergeReportsReady || mergeReportsBusy || !canEditBots}
              onClick={() => void confirmMatchesMerge()}
              startIcon={
                matchesMerge?.step === "merging" ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <MergeTypeIcon fontSize="small" />
                )
              }
            >
              {matchesMerge?.step === "merging" ? "Merging…" : "Merge"}
            </Button>
          </>
        }
      >
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Upload the reviewed report files. Rows are appended to the Matches file: No matches first,
          then Several matches; rows already present are skipped. Each file is checked against the
          expected format first - if either file has a problem, nothing is merged.
        </Typography>
        {matchesMerge && (
          <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
            {MERGE_REPORT_KINDS.map((kind) => (
              <MergeReportsSlot
                key={kind}
                kind={kind}
                slot={matchesMerge.slots[kind]}
                disabled={!canEditBots || matchesMerge.step === "merging"}
                onSelect={(e) => void handleMergeSlotFileSelect(kind, e)}
                onClear={() => clearMergeSlot(kind)}
                onToggleExample={() =>
                  updateMergeSlot(kind, { showExample: !matchesMerge.slots[kind].showExample })
                }
              />
            ))}
          </Stack>
        )}
        {matchesMerge?.error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {matchesMerge.error}
          </Alert>
        )}
      </AppDialog>

      {/* The operator's window into a bot's browser */}
      <TaskLiveViewDialog
        taskId={liveViewMjTaskId}
        canEdit={canEditBots}
        onClose={() => {
          if (liveViewMjTaskId != null) {
            setLiveViewMjDismissed((prev) =>
              prev.includes(liveViewMjTaskId)
                ? prev
                : [...prev, liveViewMjTaskId],
            );
          }
          setLiveViewMjTaskId(null);
        }}
      />

      {/* ── Matches Delete Confirmation ───────────────────────────────────────── */}
      <AppDialog
        open={matchesDeleteFilename != null}
        onClose={() => !matchesDeleting && setMatchesDeleteFilename(null)}
        maxWidth="xs"
        title="Delete matches file?"
        actions={
          <>
            <Button onClick={() => setMatchesDeleteFilename(null)} disabled={matchesDeleting}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={() => mjManageJobId != null && void confirmDeleteMatchesFile(mjManageJobId)}
              disabled={matchesDeleting || !canEditBots}
              startIcon={matchesDeleting ? <CircularProgress size={14} color="inherit" /> : undefined}
            >
              {matchesDeleting ? "Deleting…" : "Delete"}
            </Button>
          </>
        }
      >
        <Typography variant="body2">
          Are you sure you want to delete{" "}
          <strong>
            {matchesFileLabel(matchesDeleteFilename ?? "")}
          </strong>
          ? This action cannot be undone.
        </Typography>
      </AppDialog>

      {/* ── Logs Dialog (All logs / File logs / Status logs) ─────────────────── */}
      <LogsDialog
        open={logsScope != null}
        onClose={() => setLogsScope(null)}
        title={logsTitle}
        scope={logsScope}
        initialSearch={logsSearch}
        canEdit={canEditBots}
        readOnlyReason={botsReadOnlyReason}
      />

      {/* ── Send rejected files to SFTP ───────────────────────────────────────── */}
      <SftpExportDialog
        jobId={mjSftpExportJobId}
        jobName={(() => {
          if (mjSftpExportJobId == null) return "";
          const found = (childJobs as Record<string, unknown>[]).find(
            (item) => Number(item.id) === mjSftpExportJobId,
          );
          return found
            ? mjJobDisplayName({ ...found, ...(mjJobDetailById[mjSftpExportJobId] ?? {}) })
            : `Job #${mjSftpExportJobId}`;
        })()}
        canEdit={canEditBots}
        readOnlyReason={botsReadOnlyReason}
        onClose={() => setMjSftpExportJobId(null)}
      />

      {/* ── SSN Updater ───────────────────────────────────────────────────────── */}
      <SsnUpdaterDialog
        jobId={mjSsnUpdaterJobId}
        jobName={
          mjSsnUpdaterJobId != null ? mjJobDisplayName(mjJobRowById(mjSsnUpdaterJobId) ?? {}) : ""
        }
        canEdit={canEditBots}
        readOnlyReason={botsReadOnlyReason}
        onClose={() => setMjSsnUpdaterJobId(null)}
      />

      <ScheduleRunDialog
        open={mjScheduleTarget != null}
        what={
          mjScheduleTarget?.kind === "task"
            ? "this task"
            : mjScheduleTarget?.kind === "tasks"
              ? `${mjScheduleTarget.taskIds.length} selected task(s)`
              : "Run all"
        }
        existing={
          mjScheduleTarget != null && mjScheduleTarget.kind !== "tasks"
            ? mjScheduleTarget.existing
            : null
        }
        saving={isSchedulingJob || isSchedulingTask}
        onClose={() => setMjScheduleTarget(null)}
        onSubmit={mjSubmitSchedule}
      />

      <ConfirmDialog
        open={mjUnscheduleTarget != null}
        title="Cancel the scheduled run?"
        message={
          mjUnscheduleTarget
            ? `${scheduledRunTooltip(mjUnscheduleTarget.run)} Cancelling leaves the job where it is - nothing starts by itself.`
            : ""
        }
        confirmLabel="Cancel the run"
        onConfirm={() => void mjCancelSchedule()}
        onCancel={() => setMjUnscheduleTarget(null)}
      />

      {/* ── Task error popup (clickable status chip) ──────────────────────────── */}
      <AppDialog
        open={mjErrorPopupTask != null}
        onClose={() => setMjErrorPopupTask(null)}
        maxWidth="xs"
        title="Task error"
        actions={
          <>
            <Button onClick={() => setMjErrorPopupTask(null)}>Close</Button>
            {(() => {
              const blockedBy = mjErrorPopupTask
                ? mjTaskRunBlockedReason(
                    mjJobRowById(mjErrorPopupTask.jobId),
                    mjErrorPopupTask.task,
                  )
                : null;
              return (
                <Tooltip title={blockedBy ?? ""}>
                  <span>
                    <Button
                      variant="contained"
                      disabled={mjJobActionBusy || !canEditBots || blockedBy != null}
                      onClick={() => {
                        if (mjErrorPopupTask) {
                          void mjRunTaskRow(mjErrorPopupTask.jobId, mjErrorPopupTask.task);
                        }
                        setMjErrorPopupTask(null);
                      }}
                    >
                      Rerun
                    </Button>
                  </span>
                </Tooltip>
              );
            })()}
          </>
        }
      >
        <Alert severity="error" sx={{ mt: 1 }}>
          The bot ran into errors. Please rerun the task or contact the dev team.
        </Alert>
      </AppDialog>
    </Box>
  );
}
